import { sql } from "drizzle-orm";
export { reachoutInvitations, reachoutLimits } from "./reachoutSchema";
export { headphoneOrders, headphoneIdentityLocks } from "./headphoneShopSchema";
export { customerCreditNotes, customerCreditOperations, customerCreditAudit } from "./creditSchema";
export {
  salesCalendarConnections,
  salesCalendarOauthStates,
  salesAppointments,
  salesAppointmentAuditEvents,
} from "./salesAppointmentsSchema";
export type {
  SalesCalendarConnection,
  InsertSalesCalendarConnection,
  SalesAppointment,
  InsertSalesAppointment,
  SalesAppointmentAuditEvent,
} from "./salesAppointmentsSchema";
export {
  appointmentReminderSends,
  appointmentReminderEvents,
  whatsappOptOuts,
} from "./appointmentRemindersSchema";
export {
  appointmentMinutesRequests,
  salesMinutesMailboxConnections,
  salesMinutesOauthStates,
} from "./appointmentMinutesSchema";
export type { AppointmentMinutesRequest } from "./appointmentMinutesSchema";
export type {
  AppointmentReminderSend,
  AppointmentReminderEvent,
  WhatsappOptOut,
} from "./appointmentRemindersSchema";
import {
  bigint,
  boolean,
  date,
  int,
  mysqlEnum,
  longtext,
  mediumtext,
  mysqlTable,
  text,
  timestamp,
  varchar,
  decimal,
  json,
  index,
  uniqueIndex,
} from "drizzle-orm/mysql-core";

// ─── Users (staff portal logins) ───────────────────────────────────────────
export const users = mysqlTable("users", {
  id: int("id").autoincrement().primaryKey(),
  openId: varchar("openId", { length: 64 }).notNull().unique(),
  name: text("name"),
  email: varchar("email", { length: 320 }),
  loginMethod: varchar("loginMethod", { length: 64 }),
  passwordHash: varchar("passwordHash", { length: 255 }), // bcrypt hash — null for legacy/OAuth users
  role: mysqlEnum("role", [
    "sales",
    "admin",
    "service",
    "finance",
    "programming",
    "installer",
    "marketing",
    "inventory",
    "club",
  ])
    .default("sales")
    .notNull(),
  /**
   * Zero or more secondary roles (JSON string array of UserRole).
   * Participates in permission + notification resolution alongside primary `role`.
   */
  secondaryRolesJson: json("secondaryRolesJson").$type<string[] | null>(),
  /**
   * Personal default portal module: sales | service | procurement.
   * Applied on login / bare portal root only — never forces redirects off deep links.
   */
  defaultPortalModule: varchar("defaultPortalModule", { length: 32 }),
  contact: varchar("contact", { length: 50 }), // phone number for PIC email footer
  /** Optional admin-selected color used for sales dashboard bars/legend (hex, e.g. #1d4ed8). */
  color: varchar("color", { length: 16 }),
  isActive: boolean("isActive").default(true).notNull(), // false for invited-but-not-activated users
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  lastSignedIn: timestamp("lastSignedIn").defaultNow().notNull(),
  /** Last successful staff Portal authentication (not request activity or impersonation). */
  lastPortalLoginAt: timestamp("lastPortalLoginAt"),
  genericEmailNotificationsEnabled: boolean("genericEmailNotificationsEnabled")
    .default(true)
    .notNull(),
  genericWhatsAppNotificationsEnabled: boolean(
    "genericWhatsAppNotificationsEnabled"
  )
    .default(true)
    .notNull(),
  notificationPreferencesUpdatedAt: timestamp(
    "notificationPreferencesUpdatedAt"
  ),
  notificationPreferencesUpdatedByUserId: int(
    "notificationPreferencesUpdatedByUserId"
  ),
});

export type User = typeof users.$inferSelect;
export type InsertUser = typeof users.$inferInsert;

export const userGenericNotificationPrefAudit = mysqlTable(
  "user_generic_notification_pref_audit",
  {
    id: bigint("id", { mode: "number" }).autoincrement().primaryKey(),
    targetUserId: int("targetUserId").notNull(),
    changedByUserId: int("changedByUserId").notNull(),
    changedByName: varchar("changedByName", { length: 255 }),
    previousConfigJson: json("previousConfigJson")
      .$type<Record<string, unknown>>()
      .notNull(),
    newConfigJson: json("newConfigJson")
      .$type<Record<string, unknown>>()
      .notNull(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    targetIdx: index("idx_user_generic_notif_pref_audit_target").on(
      t.targetUserId
    ),
  })
);
export type UserGenericNotificationPrefAudit =
  typeof userGenericNotificationPrefAudit.$inferSelect;

// ─── Password Reset Tokens ──────────────────────────────────────────────────
export const passwordResetTokens = mysqlTable("password_reset_tokens", {
  id: int("id").autoincrement().primaryKey(),
  email: varchar("email", { length: 320 }).notNull(),
  tokenHash: varchar("tokenHash", { length: 128 }).notNull(), // SHA-256 hash of the token
  expiresAt: timestamp("expiresAt").notNull(),
  used: boolean("used").default(false).notNull(),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
});
export type PasswordResetToken = typeof passwordResetTokens.$inferSelect;

// ─── Invite Tokens (for pending staff onboarding) ───────────────────────────
export const inviteTokens = mysqlTable("invite_tokens", {
  id: int("id").autoincrement().primaryKey(),
  userId: int("userId").notNull(),
  email: varchar("email", { length: 320 }).notNull(),
  tokenHash: varchar("tokenHash", { length: 128 }).notNull(), // SHA-256 hash of the raw token
  expiresAt: timestamp("expiresAt").notNull(),
  used: boolean("used").default(false).notNull(),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
});
export type InviteToken = typeof inviteTokens.$inferSelect;

// ─── Staff (for "Reported By" dropdown, managed by admin) ──────────────────
export const staff = mysqlTable("staff", {
  id: int("id").autoincrement().primaryKey(),
  name: varchar("name", { length: 255 }).notNull(),
  active: boolean("active").default(true).notNull(),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
});

export type Staff = typeof staff.$inferSelect;
export type InsertStaff = typeof staff.$inferInsert;

// ─── Technician Leave Blocks (Team Calendar only) ────────────────────────────
export const technicianLeaveBlocks = mysqlTable("technician_leave_blocks", {
  id: int("id").autoincrement().primaryKey(),
  userId: int("userId").notNull(), // users.id
  leaveDate: timestamp("leaveDate").notNull(), // date-only semantic (SGT)
  period: mysqlEnum("period", ["full_day", "am", "pm"])
    .notNull()
    .default("full_day"),
  notes: text("notes"),
  createdByUserId: int("createdByUserId").notNull(),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
});
export type TechnicianLeaveBlock = typeof technicianLeaveBlocks.$inferSelect;
export type InsertTechnicianLeaveBlock =
  typeof technicianLeaveBlocks.$inferInsert;

/** De-dup marker so same leave/visit/PIC alert is not resent repeatedly. */
export const technicianLeaveAlertLog = mysqlTable(
  "technician_leave_alert_log",
  {
    id: int("id").autoincrement().primaryKey(),
    leaveBlockId: int("leaveBlockId").notNull(),
    visitId: int("visitId").notNull(),
    picUserId: int("picUserId").notNull(),
    sentAt: timestamp("sentAt").defaultNow().notNull(),
  }
);
export type TechnicianLeaveAlertLog =
  typeof technicianLeaveAlertLog.$inferSelect;
export type InsertTechnicianLeaveAlertLog =
  typeof technicianLeaveAlertLog.$inferInsert;

// ─── Customers ──────────────────────────────────────────────────────────────
export const customers = mysqlTable("customers", {
  id: int("id").autoincrement().primaryKey(),
  name: varchar("name", { length: 255 }).notNull(),
  relationshipType: varchar("relationshipType", { length: 40 }),
  nameLower: varchar("nameLower", { length: 255 }).notNull(), // for case-insensitive search
  contact: varchar("contact", { length: 50 }),
  email: varchar("email", { length: 320 }),
  address: text("address"),
  unitNumber: varchar("unitNumber", { length: 50 }), // separate unit/apartment number field
  postalCode: varchar("postalCode", { length: 20 }), // Singapore postal code for address lookup
  notes: text("notes"),
  /** Sales free-text profile: room, setup, taste, pain points — drives proposal copy + Cleo (Phase H). */
  customerProfileText: text("customerProfileText"),
  customerProfileParsedJson: json("customerProfileParsedJson").$type<{
    room_size?: string | null;
    current_setup?: string | null;
    music_preference?: string | null;
    pain_points?: string | null;
  } | null>(),
  salespersonId: int("salespersonId"), // users.id of assigned salesperson
  salespersonName: varchar("salespersonName", { length: 255 }), // denormalised for display
  salesPortalCustomerId: varchar("salesPortalCustomerId", { length: 128 }), // cross-portal ID from Sales Portal
  salespersonEmail: varchar("salespersonEmail", { length: 320 }), // email of assigned salesperson (from Sales Portal)
  createdByUserId: int("createdByUserId"), // users.id of user who created this customer
  creationSource: mysqlEnum("creationSource", [
    "CUSTOMER_DIRECTORY",
    "DTOOLS_REVIEW",
    "PROPOSAL_UPLOAD",
    "PROPOSAL_BUILDER",
    "SERVICE_VISIT",
    "CALENDAR",
    "REPAIR_ORDER",
    "PROJECT",
    "CLUB_BOOKING",
    "IMPORT",
    "API",
    "OTHER",
    "LEGACY",
    "UNKNOWN",
  ]).default("LEGACY"),
  picUnresolved: boolean("picUnresolved").default(false).notNull(), // true if assignedEmail from Sales Portal didn't match any user
  /** When true, excluded from normal customer directory lists (unmatched address placeholders merged to a real customer). */
  hiddenFromList: boolean("hiddenFromList").default(false).notNull(),
  /**
   * Internal test customer (e.g. Cody Leraine). Still visible in CRM, but excluded from
   * automated reminders, Cleo signals and sales/revenue figures. Index: idx_customers_is_test (migration 0291).
   */
  isTest: boolean("isTest").default(false).notNull(),
  /** If this placeholder row was superseded by matching, points at the real customer (audit). */
  mergedIntoCustomerId: int("mergedIntoCustomerId"),
  /** When true, customer may request Clarity One login links. */
  customerPortalEnabled: boolean("customerPortalEnabled")
    .default(false)
    .notNull(),
  /**
   * Clarity One Wallet visibility.
   * NULL = never configured (OFF); false = explicit OFF; true = ON.
   * Independent of wallet row existence.
   */
  kaleidescapeWalletEnabled: boolean("kaleidescapeWalletEnabled"),
  /** Xero Contacts API ContactID for invoice sync (Phase 1). */
  xeroContactId: varchar("xeroContactId", { length: 64 }),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
});

export type Customer = typeof customers.$inferSelect;
export type InsertCustomer = typeof customers.$inferInsert;

/** Paid Clarity Club bookings awaiting an explicit internal confirmation. */
export const clubBookings = mysqlTable("club_bookings", {
  id: bigint("id", { mode: "number" }).autoincrement().primaryKey(),
  reference: varchar("reference", { length: 100 }).notNull().unique(),
  bookingId: varchar("bookingId", { length: 64 }).notNull(),
  customerId: int("customerId"),
  paymentRequestId: varchar("paymentRequestId", { length: 255 }).notNull().unique(),
  paymentId: varchar("paymentId", { length: 255 }),
  customerName: varchar("customerName", { length: 255 }).notNull(),
  customerEmail: varchar("customerEmail", { length: 320 }).notNull(),
  customerPhone: varchar("customerPhone", { length: 50 }).notNull(),
  venue: varchar("venue", { length: 255 }).notNull(),
  capacity: int("capacity").notNull(),
  bookingDate: date("bookingDate").notNull(),
  startTime: varchar("startTime", { length: 30 }).notNull(),
  durationHours: int("durationHours").notNull(),
  amountPaid: decimal("amountPaid", { precision: 12, scale: 2 }).notNull(),
  currency: varchar("currency", { length: 3 }).notNull().default("SGD"),
  comments: text("comments"),
  status: mysqlEnum("status", ["awaiting_confirmation", "confirmed", "voided"]).notNull().default("awaiting_confirmation"),
  paidAt: timestamp("paidAt").notNull(),
  clubInboxNotifiedAt: timestamp("clubInboxNotifiedAt"),
  staffEmailNotifiedAt: timestamp("staffEmailNotifiedAt"),
  staffWhatsAppNotifiedAt: timestamp("staffWhatsAppNotifiedAt"),
  staffNotificationError: text("staffNotificationError"),
  confirmedAt: timestamp("confirmedAt"),
  confirmedByUserId: int("confirmedByUserId"),
  voidedAt: timestamp("voidedAt"),
  voidedByUserId: int("voidedByUserId"),
  voidSyncedAt: timestamp("voidSyncedAt"),
  voidSyncError: text("voidSyncError"),
  clubSyncedAt: timestamp("clubSyncedAt"),
  clubSyncError: text("clubSyncError"),
  customerEmailSentAt: timestamp("customerEmailSentAt"),
  customerEmailError: text("customerEmailError"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
}, t => ({
  statusDateIdx: index("club_bookings_status_date_idx").on(t.status, t.bookingDate),
}));

export type ClubBooking = typeof clubBookings.$inferSelect;
export type InsertClubBooking = typeof clubBookings.$inferInsert;

/** Primary + alternative emails. Approval capture is not marketing consent. */
export const customerEmailAddresses = mysqlTable(
  "customer_email_addresses",
  {
    id: int("id").autoincrement().primaryKey(),
    customerId: int("customerId").notNull(),
    email: varchar("email", { length: 320 }).notNull(),
    normalizedEmail: varchar("normalizedEmail", { length: 320 }).notNull(),
    type: mysqlEnum("type", ["PRIMARY", "ALTERNATIVE"])
      .notNull()
      .default("ALTERNATIVE"),
    source: mysqlEnum("source", [
      "CUSTOMER_APPROVAL",
      "STAFF",
      "IMPORT",
      "OTHER",
    ])
      .notNull()
      .default("OTHER"),
    customerProvidedAt: timestamp("customerProvidedAt"),
    verifiedAt: timestamp("verifiedAt"),
    active: boolean("active").notNull().default(true),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    createdByUserId: int("createdByUserId"),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    customerNormUnique: uniqueIndex("uq_customer_email_norm").on(
      t.customerId,
      t.normalizedEmail
    ),
    customerIdx: index("idx_customer_email_customer").on(t.customerId),
  })
);
export type CustomerEmailAddress = typeof customerEmailAddresses.$inferSelect;
export type InsertCustomerEmailAddress =
  typeof customerEmailAddresses.$inferInsert;

export const CUSTOMER_ALIAS_SOURCES = [
  "DISPLAY_NAME",
  "STAFF",
  "MERGE",
  "REMEDIATION",
  "IMPORT",
  "OTHER",
] as const;

export const customerAliases = mysqlTable(
  "customer_aliases",
  {
    id: int("id").autoincrement().primaryKey(),
    customerId: int("customerId").notNull(),
    alias: varchar("alias", { length: 255 }).notNull(),
    normalizedAlias: varchar("normalizedAlias", { length: 255 }).notNull(),
    source: mysqlEnum("source", CUSTOMER_ALIAS_SOURCES)
      .notNull()
      .default("OTHER"),
    active: boolean("active").notNull().default(true),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    createdByUserId: int("createdByUserId"),
    mergedFromCustomerId: int("mergedFromCustomerId"),
  },
  t => ({
    customerNormUnique: uniqueIndex("uq_customer_alias_norm").on(
      t.customerId,
      t.normalizedAlias
    ),
    normIdx: index("idx_customer_alias_norm").on(t.normalizedAlias, t.active),
    customerIdx: index("idx_customer_alias_customer").on(
      t.customerId,
      t.active
    ),
  })
);
export type CustomerAlias = typeof customerAliases.$inferSelect;
export type InsertCustomerAlias = typeof customerAliases.$inferInsert;

export const CUSTOMER_PHONE_NUMBER_LABELS = [
  "Mobile",
  "Home",
  "Office",
  "Overseas",
  "Assistant",
  "Spouse",
  "Other",
] as const;

export const customerPhoneNumbers = mysqlTable(
  "customer_phone_numbers",
  {
    id: int("id").autoincrement().primaryKey(),
    customerId: int("customerId").notNull(),
    phone: varchar("phone", { length: 50 }).notNull(),
    normalizedPhone: varchar("normalizedPhone", { length: 50 }).notNull(),
    label: mysqlEnum("label", CUSTOMER_PHONE_NUMBER_LABELS)
      .notNull()
      .default("Mobile"),
    isPrimary: boolean("isPrimary").notNull().default(false),
    portalAccessAllowed: boolean("portalAccessAllowed")
      .notNull()
      .default(false),
    active: boolean("active").notNull().default(true),
    verifiedAt: timestamp("verifiedAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    createdByUserId: int("createdByUserId"),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
    removedAt: timestamp("removedAt"),
    removedByUserId: int("removedByUserId"),
  },
  t => ({
    customerNormUnique: uniqueIndex("uq_customer_phone_norm").on(
      t.customerId,
      t.normalizedPhone
    ),
    normActiveIdx: index("idx_customer_phone_norm_active").on(
      t.normalizedPhone,
      t.active
    ),
    customerActiveIdx: index("idx_customer_phone_customer_active").on(
      t.customerId,
      t.active,
      t.isPrimary
    ),
  })
);
export type CustomerPhoneNumber = typeof customerPhoneNumbers.$inferSelect;
export type InsertCustomerPhoneNumber =
  typeof customerPhoneNumbers.$inferInsert;

export const customerCreationOverrideAudits = mysqlTable(
  "customer_creation_override_audits",
  {
    id: int("id").autoincrement().primaryKey(),
    newCustomerId: int("newCustomerId").notNull(),
    createdByUserId: int("createdByUserId").notNull(),
    creationSource: varchar("creationSource", { length: 40 }).notNull(),
    reason: varchar("reason", { length: 500 }).notNull(),
    reviewedCandidateIdsJson: json("reviewedCandidateIdsJson")
      .$type<number[]>()
      .notNull(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  }
);
export type CustomerCreationOverrideAudit =
  typeof customerCreationOverrideAudits.$inferSelect;

// ─── Products / Devices ─────────────────────────────────────────────────────
export const PRODUCT_CATEGORIES = [
  "Amplifier",
  "Preamplifier",
  "Integrated Amplifier",
  "Processor",
  "CD Player",
  "Network Player",
  "DAC",
  "Streamer",
  "Speaker",
  "Power Conditioner",
  "Turntable",
  "Headphone Amplifier",
  "Headphones",
  "Other",
] as const;

export type ProductCategory = (typeof PRODUCT_CATEGORIES)[number];

export const products = mysqlTable("products", {
  id: int("id").autoincrement().primaryKey(),
  serialNumber: varchar("serialNumber", { length: 255 }).notNull().unique(),
  brand: varchar("brand", { length: 255 }).notNull(),
  model: varchar("model", { length: 255 }).notNull(),
  category: varchar("category", { length: 100 }), // product category for sales funnel
  customerId: int("customerId"), // current owner
  warrantyStatus: mysqlEnum("warrantyStatus", ["yes", "no", "unknown"]).default(
    "unknown"
  ),
  warrantyExpiry: timestamp("warrantyExpiry"),
  notes: text("notes"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
});

export type Product = typeof products.$inferSelect;
export type InsertProduct = typeof products.$inferInsert;

// ─── Purchase Records ────────────────────────────────────────────────────────
export const purchaseRecords = mysqlTable("purchase_records", {
  id: int("id").autoincrement().primaryKey(),
  productId: int("productId").notNull(),
  customerId: int("customerId").notNull(),
  purchaseDate: timestamp("purchaseDate"),
  invoiceNumber: varchar("invoiceNumber", { length: 100 }),
  invoiceUrl: text("invoiceUrl"), // S3 URL
  invoiceKey: text("invoiceKey"), // S3 key
  notes: text("notes"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
});

export type PurchaseRecord = typeof purchaseRecords.$inferSelect;
export type InsertPurchaseRecord = typeof purchaseRecords.$inferInsert;

// ─── Job Number Counter (per year+month) ────────────────────────────────────
export const jobCounters = mysqlTable("job_counters", {
  id: int("id").autoincrement().primaryKey(),
  year: int("year").notNull(),
  month: int("month").notNull(), // 1-12
  lastSequence: int("lastSequence").default(0).notNull(),
});

export type JobCounter = typeof jobCounters.$inferSelect;

// ─── Service Jobs ────────────────────────────────────────────────────────────
export const serviceJobs = mysqlTable("service_jobs", {
  id: int("id").autoincrement().primaryKey(),
  jobNumber: varchar("jobNumber", { length: 30 }).notNull().unique(), // CLA-RO-2026-0305
  customerId: int("customerId").notNull(),
  productId: int("productId").notNull(),
  reportedBy: varchar("reportedBy", { length: 255 }).notNull(), // staff name from dropdown
  reportedById: int("reportedById"), // users.id of the person who logged the job
  salespersonId: int("salespersonId"), // users.id of assigned salesperson
  salespersonName: varchar("salespersonName", { length: 255 }),
  status: mysqlEnum("status", [
    "received",
    "diagnosis",
    "pending_finance",
    "awaiting_commercial_review",
    "ready_to_send_to_customer",
    "awaiting_customer_confirmation",
    "customer_declined",
    "in_progress",
    "pending_parts",
    "repair_complete",
    "awaiting_payment",
    "awaiting_collection",
    "closed",
  ])
    .default("received")
    .notNull(),
  assignedTechnicianId: int("assignedTechnicianId"), // staff.id
  assignedTechnicianName: varchar("assignedTechnicianName", { length: 255 }),
  faultReported: text("faultReported").notNull(),
  // Diagnosis fields
  diagnosedFault: text("diagnosedFault"), // technician's diagnosis (AI-polished, customer-facing)
  diagnosedFaultRaw: text("diagnosedFaultRaw"), // technician's raw notes before AI polish
  recommendedRepair: text("recommendedRepair"),
  currentProposalVersion: int("currentProposalVersion"),
  repairCostInternal: decimal("repairCostInternal", {
    precision: 10,
    scale: 2,
  }), // technician suggested amount (internal)
  partsReferToFinance: boolean("partsReferToFinance").default(false).notNull(), // technician checked 'Refer to Finance' for parts cost
  partsReferredAt: timestamp("partsReferredAt"),
  partsReferredBy: varchar("partsReferredBy", { length: 255 }),
  partsFilledByFinanceAt: timestamp("partsFilledByFinanceAt"),
  partsFilledByFinanceName: varchar("partsFilledByFinanceName", {
    length: 255,
  }),
  partsAvailable: boolean("partsAvailable"), // true = skip Waiting for Parts stage
  estimatedRepairDuration: varchar("estimatedRepairDuration", { length: 100 }), // e.g. "3–5 working days"
  // Customer-facing service report
  serviceReport: text("serviceReport"), // customer-facing summary of work done (AI-polished)
  serviceReportRaw: text("serviceReportRaw"), // technician's raw notes before AI polish
  estimatedCost: decimal("estimatedCost", { precision: 10, scale: 2 }),
  actualCost: decimal("actualCost", { precision: 10, scale: 2 }),
  consentThreshold: decimal("consentThreshold", { precision: 10, scale: 2 }), // snapshot of threshold at time of intake
  consentGiven: boolean("consentGiven").default(false).notNull(),
  collectionPreference: mysqlEnum("collectionPreference", [
    "delivery",
    "self_pickup",
  ]).notNull(),
  // Stage 1 & 2: Communication preference and public tracking
  commsPref: mysqlEnum("commsPref", ["whatsapp", "email", "both"]).default(
    "both"
  ),
  trackingToken: varchar("trackingToken", { length: 64 }).unique(), // public status page token (no login needed)
  estimatedCompletionDate: timestamp("estimatedCompletionDate"),
  warrantyAtIntake: mysqlEnum("warrantyAtIntake", ["yes", "no"]).notNull(),
  warrantyExpiry: timestamp("warrantyExpiry"),
  invoiceUrl: text("invoiceUrl"), // S3 URL
  invoiceKey: text("invoiceKey"), // S3 key
  invoiceUploadedAt: timestamp("invoiceUploadedAt"),
  invoiceUploadedBy: varchar("invoiceUploadedBy", { length: 255 }),
  // Auto-generated invoice fields (mirrors SVR billing)
  roInvoiceNumber: varchar("roInvoiceNumber", { length: 50 }), // INV-RO-YYYY-NNNN
  roInvoiceStatus: mysqlEnum("roInvoiceStatus", [
    "pending",
    "raised",
    "confirmed",
    "paid",
  ]).default("pending"),
  roInvoiceAmount: decimal("roInvoiceAmount", { precision: 10, scale: 2 }), // SGD amount billed
  roInvoiceDescription: text("roInvoiceDescription"),
  roInvoiceToken: varchar("roInvoiceToken", { length: 128 }),
  roPaymentLink: text("roPaymentLink"),
  roHitpayPaymentId: varchar("roHitpayPaymentId", { length: 128 }),
  roPaymentLinkStatus: mysqlEnum("roPaymentLinkStatus", [
    "active",
    "disabled",
  ]).default("disabled"),
  roInvoiceRaisedAt: timestamp("roInvoiceRaisedAt"), // when invoice was first generated
  roInvoiceConfirmedAt: timestamp("roInvoiceConfirmedAt"),
  roInvoiceConfirmedBy: varchar("roInvoiceConfirmedBy", { length: 255 }),
  roInvoiceSentAt: timestamp("roInvoiceSentAt"),
  roInvoiceSentBy: varchar("roInvoiceSentBy", { length: 255 }),
  roInvoiceSentChannel: varchar("roInvoiceSentChannel", { length: 20 }),
  // Payment receipt + delivery scheduling
  roPaidAt: timestamp("roPaidAt"), // when Finance marks as paid
  roPaidBy: varchar("roPaidBy", { length: 255 }), // who marked as paid
  roReceiptSentAt: timestamp("roReceiptSentAt"), // when receipt email was sent
  roDeliveryToken: varchar("roDeliveryToken", { length: 128 }).unique(), // public delivery scheduling page token
  roDeliveryPreference: mysqlEnum("roDeliveryPreference", [
    "delivery",
    "self_collection",
  ]),
  roDeliveryDate: timestamp("roDeliveryDate"), // customer-selected date
  roDeliveryNotes: text("roDeliveryNotes"), // customer notes (address or special instructions)
  roDeliverySubmittedAt: timestamp("roDeliverySubmittedAt"), // when customer submitted preference
  completedAt: timestamp("completedAt"),
  closedAt: timestamp("closedAt"),
  quoteSentAt: timestamp("quoteSentAt"), // when the admin quote was sent to customer for approval
  customerApprovedAt: timestamp("customerApprovedAt"), // when customer clicked Approve
  customerDeclinedAt: timestamp("customerDeclinedAt"), // when customer clicked Decline
  customerCharge: decimal("customerCharge", { precision: 10, scale: 2 }), // admin-set amount to bill the customer
  noCharge: boolean("noCharge").default(false).notNull(), // true if admin waives the charge entirely
  noChargeReason: varchar("noChargeReason", { length: 255 }), // optional reason for no charge
  noChargeSetAt: timestamp("noChargeSetAt"),
  noChargeSetBy: varchar("noChargeSetBy", { length: 255 }),
  repairApprovedAt: timestamp("repairApprovedAt"), // when repair was approved (auto or by customer)
  repairApprovedBy: varchar("repairApprovedBy", { length: 255 }), // who approved (admin name or 'auto-approved')
  estimatedWeeks: int("estimatedWeeks"), // technician's estimate of repair duration in weeks
  // Aging alert tracking
  agingAlertSentAt: timestamp("agingAlertSentAt"), // when the first aging alert was sent
  agingAlertCount: int("agingAlertCount").default(0).notNull(), // how many alerts sent so far
  // SLA tracking
  slaDeadline: timestamp("slaDeadline"), // 5 working days from creation (or last service update)
  slaExtendedUntil: timestamp("slaExtendedUntil"), // set when admin clicks WAIT (+3 working days)
  slaWaitCount: int("slaWaitCount").default(0).notNull(), // how many times admin has extended
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  /** Finance payment receipt email (Phase 1); complements roReceiptSentAt for backward compat. */
  receiptAcknowledgedAt: timestamp("receiptAcknowledgedAt"),
  receiptEmailSentAt: timestamp("receiptEmailSentAt"),
  receiptAcknowledgedBy: varchar("receiptAcknowledgedBy", { length: 255 }),
  receiptEmailMessageId: varchar("receiptEmailMessageId", { length: 255 }),
});

export type ServiceJob = typeof serviceJobs.$inferSelect;
export type InsertServiceJob = typeof serviceJobs.$inferInsert;

export const repairProposalVersions = mysqlTable("repair_proposal_versions", {
  id: int("id").autoincrement().primaryKey(),
  jobId: int("jobId").notNull(),
  version: int("version").notNull(),
  customerFacingDiagnosis: text("customerFacingDiagnosis").notNull(),
  proposedRepair: text("proposedRepair").notNull(),
  sellingPrice: decimal("sellingPrice", { precision: 10, scale: 2 }).notNull(),
  estimatedDuration: varchar("estimatedDuration", { length: 100 }),
  status: varchar("status", { length: 24 }).notNull().default("current"),
  createdById: int("createdById"),
  createdByName: varchar("createdByName", { length: 255 }),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  sentAt: timestamp("sentAt"),
  sentById: int("sentById"),
  sentByName: varchar("sentByName", { length: 255 }),
  sentToEmail: varchar("sentToEmail", { length: 255 }),
  deliveryLogId: bigint("deliveryLogId", { mode: "number" }),
  approvedAt: timestamp("approvedAt"),
  declinedAt: timestamp("declinedAt"),
  decisionSource: varchar("decisionSource", { length: 64 }),
  decisionIp: varchar("decisionIp", { length: 64 }),
  decisionUserAgent: varchar("decisionUserAgent", { length: 512 }),
});
export type RepairProposalVersion = typeof repairProposalVersions.$inferSelect;
export type InsertRepairProposalVersion =
  typeof repairProposalVersions.$inferInsert;

export const roWorkflowNotifyOutbox = mysqlTable("ro_workflow_notify_outbox", {
  id: int("id").autoincrement().primaryKey(),
  idempotencyKey: varchar("idempotencyKey", { length: 160 }).notNull(),
  jobId: int("jobId").notNull(),
  kind: varchar("kind", { length: 64 }).notNull(),
  payloadJson: json("payloadJson").$type<Record<string, unknown> | null>(),
  status: varchar("status", { length: 16 }).notNull().default("pending"),
  attempts: int("attempts").notNull().default(0),
  lastError: text("lastError"),
  lastAttemptAt: timestamp("lastAttemptAt"),
  sentAt: timestamp("sentAt"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
});
export type RoWorkflowNotifyOutbox = typeof roWorkflowNotifyOutbox.$inferSelect;
export type InsertRoWorkflowNotifyOutbox =
  typeof roWorkflowNotifyOutbox.$inferInsert;

export const roWorkSessions = mysqlTable(
  "ro_work_sessions",
  {
    id: int("id").autoincrement().primaryKey(),
    sessionUuid: varchar("sessionUuid", { length: 36 }).notNull(),
    repairOrderId: int("repairOrderId").notNull(),
    technicianUserId: int("technicianUserId").notNull(),
    technicianName: varchar("technicianName", { length: 255 }).notNull(),
    startedAt: timestamp("startedAt").notNull(),
    stoppedAt: timestamp("stoppedAt"),
    durationSeconds: int("durationSeconds"),
    stopReason: varchar("stopReason", { length: 128 }),
    stopNotes: text("stopNotes"),
    stopSource: varchar("stopSource", { length: 32 }),
    continuationConfirmedAt: timestamp("continuationConfirmedAt"),
    continuationConfirmedByUserId: int("continuationConfirmedByUserId"),
    /** `${technicianUserId}:${repairOrderId}` while running; null once stopped. */
    activeKey: varchar("activeKey", { length: 64 }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  (table) => ({
    uuidUq: uniqueIndex("ro_work_sessions_uuid_uq").on(table.sessionUuid),
    activeUq: uniqueIndex("ro_work_sessions_active_uq").on(table.activeKey),
    techActiveIdx: index("ro_work_sessions_tech_active_idx").on(table.technicianUserId, table.stoppedAt),
    roStartedIdx: index("ro_work_sessions_ro_started_idx").on(table.repairOrderId, table.startedAt),
    startedIdx: index("ro_work_sessions_started_idx").on(table.startedAt),
    stoppedIdx: index("ro_work_sessions_stopped_idx").on(table.stoppedAt),
  }),
);

export type RoWorkSession = typeof roWorkSessions.$inferSelect;
export type InsertRoWorkSession = typeof roWorkSessions.$inferInsert;

// ─── Job Photos ──────────────────────────────────────────────────────────────
export const jobPhotos = mysqlTable("job_photos", {
  id: int("id").autoincrement().primaryKey(),
  jobId: int("jobId").notNull(),
  url: text("url").notNull(),
  key: text("key").notNull(),
  filename: varchar("filename", { length: 255 }),
  uploadedBy: varchar("uploadedBy", { length: 255 }),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
});

export type JobPhoto = typeof jobPhotos.$inferSelect;
export type InsertJobPhoto = typeof jobPhotos.$inferInsert;

// ─── Internal Job Notes ──────────────────────────────────────────────────────
export const jobNotes = mysqlTable("job_notes", {
  id: int("id").autoincrement().primaryKey(),
  jobId: int("jobId").notNull(),
  note: text("note").notNull(),
  authorName: varchar("authorName", { length: 255 }).notNull(),
  authorId: int("authorId"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
});

export type JobNote = typeof jobNotes.$inferSelect;
export type InsertJobNote = typeof jobNotes.$inferInsert;

// ─── Service Report Entries (append-only history log per job) ──────────────────
export const serviceReportEntries = mysqlTable("service_report_entries", {
  id: int("id").autoincrement().primaryKey(),
  jobId: int("jobId").notNull(),
  entryDate: timestamp("entryDate").notNull(), // date of the work (may differ from createdAt)
  technicianName: varchar("technicianName", { length: 255 }).notNull(),
  technicianId: int("technicianId"), // users.id
  rawNotes: text("rawNotes"), // internal notes (not shown to customer)
  customerReport: text("customerReport"), // customer-facing polished report
  createdAt: timestamp("createdAt").defaultNow().notNull(),
});

export type ServiceReportEntry = typeof serviceReportEntries.$inferSelect;
export type InsertServiceReportEntry = typeof serviceReportEntries.$inferInsert;

// ─── Audit Log ───────────────────────────────────────────────────────────────
export const auditLogs = mysqlTable("audit_logs", {
  id: int("id").autoincrement().primaryKey(),
  jobId: int("jobId").notNull(),
  action: varchar("action", { length: 255 }).notNull(),
  fieldChanged: varchar("fieldChanged", { length: 100 }),
  oldValue: text("oldValue"),
  newValue: text("newValue"),
  performedBy: varchar("performedBy", { length: 255 }).notNull(),
  performedById: int("performedById"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
});

export type AuditLog = typeof auditLogs.$inferSelect;
export type InsertAuditLog = typeof auditLogs.$inferInsert;

// ─── App Settings ────────────────────────────────────────────────────────────
export const appSettings = mysqlTable("app_settings", {
  id: int("id").autoincrement().primaryKey(),
  key: varchar("key", { length: 100 }).notNull().unique(),
  value: text("value").notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  updatedBy: varchar("updatedBy", { length: 255 }),
});

export type AppSetting = typeof appSettings.$inferSelect;
export type InsertAppSetting = typeof appSettings.$inferInsert;

// ─── Delivery Order Imports ──────────────────────────────────────────────────
export const deliveryOrderImports = mysqlTable("delivery_order_imports", {
  id: int("id").autoincrement().primaryKey(),
  fileUrl: text("fileUrl").notNull(),
  fileKey: text("fileKey").notNull(),
  filename: varchar("filename", { length: 255 }),
  status: mysqlEnum("status", ["pending", "extracted", "confirmed", "failed"])
    .default("pending")
    .notNull(),
  extractedData: json("extractedData"),
  invoiceNumber: varchar("invoiceNumber", { length: 100 }),
  invoiceUrl: text("invoiceUrl"),
  invoiceKey: text("invoiceKey"),
  customersCreated: int("customersCreated").default(0),
  productsCreated: int("productsCreated").default(0),
  uploadedBy: varchar("uploadedBy", { length: 255 }),
  confirmedBy: varchar("confirmedBy", { length: 255 }),
  confirmedAt: timestamp("confirmedAt"),
  errorMessage: text("errorMessage"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
});

export type DeliveryOrderImport = typeof deliveryOrderImports.$inferSelect;
export type InsertDeliveryOrderImport =
  typeof deliveryOrderImports.$inferInsert;

// ─── OTP Tokens (kept for backwards compat, not used for login) ──────────────
export const otpTokens = mysqlTable("otp_tokens", {
  id: int("id").autoincrement().primaryKey(),
  email: varchar("email", { length: 320 }).notNull(),
  tokenHash: varchar("tokenHash", { length: 128 }).notNull(),
  expiresAt: timestamp("expiresAt").notNull(),
  used: boolean("used").default(false).notNull(),
  attempts: int("attempts").default(0).notNull(),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
});
export type OtpToken = typeof otpTokens.$inferSelect;
export type InsertOtpToken = typeof otpTokens.$inferInsert;

// ─── Site Visit Reports (SVR) ────────────────────────────────────────────────
// ─── Service Visit Status & Type ────────────────────────────────────────────
export const SVR_STATUSES = [
  "scheduled",
  "pending_review",
  "report_sent",
  "awaiting_payment",
  "closed",
  "waived",
  "cancelled",
] as const;
export type SvrStatus = (typeof SVR_STATUSES)[number];
export const SVR_VISIT_TYPES = ["planned", "urgent"] as const;
export type SvrVisitType = (typeof SVR_VISIT_TYPES)[number];
export const SVR_ARRIVAL_WINDOWS = [
  "9am-10am",
  "10am-11am",
  "11am-12pm",
  "2pm-3pm",
  "3pm-4pm",
  "4pm-5pm",
] as const;
export type SvrArrivalWindow = (typeof SVR_ARRIVAL_WINDOWS)[number];
export const SVR_BILLING_RATES = [160, 80] as const;
export const SVR_INVOICE_STATUSES = [
  "pending",
  "raised",
  "paid",
  "outstanding",
  "waived",
] as const;
export type SvrInvoiceStatus = (typeof SVR_INVOICE_STATUSES)[number];

export const SVR_ISSUE_TAGS = [
  "Product Fault",
  "Service Issue",
  "Programming Issue",
  "Installation Issue",
  "User Error",
  "Maintenance",
  "Upgrade",
  "Consultation",
  "Other",
] as const;
export type SvrIssueTag = (typeof SVR_ISSUE_TAGS)[number];

export const siteVisits = mysqlTable("site_visits", {
  id: int("id").autoincrement().primaryKey(),
  visitNumber: varchar("visitNumber", { length: 20 }).notNull().unique(), // SVR-2026-0001
  customerId: int("customerId"),
  siteId: int("siteId"), // customer_sites.id — which site is being serviced
  // PIC (Person In Charge — office staff who manages the customer relationship)
  picId: int("picId"),
  picName: varchar("picName", { length: 255 }),
  // Technician assigned to the visit
  technicianId: int("technicianId"),
  technicianName: varchar("technicianName", { length: 255 }),
  // Visit scheduling
  visitType: mysqlEnum("visitType", ["planned", "urgent"])
    .default("planned")
    .notNull(),
  visitDate: timestamp("visitDate").notNull(),
  arrivalWindow: varchar("arrivalWindow", { length: 50 }), // free-form time range e.g. "09:00-13:30" or legacy enum values
  complaintDescription: text("complaintDescription"), // original complaint from customer
  // Customer matching state (admin resolution flow for unmatched site visits)
  isCustomerUnmatched: boolean("isCustomerUnmatched").default(false).notNull(),
  customerMatchConfidence: decimal("customerMatchConfidence", {
    precision: 5,
    scale: 2,
  }),
  unmatchedRawName: varchar("unmatchedRawName", { length: 255 }),
  unmatchedRawPhone: varchar("unmatchedRawPhone", { length: 50 }),
  unmatchedRawAddress: text("unmatchedRawAddress"),
  // Option B: dedicated PIC assignment for unresolved unmatched records
  unmatchedResolutionPicId: int("unmatchedResolutionPicId"),
  unmatchedAssignedAt: timestamp("unmatchedAssignedAt"),
  unmatchedResolvedAt: timestamp("unmatchedResolvedAt"),
  unmatchedEscalatedAt: timestamp("unmatchedEscalatedAt"),
  unmatchedAssignmentNote: text("unmatchedAssignmentNote"),
  // Billing — PIC declares billing intent when closing/sending report
  // null = not yet decided (SVR still open)
  billingDecision: mysqlEnum("billingDecision", [
    "bill",
    "no_charge_warranty_goodwill",
    "no_charge_contract",
    "not_billable_new_job_within_1_year",
  ]),
  agreedRate: decimal("agreedRate", { precision: 10, scale: 2 }), // rate agreed with customer (PIC enters when billingDecision = bill)
  billingNote: text("billingNote"), // optional reason for no-charge decisions
  // Legacy billing fields (kept for backward compat)
  billable: boolean("billable").default(false).notNull(),
  serviceFee: decimal("serviceFee", { precision: 10, scale: 2 }), // charged amount when billable
  customerInformed: boolean("customerInformed").default(false).notNull(), // customer verbally informed of charge
  // Technician update (submitted after visit)
  internalNotes: text("internalNotes"), // not visible to customer
  findingsAndActions: text("findingsAndActions"), // customer-facing findings
  updateSubmittedAt: timestamp("updateSubmittedAt"),
  updateSubmittedBy: varchar("updateSubmittedBy", { length: 255 }),
  // PIC review
  customerReport: text("customerReport"), // PIC-edited customer-facing report
  status: mysqlEnum("status", [
    "scheduled",
    "pending_review",
    "report_sent",
    "awaiting_payment",
    "closed",
    "waived",
    "cancelled",
  ])
    .default("scheduled")
    .notNull(),
  closedAt: timestamp("closedAt"),
  closedBy: varchar("closedBy", { length: 255 }),
  /**
   * Set when the workflow was auto-closed on technician "done" (or by the
   * auto-close backfill) without a billing decision. Billing stays undecided
   * (billingDecision NULL, billingPending true) until Finance/PIC sets it.
   */
  autoClosedAt: timestamp("autoClosedAt"),
  billingPending: boolean("billingPending").default(false).notNull(),
  cancelledAt: timestamp("cancelledAt"),
  cancelledByUserId: int("cancelledByUserId"),
  cancelledByName: varchar("cancelledByName", { length: 255 }),
  cancellationSource: varchar("cancellationSource", { length: 64 }),
  cancellationReason: text("cancellationReason"),
  // Actual on-site times logged by Cleo when technician checks in/out
  arrivedAt: timestamp("arrivedAt"), // when technician confirmed arrival via Cleo
  departedAt: timestamp("departedAt"), // when technician marked job done via Cleo
  reportSentAt: timestamp("reportSentAt"),
  reportSentBy: varchar("reportSentBy", { length: 255 }),
  reportSentVia: mysqlEnum("reportSentVia", ["whatsapp", "email"]),
  // Waiver
  waiverRequested: boolean("waiverRequested").default(false).notNull(),
  waiverRequestedBy: varchar("waiverRequestedBy", { length: 255 }),
  waiverRequestedAt: timestamp("waiverRequestedAt"),
  waiverReason: text("waiverReason"),
  resolutionNotes: text("resolutionNotes"),
  partsCost: decimal("partsCost", { precision: 10, scale: 2 }),
  waiverApproved: boolean("waiverApproved"),
  waiverDecidedBy: varchar("waiverDecidedBy", { length: 255 }),
  waiverDecidedAt: timestamp("waiverDecidedAt"),
  // Customer WhatsApp confirmation status
  custWhatsappStatus: mysqlEnum("custWhatsappStatus", [
    "sent",
    "failed",
    "no_phone",
  ]),
  custWhatsappSentAt: timestamp("custWhatsappSentAt"),
  custWhatsappSentBy: varchar("custWhatsappSentBy", { length: 255 }),
  // Public tracking token for customer-facing SVR report page
  trackingToken: varchar("trackingToken", { length: 64 }).unique(),
  /**
   * Inventory Team request (staff-only). Not an assignee role — team flag + message.
   * Never expose via public/customer serializers or customer communications.
   */
  inventoryTeamRequested: boolean("inventoryTeamRequested")
    .default(false)
    .notNull(),
  inventoryTeamMessage: text("inventoryTeamMessage"),
  /** Set when an inventory request email was successfully dispatched (enables cancel-on-remove). */
  inventoryTeamActivatedAt: timestamp("inventoryTeamActivatedAt"),
  /** One-shot PIC heads-up after the first bring-list populate. Later edits do not re-email until visit complete. */
  bringListPicHeadsUpNotifiedAt: timestamp("bringListPicHeadsUpNotifiedAt"),
  /** Technician confirmed off-list used items (or none) at visit done. */
  bringListOffListReconciledAt: timestamp("bringListOffListReconciledAt"),
  // Legacy fields (kept for backward compat)
  customerReportRaw: text("customerReportRaw"),
  issueTags: json("issueTags"),
  followUpRequired: boolean("followUpRequired").default(false).notNull(),
  followUpDate: timestamp("followUpDate"),
  followUpNotes: text("followUpNotes"),
  createdBy: varchar("createdBy", { length: 255 }),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
});

export type SiteVisit = typeof siteVisits.$inferSelect;
export type InsertSiteVisit = typeof siteVisits.$inferInsert;

/** Extra products/accessories requested or discovered against an SVR. Physical and commercial facts stay separate. */
export const svrAdditionalItemRequests = mysqlTable(
  "svr_additional_item_requests",
  {
    id: int("id").autoincrement().primaryKey(),
    visitId: int("visitId").notNull(),
    requestText: text("requestText").notNull(),
    requestedByUserId: int("requestedByUserId").notNull(),
    requestedByName: varchar("requestedByName", { length: 255 }).notNull(),
    requestedByRole: varchar("requestedByRole", { length: 64 }),
    status: mysqlEnum("status", [
      "pending_catalogue",
      "preparing",
      "released",
      "site_reconciliation",
      "commercial_review",
      "closed",
      "cancelled",
    ])
      .notNull()
      .default("pending_catalogue"),
    inventoryNotifiedAt: timestamp("inventoryNotifiedAt"),
    picNotifiedAt: timestamp("picNotifiedAt"),
    occurrenceKey: varchar("occurrenceKey", { length: 191 }).notNull(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    occurrenceUq: uniqueIndex("svr_additional_item_requests_occurrence_uq").on(
      t.occurrenceKey
    ),
    visitStatusIdx: index("svr_additional_item_requests_visit_status_idx").on(
      t.visitId,
      t.status,
      t.createdAt
    ),
  })
);

export const svrAdditionalItems = mysqlTable(
  "svr_additional_items",
  {
    id: int("id").autoincrement().primaryKey(),
    visitId: int("visitId").notNull(),
    requestId: int("requestId"),
    sourceType: mysqlEnum("sourceType", [
      "installer_request",
      "additional_do",
      "off_list_used",
    ])
      .notNull()
      .default("installer_request"),
    sourceDocumentId: int("sourceDocumentId"),
    sourceDeliveryLineId: int("sourceDeliveryLineId"),
    occurrenceKey: varchar("occurrenceKey", { length: 191 }),
    projectProcurementId: int("projectProcurementId"),
    proposalId: int("proposalId"),
    customerId: int("customerId"),
    customerSiteId: int("customerSiteId"),
    catalogueItemId: int("catalogueItemId"),
    projectCatalogueItemId: int("projectCatalogueItemId"),
    catalogueSource: mysqlEnum("catalogueSource", [
      "retail",
      "project",
      "user_added",
      "additional_do",
    ]),
    productIdentityKey: varchar("productIdentityKey", { length: 191 }),
    productName: varchar("productName", { length: 255 }).notNull(),
    requestedQuantity: decimal("requestedQuantity", {
      precision: 12,
      scale: 3,
    }).notNull(),
    uom: varchar("uom", { length: 32 }).notNull().default("Each"),
    requestReason: text("requestReason").notNull(),
    requestedByUserId: int("requestedByUserId"),
    requestedByName: varchar("requestedByName", { length: 255 }).notNull(),
    requestedAt: timestamp("requestedAt").defaultNow().notNull(),
    issuedQuantity: decimal("issuedQuantity", { precision: 12, scale: 3 }),
    serialNumbersJson: json("serialNumbersJson").$type<string[] | null>(),
    issuedByUserId: int("issuedByUserId"),
    issuedByName: varchar("issuedByName", { length: 255 }),
    issuedAt: timestamp("issuedAt"),
    inventoryLotId: int("inventoryLotId"),
    inventoryReleaseMovementId: int("inventoryReleaseMovementId"),
    inventoryReturnMovementId: int("inventoryReturnMovementId"),
    returnedQuantity: decimal("returnedQuantity", { precision: 12, scale: 3 }),
    usageOutcome: mysqlEnum("usageOutcome", [
      "used",
      "returned",
      "left_on_site",
      "damaged_lost",
      "unable_to_confirm",
    ]),
    usedQuantity: decimal("usedQuantity", { precision: 12, scale: 3 }),
    usageNote: text("usageNote"),
    confirmedByUserId: int("confirmedByUserId"),
    confirmedByName: varchar("confirmedByName", { length: 255 }),
    confirmedAt: timestamp("confirmedAt"),
    commercialDecision: mysqlEnum("commercialDecision", [
      "pending",
      "bill_customer",
      "no_charge_goodwill",
      "included_scope",
      "warranty_internal",
    ])
      .notNull()
      .default("pending"),
    noChargeReviewStatus: mysqlEnum("noChargeReviewStatus", [
      "not_requested",
      "pending_admin",
      "approved",
      "rejected",
      "appealed",
    ])
      .notNull()
      .default("not_requested"),
    noChargeRequestedReason: text("noChargeRequestedReason"),
    noChargeAppealReason: text("noChargeAppealReason"),
    noChargeReviewedByUserId: int("noChargeReviewedByUserId"),
    noChargeReviewedByName: varchar("noChargeReviewedByName", { length: 255 }),
    noChargeReviewedAt: timestamp("noChargeReviewedAt"),
    noChargeReviewNote: text("noChargeReviewNote"),
    sellingUnitPrice: decimal("sellingUnitPrice", { precision: 12, scale: 2 }),
    commercialReason: text("commercialReason"),
    commercialDecidedByUserId: int("commercialDecidedByUserId"),
    commercialDecidedByName: varchar("commercialDecidedByName", {
      length: 255,
    }),
    commercialDecidedAt: timestamp("commercialDecidedAt"),
    billingStatus: mysqlEnum("billingStatus", [
      "not_applicable",
      "review_required",
      "price_required",
      "proposal_draft",
      "ready_for_invoice",
      "invoiced",
      "paid",
    ])
      .notNull()
      .default("not_applicable"),
    invoiceReference: varchar("invoiceReference", { length: 128 }),
    portalInvoiceId: int("portalInvoiceId"),
    generatedProposalId: int("generatedProposalId"),
    generatedVariationOrderId: int("generatedVariationOrderId"),
    financeTaskId: int("financeTaskId"),
    customerAcceptanceStatus: mysqlEnum("customerAcceptanceStatus", [
      "not_required",
      "pending",
      "accepted",
      "declined",
      "retrospective_review",
    ])
      .notNull()
      .default("pending"),
    customerAcceptanceEvidence: text("customerAcceptanceEvidence"),
    customerAcceptanceRecordedByUserId: int(
      "customerAcceptanceRecordedByUserId"
    ),
    customerAcceptanceRecordedAt: timestamp("customerAcceptanceRecordedAt"),
    terminalStatus: mysqlEnum("terminalStatus", [
      "open",
      "billed",
      "paid",
      "returned",
      "absorbed",
      "cancelled",
    ])
      .notNull()
      .default("open"),
    terminalAt: timestamp("terminalAt"),
    returnedConfirmedByUserId: int("returnedConfirmedByUserId"),
    returnedConfirmedAt: timestamp("returnedConfirmedAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    visitIdx: index("svr_additional_items_visit_idx").on(
      t.visitId,
      t.createdAt
    ),
    requestIdx: index("svr_additional_items_request_idx").on(t.requestId, t.id),
    billingIdx: index("svr_additional_items_billing_idx").on(
      t.billingStatus,
      t.commercialDecision
    ),
    occurrenceUq: uniqueIndex("svr_additional_items_occurrence_uq").on(
      t.occurrenceKey
    ),
    sourceDeliveryLineUq: uniqueIndex(
      "svr_additional_items_source_delivery_line_uq"
    ).on(t.sourceDeliveryLineId),
    queueIdx: index("svr_additional_items_queue_idx").on(
      t.terminalStatus,
      t.billingStatus,
      t.updatedAt
    ),
  })
);

export const svrAdditionalItemRequestEvents = mysqlTable(
  "svr_additional_item_request_events",
  {
    id: int("id").autoincrement().primaryKey(),
    requestId: int("requestId").notNull(),
    visitId: int("visitId").notNull(),
    eventType: varchar("eventType", { length: 64 }).notNull(),
    actorUserId: int("actorUserId"),
    actorName: varchar("actorName", { length: 255 }).notNull(),
    actorRole: varchar("actorRole", { length: 64 }),
    metadataJson: json("metadataJson").$type<Record<string, unknown> | null>(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    requestIdx: index("svr_additional_item_request_events_request_idx").on(
      t.requestId,
      t.createdAt
    ),
  })
);

export const svrAdditionalItemEvents = mysqlTable(
  "svr_additional_item_events",
  {
    id: int("id").autoincrement().primaryKey(),
    itemId: int("itemId").notNull(),
    visitId: int("visitId").notNull(),
    eventType: varchar("eventType", { length: 64 }).notNull(),
    actorUserId: int("actorUserId"),
    actorName: varchar("actorName", { length: 255 }).notNull(),
    actorRole: varchar("actorRole", { length: 64 }),
    metadataJson: json("metadataJson").$type<Record<string, unknown> | null>(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    itemIdx: index("svr_additional_item_events_item_idx").on(
      t.itemId,
      t.createdAt
    ),
  })
);

/** Durable post-completion SVR auto-polish jobs. Never send customer comms. */
export const svrAutoPolishJobs = mysqlTable("svr_auto_polish_jobs", {
  id: int("id").autoincrement().primaryKey(),
  svrId: int("svrId").notNull(),
  occurrenceKey: varchar("occurrenceKey", { length: 191 }).notNull().unique(),
  status: mysqlEnum("status", [
    "pending",
    "running",
    "succeeded",
    "failed_retryable",
    "skipped",
  ])
    .notNull()
    .default("pending"),
  attemptCount: int("attemptCount").notNull().default(0),
  lastError: text("lastError"),
  lastAttemptAt: timestamp("lastAttemptAt"),
  polishedAt: timestamp("polishedAt"),
  skipReason: varchar("skipReason", { length: 128 }),
  leaseToken: varchar("leaseToken", { length: 64 }),
  leaseExpiresAt: timestamp("leaseExpiresAt"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
});
export type SvrAutoPolishJob = typeof svrAutoPolishJobs.$inferSelect;

/**
 * SVR workflow audit events (auto-close, PIC report-send link, backfill snapshots/reverts).
 * dedupeKey is unique (NULLs allowed) and makes one-shot writes idempotent.
 */
export const svrWorkflowEvents = mysqlTable("svr_workflow_events", {
  id: int("id").autoincrement().primaryKey(),
  visitId: int("visitId"),
  eventType: varchar("eventType", { length: 64 }).notNull(),
  dedupeKey: varchar("dedupeKey", { length: 191 }).unique(),
  actorUserId: int("actorUserId"),
  actorName: varchar("actorName", { length: 255 }),
  metadataJson: text("metadataJson"),
  revertedAt: timestamp("revertedAt"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
});
export type SvrWorkflowEvent = typeof svrWorkflowEvents.$inferSelect;

// ─── SVR Counter (per year) ──────────────────────────────────────────────────
export const svrCounters = mysqlTable("svr_counters", {
  id: int("id").autoincrement().primaryKey(),
  year: int("year").notNull(),
  lastSequence: int("lastSequence").default(0).notNull(),
});

export type SvrCounter = typeof svrCounters.$inferSelect;
// ─── Site Visit Billing ──────────────────────────────────────────────────────
export const siteVisitBilling = mysqlTable("site_visit_billing", {
  id: int("id").autoincrement().primaryKey(),
  visitId: int("visitId").notNull().unique(), // one billing record per visit
  /** Denormalized from `site_visits.billingDecision` for reporting; optional on legacy rows. */
  billingClassification: varchar("billingClassification", { length: 64 }),
  rate: decimal("rate", { precision: 10, scale: 2 }), // agreed rate incl. GST (nearest cent)
  invoiceNumber: varchar("invoiceNumber", { length: 50 }), // INV-SVR-YYYY-NNNN
  invoiceStatus: mysqlEnum("invoiceStatus", [
    "pending",
    "raised",
    "paid",
    "outstanding",
    "waived",
  ])
    .default("pending")
    .notNull(),
  invoiceDescription: text("invoiceDescription"), // AI-polished description for invoice line item
  invoiceUrl: text("invoiceUrl"), // S3 URL of generated PDF
  invoiceKey: varchar("invoiceKey", { length: 500 }), // S3 key
  invoiceToken: varchar("invoiceToken", { length: 128 }), // public token for hosted invoice page
  paymentLink: text("paymentLink"), // Finance-entered CardUp/Stripe link
  preAgreedChargeEnabled: boolean("preAgreedChargeEnabled")
    .default(false)
    .notNull(),
  preAgreedChargeAmount: decimal("preAgreedChargeAmount", {
    precision: 10,
    scale: 2,
  }),
  preAgreedSalespersonId: int("preAgreedSalespersonId"),
  invoiceScheduledSendAt: timestamp("invoiceScheduledSendAt"),
  invoiceScheduleStatus: mysqlEnum("invoiceScheduleStatus", [
    "none",
    "scheduled",
    "sent",
    "cancelled",
  ])
    .default("none")
    .notNull(),
  hitpayPaymentId: varchar("hitpayPaymentId", { length: 128 }),
  paymentLinkStatus: mysqlEnum("paymentLinkStatus", ["active", "disabled"])
    .default("disabled")
    .notNull(),
  confirmedAt: timestamp("confirmedAt"), // when Finance confirmed the invoice
  confirmedBy: varchar("confirmedBy", { length: 255 }),
  invoiceSentAt: timestamp("invoiceSentAt"), // when PIC sent invoice to customer
  invoiceSentBy: varchar("invoiceSentBy", { length: 255 }),
  invoiceSentChannel: varchar("invoiceSentChannel", { length: 20 }), // 'email' | 'whatsapp'
  invoiceWhatsappSendStatus: varchar("invoiceWhatsappSendStatus", {
    length: 24,
  }),
  invoiceWhatsappProviderSid: varchar("invoiceWhatsappProviderSid", {
    length: 34,
  }),
  invoiceWhatsappSendLock: varchar("invoiceWhatsappSendLock", { length: 64 }),
  invoiceWhatsappInitiatedAt: timestamp("invoiceWhatsappInitiatedAt"),
  invoiceWhatsappInitiatedBy: varchar("invoiceWhatsappInitiatedBy", {
    length: 255,
  }),
  confirmedInvoiceFingerprint: varchar("confirmedInvoiceFingerprint", {
    length: 64,
  }),
  lastSuccessfulWhatsappFingerprint: varchar(
    "lastSuccessfulWhatsappFingerprint",
    { length: 64 }
  ),
  lastSuccessfulWhatsappAttemptId: int("lastSuccessfulWhatsappAttemptId"),
  raisedAt: timestamp("raisedAt"),
  raisedBy: varchar("raisedBy", { length: 255 }),
  paidAt: timestamp("paidAt"),
  paidBy: varchar("paidBy", { length: 255 }),
  reminderSentAt: timestamp("reminderSentAt"), // last 7-day reminder sent
  notes: text("notes"),
  // Sales Portal webhook fields
  proposalRef: varchar("proposalRef", { length: 50 }), // e.g. PROP-2026-0042 — entered by Finance at invoice-raise time
  webhookStatus: mysqlEnum("webhookStatus", [
    "pending",
    "sent",
    "failed",
    "skipped",
  ]).default("pending"), // outbound payment-collected webhook state
  webhookSentAt: timestamp("webhookSentAt"), // when webhook was successfully delivered
  webhookError: text("webhookError"), // last error if delivery failed
  /** JSON array: { type: "revise"|"waive", userName, amount?, at: ISO } — most recent first */
  billingAuditLog: text("billingAuditLog"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  /** Finance payment receipt email (Phase 1). */
  receiptAcknowledgedAt: timestamp("receiptAcknowledgedAt"),
  receiptEmailSentAt: timestamp("receiptEmailSentAt"),
  receiptAcknowledgedBy: varchar("receiptAcknowledgedBy", { length: 255 }),
  receiptEmailMessageId: varchar("receiptEmailMessageId", { length: 255 }),
});
export type SiteVisitBilling = typeof siteVisitBilling.$inferSelect;
export type InsertSiteVisitBilling = typeof siteVisitBilling.$inferInsert;

// ─── Service Insights analysis cache (derived, never source-of-truth) ───────
export const serviceInsightsAnalyses = mysqlTable(
  "service_insights_analyses",
  {
    id: bigint("id", { mode: "number" }).autoincrement().primaryKey(),
    fromDate: timestamp("fromDate").notNull(),
    toDate: timestamp("toDate").notNull(),
    analysisVersion: int("analysisVersion").notNull(),
    runKey: varchar("runKey", { length: 80 }).notNull(),
    sourceFingerprint: varchar("sourceFingerprint", { length: 64 }).notNull(),
    status: mysqlEnum("status", [
      "pending",
      "processing",
      "complete",
      "partial_failed",
      "failed",
    ])
      .notNull()
      .default("pending"),
    selectedRecordCount: int("selectedRecordCount").notNull().default(0),
    includedRecordCount: int("includedRecordCount").notNull().default(0),
    excludedRecordCount: int("excludedRecordCount").notNull().default(0),
    batchCount: int("batchCount").notNull().default(0),
    failedBatchCount: int("failedBatchCount").notNull().default(0),
    llmModel: varchar("llmModel", { length: 64 }),
    errorSummary: text("errorSummary"),
    resultJson: longtext("resultJson"),
    lastAttemptedAt: timestamp("lastAttemptedAt").notNull().defaultNow(),
    lastCompletedAt: timestamp("lastCompletedAt"),
    createdAt: timestamp("createdAt").notNull().defaultNow(),
    updatedAt: timestamp("updatedAt").notNull().defaultNow().onUpdateNow(),
  },
  t => ({
    periodIdx: index("idx_service_insights_period").on(
      t.fromDate,
      t.toDate,
      t.analysisVersion
    ),
    runKeyIdx: uniqueIndex("uniq_service_insights_run_key").on(t.runKey),
    fingerprintIdx: index("idx_service_insights_fingerprint").on(
      t.analysisVersion,
      t.sourceFingerprint,
      t.updatedAt
    ),
  })
);
export type ServiceInsightsAnalysisRow =
  typeof serviceInsightsAnalyses.$inferSelect;
export type InsertServiceInsightsAnalysisRow =
  typeof serviceInsightsAnalyses.$inferInsert;

export const serviceInsightsAnalysisBatches = mysqlTable(
  "service_insights_analysis_batches",
  {
    id: bigint("id", { mode: "number" }).autoincrement().primaryKey(),
    analysisId: bigint("analysisId", { mode: "number" }).notNull(),
    runKey: varchar("runKey", { length: 80 }).notNull(),
    fromDate: timestamp("fromDate").notNull(),
    toDate: timestamp("toDate").notNull(),
    analysisVersion: int("analysisVersion").notNull(),
    batchKey: varchar("batchKey", { length: 128 }).notNull(),
    sourceFingerprint: varchar("sourceFingerprint", { length: 64 }).notNull(),
    svrIdsJson: longtext("svrIdsJson"),
    status: mysqlEnum("status", [
      "pending",
      "processing",
      "succeeded",
      "failed",
    ])
      .notNull()
      .default("pending"),
    attemptCount: int("attemptCount").notNull().default(0),
    errorCode: varchar("errorCode", { length: 64 }),
    errorDetail: text("errorDetail"),
    resultJson: longtext("resultJson"),
    startedAt: timestamp("startedAt"),
    completedAt: timestamp("completedAt"),
    createdAt: timestamp("createdAt").notNull().defaultNow(),
    updatedAt: timestamp("updatedAt").notNull().defaultNow().onUpdateNow(),
  },
  t => ({
    analysisIdx: index("idx_service_insights_batches_analysis").on(
      t.analysisId,
      t.status
    ),
    runBatchUnique: uniqueIndex("uniq_service_insights_batch_per_run").on(
      t.runKey,
      t.batchKey,
      t.analysisVersion,
      t.sourceFingerprint
    ),
    periodIdx: index("idx_service_insights_batches_period").on(
      t.fromDate,
      t.toDate,
      t.analysisVersion
    ),
  })
);
export type ServiceInsightsAnalysisBatchRow =
  typeof serviceInsightsAnalysisBatches.$inferSelect;
export type InsertServiceInsightsAnalysisBatchRow =
  typeof serviceInsightsAnalysisBatches.$inferInsert;

/** Append-only SVR invoice WhatsApp send attempts (version-aware). */
export const svrInvoiceWhatsappSendAttempts = mysqlTable(
  "svr_invoice_whatsapp_send_attempts",
  {
    id: int("id").autoincrement().primaryKey(),
    billingId: int("billingId").notNull(),
    visitId: int("visitId").notNull(),
    invoiceFingerprint: varchar("invoiceFingerprint", { length: 64 }).notNull(),
    attemptType: varchar("attemptType", { length: 16 }).notNull(),
    initiatedByUserId: int("initiatedByUserId"),
    initiatedByName: varchar("initiatedByName", { length: 255 }),
    initiatedAt: timestamp("initiatedAt").defaultNow().notNull(),
    recipient: varchar("recipient", { length: 64 }),
    providerMessageSid: varchar("providerMessageSid", { length: 34 }),
    status: varchar("status", { length: 24 }).notNull(),
    submittedAt: timestamp("submittedAt"),
    sentAt: timestamp("sentAt"),
    deliveredAt: timestamp("deliveredAt"),
    readAt: timestamp("readAt"),
    failedAt: timestamp("failedAt"),
    failureReason: text("failureReason"),
    idempotencyKey: varchar("idempotencyKey", { length: 80 })
      .notNull()
      .unique(),
    amountSnapshot: decimal("amountSnapshot", { precision: 10, scale: 2 }),
    invoiceNumberSnapshot: varchar("invoiceNumberSnapshot", { length: 50 }),
    invoiceTokenSnapshot: varchar("invoiceTokenSnapshot", { length: 128 }),
    invoiceKeySnapshot: varchar("invoiceKeySnapshot", { length: 500 }),
    deliveryLogId: bigint("deliveryLogId", { mode: "number" }),
    legacyEvidence: boolean("legacyEvidence").notNull().default(false),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt"),
  }
);
export type SvrInvoiceWhatsappSendAttempt =
  typeof svrInvoiceWhatsappSendAttempts.$inferSelect;
export type InsertSvrInvoiceWhatsappSendAttempt =
  typeof svrInvoiceWhatsappSendAttempts.$inferInsert;

// ─── External Payment Records ────────────────────────────────────────────────
export const paymentRecords = mysqlTable("payment_records", {
  id: int("id").autoincrement().primaryKey(),
  sourceType: mysqlEnum("sourceType", ["svr", "ro", "portal"]).notNull(),
  sourceId: int("sourceId").notNull(),
  invoiceNumber: varchar("invoiceNumber", { length: 50 }).notNull(),
  provider: mysqlEnum("provider", ["hitpay"]).notNull(),
  providerPaymentRequestId: varchar("providerPaymentRequestId", {
    length: 128,
  }),
  providerPaymentId: varchar("providerPaymentId", { length: 128 }),
  amount: decimal("amount", { precision: 10, scale: 2 }),
  currency: varchar("currency", { length: 10 }),
  paidAt: timestamp("paidAt"),
  payload: json("payload"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
});
export type PaymentRecord = typeof paymentRecords.$inferSelect;
export type InsertPaymentRecord = typeof paymentRecords.$inferInsert;

/** Append-only audit log for inbound HitPay webhooks (reconciliation observability). */
export const hitpayWebhookEvents = mysqlTable("hitpay_webhook_events", {
  id: bigint("id", { mode: "number" }).autoincrement().primaryKey(),
  receivedAt: timestamp("receivedAt").defaultNow().notNull(),
  signaturePresent: boolean("signaturePresent").notNull().default(false),
  signatureValid: boolean("signatureValid").notNull().default(false),
  referenceNumber: varchar("referenceNumber", { length: 128 }),
  payloadStatus: varchar("payloadStatus", { length: 64 }),
  paymentStatus: varchar("paymentStatus", { length: 64 }),
  branch: varchar("branch", { length: 16 }).notNull().default("unknown"),
  outcome: varchar("outcome", { length: 16 }).notNull(),
  errorMessage: text("errorMessage"),
  linkedSourceType: varchar("linkedSourceType", { length: 16 }),
  linkedSourceId: int("linkedSourceId"),
  httpStatus: int("httpStatus"),
  payloadJson: json("payloadJson").$type<Record<string, unknown> | null>(),
});
export type HitpayWebhookEvent = typeof hitpayWebhookEvents.$inferSelect;

// ─── System Information (Cleo-ready) ────────────────────────────────────────
export const systemInfo = mysqlTable("system_info", {
  id: int("id").autoincrement().primaryKey(),
  customerId: int("customerId").notNull(),
  deviceName: varchar("deviceName", { length: 255 }),
  deviceType: varchar("deviceType", { length: 100 }),
  ipAddress: varchar("ipAddress", { length: 50 }),
  loginUsername: varchar("loginUsername", { length: 255 }),
  loginPassword: varchar("loginPassword", { length: 255 }),
  wifiSsid: varchar("wifiSsid", { length: 255 }),
  wifiPassword: varchar("wifiPassword", { length: 255 }),
  notes: text("notes"),
  updatedBy: varchar("updatedBy", { length: 255 }),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
});
export type SystemInfo = typeof systemInfo.$inferSelect;
export type InsertSystemInfo = typeof systemInfo.$inferInsert;
// ─── Notification Settings ───────────────────────────────────────────────────
export const notificationSettings = mysqlTable("notification_settings", {
  id: int("id").autoincrement().primaryKey(),
  type: varchar("type", { length: 100 }).notNull().unique(),
  label: varchar("label", { length: 255 }).notNull(),
  description: text("description"),
  enabled: boolean("enabled").default(true).notNull(),
  updatedBy: varchar("updatedBy", { length: 255 }),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
});
export type NotificationSetting = typeof notificationSettings.$inferSelect;
export type InsertNotificationSetting =
  typeof notificationSettings.$inferInsert;

/** Per-trigger channel policy (Phase 2b). REQUIRED registry triggers omit rows — always effectively on. */
export const notificationTriggerPolicies = mysqlTable(
  "notification_trigger_policies",
  {
    triggerId: varchar("triggerId", { length: 120 }).notNull().primaryKey(),
    emailEnabled: boolean("emailEnabled").notNull().default(true),
    whatsappEnabled: boolean("whatsappEnabled").notNull().default(true),
    internalEnabled: boolean("internalEnabled").notNull().default(true),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
    updatedByUserId: int("updatedByUserId"),
  }
);
export type NotificationTriggerPolicy =
  typeof notificationTriggerPolicies.$inferSelect;
export type InsertNotificationTriggerPolicy =
  typeof notificationTriggerPolicies.$inferInsert;

/**
 * Per-employee enable/disable for internal notification types (future sends only).
 * Absence of a row means default-enabled for the type's production eligibility rules.
 */
export const internalNotificationRecipientPrefs = mysqlTable(
  "internal_notification_recipient_prefs",
  {
    id: int("id").autoincrement().primaryKey(),
    notificationType: varchar("notificationType", { length: 120 }).notNull(),
    userId: int("userId").notNull(),
    enabled: boolean("enabled").notNull().default(true),
    mandatory: boolean("mandatory").notNull().default(false),
    updatedByUserId: int("updatedByUserId"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    uniqTypeUser: uniqueIndex("uniq_internal_notif_recipient").on(
      t.notificationType,
      t.userId
    ),
    typeIdx: index("idx_internal_notif_recipient_type").on(t.notificationType),
    userIdx: index("idx_internal_notif_recipient_user").on(t.userId),
  })
);
export type InternalNotificationRecipientPref =
  typeof internalNotificationRecipientPrefs.$inferSelect;
export type InsertInternalNotificationRecipientPref =
  typeof internalNotificationRecipientPrefs.$inferInsert;

/** Append-only audit for internal notification recipient preference changes. */
export const internalNotificationRecipientPrefAudit = mysqlTable(
  "internal_notification_recipient_pref_audit",
  {
    id: bigint("id", { mode: "number" }).autoincrement().primaryKey(),
    notificationType: varchar("notificationType", { length: 120 }).notNull(),
    changedByUserId: int("changedByUserId").notNull(),
    changedByName: varchar("changedByName", { length: 255 }),
    changedByRole: varchar("changedByRole", { length: 50 }),
    previousConfigJson: json("previousConfigJson")
      .$type<Record<string, unknown>>()
      .notNull(),
    newConfigJson: json("newConfigJson")
      .$type<Record<string, unknown>>()
      .notNull(),
    addedUserIdsJson: json("addedUserIdsJson").$type<number[] | null>(),
    removedUserIdsJson: json("removedUserIdsJson").$type<number[] | null>(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    typeIdx: index("idx_internal_notif_pref_audit_type").on(t.notificationType),
    createdIdx: index("idx_internal_notif_pref_audit_created").on(t.createdAt),
  })
);
export type InternalNotificationRecipientPrefAudit =
  typeof internalNotificationRecipientPrefAudit.$inferSelect;
export type InsertInternalNotificationRecipientPrefAudit =
  typeof internalNotificationRecipientPrefAudit.$inferInsert;

/** Delivery observability log (Phase 2e + provider lifecycle). No FKs — safe if source rows are deleted. */
export const notificationDeliveryLog = mysqlTable("notification_delivery_log", {
  id: bigint("id", { mode: "number" }).autoincrement().primaryKey(),
  triggerId: varchar("triggerId", { length: 120 }).notNull(),
  channel: varchar("channel", { length: 24 }).notNull(),
  transport: varchar("transport", { length: 32 }),
  recipientKind: varchar("recipientKind", { length: 32 }),
  recipientHash: varchar("recipientHash", { length: 128 }),
  status: varchar("status", { length: 24 }).notNull(),
  disabledReason: varchar("disabledReason", { length: 64 }),
  errorCode: varchar("errorCode", { length: 64 }),
  errorMessage: text("errorMessage"),
  initiatedByUserId: int("initiatedByUserId"),
  initiatedByRole: varchar("initiatedByRole", { length: 32 }),
  correlationId: varchar("correlationId", { length: 120 }),
  metadataJson: json("metadataJson").$type<Record<string, unknown> | null>(),
  providerMessageSid: varchar("providerMessageSid", { length: 34 }),
  providerStatus: varchar("providerStatus", { length: 24 }),
  providerErrorCode: varchar("providerErrorCode", { length: 16 }),
  providerErrorMessage: text("providerErrorMessage"),
  providerAcceptedAt: timestamp("providerAcceptedAt"),
  deliveredAt: timestamp("deliveredAt"),
  failedAt: timestamp("failedAt"),
  readAt: timestamp("readAt"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt"),
});
/** Manual site-addition requests; email delivery is independent of site creation. */
export const customerSiteRequests = mysqlTable("customer_site_requests", {
  id: int("id").autoincrement().primaryKey(),
  occurrenceId: varchar("occurrenceId", { length: 100 }).notNull(),
  customerId: int("customerId").notNull(),
  address: text("address").notNull(),
  unitNumber: varchar("unitNumber", { length: 50 }),
  postalCode: varchar("postalCode", { length: 20 }),
  actorUserId: int("actorUserId"),
  sourceJson: json("sourceJson").$type<Array<{ kind: "staff" | "cleo"; actorUserId?: number; requestId?: number; runId?: number; occurrenceId?: string; reviewedAt?: string; action?: "retry_confirmed_undelivered" | "dismissed" }>>().notNull(),
  state: varchar("state", { length: 24 }).notNull().default("pending"),
  deliveriesJson: json("deliveriesJson").$type<Record<string, "sending" | "sent" | "retry" | "review">>().notNull(),
  lastError: text("lastError"),
  leaseToken: varchar("leaseToken", { length: 64 }),
  leaseExpiresAt: timestamp("leaseExpiresAt"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
}, t => ({
  occurrenceUq: uniqueIndex("customer_site_requests_occurrence_uq").on(t.occurrenceId),
  customerIdx: index("customer_site_requests_customer_idx").on(t.customerId),
  stateIdx: index("customer_site_requests_state_idx").on(t.state, t.updatedAt),
}));

export type NotificationDeliveryLog =
  typeof notificationDeliveryLog.$inferSelect;
export type InsertNotificationDeliveryLog =
  typeof notificationDeliveryLog.$inferInsert;

/** Append-only Twilio status callback audit trail. */
export const notificationDeliveryProviderEvents = mysqlTable(
  "notification_delivery_provider_events",
  {
    id: bigint("id", { mode: "number" }).autoincrement().primaryKey(),
    deliveryLogId: bigint("deliveryLogId", { mode: "number" }),
    providerMessageSid: varchar("providerMessageSid", { length: 34 }).notNull(),
    providerStatus: varchar("providerStatus", { length: 24 }).notNull(),
    errorCode: varchar("errorCode", { length: 16 }),
    errorMessage: text("errorMessage"),
    rawPayload: json("rawPayload").$type<Record<string, unknown> | null>(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  }
);
export type NotificationDeliveryProviderEvent =
  typeof notificationDeliveryProviderEvents.$inferSelect;
export type InsertNotificationDeliveryProviderEvent =
  typeof notificationDeliveryProviderEvents.$inferInsert;

/**
 * Durable continuation for WhatsApp content that cannot be sent outside the
 * customer-service window. The original occurrence remains authoritative;
 * this row only owns opening the window and releasing that exact content.
 */
export const whatsappConversationFallbacks = mysqlTable(
  "whatsapp_conversation_fallbacks",
  {
    id: bigint("id", { mode: "number" }).autoincrement().primaryKey(),
    occurrenceKey: varchar("occurrenceKey", { length: 160 }).notNull(),
    phoneNumber: varchar("phoneNumber", { length: 30 }).notNull(),
    recipientName: varchar("recipientName", { length: 255 }).notNull(),
    detailMessage: text("detailMessage").notNull(),
    state: mysqlEnum("state", [
      "pending_window",
      "primary_sending",
      "primary_submitted",
      "primary_delivered",
      "resume_sending",
      "awaiting_response",
      "detail_sending",
      "detail_submitted",
      "detail_delivered",
      "failed",
      "superseded",
    ])
      .default("pending_window")
      .notNull(),
    sourceTriggerId: varchar("sourceTriggerId", { length: 120 }).notNull(),
    sourceDeliveryLogId: bigint("sourceDeliveryLogId", { mode: "number" }),
    failedTemplateSid: varchar("failedTemplateSid", { length: 40 }),
    primaryProviderSid: varchar("primaryProviderSid", { length: 64 }),
    resumeProviderSid: varchar("resumeProviderSid", { length: 64 }),
    detailProviderSid: varchar("detailProviderSid", { length: 64 }),
    failureCode: varchar("failureCode", { length: 32 }),
    lastError: text("lastError"),
    resumeSubmittedAt: timestamp("resumeSubmittedAt"),
    consentReceivedAt: timestamp("consentReceivedAt"),
    detailSubmittedAt: timestamp("detailSubmittedAt"),
    detailDeliveredAt: timestamp("detailDeliveredAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    occurrenceUniq: uniqueIndex("uq_whatsapp_fallback_occurrence").on(
      t.occurrenceKey
    ),
    pendingPhoneIdx: index("idx_whatsapp_fallback_pending_phone").on(
      t.phoneNumber,
      t.state
    ),
    primaryProviderIdx: index("idx_whatsapp_fallback_primary_provider").on(
      t.primaryProviderSid
    ),
    resumeProviderIdx: index("idx_whatsapp_fallback_resume_provider").on(
      t.resumeProviderSid
    ),
    detailProviderIdx: index("idx_whatsapp_fallback_detail_provider").on(
      t.detailProviderSid
    ),
  })
);
export type WhatsappConversationFallback =
  typeof whatsappConversationFallbacks.$inferSelect;

/** Per-user in-portal operational feed (Phase 1 ops center). */
export const operationalInboxNotifications = mysqlTable(
  "operational_inbox_notifications",
  {
    id: bigint("id", { mode: "number" }).autoincrement().primaryKey(),
    userId: int("userId").notNull(),
    eventType: varchar("eventType", { length: 64 }).notNull(),
    priority: varchar("priority", { length: 16 }).notNull().default("normal"),
    title: varchar("title", { length: 280 }).notNull(),
    subtitle: varchar("subtitle", { length: 280 }),
    body: text("body"),
    actionLabel: varchar("actionLabel", { length: 64 })
      .notNull()
      .default("Open"),
    deepLink: varchar("deepLink", { length: 512 }).notNull(),
    correlationId: varchar("correlationId", { length: 120 }).notNull(),
    recipientRole: varchar("recipientRole", { length: 16 }).notNull(),
    metadataJson: json("metadataJson").$type<Record<string, unknown> | null>(),
    readAt: timestamp("readAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  }
);
export type OperationalInboxNotification =
  typeof operationalInboxNotifications.$inferSelect;
export type InsertOperationalInboxNotification =
  typeof operationalInboxNotifications.$inferInsert;

/** Manual KPI collected attribution per operational month (auditable; checked before auto resolver). */
export const kpiCollectedAttributionOverrides = mysqlTable(
  "kpi_collected_attribution_overrides",
  {
    id: int("id").autoincrement().primaryKey(),
    operationalMonth: varchar("operationalMonth", { length: 7 }).notNull(),
    sourceType: varchar("sourceType", { length: 32 }).notNull(),
    sourceId: int("sourceId").notNull(),
    /** '' for full invoice row; 'partial' for portal partial-payment slice. */
    partialSlice: varchar("partialSlice", { length: 16 }).notNull().default(""),
    salespersonId: int("salespersonId").notNull(),
    assignedByUserId: int("assignedByUserId").notNull(),
    assignedAt: timestamp("assignedAt").defaultNow().notNull(),
    reason: varchar("reason", { length: 512 }),
    previousAttributionPath: text("previousAttributionPath"),
  }
);
export type KpiCollectedAttributionOverride =
  typeof kpiCollectedAttributionOverrides.$inferSelect;
export type InsertKpiCollectedAttributionOverride =
  typeof kpiCollectedAttributionOverrides.$inferInsert;

/** Chronological audit log for KPI collected PIC reassignments (append-only). */
export const kpiCollectedAttributionAudit = mysqlTable(
  "kpi_collected_attribution_audit",
  {
    id: int("id").autoincrement().primaryKey(),
    operationalMonth: varchar("operationalMonth", { length: 7 }).notNull(),
    sourceType: varchar("sourceType", { length: 32 }).notNull(),
    sourceId: int("sourceId").notNull(),
    partialSlice: varchar("partialSlice", { length: 16 }).notNull().default(""),
    previousSalespersonId: int("previousSalespersonId"),
    newSalespersonId: int("newSalespersonId").notNull(),
    assignedByUserId: int("assignedByUserId").notNull(),
    assignedAt: timestamp("assignedAt").defaultNow().notNull(),
    reason: varchar("reason", { length: 512 }),
    previousAttributionPath: text("previousAttributionPath"),
    previousDisplayPic: varchar("previousDisplayPic", { length: 128 }),
    newDisplayPic: varchar("newDisplayPic", { length: 128 }),
    attributionSource: varchar("attributionSource", { length: 128 }),
  }
);
export type KpiCollectedAttributionAudit =
  typeof kpiCollectedAttributionAudit.$inferSelect;

/** Atomic dispatch claims — one row per alert or channel correlation key. */
export const operationalNotificationDispatchClaims = mysqlTable(
  "operational_notification_dispatch_claims",
  {
    claimKey: varchar("claimKey", { length: 160 }).notNull().primaryKey(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  }
);

// ─── Cleo WhatsApp Sessions ──────────────────────────────────────────────────
// Tracks the conversation state for each phone number interacting with Cleo
export const cleoSessions = mysqlTable("cleo_sessions", {
  id: int("id").autoincrement().primaryKey(),
  phoneNumber: varchar("phoneNumber", { length: 30 }).notNull().unique(), // e.g. whatsapp:+6591234567
  role: mysqlEnum("role", ["technician", "staff", "customer", "unknown"])
    .default("unknown")
    .notNull(),
  userId: int("userId"), // users.id if matched to a portal user
  customerId: int("customerId"), // customers.id if matched to a customer
  state: varchar("state", { length: 100 }).default("idle").notNull(),
  // Current active SVR context
  activeSvrId: int("activeSvrId"), // siteVisits.id
  activeSvrNumber: varchar("activeSvrNumber", { length: 20 }),
  arrivedAt: timestamp("arrivedAt"), // time technician confirmed arrival
  // Authoritative transactional workflow — not inferred from leftover FSM state.
  activeWorkflowKind: varchar("activeWorkflowKind", { length: 32 }),
  activeWorkflowVersion: int("activeWorkflowVersion"),
  activeWorkflowStartedAt: timestamp("activeWorkflowStartedAt"),
  activeWorkflowStartedBy: varchar("activeWorkflowStartedBy", { length: 32 }),
  activeWorkflowOwnerUserId: int("activeWorkflowOwnerUserId"),
  // Pending confirmation context (what we are waiting for the user to confirm)
  pendingContext: json("pendingContext"), // arbitrary JSON for multi-step flows
  lastMessageAt: timestamp("lastMessageAt").defaultNow().notNull(),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
});
export type CleoSession = typeof cleoSessions.$inferSelect;
export type InsertCleoSession = typeof cleoSessions.$inferInsert;

// ─── Cleo Conversation Log ───────────────────────────────────────────────────
// Append-only log of all WhatsApp messages exchanged via Cleo
export const cleoMessages = mysqlTable("cleo_messages", {
  id: int("id").autoincrement().primaryKey(),
  phoneNumber: varchar("phoneNumber", { length: 30 }).notNull(),
  direction: mysqlEnum("direction", ["inbound", "outbound"]).notNull(),
  body: text("body").notNull(),
  svrId: int("svrId"), // siteVisits.id if associated with a visit
  mediaUrl: text("mediaUrl"), // S3 URL if message contained media
  twilioSid: varchar("twilioSid", { length: 64 }), // Twilio message SID for dedup
  createdAt: timestamp("createdAt").defaultNow().notNull(),
});
export type CleoMessage = typeof cleoMessages.$inferSelect;
export type InsertCleoMessage = typeof cleoMessages.$inferInsert;

/** Durable 10 PM SGT automatic SVR sign-out and notification outbox. */
export const svrAutoSignoutOccurrences = mysqlTable(
  "svr_auto_signout_occurrences",
  {
    id: int("id").autoincrement().primaryKey(),
    visitId: int("visitId").notNull(),
    cutoffDay: varchar("cutoffDay", { length: 10 }).notNull(),
    arrivedAt: timestamp("arrivedAt").notNull(),
    endedAt: timestamp("endedAt").notNull(),
    technicianUserId: int("technicianUserId"),
    notificationStatus: mysqlEnum("notificationStatus", ["pending", "sending", "submitted", "skipped", "failed"])
      .default("pending")
      .notNull(),
    notificationAttempts: int("notificationAttempts").default(0).notNull(),
    notificationClaimedAt: timestamp("notificationClaimedAt"),
    notificationSubmittedAt: timestamp("notificationSubmittedAt"),
    providerMessageSid: varchar("providerMessageSid", { length: 64 }),
    lastError: text("lastError"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  (t) => ({
    visitCutoffUq: uniqueIndex("svr_auto_signout_visit_cutoff_uq").on(t.visitId, t.cutoffDay),
    notificationQueueIdx: index("svr_auto_signout_notification_queue_idx").on(
      t.notificationStatus,
      t.notificationClaimedAt,
    ),
  }),
);
export type SvrAutoSignoutOccurrence = typeof svrAutoSignoutOccurrences.$inferSelect;
export type InsertSvrAutoSignoutOccurrence = typeof svrAutoSignoutOccurrences.$inferInsert;

// ─── Cleo Conversations (server-side persistence for Portal + WhatsApp) ──────
export const cleoConversations = mysqlTable("cleo_conversations", {
  id: int("id").autoincrement().primaryKey(),
  channel: varchar("channel", { length: 20 }).notNull(), // 'portal' | 'whatsapp'
  userId: int("userId"),
  phoneNumber: varchar("phoneNumber", { length: 30 }),
  role: varchar("role", { length: 12 }).notNull(), // 'user' | 'assistant'
  content: text("content").notNull(),
  memory: json("memory").$type<Record<string, unknown> | null>(),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
});
export type CleoConversation = typeof cleoConversations.$inferSelect;
export type InsertCleoConversation = typeof cleoConversations.$inferInsert;

/** Cleo could not confidently bind free-text WhatsApp input to an expected job field — admin resolves here. */
export const cleoInputMismatches = mysqlTable("cleo_input_mismatches", {
  id: int("id").autoincrement().primaryKey(),
  phoneNumber: varchar("phoneNumber", { length: 30 }).notNull(),
  rawMessage: text("rawMessage").notNull(),
  possibleJobId: int("possibleJobId"),
  expectedField: varchar("expectedField", { length: 64 }),
  reason: mysqlEnum("reason", [
    "low_confidence_match",
    "no_pending_input",
    "ambiguous_reply",
  ]).notNull(),
  resolvedAt: timestamp("resolvedAt"),
  resolvedByUserId: int("resolvedByUserId"),
  resolutionSiteVisitId: int("resolutionSiteVisitId"),
  resolutionAddress: text("resolutionAddress"),
  overwriteAddress: boolean("overwriteAddress").default(false).notNull(),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
});
export type CleoInputMismatch = typeof cleoInputMismatches.$inferSelect;
export type InsertCleoInputMismatch = typeof cleoInputMismatches.$inferInsert;

// ─── WhatsApp Media (photos/files sent by technicians via Cleo) ──────────────
export const whatsappMedia = mysqlTable("whatsapp_media", {
  id: int("id").autoincrement().primaryKey(),
  svrId: int("svrId").notNull(), // siteVisits.id
  phoneNumber: varchar("phoneNumber", { length: 30 }).notNull(),
  s3Url: text("s3Url").notNull(),
  s3Key: text("s3Key").notNull(),
  mimeType: varchar("mimeType", { length: 100 }),
  byteSize: int("byteSize"),
  caption: text("caption"), // any text sent with the photo
  createdAt: timestamp("createdAt").defaultNow().notNull(),
});
export type WhatsappMedia = typeof whatsappMedia.$inferSelect;
export type InsertWhatsappMedia = typeof whatsappMedia.$inferInsert;

// ─── Customer Notices (status-change notifications sent to customers) ─────────
// Created automatically when SVR or RO status changes; PIC sends via Email/WhatsApp
export const customerNotices = mysqlTable("customer_notices", {
  id: int("id").autoincrement().primaryKey(),
  sourceType: mysqlEnum("sourceType", ["svr", "ro"]).notNull(),
  sourceId: int("sourceId").notNull(), // siteVisits.id or serviceJobs.id
  referenceNumber: varchar("referenceNumber", { length: 20 }), // SVR-2026-0001 or CLA-2026-0001
  customerId: int("customerId").notNull(),
  customerName: varchar("customerName", { length: 255 }),
  picId: int("picId"), // users.id of the PIC/salesperson
  picName: varchar("picName", { length: 255 }),
  statusAtCreate: varchar("statusAtCreate", { length: 100 }).notNull(), // status that triggered this notice
  statusLabel: varchar("statusLabel", { length: 100 }), // human-readable label
  channel: mysqlEnum("channel", ["whatsapp", "email"]), // null until sent
  messageBody: text("messageBody"), // null until sent
  sentAt: timestamp("sentAt"), // null = pending
  sentBy: varchar("sentBy", { length: 255 }),
  sentById: int("sentById"),
  dismissedAt: timestamp("dismissedAt"), // null = not dismissed
  dismissedBy: varchar("dismissedBy", { length: 255 }),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
});
export type CustomerNotice = typeof customerNotices.$inferSelect;
export type InsertCustomerNotice = typeof customerNotices.$inferInsert;

// ─── Invoice Counters ─────────────────────────────────────────────────────────
export const invoiceCounters = mysqlTable("invoice_counters", {
  id: int("id").autoincrement().primaryKey(),
  year: int("year").notNull().unique(),
  lastSequence: int("lastSequence").default(0).notNull(),
});

export type InvoiceCounter = typeof invoiceCounters.$inferSelect;

// ─── RO Invoice Counters ──────────────────────────────────────────────────────
export const roInvoiceCounters = mysqlTable("ro_invoice_counters", {
  id: int("id").autoincrement().primaryKey(),
  year: int("year").notNull().unique(),
  lastSequence: int("lastSequence").default(0).notNull(),
});
export type ROInvoiceCounter = typeof roInvoiceCounters.$inferSelect;

// ─── Site Visit Assignees (multi-tech/programmer/installer) ──────────────────
// One row per person assigned to a site visit (technician, programmer, installer)
export const siteVisitAssignees = mysqlTable("site_visit_assignees", {
  id: int("id").autoincrement().primaryKey(),
  visitId: int("visitId").notNull(), // siteVisits.id
  userId: int("userId").notNull(), // users.id
  userName: varchar("userName", { length: 255 }).notNull(),
  role: mysqlEnum("role", ["technician", "programmer", "installer"]).notNull(),
  notifiedAt: timestamp("notifiedAt"), // when WhatsApp/email was sent to this person
  createdAt: timestamp("createdAt").defaultNow().notNull(),
});
export type SiteVisitAssignee = typeof siteVisitAssignees.$inferSelect;
export type InsertSiteVisitAssignee = typeof siteVisitAssignees.$inferInsert;

// ─── Customer Sites (multi-site support) ─────────────────────────────────────
// Each customer can have multiple service sites (addresses)
export const customerSites = mysqlTable(
  "customer_sites",
  {
    id: int("id").autoincrement().primaryKey(),
    customerId: int("customerId").notNull(), // customers.id
    label: varchar("label", { length: 100 }), // e.g. "Main Residence", "Office"
    address: text("address").notNull(),
    unitNumber: varchar("unitNumber", { length: 50 }),
    postalCode: varchar("postalCode", { length: 20 }),
    isDefault: boolean("isDefault").default(false).notNull(),
    archivedAt: timestamp("archivedAt"),
    archivedByUserId: int("archivedByUserId"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    customerActiveIdx: index("idx_customer_sites_customer_archived").on(
      t.customerId,
      t.archivedAt
    ),
  })
);
export type CustomerSite = typeof customerSites.$inferSelect;
export type InsertCustomerSite = typeof customerSites.$inferInsert;

export const CUSTOMER_SITE_AUDIT_ACTIONS = [
  "created",
  "updated",
  "default_changed",
  "archived",
  "restored",
  "duplicate_detected",
] as const;
export type CustomerSiteAuditAction =
  (typeof CUSTOMER_SITE_AUDIT_ACTIONS)[number];

/** Append-only customer site governance audit. Never stores credentials or raw internals. */
export const customerSiteAuditEvents = mysqlTable(
  "customer_site_audit_events",
  {
    id: int("id").autoincrement().primaryKey(),
    occurrenceId: varchar("occurrenceId", { length: 80 }).notNull(),
    siteId: int("siteId").notNull(),
    customerId: int("customerId").notNull(),
    commandKind: varchar("commandKind", { length: 32 })
      .notNull()
      .default("add"),
    commandFingerprint: varchar("commandFingerprint", { length: 191 })
      .notNull()
      .default(""),
    targetSiteId: int("targetSiteId"),
    action: mysqlEnum("action", CUSTOMER_SITE_AUDIT_ACTIONS).notNull(),
    actorUserId: int("actorUserId").notNull(),
    actorRole: varchar("actorRole", { length: 32 }).notNull(),
    actorNameSnapshot: varchar("actorNameSnapshot", { length: 255 }),
    sourceSurface: varchar("sourceSurface", { length: 64 })
      .notNull()
      .default("customer_card"),
    reason: varchar("reason", { length: 500 }),
    beforeSnapshotJson: json("beforeSnapshotJson"),
    afterSnapshotJson: json("afterSnapshotJson"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    occurrenceUid: uniqueIndex("uq_customer_site_audit_occurrence").on(
      t.occurrenceId
    ),
    siteIdx: index("idx_customer_site_audit_site").on(t.siteId, t.createdAt),
    customerIdx: index("idx_customer_site_audit_customer").on(
      t.customerId,
      t.createdAt
    ),
  })
);
export type CustomerSiteAuditEvent =
  typeof customerSiteAuditEvents.$inferSelect;
export type InsertCustomerSiteAuditEvent =
  typeof customerSiteAuditEvents.$inferInsert;

// ─── Customer logs (walk-in / touchpoint capture, sales) ───────────────────
export const customerLogs = mysqlTable(
  "customer_logs",
  {
    id: int("id").autoincrement().primaryKey(),
    customerId: int("customerId").notNull(),
    salespersonId: int("salespersonId"),
    salespersonName: varchar("salespersonName", { length: 255 }),
    nameSnapshot: varchar("nameSnapshot", { length: 255 }),
    phoneSnapshot: varchar("phoneSnapshot", { length: 50 }),
    leadSource: varchar("leadSource", { length: 100 })
      .notNull()
      .default("Walk-in"),
    referredBy: varchar("referredBy", { length: 255 }),
    sourceNotes: varchar("sourceNotes", { length: 500 }),
    notes: text("notes").notNull(),
    outcomeNext: text("outcomeNext"),
    createdNewCustomer: boolean("createdNewCustomer").default(false).notNull(),
    followedUpAt: timestamp("followedUpAt"),
    createdByUserId: int("createdByUserId"),
    proposalId: int("proposalId"),
    cleoSignalId: int("cleoSignalId"),
    customerReminderId: int("customerReminderId"),
    interactionSource: varchar("interactionSource", { length: 32 }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    proposalIdIdx: index("idx_customer_logs_proposal_id").on(t.proposalId),
    cleoSignalIdIdx: index("idx_customer_logs_cleo_signal_id").on(
      t.cleoSignalId
    ),
    customerReminderIdIdx: index("idx_customer_logs_customer_reminder_id").on(
      t.customerReminderId
    ),
    cleoSignalIdempotencyUid: uniqueIndex("uq_customer_logs_cleo_signal_id").on(
      t.cleoSignalId
    ),
  })
);
export type CustomerLog = typeof customerLogs.$inferSelect;
export type InsertCustomerLog = typeof customerLogs.$inferInsert;

// ─── Sales Deal Log (inbound from Sales Portal webhook) ──────────────────────
export const salesDealLog = mysqlTable("sales_deal_log", {
  id: int("id").autoincrement().primaryKey(),
  customerId: int("customerId").notNull(), // matched Service Portal customer
  event: varchar("event", { length: 50 }).notNull(), // "deal.projected" | "deal.approved" | "deal.completed"
  quoteRef: varchar("quoteRef", { length: 100 }).notNull(),
  salesPortalProposalId: int("salesPortalProposalId"),
  salespersonName: varchar("salespersonName", { length: 255 }),
  totalAmount: decimal("totalAmount", { precision: 12, scale: 2 }),
  occurredAt: timestamp("occurredAt").notNull(),
  salespersonEmail: varchar("salespersonEmail", { length: 320 }),
  invoiceStatus: mysqlEnum("invoiceStatus", ["none", "raised", "paid"])
    .default("none")
    .notNull(),
  invoiceNumber: varchar("invoiceNumber", { length: 50 }),
  invoiceAmount: decimal("invoiceAmount", { precision: 12, scale: 2 }),
  invoiceDescription: text("invoiceDescription"),
  invoiceToken: varchar("invoiceToken", { length: 128 }),
  gstApplied: boolean("gstApplied").default(false).notNull(),
  stripeFeeApplied: boolean("stripeFeeApplied").default(false).notNull(),
  paymentTerms: varchar("paymentTerms", { length: 50 }),
  invoiceRaisedAt: timestamp("invoiceRaisedAt"),
  invoiceRaisedBy: varchar("invoiceRaisedBy", { length: 255 }),
  invoicePaidAt: timestamp("invoicePaidAt"),
  invoicePaidBy: varchar("invoicePaidBy", { length: 255 }),
  proposalUrl: varchar("proposalUrl", { length: 512 }), // e.g. https://sales.claritygroup.com.sg/proposal/abc123
  rawPayload: json("rawPayload"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
});
export type SalesDealLog = typeof salesDealLog.$inferSelect;
export type InsertSalesDealLog = typeof salesDealLog.$inferInsert;

// ─── Webhook Log (inbound webhook delivery audit trail) ──────────────────────
export const webhookLog = mysqlTable("webhook_log", {
  id: int("id").autoincrement().primaryKey(),
  source: varchar("source", { length: 50 }).notNull().default("sales_portal"),
  event: varchar("event", { length: 100 }).notNull(),
  quoteRef: varchar("quoteRef", { length: 100 }),
  salesPortalProposalId: int("salesPortalProposalId"),
  matchedCustomerId: int("matchedCustomerId"),
  matchedCustomerName: varchar("matchedCustomerName", { length: 255 }),
  matchMethod: varchar("matchMethod", { length: 50 }), // "direct_id" | "email" | "phone" | "none"
  action: varchar("action", { length: 100 }).notNull(),
  statusCode: int("statusCode").notNull().default(200),
  errorMessage: text("errorMessage"),
  rawPayload: json("rawPayload"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
});
export type WebhookLog = typeof webhookLog.$inferSelect;
export type InsertWebhookLog = typeof webhookLog.$inferInsert;

// ─── Suppliers (procurement directory) ───────────────────────────────────────
export const suppliers = mysqlTable("suppliers", {
  id: int("id").autoincrement().primaryKey(),
  name: varchar("name", { length: 255 }).notNull(),
  contactPerson: varchar("contactPerson", { length: 255 }),
  email: varchar("email", { length: 255 }),
  phone: varchar("phone", { length: 50 }),
  address: text("address"),
  brands: text("brands"), // JSON-serialised string array of brand names
  paymentTerms: varchar("paymentTerms", { length: 255 }),
  notes: text("notes"),
  active: boolean("active").notNull().default(true),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
});

export type Supplier = typeof suppliers.$inferSelect;
export type InsertSupplier = typeof suppliers.$inferInsert;

// ─── Integration Sync Log (outbound/inbound audit trail) ─────────────────────
export const integrationSyncLog = mysqlTable("integration_sync_log", {
  id: int("id").autoincrement().primaryKey(),
  direction: varchar("direction", { length: 20 }).notNull(),
  event_type: varchar("event_type", { length: 50 }).notNull(),
  payload: json("payload"),
  response_code: int("response_code"),
  response_body: text("response_body"),
  matched_customer_id: int("matched_customer_id"),
  matched_customer_name: varchar("matched_customer_name", { length: 255 }),
  success: boolean("success").default(false),
  error_message: text("error_message"),
  created_at: bigint("created_at", { mode: "number" }).notNull(),
});

export type IntegrationSyncLog = typeof integrationSyncLog.$inferSelect;
export type InsertIntegrationSyncLog = typeof integrationSyncLog.$inferInsert;

/** Xero mirror sync state for deal + portal invoices (Phase 1). */
export const XERO_SYNC_STATUSES = [
  "pending",
  "synced",
  "failed",
  "skipped",
  "voided",
  "action_required",
] as const;
export type XeroSyncStatus = (typeof XERO_SYNC_STATUSES)[number];

export type XeroPaymentAuditEventType =
  | "xero.payment_sync_started"
  | "xero.payment_synced"
  | "xero.payment_sync_failed"
  | "xero.payment_retry_requested"
  | "xero.inbound_paid_hydrate";

export type XeroPaymentAuditEvent = {
  type: XeroPaymentAuditEventType;
  at: string;
  dealInvoiceId?: number;
  portalInvoiceId?: number;
  xeroInvoiceId?: string | null;
  xeroPaymentId?: string | null;
  paidAmount?: number;
  paidDate?: string;
  paymentKey?: string;
  errorMessage?: string;
  /** Finance/Admin who initiated Send to Xero (or system for gateway auto-sync). */
  actorName?: string | null;
  note?: string | null;
};

// ─── Deal Invoices (per-instalment invoices for Sales Portal proposals) ───────
export const dealInvoices = mysqlTable(
  "deal_invoices",
  {
    creditAppliedAmount: decimal("creditAppliedAmount", { precision: 12, scale: 2 }).notNull().default("0.00"),
    creditSettledAt: timestamp("creditSettledAt"),
    creditOperationId: int("creditOperationId"),
    id: int("id").autoincrement().primaryKey(),
    dealId: int("dealId").notNull(),
    customerId: int("customerId").notNull(),
    quoteRef: varchar("quoteRef", { length: 100 }).notNull(),
    invoiceNumber: varchar("invoiceNumber", { length: 50 }).notNull(),
    invoiceToken: varchar("invoiceToken", { length: 128 }).notNull(),
    instalmentStage: varchar("instalmentStage", { length: 100 })
      .notNull()
      .default("Confirmation"),
    instalmentPercentage: decimal("instalmentPercentage", {
      precision: 5,
      scale: 2,
    })
      .notNull()
      .default("50.00"),
    baseAmount: decimal("baseAmount", { precision: 12, scale: 2 }).notNull(),
    instalmentBaseAmount: decimal("instalmentBaseAmount", {
      precision: 12,
      scale: 2,
    }).notNull(),
    gstApplied: boolean("gstApplied").notNull().default(false),
    gstAmount: decimal("gstAmount", { precision: 12, scale: 2 })
      .notNull()
      .default("0"),
    stripeFeeApplied: boolean("stripeFeeApplied").notNull().default(false),
    stripeFeeAmount: decimal("stripeFeeAmount", { precision: 12, scale: 2 })
      .notNull()
      .default("0"),
    finalAmount: decimal("finalAmount", { precision: 12, scale: 2 }).notNull(),
    paymentMethod: mysqlEnum("paymentMethod", [
      "paynow",
      "card",
      "bank_transfer",
      "cash",
    ])
      .notNull()
      .default("paynow"),
    description: text("description"),
    invoiceStatus: mysqlEnum("invoiceStatus", [
      "raised",
      "paid",
      "outstanding",
      "voided",
    ])
      .notNull()
      .default("raised"),
    raisedAt: timestamp("raisedAt").defaultNow().notNull(),
    raisedBy: varchar("raisedBy", { length: 255 }),
    paidAt: timestamp("paidAt"),
    paidBy: varchar("paidBy", { length: 255 }),
    /** First time the customer opened this invoice (public page, not staff) or clicked through to it. */
    customerOpenedAt: timestamp("customerOpenedAt"),
    customerOpenedSource: varchar("customerOpenedSource", { length: 32 }),
    salesPortalNotified: boolean("salesPortalNotified")
      .notNull()
      .default(false),
    stripePaymentLink: text("stripePaymentLink"), // legacy; do not use for new links — see paymentLink (HitPay)
    paymentLink: text("paymentLink"),
    hitpayPaymentId: varchar("hitpayPaymentId", { length: 128 }),
    cardPaymentLinkEnabled: boolean("cardPaymentLinkEnabled")
      .notNull()
      .default(false),
    scheduledDate: varchar("scheduledDate", { length: 20 }), // optional expected payment date (YYYY-MM) for non-confirmation terms
    // Send tracking — who sent the invoice to the customer and when
    whatsappSentAt: timestamp("whatsappSentAt"),
    whatsappSentBy: varchar("whatsappSentBy", { length: 255 }),
    emailSentAt: timestamp("emailSentAt"),
    emailSentBy: varchar("emailSentBy", { length: 255 }),
    reminderDay5SentAt: timestamp("reminderDay5SentAt"),
    reminderDay10SentAt: timestamp("reminderDay10SentAt"),
    escalatedDay14At: timestamp("escalatedDay14At"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    /** Finance payment receipt email (Phase 1); idempotent — not invoice emailSentAt. */
    receiptAcknowledgedAt: timestamp("receiptAcknowledgedAt"),
    receiptEmailSentAt: timestamp("receiptEmailSentAt"),
    receiptAcknowledgedBy: varchar("receiptAcknowledgedBy", { length: 255 }),
    receiptEmailMessageId: varchar("receiptEmailMessageId", { length: 255 }),
    xeroInvoiceId: varchar("xeroInvoiceId", { length: 64 }),
    xeroInvoiceNumber: varchar("xeroInvoiceNumber", { length: 50 }),
    xeroSyncStatus: mysqlEnum("xeroSyncStatus", XERO_SYNC_STATUSES),
    xeroLastSyncedAt: timestamp("xeroLastSyncedAt"),
    xeroLastError: text("xeroLastError"),
    xeroPaymentId: varchar("xeroPaymentId", { length: 64 }),
    xeroPaymentSyncedAt: timestamp("xeroPaymentSyncedAt"),
    xeroPaymentSyncStatus: mysqlEnum(
      "xeroPaymentSyncStatus",
      XERO_SYNC_STATUSES
    ),
    xeroPaymentSyncError: text("xeroPaymentSyncError"),
    /** Per-payment idempotency records `{ key, xeroPaymentId, amount, paidAt }[]` (partial + full). */
    xeroPaymentsJson: json("xeroPaymentsJson").$type<Array<{
      key: string;
      xeroPaymentId: string;
      amount: number;
      paidAt: string;
    }> | null>(),
    /** Append-only audit trail for Xero payment sync timeline events. */
    xeroPaymentAuditLog: json("xeroPaymentAuditLog").$type<
      XeroPaymentAuditEvent[] | null
    >(),
    /** Set on the cancelled original when Finance regenerates a replacement invoice. */
    replacedByInvoiceId: int("replacedByInvoiceId"),
    /** Set on the replacement invoice pointing at the cancelled original. */
    replacesInvoiceId: int("replacesInvoiceId"),
    regeneratedAt: timestamp("regeneratedAt"),
    regeneratedByUserId: int("regeneratedByUserId"),
    /** `regeneration` when voided via Regenerate Invoice; null for other voids. */
    cancellationKind: varchar("cancellationKind", { length: 32 }),
    /**
     * Smart Group Billing role:
     * - customer_wrapper: customer-facing total/payment; never sync to Xero in mixed/existing modes
     * - accounting_component: Xero-only subtotal for previously uninvoiced obligations
     * - full_synced: classic Case A — customer-facing parent that is also the accounting invoice
     */
    groupBillingRole: varchar("groupBillingRole", { length: 40 }),
    /** Accounting component → customer-facing wrapper id. */
    groupBillingWrapperInvoiceId: int("groupBillingWrapperInvoiceId"),
    skipXeroInvoiceSync: boolean("skipXeroInvoiceSync")
      .notNull()
      .default(false),
    suppressCustomerComms: boolean("suppressCustomerComms")
      .notNull()
      .default(false),
    suppressPaymentLink: boolean("suppressPaymentLink")
      .notNull()
      .default(false),
  },
  t => ({
    replacedByUid: uniqueIndex("deal_invoices_replaced_by_uid").on(
      t.replacedByInvoiceId
    ),
    replacesUid: uniqueIndex("deal_invoices_replaces_uid").on(
      t.replacesInvoiceId
    ),
  })
);

export type DealInvoice = typeof dealInvoices.$inferSelect;
export type InsertDealInvoice = typeof dealInvoices.$inferInsert;

/** Per-proposal lines on a consolidated Group Billing parent deal invoice. */
export const dealInvoiceAllocations = mysqlTable("deal_invoice_allocations", {
  id: int("id").autoincrement().primaryKey(),
  parentDealInvoiceId: int("parentDealInvoiceId").notNull(),
  billingEventId: int("billingEventId").notNull(),
  billingGroupId: int("billingGroupId").notNull(),
  proposalId: int("proposalId").notNull(),
  proposalRefSnapshot: varchar("proposalRefSnapshot", { length: 64 }).notNull(),
  proposalTitleSnapshot: varchar("proposalTitleSnapshot", { length: 255 }),
  projectId: int("projectId"),
  customerId: int("customerId").notNull(),
  revenueTrancheId: int("revenueTrancheId"),
  sequenceIndex: int("sequenceIndex").notNull().default(0),
  instalmentStage: varchar("instalmentStage", { length: 100 }).notNull(),
  /** Unique while open; rewritten on void so recreate can reclaim (MySQL has no partial unique). */
  obligationKey: varchar("obligationKey", { length: 191 }).notNull().unique(),
  allocatedAmountSgd: decimal("allocatedAmountSgd", {
    precision: 12,
    scale: 2,
  }).notNull(),
  /** Underlying Portal/Xero accounting invoice for this allocation (may differ from parent wrapper). */
  accountingDealInvoiceId: int("accountingDealInvoiceId"),
  /** existing_xero_invoice | new_xero_consolidated */
  accountingTreatment: varchar("accountingTreatment", { length: 40 }),
  allocatedGstAmountSgd: decimal("allocatedGstAmountSgd", {
    precision: 12,
    scale: 2,
  }),
  allocatedCardFeeAmountSgd: decimal("allocatedCardFeeAmountSgd", {
    precision: 12,
    scale: 2,
  }),
  allocatedPayableAmountSgd: decimal("allocatedPayableAmountSgd", {
    precision: 12,
    scale: 2,
  }),
  collectedAmountSgd: decimal("collectedAmountSgd", { precision: 12, scale: 2 })
    .notNull()
    .default("0.00"),
  /** pending | synced | reconciliation_required | skipped | null */
  xeroPaymentReconciliationStatus: varchar("xeroPaymentReconciliationStatus", {
    length: 40,
  }),
  salespersonIdSnapshot: int("salespersonIdSnapshot"),
  picUserIdSnapshot: int("picUserIdSnapshot"),
  allocationStatus: mysqlEnum("allocationStatus", [
    "open",
    "collected",
    "voided",
  ])
    .notNull()
    .default("open"),
  collectedAt: timestamp("collectedAt"),
  voidedAt: timestamp("voidedAt"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
});

export type DealInvoiceAllocation = typeof dealInvoiceAllocations.$inferSelect;
export type InsertDealInvoiceAllocation =
  typeof dealInvoiceAllocations.$inferInsert;

// ─── Legacy Invoice Imports (finance/admin uploaded external invoices) ───────
export const legacyInvoices = mysqlTable("legacy_invoices", {
  id: int("id").autoincrement().primaryKey(),
  invoiceNumber: varchar("invoiceNumber", { length: 100 }).notNull().unique(),
  customerId: int("customerId").notNull(),
  customerNameSnapshot: varchar("customerNameSnapshot", {
    length: 255,
  }).notNull(),
  customerAddressSnapshot: text("customerAddressSnapshot"),
  customerContactSnapshot: varchar("customerContactSnapshot", { length: 64 }),
  customerEmailSnapshot: varchar("customerEmailSnapshot", { length: 320 }),
  invoiceDate: timestamp("invoiceDate"),
  amount: decimal("amount", { precision: 12, scale: 2 }).notNull(),
  source: varchar("source", { length: 32 }).notNull().default("legacy_import"),
  uploadedPdfUrl: text("uploadedPdfUrl"),
  uploadedPdfKey: text("uploadedPdfKey"),
  status: mysqlEnum("status", ["unpaid", "paid", "voided"])
    .notNull()
    .default("unpaid"),
  paidAt: timestamp("paidAt"),
  paidBy: varchar("paidBy", { length: 255 }),
  createdByUserId: int("createdByUserId").notNull(),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
});
export type LegacyInvoice = typeof legacyInvoices.$inferSelect;
export type InsertLegacyInvoice = typeof legacyInvoices.$inferInsert;

/** Legacy PDF import sessions (audit trail) that can convert into portal invoices. */
export const legacyInvoiceImports = mysqlTable("legacy_invoice_imports", {
  id: int("id").autoincrement().primaryKey(),
  storageKey: text("storageKey").notNull(),
  originalFilename: varchar("originalFilename", { length: 512 }).notNull(),
  rawExtractedText: text("rawExtractedText"),
  parsedJson: json("parsedJson"),
  warningsJson: json("warningsJson"),
  confidence: decimal("confidence", { precision: 5, scale: 2 }),
  status: mysqlEnum("status", ["uploaded", "parsed", "converted", "failed"])
    .notNull()
    .default("uploaded"),
  createdByUserId: int("createdByUserId").notNull(),
  convertedPortalInvoiceId: int("convertedPortalInvoiceId"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
});
export type LegacyInvoiceImport = typeof legacyInvoiceImports.$inferSelect;
export type InsertLegacyInvoiceImport =
  typeof legacyInvoiceImports.$inferInsert;

/** Finance-created invoices from Service Portal (Create Invoice flow). */
export const portalInvoices = mysqlTable(
  "portal_invoices",
  {
    creditAppliedAmount: decimal("creditAppliedAmount", { precision: 12, scale: 2 }).notNull().default("0.00"),
    creditSettledAt: timestamp("creditSettledAt"),
    creditOperationId: int("creditOperationId"),
    id: int("id").autoincrement().primaryKey(),
    invoiceNumber: varchar("invoiceNumber", { length: 50 }).notNull().unique(),
    invoiceToken: varchar("invoiceToken", { length: 128 }).notNull().unique(),
    customerId: int("customerId").notNull(),
    billingAddress: text("billingAddress"),
    description: text("description").notNull(),
    lineItemsJson: json("lineItemsJson").$type<
      | {
          id?: string;
          description: string;
          quantity?: string | number;
          unitAmount?: string;
          amount?: string;
          basis?: "invoice_base" | "legacy_as_stored";
        }[]
      | null
    >(),
    amountInclGst: decimal("amountInclGst", {
      precision: 12,
      scale: 2,
    }).notNull(),
    gstApplied: boolean("gstApplied").notNull().default(true),
    paymentMethod: varchar("paymentMethod", { length: 32 })
      .notNull()
      .default("paynow"),
    paymentTerms: text("paymentTerms"),
    dueDate: varchar("dueDate", { length: 10 }),
    invoiceDate: timestamp("invoiceDate").notNull(),
    sourceType: mysqlEnum("sourceType", ["manual", "ro", "svr", "proposal"])
      .notNull()
      .default("manual"),
    sourceJobId: int("sourceJobId"),
    sourceVisitId: int("sourceVisitId"),
    sourceProposalId: int("sourceProposalId"),
    /** Optional explicit linkage to proposal_headers for manual/project invoices. */
    proposalId: int("proposalId"),
    sourceRef: varchar("sourceRef", { length: 100 }),
    picName: varchar("picName", { length: 255 }),
    picEmail: varchar("picEmail", { length: 320 }),
    /** Primary PIC for KPI / NBA: users.id. Nullable until backfill or explicit assignment. */
    picUserId: int("picUserId"),
    /** Optional uploaded approved proposal (PDF) attached to this invoice. */
    originalProposalStorageKey: text("originalProposalStorageKey"),
    originalProposalFileName: varchar("originalProposalFileName", {
      length: 512,
    }),
    pdfKey: text("pdfKey"),
    paymentLink: text("paymentLink"),
    hitpayPaymentId: varchar("hitpayPaymentId", { length: 128 }),
    cardPaymentLinkEnabled: boolean("cardPaymentLinkEnabled")
      .notNull()
      .default(false),
    cardAdminFeeApplied: boolean("cardAdminFeeApplied")
      .notNull()
      .default(false),
    invoiceStatus: mysqlEnum("invoiceStatus", [
      "raised",
      "partial",
      "paid",
      "voided",
    ])
      .notNull()
      .default("raised"),
    paidAt: timestamp("paidAt"),
    paidBy: varchar("paidBy", { length: 255 }),
    /** Append-only partial payment events `{ amount, paidAt, notes? }` for KPI + audit (see 0060 migration). */
    partialPaymentsJson: json("partialPaymentsJson").$type<Array<{
      amount: number;
      paidAt: string;
      notes?: string;
    }> | null>(),
    whatsappSentAt: timestamp("whatsappSentAt"),
    emailSentAt: timestamp("emailSentAt"),
    firstSentAt: timestamp("firstSentAt"),
    /** Finance payment receipt email (Phase 1); not invoice send tracking columns above. */
    receiptAcknowledgedAt: timestamp("receiptAcknowledgedAt"),
    receiptEmailSentAt: timestamp("receiptEmailSentAt"),
    receiptAcknowledgedBy: varchar("receiptAcknowledgedBy", { length: 255 }),
    receiptEmailMessageId: varchar("receiptEmailMessageId", { length: 255 }),
    createdByUserId: int("createdByUserId").notNull(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
    xeroInvoiceId: varchar("xeroInvoiceId", { length: 64 }),
    xeroInvoiceNumber: varchar("xeroInvoiceNumber", { length: 50 }),
    xeroSyncStatus: mysqlEnum("xeroSyncStatus", XERO_SYNC_STATUSES),
    xeroLastSyncedAt: timestamp("xeroLastSyncedAt"),
    xeroLastError: text("xeroLastError"),
    xeroPaymentId: varchar("xeroPaymentId", { length: 64 }),
    xeroPaymentSyncedAt: timestamp("xeroPaymentSyncedAt"),
    xeroPaymentSyncStatus: mysqlEnum(
      "xeroPaymentSyncStatus",
      XERO_SYNC_STATUSES
    ),
    xeroPaymentSyncError: text("xeroPaymentSyncError"),
    xeroPaymentsJson: json("xeroPaymentsJson").$type<Array<{
      key: string;
      xeroPaymentId: string;
      amount: number;
      paidAt: string;
    }> | null>(),
    xeroPaymentAuditLog: json("xeroPaymentAuditLog").$type<
      XeroPaymentAuditEvent[] | null
    >(),
    /** Set on the cancelled original when Finance regenerates a replacement invoice. */
    replacedByInvoiceId: int("replacedByInvoiceId"),
    /** Set on the replacement invoice pointing at the cancelled original. */
    replacesInvoiceId: int("replacesInvoiceId"),
    regeneratedAt: timestamp("regeneratedAt"),
    regeneratedByUserId: int("regeneratedByUserId"),
    /** `regeneration` when voided via Regenerate Invoice; null for other voids. */
    cancellationKind: varchar("cancellationKind", { length: 32 }),
    /** Set when replacement row committed but PDF/document generation failed (retryable). */
    documentGenerationError: text("documentGenerationError"),
  },
  t => ({
    replacedByUid: uniqueIndex("portal_invoices_replaced_by_uid").on(
      t.replacedByInvoiceId
    ),
    replacesUid: uniqueIndex("portal_invoices_replaces_uid").on(
      t.replacesInvoiceId
    ),
  })
);
export type PortalInvoice = typeof portalInvoices.$inferSelect;
export type InsertPortalInvoice = typeof portalInvoices.$inferInsert;

/** Payment schedule rows for portal invoices (tranche-aware collection + Rev Cal). */
export const portalInvoiceSchedules = mysqlTable("portal_invoice_schedules", {
  id: int("id").autoincrement().primaryKey(),
  invoiceId: int("invoiceId").notNull(),
  label: varchar("label", { length: 255 }).notNull(),
  percentage: decimal("percentage", { precision: 6, scale: 2 }),
  amount: decimal("amount", { precision: 12, scale: 2 }).notNull(),
  expectedDate: varchar("expectedDate", { length: 10 }),
  status: mysqlEnum("status", [
    "pending",
    "invoiced",
    "partially_paid",
    "paid",
    "overdue",
  ])
    .notNull()
    .default("invoiced"),
  paidAmount: decimal("paidAmount", { precision: 12, scale: 2 })
    .notNull()
    .default("0.00"),
  collectedAt: timestamp("collectedAt"),
  sortOrder: int("sortOrder").notNull().default(0),
  linkedProposalTrancheId: int("linkedProposalTrancheId"),
  notes: text("notes"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
});
export type PortalInvoiceSchedule = typeof portalInvoiceSchedules.$inferSelect;
export type InsertPortalInvoiceSchedule =
  typeof portalInvoiceSchedules.$inferInsert;

// ─── Finance Tasks (auto-created when a deal arrives from Sales Portal) ────────
// Finance team must raise an invoice for each new deal; tasks track this queue.
export const financeTasks = mysqlTable("finance_tasks", {
  id: int("id").autoincrement().primaryKey(),
  dealId: int("dealId"), // sales_deal_log.id (nullable for unmatched proposals)
  customerId: int("customerId"), // customers.id (nullable for unmatched proposals)
  quoteRef: varchar("quoteRef", { length: 100 }).notNull(),
  customerName: varchar("customerName", { length: 255 }),
  salespersonName: varchar("salespersonName", { length: 255 }),
  totalAmount: decimal("totalAmount", { precision: 12, scale: 2 }),
  status: mysqlEnum("status", [
    "pending",
    "done",
    "dismissed",
    "partial",
    "outstanding",
  ])
    .default("pending")
    .notNull(),
  completedAt: timestamp("completedAt"),
  completedBy: varchar("completedBy", { length: 255 }),
  // Payment reconciliation fields
  amountCollected: decimal("amountCollected", { precision: 12, scale: 2 }), // actual amount received
  amountOutstanding: decimal("amountOutstanding", { precision: 12, scale: 2 }), // shortfall (totalAmount - amountCollected)
  collectedAt: timestamp("collectedAt"), // when Finance recorded the payment
  collectedBy: varchar("collectedBy", { length: 255 }), // who recorded it
  paymentMethod: varchar("paymentMethod", { length: 50 }), // cash, cheque, bank_transfer, paynow, etc.
  paymentNote: text("paymentNote"), // free-text note (e.g. "cheque no. 123456")
  amountOverridden: boolean("amountOverridden").default(false).notNull(), // true when Finance manually corrected the amount
  amountOverriddenBy: varchar("amountOverriddenBy", { length: 255 }), // who made the correction
  amountOverriddenAt: timestamp("amountOverriddenAt"), // when the correction was made
  createdAt: timestamp("createdAt").defaultNow().notNull(),
});

export type FinanceTask = typeof financeTasks.$inferSelect;
export type InsertFinanceTask = typeof financeTasks.$inferInsert;

// ─── Revenue Entries (Finance manual log: payments + projected) ────────────────
export const revenueEntries = mysqlTable("revenue_entries", {
  id: int("id").autoincrement().primaryKey(),
  type: mysqlEnum("type", ["payment", "projected"]).notNull(),
  category: mysqlEnum("category", [
    "project",
    "service",
    "maintenance",
    "quick_sale",
    "other",
  ])
    .notNull()
    .default("other"),
  customerId: int("customerId"), // nullable — may not be in DB
  customerName: varchar("customerName", { length: 255 }).notNull(),
  amount: decimal("amount", { precision: 12, scale: 2 }).notNull(),
  picName: varchar("picName", { length: 255 }), // salesperson / PIC
  description: text("description"), // optional free text
  entryDate: timestamp("entryDate").notNull(), // date of payment or expected date
  loggedBy: varchar("loggedBy", { length: 255 }).notNull(), // name of finance user who logged
  sapoSynced: boolean("sapoSynced").default(false).notNull(),
  sapoRef: varchar("sapoRef", { length: 128 }), // reference returned by SaPo
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
});
export type RevenueEntry = typeof revenueEntries.$inferSelect;
export type InsertRevenueEntry = typeof revenueEntries.$inferInsert;

// ─── Outbound Webhook Sent Log (duplicate guard for SerPo → SaPo webhooks) ────
// Tracks every webhook successfully sent to SaPo so we never fire the same
// invoiceNumber + eventType combination twice.
export const webhookSentLog = mysqlTable("webhook_sent_log", {
  id: int("id").autoincrement().primaryKey(),
  invoiceNumber: varchar("invoiceNumber", { length: 100 }).notNull(),
  eventType: varchar("eventType", { length: 64 }).notNull(), // e.g. "invoice.raised", "invoice.paid"
  sentAt: timestamp("sentAt").defaultNow().notNull(),
  responseStatus: int("responseStatus"), // HTTP status code from SaPo
  payload: text("payload"), // JSON snapshot for audit
});
export type WebhookSentLog = typeof webhookSentLog.$inferSelect;
export type InsertWebhookSentLog = typeof webhookSentLog.$inferInsert;

// ─── Staff Broadcasts ────────────────────────────────────────────────────────
// Tracks admin-sent broadcast messages to all staff, and their acknowledgements.
export const broadcastMessages = mysqlTable("broadcast_messages", {
  id: int("id").autoincrement().primaryKey(),
  message: text("message").notNull(),
  sentBy: varchar("sentBy", { length: 255 }).notNull(), // name of admin who sent it
  sentById: int("sentById").notNull(),
  sentAt: timestamp("sentAt").defaultNow().notNull(),
  recipientCount: int("recipientCount").default(0).notNull(), // how many were sent to
});
export type BroadcastMessage = typeof broadcastMessages.$inferSelect;

export const broadcastAcknowledgements = mysqlTable(
  "broadcast_acknowledgements",
  {
    id: int("id").autoincrement().primaryKey(),
    broadcastId: int("broadcastId").notNull(),
    userId: int("userId").notNull(),
    userName: varchar("userName", { length: 255 }).notNull(),
    acknowledgedAt: timestamp("acknowledgedAt").defaultNow().notNull(),
    method: mysqlEnum("method", ["whatsapp", "portal"])
      .default("portal")
      .notNull(),
  }
);
export type BroadcastAcknowledgement =
  typeof broadcastAcknowledgements.$inferSelect;

// ─── Internal Issue Reports ──────────────────────────────────────────────────
export const issueReports = mysqlTable("issue_reports", {
  id: int("id").autoincrement().primaryKey(),
  title: varchar("title", { length: 255 }).notNull(),
  description: text("description").notNull(),
  urgency: mysqlEnum("urgency", ["low", "medium", "critical"])
    .default("medium")
    .notNull(),
  screenshotUrl: text("screenshotUrl"),
  pageUrl: text("pageUrl").notNull(),
  userName: varchar("userName", { length: 255 }).notNull(),
  status: mysqlEnum("status", ["new", "reviewing", "resolved"])
    .default("new")
    .notNull(),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
});
export type IssueReport = typeof issueReports.$inferSelect;
export type InsertIssueReport = typeof issueReports.$inferInsert;

// ─── Cleo Operations Desk ──────────────────────────────────────────────────
// Governed, request-scoped staff reports. This is deliberately separate from
// ordinary Cleo conversation memory and the legacy one-shot issue reporter.
export const cleoOperationsRequests = mysqlTable(
  "cleo_operations_requests",
  {
    id: bigint("id", { mode: "number" }).autoincrement().primaryKey(),
    requestKey: varchar("requestKey", { length: 24 }).notNull(),
    submissionKey: varchar("submissionKey", { length: 64 }).notNull(),
    reporterUserId: int("reporterUserId").notNull(),
    reporterName: varchar("reporterName", { length: 255 }).notNull(),
    reporterRole: varchar("reporterRole", { length: 32 }).notNull(),
    kind: mysqlEnum("kind", [
      "bug",
      "feature_request",
      "help",
      "data_issue",
      "permission",
      "business_decision",
      "critical_incident",
    ]).notNull(),
    severity: mysqlEnum("severity", ["normal", "high", "critical"])
      .default("normal")
      .notNull(),
    reporterPriority: mysqlEnum("reporterPriority", [
      "urgent",
      "must_have",
      "good_to_have",
    ]),
    effectivePriority: mysqlEnum("effectivePriority", [
      "urgent",
      "must_have",
      "good_to_have",
    ]),
    priorityReason: text("priorityReason"),
    priorityAssessmentJson: json("priorityAssessmentJson").$type<Record<
      string,
      unknown
    > | null>(),
    queuePosition: int("queuePosition"),
    status: mysqlEnum("status", [
      "submitted",
      "needs_reporter_answer",
      "investigating",
      "needs_business_decision",
      "ready_for_engineering",
      "engineering_in_progress",
      "awaiting_owner_apply",
      "ready_for_testing",
      "implemented",
      "duplicate",
      "declined",
      "dismissed",
      "unable_to_reproduce",
    ])
      .default("submitted")
      .notNull(),
    title: varchar("title", { length: 280 }).notNull(),
    summary: text("summary").notNull(),
    expectedBehaviour: text("expectedBehaviour"),
    pageUrl: varchar("pageUrl", { length: 1024 }).notNull(),
    pageContextJson: json("pageContextJson").$type<Record<
      string,
      unknown
    > | null>(),
    clientContextJson: json("clientContextJson").$type<Record<
      string,
      unknown
    > | null>(),
    safeDiagnosticsJson: json("safeDiagnosticsJson").$type<Record<
      string,
      unknown
    > | null>(),
    investigationJson: json("investigationJson").$type<Record<
      string,
      unknown
    > | null>(),
    engineeringPrompt: longtext("engineeringPrompt"),
    engineeringResultJson: json("engineeringResultJson").$type<Record<
      string,
      unknown
    > | null>(),
    assignedTesterUserId: int("assignedTesterUserId"),
    assignedTesterByUserId: int("assignedTesterByUserId"),
    assignedTesterAt: timestamp("assignedTesterAt"),
    testerAssignmentKey: varchar("testerAssignmentKey", { length: 64 }),
    readyForTestingAt: timestamp("readyForTestingAt"),
    implementedAt: timestamp("implementedAt"),
    supersedesRequestId: bigint("supersedesRequestId", { mode: "number" }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    requestKeyUniq: uniqueIndex("uq_cleo_ops_request_key").on(t.requestKey),
    submissionUniq: uniqueIndex("uq_cleo_ops_submission_key").on(
      t.submissionKey
    ),
    reporterStatusIdx: index("idx_cleo_ops_reporter_status").on(
      t.reporterUserId,
      t.status
    ),
    statusUpdatedIdx: index("idx_cleo_ops_status_updated").on(
      t.status,
      t.updatedAt
    ),
    priorityStatusIdx: index("idx_cleo_ops_priority_status").on(
      t.effectivePriority,
      t.status,
      t.updatedAt
    ),
    assignedTesterIdx: index("idx_cleo_ops_assigned_tester").on(
      t.assignedTesterUserId,
      t.status
    ),
  })
);
export type CleoOperationsRequest = typeof cleoOperationsRequests.$inferSelect;
export type InsertCleoOperationsRequest =
  typeof cleoOperationsRequests.$inferInsert;

// Guide-first interactions are kept separately from engineering requests. A
// staff member who finds the existing feature and confirms that it solved the
// need must not create an OPS request or an engineering email.
export const cleoOperationsGuidanceInteractions = mysqlTable(
  "cleo_operations_guidance_interactions",
  {
    id: bigint("id", { mode: "number" }).autoincrement().primaryKey(),
    interactionKey: varchar("interactionKey", { length: 64 }).notNull(),
    reporterUserId: int("reporterUserId").notNull(),
    reporterName: varchar("reporterName", { length: 255 }).notNull(),
    reporterRole: varchar("reporterRole", { length: 32 }).notNull(),
    requestedKind: mysqlEnum("requestedKind", [
      "bug",
      "feature_request",
    ]).notNull(),
    summary: text("summary").notNull(),
    expectedBehaviour: text("expectedBehaviour"),
    pageUrl: varchar("pageUrl", { length: 1024 }).notNull(),
    pageContextJson: json("pageContextJson").$type<Record<
      string,
      unknown
    > | null>(),
    capabilityId: varchar("capabilityId", { length: 96 }),
    guidanceJson: json("guidanceJson").$type<Record<string, unknown> | null>(),
    outcome: mysqlEnum("outcome", [
      "guidance_offered",
      "solved",
      "existing_feature_failed",
      "different_need",
      "no_match",
    ]).notNull(),
    requestId: bigint("requestId", { mode: "number" }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    interactionUniq: uniqueIndex("uq_cleo_ops_guidance_interaction").on(
      t.interactionKey
    ),
    reporterIdx: index("idx_cleo_ops_guidance_reporter").on(
      t.reporterUserId,
      t.updatedAt
    ),
  })
);
export type CleoOperationsGuidanceInteraction =
  typeof cleoOperationsGuidanceInteractions.$inferSelect;

export const cleoOperationsMessages = mysqlTable(
  "cleo_operations_messages",
  {
    id: bigint("id", { mode: "number" }).autoincrement().primaryKey(),
    requestId: bigint("requestId", { mode: "number" }).notNull(),
    authorKind: mysqlEnum("authorKind", [
      "reporter",
      "cleo",
      "admin",
      "engineering",
    ]).notNull(),
    authorUserId: int("authorUserId"),
    body: text("body").notNull(),
    messageKind: varchar("messageKind", { length: 32 })
      .default("conversation")
      .notNull(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    requestIdx: index("idx_cleo_ops_messages_request").on(t.requestId, t.id),
  })
);
export type CleoOperationsMessage = typeof cleoOperationsMessages.$inferSelect;

export const cleoOperationsAttachments = mysqlTable(
  "cleo_operations_attachments",
  {
    id: bigint("id", { mode: "number" }).autoincrement().primaryKey(),
    requestId: bigint("requestId", { mode: "number" }).notNull(),
    uploadedByUserId: int("uploadedByUserId").notNull(),
    storageKey: varchar("storageKey", { length: 512 }),
    originalName: varchar("originalName", { length: 255 }).notNull(),
    mimeType: varchar("mimeType", { length: 64 }).notNull(),
    byteSize: int("byteSize").notNull(),
    caption: varchar("caption", { length: 500 }),
    evidenceSummary: text("evidenceSummary"),
    deleteAfter: timestamp("deleteAfter").notNull(),
    deletedAt: timestamp("deletedAt"),
    deleteReason: varchar("deleteReason", { length: 64 }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    requestIdx: index("idx_cleo_ops_attachments_request").on(t.requestId),
  })
);
export type CleoOperationsAttachment =
  typeof cleoOperationsAttachments.$inferSelect;

export const cleoOperationsEvents = mysqlTable(
  "cleo_operations_events",
  {
    id: bigint("id", { mode: "number" }).autoincrement().primaryKey(),
    requestId: bigint("requestId", { mode: "number" }).notNull(),
    occurrenceKey: varchar("occurrenceKey", { length: 160 }).notNull(),
    eventType: varchar("eventType", { length: 64 }).notNull(),
    actorUserId: int("actorUserId"),
    fromStatus: varchar("fromStatus", { length: 40 }),
    toStatus: varchar("toStatus", { length: 40 }),
    metadataJson: json("metadataJson").$type<Record<string, unknown> | null>(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    occurrenceUniq: uniqueIndex("uq_cleo_ops_event_occurrence").on(
      t.occurrenceKey
    ),
    requestIdx: index("idx_cleo_ops_events_request").on(t.requestId, t.id),
  })
);
export type CleoOperationsEvent = typeof cleoOperationsEvents.$inferSelect;

export const cleoOperationsDecisions = mysqlTable(
  "cleo_operations_decisions",
  {
    id: bigint("id", { mode: "number" }).autoincrement().primaryKey(),
    requestId: bigint("requestId", { mode: "number" }).notNull(),
    question: text("question").notNull(),
    answer: text("answer"),
    answeredByUserId: int("answeredByUserId"),
    answeredAt: timestamp("answeredAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({ requestIdx: index("idx_cleo_ops_decisions_request").on(t.requestId) })
);
export type CleoOperationsDecision =
  typeof cleoOperationsDecisions.$inferSelect;

export const cleoOperationsWhatsappNotifications = mysqlTable(
  "cleo_operations_whatsapp_notifications",
  {
    id: bigint("id", { mode: "number" }).autoincrement().primaryKey(),
    requestId: bigint("requestId", { mode: "number" }).notNull(),
    recipientUserId: int("recipientUserId").notNull(),
    phoneNumber: varchar("phoneNumber", { length: 30 }).notNull(),
    occurrenceKey: varchar("occurrenceKey", { length: 160 }).notNull(),
    detailMessage: text("detailMessage").notNull(),
    state: mysqlEnum("state", [
      "pending_window",
      "template_sent",
      "detail_sent",
      "failed",
    ])
      .default("pending_window")
      .notNull(),
    templateSid: varchar("templateSid", { length: 40 }),
    providerSid: varchar("providerSid", { length: 64 }),
    templateSentAt: timestamp("templateSentAt"),
    consentReceivedAt: timestamp("consentReceivedAt"),
    detailSentAt: timestamp("detailSentAt"),
    lastError: text("lastError"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    occurrenceUniq: uniqueIndex("uq_cleo_ops_whatsapp_occurrence").on(
      t.occurrenceKey
    ),
    pendingPhoneIdx: index("idx_cleo_ops_whatsapp_pending_phone").on(
      t.phoneNumber,
      t.state
    ),
  })
);
export type CleoOperationsWhatsappNotification =
  typeof cleoOperationsWhatsappNotifications.$inferSelect;

/** One immutable monthly allowance row. Spending is derived from run usage and cannot be edited by Admin. */
export const cleoOperationsAutomationBudgets = mysqlTable(
  "cleo_operations_automation_budgets",
  {
    id: bigint("id", { mode: "number" }).autoincrement().primaryKey(),
    monthKey: varchar("monthKey", { length: 7 }).notNull(),
    limitCents: int("limitCents").default(5000).notNull(),
    mode: mysqlEnum("mode", ["autonomous", "paused", "manual_until_next_month"])
      .default("autonomous")
      .notNull(),
    updatedByUserId: int("updatedByUserId"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    monthUniq: uniqueIndex("uq_cleo_ops_automation_budget_month").on(
      t.monthKey
    ),
  })
);

export const cleoOperationsAutomationBudgetAdjustments = mysqlTable(
  "cleo_operations_automation_budget_adjustments",
  {
    id: bigint("id", { mode: "number" }).autoincrement().primaryKey(),
    monthKey: varchar("monthKey", { length: 7 }).notNull(),
    occurrenceKey: varchar("occurrenceKey", { length: 128 }).notNull(),
    actorUserId: int("actorUserId").notNull(),
    previousLimitCents: int("previousLimitCents").notNull(),
    newLimitCents: int("newLimitCents").notNull(),
    previousMode: varchar("previousMode", { length: 40 }).notNull(),
    newMode: varchar("newMode", { length: 40 }).notNull(),
    reason: text("reason").notNull(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    occurrenceUniq: uniqueIndex("uq_cleo_ops_budget_adjustment_occurrence").on(
      t.occurrenceKey
    ),
    monthIdx: index("idx_cleo_ops_budget_adjustment_month").on(
      t.monthKey,
      t.id
    ),
  })
);

export const cleoOperationsAutomationRuns = mysqlTable(
  "cleo_operations_automation_runs",
  {
    id: bigint("id", { mode: "number" }).autoincrement().primaryKey(),
    requestId: bigint("requestId", { mode: "number" }).notNull(),
    attemptNumber: int("attemptNumber").default(1).notNull(),
    phase: mysqlEnum("phase", ["discovery", "engineering", "apply"])
      .default("engineering")
      .notNull(),
    status: mysqlEnum("status", [
      "queued",
      "running",
      "awaiting_budget",
      "awaiting_business_decision",
      "succeeded",
      "failed",
      "handed_off",
      "cancelled",
    ])
      .default("queued")
      .notNull(),
    model: varchar("model", { length: 80 }).default("gpt-5.6-luna").notNull(),
    budgetCents: int("budgetCents").default(300).notNull(),
    reservedCents: int("reservedCents").default(0).notNull(),
    actualCents: int("actualCents").default(0).notNull(),
    estimatedAdditionalCents: int("estimatedAdditionalCents"),
    jobTokenHash: varchar("jobTokenHash", { length: 64 }),
    providerCallLeaseId: varchar("providerCallLeaseId", { length: 64 }),
    providerCallStartedAt: timestamp("providerCallStartedAt"),
    dispatchId: varchar("dispatchId", { length: 64 }),
    branchName: varchar("branchName", { length: 255 }),
    pullRequestUrl: varchar("pullRequestUrl", { length: 1024 }),
    mergeCommit: varchar("mergeCommit", { length: 64 }),
    deploymentId: varchar("deploymentId", { length: 128 }),
    handoffPrompt: longtext("handoffPrompt"),
    resultJson: json("resultJson").$type<Record<string, unknown> | null>(),
    lastError: text("lastError"),
    activityStage: varchar("activityStage", { length: 80 }),
    lastHeartbeatAt: timestamp("lastHeartbeatAt"),
    lastProgressAt: timestamp("lastProgressAt"),
    progressJson: json("progressJson").$type<{
      summary?: string;
      milestones?: Array<{ kind: string; label: string; at: string }>;
      toolCounts?: Record<string, number>;
    } | null>(),
    lastGithubSyncAt: timestamp("lastGithubSyncAt"),
    githubStatus: varchar("githubStatus", { length: 40 }),
    startedAt: timestamp("startedAt"),
    completedAt: timestamp("completedAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    requestAttemptUniq: uniqueIndex("uq_cleo_ops_automation_run_attempt").on(
      t.requestId,
      t.phase,
      t.attemptNumber
    ),
    statusIdx: index("idx_cleo_ops_automation_run_status").on(
      t.status,
      t.updatedAt
    ),
  })
);

export const cleoOperationsAutomationUsage = mysqlTable(
  "cleo_operations_automation_usage",
  {
    id: bigint("id", { mode: "number" }).autoincrement().primaryKey(),
    runId: bigint("runId", { mode: "number" }).notNull(),
    providerResponseId: varchar("providerResponseId", { length: 128 }),
    model: varchar("model", { length: 80 }).notNull(),
    inputTokens: int("inputTokens").default(0).notNull(),
    cachedInputTokens: int("cachedInputTokens").default(0).notNull(),
    outputTokens: int("outputTokens").default(0).notNull(),
    costCents: int("costCents").default(0).notNull(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    responseUniq: uniqueIndex("uq_cleo_ops_automation_usage_response").on(
      t.runId,
      t.providerResponseId
    ),
    runIdx: index("idx_cleo_ops_automation_usage_run").on(t.runId, t.id),
  })
);

/** Retail won: one physical row per payment-term tranche (source of truth for KPI projected/collected + Revenue Calendar). */
export const proposalRevenueTranches = mysqlTable("proposal_revenue_tranches", {
  id: int("id").autoincrement().primaryKey(),
  proposalId: int("proposalId").notNull(),
  sequenceIndex: int("sequenceIndex").notNull().default(0),
  stageLabel: varchar("stageLabel", { length: 255 }).notNull(),
  percentage: decimal("percentage", { precision: 6, scale: 2 }).notNull(),
  amountSgd: decimal("amountSgd", { precision: 12, scale: 2 }).notNull(),
  /** Authoritative GST-inclusive customer payable for this tranche (ex-card fee). */
  payableAmountSgd: decimal("payableAmountSgd", { precision: 12, scale: 2 }),
  /** Authoritative GST component; payableAmountSgd - amountSgd. */
  gstAmountSgd: decimal("gstAmountSgd", { precision: 12, scale: 2 }),
  timingKind: mysqlEnum("timingKind", ["immediate", "scheduled"])
    .notNull()
    .default("scheduled"),
  /** YYYY-MM for scheduled tranches; editable in Revenue Calendar. */
  expectedMonth: varchar("expectedMonth", { length: 7 }),
  /** YYYY-MM-DD expected collection date for future unpaid tranches (Payment 2+). */
  expectedCollectionDate: date("expectedCollectionDate"),
  dealInvoiceId: int("dealInvoiceId"),
  collectedAt: timestamp("collectedAt"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
});

export type ProposalRevenueTranche =
  typeof proposalRevenueTranches.$inferSelect;
export type InsertProposalRevenueTranche =
  typeof proposalRevenueTranches.$inferInsert;

// ─── Proposals (internal) ────────────────────────────────────────────────────
export const proposalCounters = mysqlTable("proposal_counters", {
  id: int("id").autoincrement().primaryKey(),
  year: int("year").notNull().unique(),
  lastSequence: int("lastSequence").default(0).notNull(),
});

export const proposalHeaders = mysqlTable("proposal_headers", {
  id: int("id").autoincrement().primaryKey(),
  proposalRef: varchar("proposalRef", { length: 32 }).notNull().unique(),
  parentProposalId: int("parentProposalId"),
  customerId: int("customerId"),
  customerNameSnapshot: varchar("customerNameSnapshot", { length: 255 }),
  customerContactSnapshot: varchar("customerContactSnapshot", { length: 255 }),
  publicViewCount: int("publicViewCount").notNull().default(0),
  lastPublicViewedAt: timestamp("lastPublicViewedAt"),
  salespersonId: int("salespersonId").notNull(),
  salespersonNameSnapshot: varchar("salespersonNameSnapshot", { length: 255 }),
  salespersonEmailSnapshot: varchar("salespersonEmailSnapshot", {
    length: 320,
  }),
  salespersonPhoneSnapshot: varchar("salespersonPhoneSnapshot", { length: 50 }),
  /** `retail_proposal` (catalogue) vs `project_proposal` (project / spreadsheet upload). Never NULL/blank at write time. */
  proposalSource: varchar("proposalSource", { length: 32 })
    .notNull()
    .default("retail_proposal"),
  status: mysqlEnum("status", ["draft", "sent", "approved", "rejected"])
    .notNull()
    .default("draft"),
  pipelineStage: mysqlEnum("pipelineStage", [
    "draft",
    "prospect",
    "projected",
    "completed",
    "lost",
  ])
    .notNull()
    .default("draft"),
  lostReason: varchar("lostReason", { length: 100 }),
  lostReasonDetail: text("lostReasonDetail"),
  approvedAt: timestamp("approvedAt"),
  /** Email entered by customer at public approval — never overwrites customers.email. */
  approvedEmail: varchar("approvedEmail", { length: 320 }),
  /** Generated or copied approved proposal PDF for post-approval comms. */
  approvedProposalPdfKey: text("approvedProposalPdfKey"),
  /** generated | uploaded_original — durable commercial vs customer-upload distinction. */
  approvedProposalPdfKind: varchar("approvedProposalPdfKind", { length: 32 }),
  /** Commercial renderer version for Portal-generated approved PDFs. */
  approvedProposalPdfRendererVersion: varchar(
    "approvedProposalPdfRendererVersion",
    { length: 32 }
  ),
  approvedProposalPdfGeneratedAt: timestamp("approvedProposalPdfGeneratedAt"),
  /** Immutable accepted commercial snapshot for permanent Link B (JSON). */
  acceptedSnapshotJson: json("acceptedSnapshotJson"),
  acceptedSnapshotCapturedAt: timestamp("acceptedSnapshotCapturedAt"),
  /** customer_approval | staff_won */
  acceptanceSource: varchar("acceptanceSource", { length: 32 }),
  /** Deposit payment reminder WhatsApp (15 min after customer approval): claimed once per proposal, ever. */
  depositPaymentReminderAt: timestamp("depositPaymentReminderAt"),
  /** "sent" | "failed" | "skip:<reason>" */
  depositPaymentReminderOutcome: varchar("depositPaymentReminderOutcome", { length: 64 }),
  totalAmount: decimal("totalAmount", { precision: 12, scale: 2 }).notNull(),
  currency: varchar("currency", { length: 8 }).notNull().default("SGD"),
  paymentMethod: mysqlEnum("paymentMethod", [
    "paynow",
    "card",
    "bank_transfer",
    "cash",
  ])
    .notNull()
    .default("paynow"),
  paymentTermsJson: json("paymentTermsJson"),
  /** Customer-owned equipment that Sales has agreed must be collected/handled. Never a sold/BOM line. */
  hasTradeInItems: boolean("hasTradeInItems").notNull().default(false),
  tradeInItemsText: text("tradeInItemsText"),
  cardFeeEnabled: boolean("cardFeeEnabled").notNull().default(false),
  cardFeePercent: decimal("cardFeePercent", { precision: 5, scale: 2 })
    .notNull()
    .default("0.00"),
  /** When true, generated invoices add 9% GST on base (not shown on customer proposal). */
  invoiceGstEnabled: boolean("invoiceGstEnabled").notNull().default(false),
  /** When true, invoice generation creates a HitPay card/PayNow checkout link. */
  cardPaymentLinkEnabled: boolean("cardPaymentLinkEnabled")
    .notNull()
    .default(false),
  notes: text("notes"),
  /** Snapshot of customer profile at proposal creation (immutable for this quote). */
  customerProfileTextSnapshot: text("customerProfileTextSnapshot"),
  customerProfileParsedJson: json("customerProfileParsedJson").$type<{
    room_size?: string | null;
    current_setup?: string | null;
    music_preference?: string | null;
    pain_points?: string | null;
  } | null>(),
  proposalIntent: mysqlEnum("proposalIntent", ["product_led", "solution_led"]),
  customerExpertise: mysqlEnum("customerExpertise", [
    "explorer",
    "enthusiast",
    "connoisseur",
  ]),
  /** Manus-style pricing: list total, discounts, agreed price, customer-facing toggles. */
  retailPricingJson: json("retailPricingJson").$type<{
    /** Frozen first-import D-Tools total — never recalculated after import. */
    originalListPrice?: number;
    listTotal?: number;
    specialDiscountSgd?: number;
    agreedPriceSgd?: number;
    enteredAgreedPriceSgd?: number;
    agreedPriceBasis?: "before_gst" | "gst_inclusive";
    showDiscountPctToCustomer?: boolean;
    pricesVisibleToCustomer?: boolean;
  } | null>(),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  /** When set, proposal commercial process is cancelled; customer Link B/PDF revoked. */
  cancelledAt: timestamp("cancelledAt"),
  cancelledByUserId: int("cancelledByUserId"),
  cancellationCategory: varchar("cancellationCategory", { length: 64 }),
  cancellationReason: text("cancellationReason"),
  cancellationSource: varchar("cancellationSource", { length: 32 }),
  previousStatusBeforeCancel: varchar("previousStatusBeforeCancel", {
    length: 32,
  }),
  previousPipelineStageBeforeCancel: varchar(
    "previousPipelineStageBeforeCancel",
    { length: 32 }
  ),
  cancellationMetaJson: json("cancellationMetaJson"),
  /**
   * Soft-archive: hidden from active Proposals tabs / pipeline counts.
   * Does not cancel, lose, invoice, or delete. Orthogonal to cancelledAt.
   */
  archivedAt: timestamp("archivedAt"),
  archivedByUserId: int("archivedByUserId"),
  archiveReason: varchar("archiveReason", { length: 64 }),
  /** Hidden project tree foundation (Project -> Category -> Option). */
  projectId: int("projectId"),
  categoryId: int("categoryId"),
  optionId: int("optionId"),
  proposalName: varchar("proposalName", { length: 255 }),
  /** External project/proposal reference from uploaded D-Tools docs. */
  projectNumber: varchar("projectNumber", { length: 100 }),
  revisionNumber: int("revisionNumber").notNull().default(1),
  isLatestRevision: boolean("isLatestRevision").notNull().default(true),
  originalProposalStorageKey: text("originalProposalStorageKey"),
  originalProposalFileName: varchar("originalProposalFileName", {
    length: 512,
  }),
  /** Full verbatim PDF text extraction (immutable snapshot; never overwritten by parsing). */
  rawExtractedText: text("rawExtractedText"),
  parsedText: text("parsedText"),
  parsedMetadataJson: json("parsedMetadataJson"),
  parseWarningsJson: json("parseWarningsJson"),
  parseConfidence: decimal("parseConfidence", { precision: 5, scale: 2 }),
  lifecycleStatus: varchar("lifecycleStatus", { length: 32 })
    .notNull()
    .default("draft"),
  sentAt: timestamp("sentAt"),
  /** Customer Link B access expires at this time (14 days from send/renew). */
  linkBExpiresAt: timestamp("linkBExpiresAt"),
  firstViewedAt: timestamp("firstViewedAt"),
  lastViewedAt: timestamp("lastViewedAt"),
  lostAt: timestamp("lostAt"),
  invoicedAt: timestamp("invoicedAt"),
  paidAt: timestamp("paidAt"),
  /** `standard` = auto-invoice on approval; `group` = defer until group billing event. */
  billingMode: mysqlEnum("billingMode", ["standard", "group"])
    .notNull()
    .default("standard"),
  billingGroupId: int("billingGroupId"),
  groupBillingCleoDay1SentAt: timestamp("groupBillingCleoDay1SentAt"),
  groupBillingCleoDay3SentAt: timestamp("groupBillingCleoDay3SentAt"),
  groupBillingCleoDay5EscalatedAt: timestamp("groupBillingCleoDay5EscalatedAt"),
  engagementViewCount: int("engagementViewCount").notNull().default(0),
  engagementSessionCount: int("engagementSessionCount").notNull().default(0),
  engagementTimeSpentSeconds: int("engagementTimeSpentSeconds")
    .notNull()
    .default(0),
  lastInteractionAt: timestamp("lastInteractionAt"),
  /**
   * Shared Sales / Procurement authority for when the project must be finished.
   * Compatibility projection from fulfilment timeline (final day of planning month).
   * Do not write independently — use proposalFulfilmentTimelineService.
   */
  expectedCompletionDate: date("expectedCompletionDate"),
  /**
   * Canonical Target goods-ready / delivery timing.
   * `unset` only for pre-feature / unknown / legacy rows — not new portal_native.
   */
  fulfilmentTimelineType: mysqlEnum("fulfilmentTimelineType", [
    "unset",
    "asap",
    "month_year",
  ])
    .notNull()
    .default("unset"),
  /** Customer-agreed target month (YYYY-MM). Required when type is month_year; null for asap/unset. */
  targetFulfilmentMonth: varchar("targetFulfilmentMonth", { length: 7 }),
  /** Internal Procurement forecast month (YYYY-MM). Especially important for ASAP. */
  procurementForecastMonth: varchar("procurementForecastMonth", { length: 7 }),
  /**
   * How this proposal entered Portal timeline tracking.
   * Existing rows stay `unknown` until explicit triage — never bulk-guessed.
   */
  procurementTrackingOrigin: mysqlEnum("procurementTrackingOrigin", [
    "unknown",
    "portal_native",
    "legacy_import",
  ])
    .notNull()
    .default("unknown"),
  fulfilmentTimelineUpdatedByUserId: int("fulfilmentTimelineUpdatedByUserId"),
  fulfilmentTimelineUpdatedAt: timestamp("fulfilmentTimelineUpdatedAt"),
  procurementForecastUpdatedByUserId: int("procurementForecastUpdatedByUserId"),
  procurementForecastUpdatedAt: timestamp("procurementForecastUpdatedAt"),
});

export const proposalItems = mysqlTable("proposal_items", {
  id: int("id").autoincrement().primaryKey(),
  proposalId: int("proposalId").notNull(),
  optionId: int("optionId"),
  /** Optional link to a future `catalogue_items` row; no DB FK in v1. */
  catalogueItemId: int("catalogueItemId"),
  name: varchar("name", { length: 512 }),
  brand: varchar("brand", { length: 255 }),
  model: varchar("model", { length: 255 }),
  /** Customer-friendly title; defaults to model for legacy rows. */
  displayName: varchar("displayName", { length: 512 }),
  displayNameManuallyEdited: boolean("displayNameManuallyEdited")
    .notNull()
    .default(false),
  category: varchar("category", { length: 255 }),
  rawText: text("rawText"),
  extractionConfidence: decimal("extractionConfidence", {
    precision: 5,
    scale: 2,
  }),
  descriptionSnapshot: text("descriptionSnapshot").notNull(),
  imageUrlSnapshot: text("imageUrlSnapshot"),
  unitPriceSnapshot: decimal("unitPriceSnapshot", {
    precision: 12,
    scale: 2,
  }).notNull(),
  quantity: int("quantity").notNull(),
  lineTotal: decimal("lineTotal", { precision: 12, scale: 2 }).notNull(),
  sortOrder: int("sortOrder").notNull().default(0),
  /** Cleo-generated product description — what the product is (DTOOLS JSON import). */
  cleoDescription: text("cleoDescription"),
  /** Cleo hover explanation JSON — what this item is for / why the customer needs it. */
  customerExplanation: text("customerExplanation"),
  /**
   * Portal pricing method for this line: `fixed` (default/null) or `percentage_of_equipment`.
   * Percentage lines recalculate from visible equipment cost; D-Tools imports leave null/fixed.
   */
  pricingMethod: varchar("pricingMethod", { length: 32 }),
  /** Proposal-local % when pricingMethod = percentage_of_equipment (does not mutate catalogue). */
  percentageOfEquipment: decimal("percentageOfEquipment", {
    precision: 8,
    scale: 4,
  }),
  /**
   * Immutable configurator snapshot (null for ordinary catalogue lines).
   * Transparent cable and Canvas soundbar share this column with distinct schemaVersion values.
   * Historical prices must never be recalculated from a later price list.
   */
  configurationSnapshotJson: json("configurationSnapshotJson"),
});

export const hrsRackConfigurations = mysqlTable(
  "hrs_rack_configurations",
  {
    id: int("id").autoincrement().primaryKey(),
    ownerUserId: int("ownerUserId").notNull(),
    customerName: varchar("customerName", { length: 255 }).notNull(),
    configurationName: varchar("configurationName", { length: 255 }).notNull(),
    status: varchar("status", { length: 32 }).notNull().default("draft"),
    configurationJson: json("configurationJson").notNull(),
    configuredPriceSgd: decimal("configuredPriceSgd", {
      precision: 12,
      scale: 2,
    }).notNull(),
    proposalId: int("proposalId"),
    proposalItemId: int("proposalItemId"),
    sourceConfigurationId: int("sourceConfigurationId"),
    deletedAt: timestamp("deletedAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  table => ({
    ownerStatusUpdatedIdx: index("idx_hrs_rack_owner_status_updated").on(
      table.ownerUserId,
      table.status,
      table.updatedAt
    ),
    proposalIdx: index("idx_hrs_rack_proposal").on(
      table.proposalId,
      table.proposalItemId
    ),
    ownerDeletedIdx: index("idx_hrs_rack_owner_deleted").on(
      table.ownerUserId,
      table.deletedAt
    ),
  })
);

export const proposalProjects = mysqlTable("proposal_projects", {
  id: int("id").autoincrement().primaryKey(),
  /** Public customer-safe slug for interactive project proposal pages. */
  publicSlug: varchar("publicSlug", { length: 120 }).unique(),
  customerId: int("customerId"),
  picUserId: int("picUserId"),
  /** Staff who created the project shell (Build 44A). */
  createdByUserId: int("createdByUserId"),
  projectNumber: varchar("projectNumber", { length: 100 }),
  projectName: varchar("projectName", { length: 255 }),
  /** Site / job address for this sales project (may differ from customer profile). */
  projectAddress: text("projectAddress"),
  classification: varchar("classification", { length: 64 }),
  /** Sales project lifecycle: draft | active | won | lost | archived (not proposal_headers). */
  status: varchar("status", { length: 32 }).notNull().default("active"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
});

export const projectCategories = mysqlTable("project_categories", {
  id: int("id").autoincrement().primaryKey(),
  projectId: int("projectId").notNull(),
  categoryName: varchar("categoryName", { length: 255 }).notNull(),
  /** Category-level pipeline (Build 44A). */
  status: varchar("status", { length: 32 }).notNull().default("draft"),
  /** Deterministic ordering within a project (staff + customer nav). */
  displayOrder: int("displayOrder").notNull().default(0),
  classification: varchar("classification", { length: 64 }),
  internalNotes: text("internalNotes"),
  customerDescription: text("customerDescription"),
  customerDescriptionPolished: text("customerDescriptionPolished"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
});

export const projectOptions = mysqlTable("project_options", {
  id: int("id").autoincrement().primaryKey(),
  projectId: int("projectId").notNull(),
  categoryId: int("categoryId").notNull(),
  optionName: varchar("optionName", { length: 255 }).notNull(),
  /** Option / quote shell lifecycle (Build 44A). */
  status: varchar("status", { length: 32 }).notNull().default("draft"),
  /** Deterministic ordering within a category (not driven by star or timestamps). */
  displayOrder: int("displayOrder").notNull().default(0),
  isDefault: boolean("isDefault").notNull().default(false),
  /** At most one starred option per category — “likely” option for display/KPI later. */
  isStarred: boolean("isStarred").notNull().default(false),
  totalAmount: decimal("totalAmount", { precision: 12, scale: 2 }),
  latestRevisionId: int("latestRevisionId"),
  viewCount: int("viewCount").notNull().default(0),
  lastViewedAt: timestamp("lastViewedAt"),
  internalNotes: text("internalNotes"),
  customerDescription: text("customerDescription"),
  customerDescriptionPolished: text("customerDescriptionPolished"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
});

/** Server-backed D-Tools JSON staging when localStorage quota is exceeded. */
export const dtoolsJsonStagingSessions = mysqlTable(
  "dtools_json_staging_sessions",
  {
    sessionId: varchar("sessionId", { length: 128 }).primaryKey(),
    userId: int("userId").notNull(),
    payloadJson: longtext("payloadJson").notNull(),
    byteLength: int("byteLength").notNull(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  }
);

export const projectCatalogueItems = mysqlTable("project_catalogue_items", {
  id: int("id").autoincrement().primaryKey(),
  catalogueKey: varchar("catalogueKey", { length: 64 }).notNull().unique(),
  normalizedName: varchar("normalizedName", { length: 512 }).notNull(),
  brand: varchar("brand", { length: 255 }),
  model: varchar("model", { length: 255 }),
  /** Short official product name (not description). */
  productName: varchar("productName", { length: 512 }),
  /** Customer-friendly title; defaults to model for legacy rows. */
  displayName: varchar("displayName", { length: 512 }),
  displayNameManuallyEdited: boolean("displayNameManuallyEdited")
    .notNull()
    .default(false),
  brandNorm: varchar("brandNorm", { length: 255 }),
  modelNorm: varchar("modelNorm", { length: 255 }),
  category: varchar("category", { length: 255 }),
  subcategory: varchar("subcategory", { length: 255 }),
  polishedDescription: text("polishedDescription"),
  originalDtoolsDescription: text("originalDtoolsDescription"),
  /** Cross-proposal cache — Cleo product description (brand + model). */
  cleoDescription: text("cleoDescription"),
  /** Cross-proposal cache — Cleo hover explanation JSON (brand + model). */
  customerExplanation: text("customerExplanation"),
  imageUrl: varchar("imageUrl", { length: 2048 }),
  imageSourceMetadata: json("imageSourceMetadata").$type<Record<
    string,
    unknown
  > | null>(),
  lastPolishedAt: timestamp("lastPolishedAt"),
  lastImageSelectedAt: timestamp("lastImageSelectedAt"),
  dtoolsCatalogId: varchar("dtoolsCatalogId", { length: 128 }),
  dtoolsComponentId: varchar("dtoolsComponentId", { length: 128 }),
  dtoolsSiItemId: varchar("dtoolsSiItemId", { length: 128 }),
  avgQuotedPrice: decimal("avgQuotedPrice", { precision: 12, scale: 2 }),
  lastKnownCost: decimal("lastKnownCost", { precision: 12, scale: 2 }),
  usuallyOptional: boolean("usuallyOptional").notNull().default(false),
  usuallyNonBillable: boolean("usuallyNonBillable").notNull().default(false),
  usuallyAccessory: boolean("usuallyAccessory").notNull().default(false),
  usuallyBulkWire: boolean("usuallyBulkWire").notNull().default(false),
  /** Low-confidence name / missing structured fields — flag for staff review. */
  needsReview: boolean("needsReview").notNull().default(false),
  nameConfidence: varchar("nameConfidence", { length: 16 }),
  /** Deterministic ingestion class from shared classifier. */
  ingestionClass: varchar("ingestionClass", { length: 32 }),
  /** Soft-archive non-product entries — IDs preserved for proposal refs. */
  catalogueArchivedAt: timestamp("catalogueArchivedAt"),
  isServiceLabour: boolean("isServiceLabour").notNull().default(false),
  /** Proposal usage frequency (proposalUsageCount). */
  usageCount: int("usageCount").notNull().default(0),
  lastUsedAt: timestamp("lastUsedAt"),
  lastImportedProjectRef: varchar("lastImportedProjectRef", { length: 128 }),
  lastImportedProjectName: varchar("lastImportedProjectName", { length: 255 }),
  lastImportedCustomerName: varchar("lastImportedCustomerName", {
    length: 255,
  }),
  commonSystemNamesJson: json("commonSystemNamesJson").$type<string[] | null>(),
  commonRoomNamesJson: json("commonRoomNamesJson").$type<string[] | null>(),
  sourceType: varchar("sourceType", { length: 32 })
    .notNull()
    .default("dtools_json"),
  rawDtoolsIdsJson: json("rawDtoolsIdsJson").$type<Record<
    string,
    unknown
  > | null>(),
  /** Full imported field snapshot for audit (never used as display title). */
  rawImportedMetadataJson: json("rawImportedMetadataJson").$type<Record<
    string,
    unknown
  > | null>(),
  lastImportedByUserId: int("lastImportedByUserId"),
  lastEditedByUserId: int("lastEditedByUserId"),
  /**
   * Portal catalogue pricing: `fixed` (default) or `percentage_of_equipment` (Clarity brand only).
   */
  pricingMethod: varchar("pricingMethod", { length: 32 })
    .notNull()
    .default("fixed"),
  /** Default % when pricingMethod = percentage_of_equipment. */
  defaultPercentage: decimal("defaultPercentage", { precision: 8, scale: 4 }),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
});

export const proposalViewSessions = mysqlTable("proposal_view_sessions", {
  id: int("id").autoincrement().primaryKey(),
  /** Proposal revision row id (`proposal_headers.id`) — same as `revisionId`. */
  proposalId: int("proposalId").notNull(),
  projectId: int("projectId"),
  revisionId: int("revisionId").notNull(),
  sessionId: varchar("sessionId", { length: 128 }).notNull(),
  totalTimeSpentSeconds: int("totalTimeSpentSeconds").notNull().default(0),
  sessionStartedAt: timestamp("sessionStartedAt").defaultNow().notNull(),
  sessionEndedAt: timestamp("sessionEndedAt"),
  firstViewedAt: timestamp("firstViewedAt").defaultNow().notNull(),
  lastViewedAt: timestamp("lastViewedAt").defaultNow().onUpdateNow().notNull(),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
});

export const proposalTimelineEvents = mysqlTable("proposal_timeline_events", {
  id: int("id").autoincrement().primaryKey(),
  proposalId: int("proposalId").notNull(),
  eventType: varchar("eventType", { length: 64 }).notNull(),
  eventLabel: varchar("eventLabel", { length: 255 }).notNull(),
  actorName: varchar("actorName", { length: 255 }),
  actorRole: varchar("actorRole", { length: 50 }),
  metadataJson: json("metadataJson"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
});

export const customerReminders = mysqlTable("customer_reminders", {
  id: int("id").autoincrement().primaryKey(),
  customerId: int("customerId").notNull(),
  proposalId: int("proposalId"),
  salespersonId: int("salespersonId").notNull(),
  note: text("note"),
  /** Calendar date in Singapore for summary reminders. */
  reminderDate: varchar("reminderDate", { length: 10 }).notNull(),
  /** Optional exact datetime (UTC); when set, sends exact-time reminder. */
  dueAt: timestamp("dueAt"),
  status: mysqlEnum("status", ["pending", "done", "cancelled"])
    .notNull()
    .default("pending"),
  summarySentAt: timestamp("summarySentAt"),
  exactSentAt: timestamp("exactSentAt"),
  doneAt: timestamp("doneAt"),
  cancelledAt: timestamp("cancelledAt"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
});

export type ProposalHeader = typeof proposalHeaders.$inferSelect;
export type InsertProposalHeader = typeof proposalHeaders.$inferInsert;
export type ProposalItem = typeof proposalItems.$inferSelect;
export type ProposalProject = typeof proposalProjects.$inferSelect;
export type InsertProposalProject = typeof proposalProjects.$inferInsert;
export type ProjectCategory = typeof projectCategories.$inferSelect;
export type InsertProjectCategory = typeof projectCategories.$inferInsert;
export type ProjectOption = typeof projectOptions.$inferSelect;
export type InsertProjectOption = typeof projectOptions.$inferInsert;
export type ProjectCatalogueItem = typeof projectCatalogueItems.$inferSelect;
export type InsertProjectCatalogueItem =
  typeof projectCatalogueItems.$inferInsert;
export type ProposalViewSession = typeof proposalViewSessions.$inferSelect;
export type InsertProposalViewSession =
  typeof proposalViewSessions.$inferInsert;

// ─── Cleo — customer-facing proposal Q&A (LINK B) logging ───────────────────
export const cleoProposalInteractions = mysqlTable(
  "cleo_proposal_interactions",
  {
    id: int("id").autoincrement().primaryKey(),
    proposalId: int("proposalId").notNull(),
    customerId: int("customerId"),
    question: text("question").notNull(),
    response: text("response").notNull(),
    topic: varchar("topic", { length: 32 }),
    sentiment: varchar("sentiment", { length: 24 }),
    detectedSignals: json("detectedSignals").$type<Array<{
      type: string;
      confidence: number;
    }> | null>(),
    sessionId: varchar("sessionId", { length: 64 }),
    metadataJson: json("metadataJson").$type<Record<string, unknown> | null>(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  }
);
export type CleoProposalInteraction =
  typeof cleoProposalInteractions.$inferSelect;
export type InsertCleoProposalInteraction =
  typeof cleoProposalInteractions.$inferInsert;

/**
 * Ask Cleo chat session — compare-with slot.
 * Staff Link A: deferred PIC digest (close / navigate-away / 15m idle).
 * Customer Link B: same rails and honest trail; PIC WhatsApp digest is never sent.
 */
export const cleoLinkAAskSessions = mysqlTable(
  "cleo_link_a_ask_sessions",
  {
    id: int("id").autoincrement().primaryKey(),
    proposalId: int("proposalId").notNull(),
    sessionId: varchar("sessionId", { length: 64 }).notNull().unique(),
    staffUserId: int("staffUserId"),
    boundProposalItemId: int("boundProposalItemId"),
    compareTarget: text("compareTarget"),
    compareKind: varchar("compareKind", { length: 32 }),
    awaitingCompareTarget: boolean("awaitingCompareTarget").notNull().default(false),
    questionsDigestJson: json("questionsDigestJson").$type<
      Array<{ question: string; response: string; at?: string }> | null
    >(),
    lastActivityAt: timestamp("lastActivityAt").defaultNow().notNull(),
    endedAt: timestamp("endedAt"),
    endReason: varchar("endReason", { length: 32 }),
    picDigestAt: timestamp("picDigestAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
);
export type CleoLinkAAskSession = typeof cleoLinkAAskSessions.$inferSelect;
export type InsertCleoLinkAAskSession = typeof cleoLinkAAskSessions.$inferInsert;
export type InsertProposalItem = typeof proposalItems.$inferInsert;
export type ProposalTimelineEvent = typeof proposalTimelineEvents.$inferSelect;
export type InsertProposalTimelineEvent =
  typeof proposalTimelineEvents.$inferInsert;
export type CustomerReminder = typeof customerReminders.$inferSelect;
export type InsertCustomerReminder = typeof customerReminders.$inferInsert;

/** Family-scoped revise contributors (commercial owner + anyone who successfully revised). */
export const proposalRevisionContributors = mysqlTable(
  "proposal_revision_contributors",
  {
    id: int("id").autoincrement().primaryKey(),
    rootProposalId: int("rootProposalId").notNull(),
    userId: int("userId").notNull(),
    firstContributedAt: timestamp("firstContributedAt").defaultNow().notNull(),
    source: mysqlEnum("source", [
      "commercial_owner",
      "revision_save",
      "derived_timeline",
    ]).notNull(),
  },
  t => ({
    rootUserUnique: uniqueIndex(
      "uq_proposal_revision_contributors_root_user"
    ).on(t.rootProposalId, t.userId),
    rootIdx: index("idx_proposal_revision_contributors_root").on(
      t.rootProposalId
    ),
  })
);
export type ProposalRevisionContributor =
  typeof proposalRevisionContributors.$inferSelect;
export type InsertProposalRevisionContributor =
  typeof proposalRevisionContributors.$inferInsert;

/** Durable record of hard-deleted proposals (survives header/timeline deletion). */
export const proposalDeletionAudit = mysqlTable(
  "proposal_deletion_audit",
  {
    id: int("id").autoincrement().primaryKey(),
    proposalId: int("proposalId").notNull(),
    proposalRef: varchar("proposalRef", { length: 64 }).notNull(),
    parentProposalId: int("parentProposalId"),
    customerId: int("customerId"),
    actorUserId: int("actorUserId").notNull(),
    actorName: varchar("actorName", { length: 255 }),
    actorRole: varchar("actorRole", { length: 50 }),
    reason: varchar("reason", { length: 255 }),
    snapshotJson: json("snapshotJson"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    proposalIdx: index("idx_proposal_deletion_audit_proposal").on(t.proposalId),
    createdIdx: index("idx_proposal_deletion_audit_created").on(t.createdAt),
  })
);
export type ProposalDeletionAudit = typeof proposalDeletionAudit.$inferSelect;

// ─── Sales Catalogue (Phase D.5.1 foundation) ───────────────────────────────
export const catalogueUploads = mysqlTable("catalogue_uploads", {
  id: int("id").autoincrement().primaryKey(),
  fileName: varchar("fileName", { length: 512 }).notNull(),
  uploadedBy: int("uploadedBy").notNull(), // users.id
  uploadedAt: timestamp("uploadedAt").defaultNow().notNull(),
  productCount: int("productCount").notNull().default(0),
  versionNumber: int("versionNumber").notNull(),
  status: mysqlEnum("status", ["previewed", "imported", "failed"])
    .notNull()
    .default("previewed"),
  fileHash: varchar("fileHash", { length: 128 }),
  errorSummary: text("errorSummary"),
});

export const catalogueItems = mysqlTable(
  "catalogue_items",
  {
    id: int("id").autoincrement().primaryKey(),
    brand: varchar("brand", { length: 255 }).notNull(),
    model: varchar("model", { length: 255 }).notNull(),
    name: varchar("name", { length: 512 }).notNull(),
    /** Customer-friendly title shown in sales catalogue and proposals. */
    displayName: varchar("displayName", { length: 512 }),
    displayNameManuallyEdited: boolean("displayNameManuallyEdited")
      .notNull()
      .default(false),
    /**
     * Effective selected description (mirrors original or AI based on selectedDescriptionSource).
     * Retained for search / legacy readers; dual fields below are authoritative.
     */
    description: text("description"),
    /** Price-list / staff original description — never overwritten by AI generation. */
    originalDescription: text("originalDescription"),
    /** Customer-friendly AI-generated description (separate from original). */
    aiGeneratedDescription: text("aiGeneratedDescription"),
    /** Which description is active for proposals: original | ai */
    selectedDescriptionSource: varchar("selectedDescriptionSource", {
      length: 16,
    })
      .notNull()
      .default("original"),
    /** e.g. file, ai — legacy provenance for the effective `description` column */
    descriptionSource: varchar("descriptionSource", { length: 32 }),
    /** pending = awaiting AI; generated = AI; failed = last AI attempt failed; manual = from file or staff edit. */
    descriptionStatus: mysqlEnum("descriptionStatus", [
      "pending",
      "generated",
      "failed",
      "manual",
    ])
      .notNull()
      .default("pending"),
    /** Finish / colour / color from the price list (e.g. Black Oak). */
    finish: varchar("finish", { length: 255 }),
    /** Unit of measure (e.g. each, pair, m). */
    unit: varchar("unit", { length: 128 }),
    /** Section / subheader from the workbook (e.g. SPEAKERS, AMPLIFIERS). */
    productFamily: varchar("productFamily", { length: 255 }),
    category: varchar("category", { length: 255 }).notNull(),
    price: decimal("price", { precision: 12, scale: 2 }).notNull(),
    currency: varchar("currency", { length: 8 }).notNull().default("SGD"),
    sku: varchar("sku", { length: 128 }),
    imageUrl: text("imageUrl"),
    brochureUrl: text("brochureUrl"),
    specsUrl: text("specsUrl"),
    reviewsUrl: text("reviewsUrl"),
    /** Legacy opt-in website flag retained for backwards compatibility. */
    showOnWebsite: boolean("showOnWebsite").notNull().default(false),
    /** Explicit exception to automatic website eligibility. */
    hideFromWebsite: boolean("hideFromWebsite").notNull().default(false),
    /** Product is currently available to experience in the Clarity showroom. */
    inResidence: boolean("inResidence").notNull().default(false),
    /** Exact official manufacturer product page used by public Read More links. */
    officialProductUrl: text("officialProductUrl"),
    enrichmentStatus: mysqlEnum("enrichmentStatus", [
      "pending",
      "partial",
      "complete",
    ])
      .notNull()
      .default("pending"),
    /** User-created custom product rows never touched by import sync. */
    isCustom: boolean("isCustom").notNull().default(false),
    /** Pending/approved workflow for custom rows only. */
    approvalStatus: mysqlEnum("approvalStatus", ["pending", "approved"])
      .notNull()
      .default("approved"),
    /** Creator for custom rows (null for imported rows). */
    createdByUserId: int("createdByUserId"),
    /** First successful attempt to notify admins about this custom product. */
    customProductAdminNotifiedAt: timestamp("customProductAdminNotifiedAt"),
    isActive: boolean("isActive").notNull().default(true),
    lastUpdatedAt: timestamp("lastUpdatedAt")
      .defaultNow()
      .onUpdateNow()
      .notNull(),
    sourceUploadId: int("sourceUploadId"),
    /**
     * Order in the import file (0-based) after deduplication, first row wins. Used for list UI (brand → productFamily,
     * preserving Excel order). Updated on each row’s master upload; legacy rows use id as tie-break.
     */
    importListPosition: int("importListPosition").notNull().default(0),
    // Normalised matching helpers for upload preview/import safety.
    brandNorm: varchar("brandNorm", { length: 255 }),
    modelNorm: varchar("modelNorm", { length: 255 }),
    skuNorm: varchar("skuNorm", { length: 128 }),
    matchKey: varchar("matchKey", { length: 512 }),
    /**
     * Deterministic Brand+compact-Model uniqueness key (`brandNorm|modelNorm`).
     * Unique when set — prevents concurrent custom-product duplicate inserts.
     * Does not collapse identical model codes across different brands.
     */
    identityKey: varchar("identityKey", { length: 512 }),
    /** How category was chosen on last import (audit). */
    classificationSource: varchar("classificationSource", { length: 32 }),
    /**
     * Portal catalogue pricing: `fixed` (default) or `percentage_of_equipment` (Clarity brand only).
     */
    pricingMethod: varchar("pricingMethod", { length: 32 })
      .notNull()
      .default("fixed"),
    /** Default % when pricingMethod = percentage_of_equipment. */
    defaultPercentage: decimal("defaultPercentage", { precision: 8, scale: 4 }),
    /** Explicit product-level warranty period (months) when known authoritatively. */
    warrantyDurationMonths: int("warrantyDurationMonths"),
    /** Optional product-level warranty label from authoritative source. */
    warrantyLabel: varchar("warrantyLabel", { length: 255 }),
    /** Optional authoritative manufacturer warranty URL for this catalogue product. */
    warrantyTermsUrl: text("warrantyTermsUrl"),
    /**
     * Cat Intel canonical grouping.
     * NULL = this row is a canonical root (or not in a duplicate group).
     * Set to another catalogue_items.id when this row is a linked duplicate.
     */
    canonicalCatalogueItemId: int("canonicalCatalogueItemId"),
    /** none | linked | kept_separate */
    duplicateStatus: varchar("duplicateStatus", { length: 32 })
      .notNull()
      .default("none"),
    duplicateReviewedAt: timestamp("duplicateReviewedAt"),
    duplicateReviewedBy: int("duplicateReviewedBy"),
    duplicateReason: varchar("duplicateReason", { length: 255 }),
    /**
     * Cleo-assisted custom-product identity provenance (custom rows only).
     * Never overloads description fields; imported catalogue rows remain untouched.
     */
    identificationSource: varchar("identificationSource", { length: 64 }),
    identificationConfidence: varchar("identificationConfidence", {
      length: 16,
    }),
    identificationEvidenceJson: json("identificationEvidenceJson"),
    identificationOriginalBrand: varchar("identificationOriginalBrand", {
      length: 255,
    }),
    identificationOriginalModel: varchar("identificationOriginalModel", {
      length: 255,
    }),
    /** Cleo's suggested Customer Friendly Name before staff edits (audit). */
    identificationSuggestedCustomerFriendlyName: varchar(
      "identificationSuggestedCustomerFriendlyName",
      {
        length: 512,
      }
    ),
    identificationConfirmedByUserId: int("identificationConfirmedByUserId"),
    identificationConfirmedAt: timestamp("identificationConfirmedAt"),
    /**
     * When true, later AI enrichment must not overwrite catalogue_items.name
     * (Customer Friendly Name).
     */
    nameManuallyEdited: boolean("nameManuallyEdited").notNull().default(false),
    /**
     * When set, Sales Catalogue opens a structured configurator instead of a fixed SKU row.
     * Example: `transparent_cable_v1`.
     */
    configuratorType: varchar("configuratorType", { length: 64 }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    configuratorTypeUid: uniqueIndex("uq_catalogue_items_configurator_type").on(
      t.configuratorType
    ),
    publicWebsiteIdx: index("idx_catalogue_public_website").on(
      t.showOnWebsite,
      t.isActive,
      t.brand,
    ),
    publicWebsiteHiddenIdx: index("idx_catalogue_public_website_hidden").on(
      t.hideFromWebsite,
      t.isActive,
      t.brand,
    ),
  })
);

/**
 * Canonical image for a resolved Transparent dataset family.
 * Not owned by the launcher catalogue row, a proposal, or a configured length.
 */
export const transparentProductImages = mysqlTable(
  "transparent_product_images",
  {
    id: int("id").autoincrement().primaryKey(),
    canonicalKey: varchar("canonicalKey", { length: 191 }).notNull(),
    familyId: varchar("familyId", { length: 128 }).notNull(),
    rangeNorm: varchar("rangeNorm", { length: 128 }),
    imageUrl: text("imageUrl").notNull(),
    searchQuery: varchar("searchQuery", { length: 512 }),
    approvedByUserId: int("approvedByUserId"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    canonicalUid: uniqueIndex("uq_transparent_product_images_canonical").on(
      t.canonicalKey
    ),
    familyIdx: index("idx_transparent_product_images_family").on(t.familyId),
  })
);

export const catalogueItemAdminAudit = mysqlTable(
  "catalogue_item_admin_audit",
  {
    id: int("id").autoincrement().primaryKey(),
    catalogueItemId: int("catalogueItemId").notNull(),
    action: varchar("action", { length: 32 }).notNull(),
    previousStateJson: json("previousStateJson"),
    resultingStateJson: json("resultingStateJson"),
    reason: text("reason"),
    actorUserId: int("actorUserId").notNull(),
    actorNameSnapshot: varchar("actorNameSnapshot", { length: 255 }),
    actorEmailSnapshot: varchar("actorEmailSnapshot", { length: 320 }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  }
);

export const catalogueChanges = mysqlTable("catalogue_changes", {
  id: int("id").autoincrement().primaryKey(),
  uploadId: int("uploadId").notNull(),
  changeType: mysqlEnum("changeType", [
    "new",
    "removed",
    "price_changed",
    "updated",
    "sku_changed",
  ]).notNull(),
  productRef: varchar("productRef", { length: 512 }).notNull(),
  oldValue: json("oldValue"),
  newValue: json("newValue"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
});

/** Background Cleo homeowner explanations for proposal_items after DTOOLS JSON import. */
export const proposalItemExplanationJobs = mysqlTable(
  "proposal_item_explanation_jobs",
  {
    id: int("id").autoincrement().primaryKey(),
    proposalItemId: int("proposalItemId").notNull(),
    status: mysqlEnum("status", [
      "queued",
      "processing",
      "retry",
      "failed",
      "done",
    ])
      .notNull()
      .default("queued"),
    attempts: int("attempts").notNull().default(0),
    lastError: text("lastError"),
    nextRunAt: timestamp("nextRunAt"),
    lockedAt: timestamp("lockedAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  }
);

// Optional future queue (not used in D.5.1 runtime yet).
export const catalogueEnrichmentJobs = mysqlTable("catalogue_enrichment_jobs", {
  id: int("id").autoincrement().primaryKey(),
  catalogueItemId: int("catalogueItemId").notNull(),
  status: mysqlEnum("status", [
    "queued",
    "processing",
    "retry",
    "failed",
    "done",
  ])
    .notNull()
    .default("queued"),
  attempts: int("attempts").notNull().default(0),
  lastError: text("lastError"),
  nextRunAt: timestamp("nextRunAt"),
  lockedAt: timestamp("lockedAt"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
});

export type CatalogueUpload = typeof catalogueUploads.$inferSelect;
export type InsertCatalogueUpload = typeof catalogueUploads.$inferInsert;
export type CatalogueItem = typeof catalogueItems.$inferSelect;
export type InsertCatalogueItem = typeof catalogueItems.$inferInsert;
export type CatalogueItemAdminAudit =
  typeof catalogueItemAdminAudit.$inferSelect;
export type InsertCatalogueItemAdminAudit =
  typeof catalogueItemAdminAudit.$inferInsert;
export type CatalogueChange = typeof catalogueChanges.$inferSelect;
export type InsertCatalogueChange = typeof catalogueChanges.$inferInsert;
export type CatalogueEnrichmentJob =
  typeof catalogueEnrichmentJobs.$inferSelect;
export type InsertCatalogueEnrichmentJob =
  typeof catalogueEnrichmentJobs.$inferInsert;
export type ProposalItemExplanationJob =
  typeof proposalItemExplanationJobs.$inferSelect;
export type InsertProposalItemExplanationJob =
  typeof proposalItemExplanationJobs.$inferInsert;

/** Reusable sales disclaimers attached to D-Tools proposal system overviews. */
export const salesDisclaimers = mysqlTable("sales_disclaimers", {
  id: int("id").autoincrement().primaryKey(),
  name: varchar("name", { length: 255 }).notNull(),
  category: varchar("category", { length: 128 }).notNull(),
  content: mediumtext("content").notNull(),
  isActive: boolean("isActive").notNull().default(true),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
});

export type SalesDisclaimer = typeof salesDisclaimers.$inferSelect;
export type InsertSalesDisclaimer = typeof salesDisclaimers.$inferInsert;

// ─── Staff digital name cards (public /u/:slug) ───────────────────────────────
export const staffDigitalCards = mysqlTable("staff_digital_cards", {
  id: int("id").autoincrement().primaryKey(),
  userId: int("userId").notNull(),
  publicSlug: varchar("publicSlug", { length: 64 }).notNull(),
  publicBio: text("publicBio"),
  publicJobTitle: varchar("publicJobTitle", { length: 120 }),
  /** Name shown on public card / vCard; falls back to users.name when null. */
  publicDisplayName: varchar("publicDisplayName", { length: 120 }),
  profilePhotoUrl: text("profilePhotoUrl"),
  profilePhotoKey: varchar("profilePhotoKey", { length: 512 }),
  whatsappNumber: varchar("whatsappNumber", { length: 32 }),
  /** Public card phone (falls back to users.contact when null). */
  publicPhone: varchar("publicPhone", { length: 32 }),
  /** Public card email (falls back to users.email when null). */
  publicEmail: varchar("publicEmail", { length: 320 }),
  theme: varchar("theme", { length: 32 }).default("default").notNull(),
  digitalCardEnabled: boolean("digitalCardEnabled").default(false).notNull(),
  showPhone: boolean("showPhone").default(true).notNull(),
  showEmail: boolean("showEmail").default(true).notNull(),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().notNull(),
});

export const staffDigitalCardEvents = mysqlTable("staff_digital_card_events", {
  id: bigint("id", { mode: "number" }).autoincrement().primaryKey(),
  cardId: int("cardId").notNull(),
  eventType: mysqlEnum("eventType", [
    "view",
    "qr_scan",
    "vcf_download",
    "share",
    "wallet_add",
  ]).notNull(),
  metadataJson: json("metadataJson").$type<Record<string, unknown> | null>(),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
});

export type StaffDigitalCard = typeof staffDigitalCards.$inferSelect;
export type InsertStaffDigitalCard = typeof staffDigitalCards.$inferInsert;
export type StaffDigitalCardEvent = typeof staffDigitalCardEvents.$inferSelect;
export type InsertStaffDigitalCardEvent =
  typeof staffDigitalCardEvents.$inferInsert;

/** Finance KPI ↔ Rev Cal reconciliation links (merge / include / create) — append-only audit. */
export const financeReconciliationLinks = mysqlTable(
  "finance_reconciliation_links",
  {
    id: int("id").autoincrement().primaryKey(),
    operationalMonth: varchar("operationalMonth", { length: 7 }).notNull(),
    linkType: mysqlEnum("linkType", [
      "merge",
      "include_in_kpi",
      "create_rev_cal",
    ]).notNull(),
    kpiRowId: varchar("kpiRowId", { length: 128 }),
    revCalRowId: varchar("revCalRowId", { length: 128 }),
    status: mysqlEnum("status", ["active", "superseded"])
      .notNull()
      .default("active"),
    chosenTruth: mysqlEnum("chosenTruth", ["kpi", "revcal"]),
    matchConfidencePct: int("matchConfidencePct"),
    oldKpiAmount: decimal("oldKpiAmount", { precision: 12, scale: 2 }),
    oldRevCalAmount: decimal("oldRevCalAmount", { precision: 12, scale: 2 }),
    newAmount: decimal("newAmount", { precision: 12, scale: 2 }),
    oldKpiMonth: varchar("oldKpiMonth", { length: 7 }),
    oldRevCalMonth: varchar("oldRevCalMonth", { length: 7 }),
    newMonth: varchar("newMonth", { length: 7 }),
    createdByUserId: int("createdByUserId").notNull(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    metadataJson: json("metadataJson").$type<Record<string, unknown> | null>(),
  }
);

export type FinanceReconciliationLink =
  typeof financeReconciliationLinks.$inferSelect;
export type InsertFinanceReconciliationLink =
  typeof financeReconciliationLinks.$inferInsert;

// ─── Knowledge Centre articles (public /articles, admin /admin/articles) ─────
export const knowledgeArticles = mysqlTable("knowledge_articles", {
  id: int("id").autoincrement().primaryKey(),
  slug: varchar("slug", { length: 200 }).notNull(),
  title: varchar("title", { length: 500 }).notNull(),
  category: varchar("category", { length: 64 }).notNull(),
  targetWhatsappGroup: varchar("targetWhatsappGroup", { length: 64 }).notNull(),
  tags: json("tags").$type<string[] | null>(),
  heroImageKey: varchar("heroImageKey", { length: 512 }),
  heroImageUrl: text("heroImageUrl"),
  heroImageAlt: varchar("heroImageAlt", { length: 500 }),
  excerpt: text("excerpt"),
  body: text("body"),
  faqJson: json("faqJson").$type<Array<{
    question: string;
    answer: string;
  }> | null>(),
  ctaLabel: varchar("ctaLabel", { length: 128 }),
  ctaLink: varchar("ctaLink", { length: 2048 }),
  metaTitle: varchar("metaTitle", { length: 255 }),
  metaDescription: varchar("metaDescription", { length: 500 }),
  ogTitle: varchar("ogTitle", { length: 255 }),
  ogDescription: varchar("ogDescription", { length: 500 }),
  seoKeywords: varchar("seoKeywords", { length: 500 }),
  whatsappTeaser: text("whatsappTeaser"),
  status: mysqlEnum("status", ["draft", "published"])
    .notNull()
    .default("draft"),
  featured: boolean("featured").notNull().default(false),
  viewCount: int("viewCount").notNull().default(0),
  whatsappCopyCount: int("whatsappCopyCount").notNull().default(0),
  ctaClickCount: int("ctaClickCount").notNull().default(0),
  publishedAt: timestamp("publishedAt"),
  authorUserId: int("authorUserId"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
});

export const knowledgeArticleEvents = mysqlTable("knowledge_article_events", {
  id: bigint("id", { mode: "number" }).autoincrement().primaryKey(),
  articleId: int("articleId").notNull(),
  eventType: mysqlEnum("eventType", [
    "view",
    "whatsapp_copy",
    "cta_click",
  ]).notNull(),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
});

export type KnowledgeArticle = typeof knowledgeArticles.$inferSelect;
export type InsertKnowledgeArticle = typeof knowledgeArticles.$inferInsert;
export type KnowledgeArticleEvent = typeof knowledgeArticleEvents.$inferSelect;
export type InsertKnowledgeArticleEvent =
  typeof knowledgeArticleEvents.$inferInsert;

// ─── Customer / Product Intelligence (Cleo memory layer) ─────────────────────
export const customerInterestSignals = mysqlTable("customer_interest_signals", {
  id: int("id").autoincrement().primaryKey(),
  customerId: int("customerId").notNull(),
  interestType: mysqlEnum("interestType", [
    "product",
    "brand",
    "category",
  ]).notNull(),
  catalogueItemId: int("catalogueItemId"),
  brand: varchar("brand", { length: 255 }),
  model: varchar("model", { length: 255 }),
  category: varchar("category", { length: 255 }),
  rawMention: varchar("rawMention", { length: 512 }),
  intent: mysqlEnum("intent", [
    "general_interest",
    "quotation_requested",
    "demo_requested",
    "pricing_requested",
    "comparison",
    "follow_up_later",
    "rejected",
    "purchased",
  ])
    .notNull()
    .default("general_interest"),
  sentiment: mysqlEnum("sentiment", [
    "very_interested",
    "interested",
    "interested_delayed",
    "neutral",
    "not_interested",
    "lost",
  ])
    .notNull()
    .default("neutral"),
  objections: json("objections").$type<string[] | null>(),
  budgetSignal: varchar("budgetSignal", { length: 64 }),
  projectStage: varchar("projectStage", { length: 128 }),
  followUpTiming: varchar("followUpTiming", { length: 128 }),
  purchaseLikelihood: int("purchaseLikelihood"),
  sourceType: mysqlEnum("sourceType", [
    "customer_log",
    "nba_update",
    "proposal_item",
    "cleo_qa",
    "manual_note",
    "management_event",
    "backfill",
  ]).notNull(),
  sourceId: int("sourceId"),
  dedupeKey: varchar("dedupeKey", { length: 191 }).notNull(),
  confidence: decimal("confidence", { precision: 5, scale: 2 })
    .notNull()
    .default("0.50"),
  extractedAt: timestamp("extractedAt").defaultNow().notNull(),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  createdByUserId: int("createdByUserId"),
});

export type CustomerInterestSignal =
  typeof customerInterestSignals.$inferSelect;
export type InsertCustomerInterestSignal =
  typeof customerInterestSignals.$inferInsert;

// Public Canvas event funnel. Customer ownership remains independent and may be unassigned.
export const canvasPublicQuotes = mysqlTable("canvas_public_quotes", {
  id: int("id").autoincrement().primaryKey(),
  tokenHash: varchar("tokenHash", { length: 64 }).notNull().unique(),
  reference: varchar("reference", { length: 32 }).notNull().unique(),
  parentQuoteId: int("parentQuoteId"),
  customerId: int("customerId").notNull(),
  customerName: varchar("customerName", { length: 255 }).notNull(),
  customerEmail: varchar("customerEmail", { length: 320 }),
  deliveryChannel: mysqlEnum("deliveryChannel", ["email", "phone"]).notNull(),
  deliveryDestination: varchar("deliveryDestination", { length: 320 }).notNull(),
  campaign: varchar("campaign", { length: 128 }).notNull().default("canvas-hifi-event"),
  configurationJson: json("configurationJson").notNull(),
  totalAmount: decimal("totalAmount", { precision: 12, scale: 2 }).notNull(),
  status: mysqlEnum("status", ["sent", "confirmed", "superseded"]).notNull().default("sent"),
  marketingConsent: boolean("marketingConsent").notNull().default(false),
  marketingConsentText: text("marketingConsentText"),
  marketingConsentedAt: timestamp("marketingConsentedAt"),
  openedAt: timestamp("openedAt"),
  confirmedAt: timestamp("confirmedAt"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
});

export const canvasPublicEvents = mysqlTable("canvas_public_events", {
  id: bigint("id", { mode: "number" }).autoincrement().primaryKey(),
  quoteId: int("quoteId"),
  sessionId: varchar("sessionId", { length: 128 }),
  campaign: varchar("campaign", { length: 128 }).notNull().default("canvas-hifi-event"),
  eventType: varchar("eventType", { length: 64 }).notNull(),
  dedupeKey: varchar("dedupeKey", { length: 191 }).notNull().unique(),
  metadataJson: json("metadataJson"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
});

export const canvasPublicOtps = mysqlTable("canvas_public_otps", {
  id: int("id").autoincrement().primaryKey(),
  challengeId: varchar("challengeId", { length: 64 }).notNull().unique(),
  channel: mysqlEnum("channel", ["email", "phone"]).notNull(),
  destination: varchar("destination", { length: 320 }).notNull(),
  otpHash: varchar("otpHash", { length: 64 }).notNull(),
  verificationTokenHash: varchar("verificationTokenHash", { length: 64 }),
  attempts: int("attempts").notNull().default(0),
  expiresAt: timestamp("expiresAt").notNull(),
  verifiedAt: timestamp("verifiedAt"),
  consumedAt: timestamp("consumedAt"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
});

export const productEvents = mysqlTable("product_events", {
  id: int("id").autoincrement().primaryKey(),
  eventType: mysqlEnum("eventType", [
    "promotion",
    "demo_available",
    "launch",
    "price_change",
    "stock_available",
    "management_campaign",
  ]).notNull(),
  title: varchar("title", { length: 500 }).notNull(),
  description: text("description"),
  catalogueItemId: int("catalogueItemId"),
  brand: varchar("brand", { length: 255 }),
  model: varchar("model", { length: 255 }),
  category: varchar("category", { length: 255 }),
  effectiveFrom: varchar("effectiveFrom", { length: 10 }),
  effectiveTo: varchar("effectiveTo", { length: 10 }),
  status: mysqlEnum("status", ["draft", "published", "archived"])
    .notNull()
    .default("draft"),
  createdByUserId: int("createdByUserId").notNull(),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
});

export type ProductEvent = typeof productEvents.$inferSelect;
export type InsertProductEvent = typeof productEvents.$inferInsert;

// ─── Customer Actions (accountable follow-up) ─────────────────────────────────
export const customerActions = mysqlTable("customer_actions", {
  id: int("id").autoincrement().primaryKey(),
  customerId: int("customerId").notNull(),
  ownerUserId: int("ownerUserId").notNull(),
  title: varchar("title", { length: 500 }).notNull(),
  description: text("description"),
  actionType: mysqlEnum("actionType", [
    "follow_up",
    "demo",
    "quote",
    "objection",
    "customer_care",
    "other",
  ])
    .notNull()
    .default("follow_up"),
  priority: mysqlEnum("priority", ["low", "normal", "high"])
    .notNull()
    .default("normal"),
  status: mysqlEnum("status", ["open", "snoozed", "done", "cancelled"])
    .notNull()
    .default("open"),
  /** Calendar due date (YYYY-MM-DD, Singapore). */
  dueDate: varchar("dueDate", { length: 10 }),
  dueAt: timestamp("dueAt"),
  snoozedUntil: varchar("snoozedUntil", { length: 10 }),
  sourceType: mysqlEnum("sourceType", [
    "customer_log",
    "proposal",
    "manual",
    "cleo",
    "system",
  ]).notNull(),
  sourceId: int("sourceId"),
  proposalId: int("proposalId"),
  reminderId: int("reminderId"),
  completionNote: text("completionNote"),
  dedupeKey: varchar("dedupeKey", { length: 191 }).notNull(),
  createdByUserId: int("createdByUserId"),
  doneAt: timestamp("doneAt"),
  cancelledAt: timestamp("cancelledAt"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
});

export type CustomerAction = typeof customerActions.$inferSelect;
export type InsertCustomerAction = typeof customerActions.$inferInsert;

// ─── Clarity One — Customer Auth ─────────────────────────────────────────────
export const clarityOneLoginTokens = mysqlTable("clarity_one_login_tokens", {
  id: int("id").autoincrement().primaryKey(),
  customerId: int("customerId").notNull(),
  token: varchar("token", { length: 64 }).notNull().unique(),
  phoneNormalized: varchar("phoneNormalized", { length: 8 }).notNull(),
  expiresAt: timestamp("expiresAt").notNull(),
  usedAt: timestamp("usedAt"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
});

export type ClarityOneLoginToken = typeof clarityOneLoginTokens.$inferSelect;
export type InsertClarityOneLoginToken =
  typeof clarityOneLoginTokens.$inferInsert;

/** @deprecated Magic-link tokens — retained for historical rows only. */
export const clarityOneLoginOtps = mysqlTable("clarity_one_login_otps", {
  id: int("id").autoincrement().primaryKey(),
  customerId: int("customerId").notNull(),
  phoneNormalized: varchar("phoneNormalized", { length: 20 }).notNull(),
  otpHash: varchar("otpHash", { length: 64 }).notNull(),
  expiresAt: timestamp("expiresAt").notNull(),
  usedAt: timestamp("usedAt"),
  attemptCount: int("attemptCount").default(0).notNull(),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
});

export type ClarityOneLoginOtp = typeof clarityOneLoginOtps.$inferSelect;
export type InsertClarityOneLoginOtp = typeof clarityOneLoginOtps.$inferInsert;

export const clarityOneLoginAuditLog = mysqlTable(
  "clarity_one_login_audit_log",
  {
    id: int("id").autoincrement().primaryKey(),
    eventType: mysqlEnum("eventType", [
      "login_request",
      "login_success",
      "login_failure",
      "otp_requested",
      "otp_sent",
      "otp_send_failed",
      "otp_verified",
      "otp_failed",
      "otp_expired",
    ]).notNull(),
    phoneNormalized: varchar("phoneNormalized", { length: 20 }),
    customerId: int("customerId"),
    reason: varchar("reason", { length: 255 }),
    ipAddress: varchar("ipAddress", { length: 45 }),
    deviceType: varchar("deviceType", { length: 32 }),
    browser: varchar("browser", { length: 64 }),
    userAgent: varchar("userAgent", { length: 512 }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  }
);

export type ClarityOneLoginAuditLog =
  typeof clarityOneLoginAuditLog.$inferSelect;
export type InsertClarityOneLoginAuditLog =
  typeof clarityOneLoginAuditLog.$inferInsert;

export const clarityOneImpersonationTokens = mysqlTable(
  "clarity_one_impersonation_tokens",
  {
    id: int("id").autoincrement().primaryKey(),
    customerId: int("customerId").notNull(),
    staffUserId: int("staffUserId").notNull(),
    token: varchar("token", { length: 64 }).notNull().unique(),
    expiresAt: timestamp("expiresAt").notNull(),
    usedAt: timestamp("usedAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  }
);

export type ClarityOneImpersonationToken =
  typeof clarityOneImpersonationTokens.$inferSelect;
export type InsertClarityOneImpersonationToken =
  typeof clarityOneImpersonationTokens.$inferInsert;

export const clarityOneStaffImpersonationAuditLog = mysqlTable(
  "clarity_one_staff_impersonation_audit_log",
  {
    id: int("id").autoincrement().primaryKey(),
    staffUserId: int("staffUserId").notNull(),
    customerId: int("customerId").notNull(),
    ipAddress: varchar("ipAddress", { length: 45 }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  }
);

export type ClarityOneStaffImpersonationAuditLog =
  typeof clarityOneStaffImpersonationAuditLog.$inferSelect;
export type InsertClarityOneStaffImpersonationAuditLog =
  typeof clarityOneStaffImpersonationAuditLog.$inferInsert;

export const clarityOneEngagementEvents = mysqlTable(
  "clarity_one_engagement_events",
  {
    id: int("id").autoincrement().primaryKey(),
    customerId: int("customerId").notNull(),
    eventType: mysqlEnum("eventType", [
      "login",
      "login_impersonation",
      "logout",
      "home_page_view",
      "wallet_page_view",
      "transaction_note_edit",
      "profile_updated",
      "statement_page_view",
      "statement_download",
    ]).notNull(),
    metadata: json("metadata"),
    staffUserId: int("staffUserId"),
    ipAddress: varchar("ipAddress", { length: 45 }),
    deviceType: varchar("deviceType", { length: 32 }),
    browser: varchar("browser", { length: 64 }),
    userAgent: varchar("userAgent", { length: 512 }),
    sessionUuid: varchar("sessionUuid", { length: 36 }),
    /** SHA-256 of session uuid + page + document occurrence. Never a raw cookie. */
    occurrenceKey: varchar("occurrenceKey", { length: 64 }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  table => [uniqueIndex("uq_c1_engagement_occurrence").on(table.occurrenceKey)]
);

export type ClarityOneEngagementEvent =
  typeof clarityOneEngagementEvents.$inferSelect;
export type InsertClarityOneEngagementEvent =
  typeof clarityOneEngagementEvents.$inferInsert;

export const clarityOneCustomerEngagement = mysqlTable(
  "clarity_one_customer_engagement",
  {
    customerId: int("customerId").primaryKey(),
    lastLoginAt: timestamp("lastLoginAt"),
    lastLoginMethod: varchar("lastLoginMethod", { length: 32 }),
    loginCountLifetime: int("loginCountLifetime").notNull().default(0),
    loginCount30Days: int("loginCount30Days").notNull().default(0),
    loginCountThisMonth: int("loginCountThisMonth").notNull().default(0),
    walletPageViewsLifetime: int("walletPageViewsLifetime")
      .notNull()
      .default(0),
    transactionNoteEditsLifetime: int("transactionNoteEditsLifetime")
      .notNull()
      .default(0),
    mostRecentActivityAt: timestamp("mostRecentActivityAt"),
    mostRecentActivityType: varchar("mostRecentActivityType", { length: 64 }),
    engagementScore: decimal("engagementScore", { precision: 5, scale: 2 }),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  }
);

export type ClarityOneCustomerEngagement =
  typeof clarityOneCustomerEngagement.$inferSelect;
export type InsertClarityOneCustomerEngagement =
  typeof clarityOneCustomerEngagement.$inferInsert;

export const clarityOneCustomerProfileAuditLog = mysqlTable(
  "clarity_one_customer_profile_audit_log",
  {
    id: int("id").autoincrement().primaryKey(),
    customerId: int("customerId").notNull(),
    fieldName: varchar("fieldName", { length: 32 }).notNull(),
    previousValue: varchar("previousValue", { length: 512 }),
    newValue: varchar("newValue", { length: 512 }),
    ipAddress: varchar("ipAddress", { length: 45 }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  }
);

export type ClarityOneCustomerProfileAuditLog =
  typeof clarityOneCustomerProfileAuditLog.$inferSelect;
export type InsertClarityOneCustomerProfileAuditLog =
  typeof clarityOneCustomerProfileAuditLog.$inferInsert;

export const clarityOneCustomerSessions = mysqlTable(
  "clarity_one_customer_sessions",
  {
    id: int("id").autoincrement().primaryKey(),
    sessionUuid: varchar("sessionUuid", { length: 36 }).notNull().unique(),
    customerId: int("customerId").notNull(),
    loginAt: timestamp("loginAt").defaultNow().notNull(),
    logoutAt: timestamp("logoutAt"),
    durationSeconds: int("durationSeconds"),
    ipAddress: varchar("ipAddress", { length: 45 }),
    deviceType: varchar("deviceType", { length: 32 }),
    browser: varchar("browser", { length: 64 }),
    userAgent: text("userAgent"),
    deviceFingerprint: varchar("deviceFingerprint", { length: 64 }),
    loginMethod: varchar("loginMethod", { length: 32 }),
    staffUserId: int("staffUserId"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  }
);

export type ClarityOneCustomerSession =
  typeof clarityOneCustomerSessions.$inferSelect;
export type InsertClarityOneCustomerSession =
  typeof clarityOneCustomerSessions.$inferInsert;

/** Diagnostics when Clarity One auto-activation is skipped or applied on WON. */
export const clarityOneActivationDiagnostics = mysqlTable(
  "clarity_one_activation_diagnostics",
  {
    id: int("id").autoincrement().primaryKey(),
    customerId: int("customerId").notNull(),
    reason: varchar("reason", { length: 64 }).notNull(),
    phoneNormalized: varchar("phoneNormalized", { length: 32 }),
    proposalId: int("proposalId"),
    proposalRef: varchar("proposalRef", { length: 64 }),
    detail: text("detail"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  }
);

/** First confirmed cash payment creates independent, once-per-customer welcome deliveries. */
export const clarityOnePaymentOnboarding = mysqlTable("clarity_one_payment_onboarding", {
  id: int("id").autoincrement().primaryKey(),
  customerId: int("customerId").notNull(),
  channel: mysqlEnum("channel", ["email", "whatsapp"]).notNull(),
  paymentSource: varchar("paymentSource", { length: 16 }).notNull(),
  paymentId: int("paymentId").notNull(),
  paymentFingerprint: varchar("paymentFingerprint", { length: 64 }).notNull(),
  status: mysqlEnum("status", ["pending", "sending", "sent", "awaiting_response", "blocked", "review_required", "cancelled"]).notNull().default("pending"),
  activatedAt: timestamp("activatedAt"),
  sentAt: timestamp("sentAt"),
  recipientHash: varchar("recipientHash", { length: 64 }),
  providerMessageId: varchar("providerMessageId", { length: 255 }),
  lastError: text("lastError"),
  staffAlertedAt: timestamp("staffAlertedAt"),
  nextRetryAt: timestamp("nextRetryAt"),
  leaseToken: varchar("leaseToken", { length: 64 }),
  leaseExpiresAt: timestamp("leaseExpiresAt"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
}, t => ({
  customerChannelUq: uniqueIndex("clarity_one_payment_welcome_customer_channel_uq").on(t.customerId, t.channel),
  retryIdx: index("clarity_one_payment_welcome_retry_idx").on(t.status, t.nextRetryAt),
}));
export const clarityOnePaymentOnboardingRollout = mysqlTable("clarity_one_payment_onboarding_rollout", {
  id: int("id").primaryKey(),
  startedAt: timestamp("startedAt").defaultNow().notNull(),
});

export type ClarityOneActivationDiagnostic =
  typeof clarityOneActivationDiagnostics.$inferSelect;
export type InsertClarityOneActivationDiagnostic =
  typeof clarityOneActivationDiagnostics.$inferInsert;

// ─── Kaleidescape Wallet ─────────────────────────────────────────────────────
export const kaleidescapeWallets = mysqlTable("kaleidescape_wallets", {
  id: int("id").autoincrement().primaryKey(),
  customerId: int("customerId").notNull().unique(),
  openingBalance: decimal("openingBalance", { precision: 12, scale: 2 })
    .notNull()
    .default("0.00"),
  currentBalance: decimal("currentBalance", { precision: 12, scale: 2 })
    .notNull()
    .default("0.00"),
  cardLast4: varchar("cardLast4", { length: 4 }),
  internalNotes: text("internalNotes"),
  /** Date from which Total Topped Up / Total Spent are tracked in Clarity One. */
  totalsTrackedSince: varchar("totalsTrackedSince", { length: 10 })
    .notNull()
    .default("2026-07-05"),
  lastUpdatedAt: timestamp("lastUpdatedAt").defaultNow().notNull(),
  createdByUserId: int("createdByUserId"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  /** Set when wallet is retired by customer-merge wallet reconciliation (never deleted). */
  retiredAt: timestamp("retiredAt"),
  retirementReason: varchar("retirementReason", { length: 64 }),
  survivingWalletId: int("survivingWalletId"),
  mergeOccurrenceId: int("mergeOccurrenceId"),
});

export type KaleidescapeWallet = typeof kaleidescapeWallets.$inferSelect;
export type InsertKaleidescapeWallet = typeof kaleidescapeWallets.$inferInsert;

export const KALEIDESCAPE_WALLET_TRANSACTION_TYPES = [
  "add_money",
  "spend_money",
  /** Administrative merge reconcile — never counts as top-up/revenue/payment. */
  "merge_transfer_in",
  /** Administrative merge reconcile — never counts as spend/purchase. */
  "merge_transfer_out",
] as const;
export type KaleidescapeWalletTransactionType =
  (typeof KALEIDESCAPE_WALLET_TRANSACTION_TYPES)[number];

export const kaleidescapeWalletTransactions = mysqlTable(
  "kaleidescape_wallet_transactions",
  {
    id: int("id").autoincrement().primaryKey(),
    walletId: int("walletId").notNull(),
    type: mysqlEnum("type", [
      ...KALEIDESCAPE_WALLET_TRANSACTION_TYPES,
    ]).notNull(),
    amount: decimal("amount", { precision: 12, scale: 2 }).notNull(),
    transactionDate: varchar("transactionDate", { length: 10 }).notNull(),
    remarks: text("remarks"),
    customerNotes: text("customerNotes"),
    deletedAt: timestamp("deletedAt"),
    createdByUserId: int("createdByUserId"),
    updatedByUserId: int("updatedByUserId"),
    /** Set only for the wallet credit created by top-up approval. Unique so retries cannot double-credit. */
    topUpRequestId: int("topUpRequestId").unique(),
    /** Links paired merge transfer entries to the reconcile audit row. */
    walletReconcileOccurrenceId: int("walletReconcileOccurrenceId"),
    mergeOccurrenceId: int("mergeOccurrenceId"),
    counterpartWalletId: int("counterpartWalletId"),
    reconcileIdempotencyKey: varchar("reconcileIdempotencyKey", {
      length: 128,
    }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  }
);

export type KaleidescapeWalletTransaction =
  typeof kaleidescapeWalletTransactions.$inferSelect;
export type InsertKaleidescapeWalletTransaction =
  typeof kaleidescapeWalletTransactions.$inferInsert;

export const kaleidescapeTopUpRequests = mysqlTable(
  "kaleidescape_top_up_requests",
  {
    id: int("id").autoincrement().primaryKey(),
    customerId: int("customerId").notNull(),
    walletId: int("walletId").notNull(),
    requestedUsdAmount: decimal("requestedUsdAmount", {
      precision: 12,
      scale: 2,
    }).notNull(),
    exchangeRateUsdToSgd: decimal("exchangeRateUsdToSgd", {
      precision: 8,
      scale: 4,
    }).notNull(),
    adminFeePercent: decimal("adminFeePercent", {
      precision: 5,
      scale: 2,
    }).notNull(),
    payableSgdAmount: decimal("payableSgdAmount", {
      precision: 12,
      scale: 2,
    }).notNull(),
    status: mysqlEnum("status", [
      "pending_payment",
      "payment_received",
      "awaiting_wallet_credit",
      "completed",
      "cancelled",
    ])
      .notNull()
      .default("pending_payment"),
    paymentInstructionsSnapshot: text("paymentInstructionsSnapshot"),
    creditedTransactionId: int("creditedTransactionId"),
    requestedAt: timestamp("requestedAt").defaultNow().notNull(),
    paymentReceivedAt: timestamp("paymentReceivedAt"),
    approvedAt: timestamp("approvedAt"),
    approvedBy: int("approvedBy"),
    cancelledAt: timestamp("cancelledAt"),
    cancelledBy: int("cancelledBy"),
    cancellationReason: varchar("cancellationReason", { length: 512 }),
    /** Wallet balance immediately after the authoritative top-up credit. */
    creditedBalanceUsd: decimal("creditedBalanceUsd", { precision: 12, scale: 2 }),
    /** Set once the completed-top-up WhatsApp occurrence is durably skipped or queued. */
    customerWhatsappEnqueuedAt: timestamp("customerWhatsappEnqueuedAt"),
    /** Staff dismissed from Kaleidescape dashboard Action Required — does not cancel the request. */
    dashboardDismissedAt: timestamp("dashboardDismissedAt"),
    dashboardDismissedBy: int("dashboardDismissedBy"),
    dashboardDismissedReason: varchar("dashboardDismissedReason", {
      length: 255,
    }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  }
);

export type KaleidescapeTopUpRequest =
  typeof kaleidescapeTopUpRequests.$inferSelect;
export type InsertKaleidescapeTopUpRequest =
  typeof kaleidescapeTopUpRequests.$inferInsert;

export const kaleidescapeWalletAuditLog = mysqlTable(
  "kaleidescape_wallet_audit_log",
  {
    id: int("id").autoincrement().primaryKey(),
    eventType: mysqlEnum("eventType", [
      "top_up_requested",
      "payment_submitted",
      "invoice_generated",
      "payment_received",
      "operator_notified",
      "customer_notified",
      "top_up_approved",
      "top_up_cancelled",
      "wallet_credit_created",
      "wallet_debit_created",
      "low_balance_flagged",
      "low_balance_threshold_crossed",
    ]).notNull(),
    customerId: int("customerId"),
    walletId: int("walletId"),
    topUpRequestId: int("topUpRequestId"),
    transactionId: int("transactionId"),
    staffUserId: int("staffUserId"),
    metadata: text("metadata"),
    ipAddress: varchar("ipAddress", { length: 45 }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  }
);

export type KaleidescapeWalletAuditLog =
  typeof kaleidescapeWalletAuditLog.$inferSelect;

export const kaleidescapeLowBalanceState = mysqlTable(
  "kaleidescape_low_balance_state",
  {
    customerId: int("customerId").primaryKey(),
    walletId: int("walletId").notNull(),
    isLowBalance: boolean("isLowBalance").notNull().default(false),
    wasAboveThreshold: boolean("wasAboveThreshold").notNull().default(true),
    lastReminderAt: timestamp("lastReminderAt"),
    lastCrossedBelowAt: timestamp("lastCrossedBelowAt"),
    pendingNotificationAt: timestamp("pendingNotificationAt"),
    /** Monotonic id of the latest authoritative above-to-below crossing. 0 means none. */
    crossingOccurrence: int("crossingOccurrence").notNull().default(0),
    /** Highest crossing occurrence whose notification intent has been recorded. */
    notifiedCrossingOccurrence: int("notifiedCrossingOccurrence").notNull().default(0),
    /** Balance captured at the latest crossing. Later debits must not rewrite the message. */
    lastCrossingBalanceUsd: decimal("lastCrossingBalanceUsd", { precision: 12, scale: 2 }),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  }
);

export const kaleidescapeCustomerNotificationIntents = mysqlTable(
  "kaleidescape_customer_notification_intents",
  {
    id: int("id").autoincrement().primaryKey(),
    occurrenceKey: varchar("occurrenceKey", { length: 191 }).notNull(),
    kind: mysqlEnum("kind", ["low_balance", "wallet_credited"]).notNull(),
    walletId: int("walletId").notNull(),
    customerId: int("customerId").notNull(),
    topUpRequestId: int("topUpRequestId"),
    crossingOccurrence: int("crossingOccurrence"),
    balanceUsd: decimal("balanceUsd", { precision: 12, scale: 2 }).notNull(),
    creditedUsd: decimal("creditedUsd", { precision: 12, scale: 2 }),
    status: mysqlEnum("status", ["pending", "skipped", "queued", "failed"])
      .notNull()
      .default("pending"),
    skipReason: varchar("skipReason", { length: 64 }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  (t) => [uniqueIndex("kaleid_customer_notif_occ_uq").on(t.occurrenceKey)],
);

export type KaleidescapeCustomerNotificationIntent =
  typeof kaleidescapeCustomerNotificationIntents.$inferSelect;

export type KaleidescapeLowBalanceState =
  typeof kaleidescapeLowBalanceState.$inferSelect;

// ─── Sandbox: Project Catalogue (planning / markup) ─────────────────────────

export const sandboxProjectCatalogueItems = mysqlTable(
  "sandbox_project_catalogue_items",
  {
    id: int("id").autoincrement().primaryKey(),
    /** Link to project_catalogue_items when sourceType is project_memory. */
    projectCatalogueItemId: int("projectCatalogueItemId"),
    sourceType: mysqlEnum("sourceType", [
      "manual",
      "project_memory",
      "sales_catalogue",
    ])
      .notNull()
      .default("manual"),
    manufacturerNorm: varchar("manufacturerNorm", { length: 255 }),
    productNameNorm: varchar("productNameNorm", { length: 512 }),
    manufacturer: varchar("manufacturer", { length: 255 }).notNull(),
    productName: varchar("productName", { length: 512 }).notNull(),
    /** Customer-friendly title for markup / project catalogue UI. */
    displayName: varchar("displayName", { length: 512 }),
    displayNameManuallyEdited: boolean("displayNameManuallyEdited")
      .notNull()
      .default(false),
    category: varchar("category", { length: 255 }),
    imageUrl: varchar("imageUrl", { length: 2048 }),
    width: decimal("width", { precision: 10, scale: 2 }),
    height: decimal("height", { precision: 10, scale: 2 }),
    depth: decimal("depth", { precision: 10, scale: 2 }),
    dimensionUnit: mysqlEnum("dimensionUnit", ["mm", "cm", "inch"])
      .notNull()
      .default("mm"),
    weight: decimal("weight", { precision: 10, scale: 2 }),
    weightUnit: varchar("weightUnit", { length: 16 }),
    rackUnits: decimal("rackUnits", { precision: 5, scale: 1 }),
    powerConsumption: varchar("powerConsumption", { length: 64 }),
    voltage: varchar("voltage", { length: 64 }),
    placementType: mysqlEnum("placementType", [
      "speaker",
      "subwoofer",
      "projector",
      "screen",
      "camera",
      "keypad",
      "rack",
      "network",
      "lighting",
      "other",
    ])
      .notNull()
      .default("other"),
    notes: text("notes"),
    datasheetUrl: varchar("datasheetUrl", { length: 2048 }),
    /** Extensible metadata for future rack/camera/speaker placement workflows. */
    planningMetadata: json("planningMetadata").$type<Record<
      string,
      unknown
    > | null>(),
    createdByUserId: int("createdByUserId").notNull(),
    updatedByUserId: int("updatedByUserId"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  }
);

export type SandboxProjectCatalogueItem =
  typeof sandboxProjectCatalogueItems.$inferSelect;
export type InsertSandboxProjectCatalogueItem =
  typeof sandboxProjectCatalogueItems.$inferInsert;

// ─── Install Projects (operational workspace) ───────────────────────────────

export const installProjects = mysqlTable("install_projects", {
  id: int("id").autoincrement().primaryKey(),
  projectRef: varchar("projectRef", { length: 32 }).notNull().unique(),
  projectName: varchar("projectName", { length: 255 }).notNull(),
  customerId: int("customerId").notNull(),
  proposalId: int("proposalId").notNull().unique(),
  proposalRef: varchar("proposalRef", { length: 128 }).notNull(),
  proposalRevisionNumber: int("proposalRevisionNumber").notNull().default(1),
  status: mysqlEnum("status", ["live", "completed"]).notNull().default("live"),
  createdByUserId: int("createdByUserId").notNull(),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
});

export const installProjectBomItems = mysqlTable("install_project_bom_items", {
  id: int("id").autoincrement().primaryKey(),
  projectId: int("projectId").notNull(),
  proposalLineItemId: int("proposalLineItemId"),
  productName: varchar("productName", { length: 512 }).notNull(),
  manufacturer: varchar("manufacturer", { length: 255 }),
  model: varchar("model", { length: 255 }),
  quantitySold: int("quantitySold").notNull().default(1),
  imageUrl: varchar("imageUrl", { length: 2048 }),
  systemName: varchar("systemName", { length: 255 }),
  roomName: varchar("roomName", { length: 255 }),
  sortOrder: int("sortOrder").notNull().default(0),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
});

export const installProjectDrawings = mysqlTable("install_project_drawings", {
  id: int("id").autoincrement().primaryKey(),
  projectId: int("projectId").notNull(),
  storageKey: varchar("storageKey", { length: 512 }).notNull(),
  fileName: varchar("fileName", { length: 255 }).notNull(),
  version: int("version").notNull().default(1),
  isCurrentVersion: boolean("isCurrentVersion").notNull().default(true),
  replacedDrawingId: int("replacedDrawingId"),
  pageWidthPts: decimal("pageWidthPts", { precision: 10, scale: 2 }),
  pageHeightPts: decimal("pageHeightPts", { precision: 10, scale: 2 }),
  uploadedByUserId: int("uploadedByUserId").notNull(),
  uploadedAt: timestamp("uploadedAt").defaultNow().notNull(),
});

export const installProjectLayouts = mysqlTable("install_project_layouts", {
  id: int("id").autoincrement().primaryKey(),
  projectId: int("projectId").notNull(),
  name: varchar("name", { length: 255 }).notNull(),
  drawingId: int("drawingId").notNull(),
  drawingVersion: int("drawingVersion").notNull(),
  markerDataJson: json("markerDataJson").$type<unknown>().notNull(),
  flattenedPdfStorageKey: varchar("flattenedPdfStorageKey", { length: 512 }),
  createdByUserId: int("createdByUserId").notNull(),
  updatedByUserId: int("updatedByUserId"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
});

export const installProjectLayoutApprovals = mysqlTable(
  "install_project_layout_approvals",
  {
    id: int("id").autoincrement().primaryKey(),
    projectId: int("projectId").notNull(),
    layoutId: int("layoutId").notNull(),
    customerFileId: int("customerFileId"),
    layoutFingerprint: varchar("layoutFingerprint", { length: 64 }).notNull(),
    drawingVersion: int("drawingVersion").notNull(),
    customerId: int("customerId").notNull(),
    approvedByCustomerId: int("approvedByCustomerId").notNull(),
    approvedAt: timestamp("approvedAt").defaultNow().notNull(),
  }
);

export const installProjectCustomerFiles = mysqlTable(
  "install_project_customer_files",
  {
    id: int("id").autoincrement().primaryKey(),
    projectId: int("projectId").notNull(),
    fileType: mysqlEnum("fileType", [
      "drawing",
      "layout_pdf",
      "other_pdf",
    ]).notNull(),
    label: varchar("label", { length: 255 }).notNull(),
    storageKey: varchar("storageKey", { length: 512 }).notNull(),
    sourceDrawingId: int("sourceDrawingId"),
    sourceLayoutId: int("sourceLayoutId"),
    visibleToCustomer: boolean("visibleToCustomer").notNull().default(false),
    addedByUserId: int("addedByUserId").notNull(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  }
);

export const installProjectShareLinks = mysqlTable(
  "install_project_share_links",
  {
    id: int("id").autoincrement().primaryKey(),
    projectId: int("projectId").notNull(),
    token: varchar("token", { length: 96 }).notNull().unique(),
    revokedAt: timestamp("revokedAt"),
    createdByUserId: int("createdByUserId").notNull(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  }
);

export const installProjectActivity = mysqlTable("install_project_activity", {
  id: int("id").autoincrement().primaryKey(),
  projectId: int("projectId").notNull(),
  actionType: varchar("actionType", { length: 64 }).notNull(),
  actionLabel: varchar("actionLabel", { length: 512 }).notNull(),
  actorUserId: int("actorUserId").notNull(),
  actorName: varchar("actorName", { length: 255 }).notNull(),
  relatedObjectType: varchar("relatedObjectType", { length: 64 }),
  relatedObjectId: int("relatedObjectId"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
});

export type InstallProject = typeof installProjects.$inferSelect;
export type InstallProjectBomItem = typeof installProjectBomItems.$inferSelect;
export type InstallProjectLayout = typeof installProjectLayouts.$inferSelect;

// ─── Procurement handover (retail proposals, Phase 1) ───────────────────────

export const procurementHandovers = mysqlTable("procurement_handovers", {
  id: int("id").autoincrement().primaryKey(),
  customerId: int("customerId").notNull(),
  customerName: varchar("customerName", { length: 255 }).notNull(),
  proposalId: int("proposalId").notNull(),
  proposalRef: varchar("proposalRef", { length: 128 }).notNull(),
  proposalRevisionId: int("proposalRevisionId").notNull(),
  salespersonId: int("salespersonId").notNull(),
  salespersonName: varchar("salespersonName", { length: 255 }),
  dealInvoiceId: int("dealInvoiceId").notNull().unique(),
  amountReceived: decimal("amountReceived", {
    precision: 12,
    scale: 2,
  }).notNull(),
  paymentReceivedAt: timestamp("paymentReceivedAt").notNull(),
  expectedDeliveryType: mysqlEnum("expectedDeliveryType", [
    "unset",
    "asap",
    "month_year",
  ])
    .notNull()
    .default("unset"),
  expectedDeliveryMonth: varchar("expectedDeliveryMonth", { length: 7 }),
  deliveryTimingUpdatedByUserId: int("deliveryTimingUpdatedByUserId"),
  deliveryTimingUpdatedAt: timestamp("deliveryTimingUpdatedAt"),
  procurementStatus: mysqlEnum("procurementStatus", [
    "awaiting_delivery_timing",
    "awaiting_order",
    "ordered",
  ])
    .notNull()
    .default("awaiting_delivery_timing"),
  orderedByUserId: int("orderedByUserId"),
  orderedAt: timestamp("orderedAt"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
});

export const procurementHandoverActivity = mysqlTable(
  "procurement_handover_activity",
  {
    id: int("id").autoincrement().primaryKey(),
    handoverId: int("handoverId").notNull(),
    proposalId: int("proposalId").notNull(),
    actionType: varchar("actionType", { length: 64 }).notNull(),
    actionLabel: varchar("actionLabel", { length: 512 }).notNull(),
    actorUserId: int("actorUserId"),
    actorName: varchar("actorName", { length: 255 }).notNull(),
    metadataJson: json("metadataJson"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  }
);

export type ProcurementHandover = typeof procurementHandovers.$inferSelect;
export type ProcurementHandoverActivity =
  typeof procurementHandoverActivity.$inferSelect;

// ─── Project procurement (structured equipment pick lists) ───────────────────

export const projectProcurements = mysqlTable(
  "project_procurements",
  {
    id: int("id").autoincrement().primaryKey(),
    /** Commercial-head / accepted proposal id (unique active record per proposal family). */
    proposalId: int("proposalId").notNull(),
    proposalRef: varchar("proposalRef", { length: 128 }).notNull(),
    proposalRevisionId: int("proposalRevisionId").notNull(),
    proposalSource: varchar("proposalSource", { length: 32 }).notNull(),
    customerId: int("customerId").notNull(),
    customerName: varchar("customerName", { length: 255 }).notNull(),
    projectName: varchar("projectName", { length: 512 }),
    salespersonId: int("salespersonId").notNull(),
    salespersonName: varchar("salespersonName", { length: 255 }),
    /** Triggering first-required tranche / deal invoice. */
    triggeringDealInvoiceId: int("triggeringDealInvoiceId"),
    triggeringTrancheId: int("triggeringTrancheId"),
    firstPaymentReceivedAt: timestamp("firstPaymentReceivedAt"),
    status: mysqlEnum("status", [
      "active",
      "payment_reversed_hold",
      "deactivated",
      "completed",
    ])
      .notNull()
      .default("active"),
    attentionState: varchar("attentionState", { length: 64 })
      .notNull()
      .default("new_review"),
    /** Human purchasing reference (e.g. 10KLR) — locked once the first PO is issued. */
    projectReference: varchar("projectReference", { length: 64 }),
    projectReferenceLockedAt: timestamp("projectReferenceLockedAt"),
    /** `suggested` | `manual` | `confirmed_duplicate` */
    projectReferenceSource: varchar("projectReferenceSource", { length: 32 }),
    siteAddressSnapshot: text("siteAddressSnapshot"),
    procurementLeadTimeMonths: int("procurementLeadTimeMonths")
      .notNull()
      .default(6),
    /** Expected completion − lead time months; recalculated whenever either changes. */
    toOrderByDate: date("toOrderByDate"),
    /**
     * Procurement-owned operational forecast.
     * Never projected back onto proposal_headers customer timeline fields.
     */
    procurementForecastMode: mysqlEnum("procurementForecastMode", [
      "asap",
      "month_year",
    ]),
    procurementForecastTargetMonth: varchar("procurementForecastTargetMonth", {
      length: 7,
    }),
    procurementForecastOrderByDate: date("procurementForecastOrderByDate"),
    procurementForecastSupplierReadyMonth: varchar(
      "procurementForecastSupplierReadyMonth",
      {
        length: 7,
      }
    ),
    procurementForecastCustomerDeliveryMonth: varchar(
      "procurementForecastCustomerDeliveryMonth",
      {
        length: 7,
      }
    ),
    procurementForecastLeadTimeMonths: int("procurementForecastLeadTimeMonths"),
    procurementForecastNote: text("procurementForecastNote"),
    procurementForecastUpdatedAt: timestamp("procurementForecastUpdatedAt"),
    procurementForecastUpdatedByUserId: int(
      "procurementForecastUpdatedByUserId"
    ),
    procurementForecastSource: varchar("procurementForecastSource", {
      length: 64,
    }),
    totalEquipmentValue: decimal("totalEquipmentValue", {
      precision: 14,
      scale: 2,
    })
      .notNull()
      .default("0.00"),
    orderedEquipmentValue: decimal("orderedEquipmentValue", {
      precision: 14,
      scale: 2,
    })
      .notNull()
      .default("0.00"),
    inStockEquipmentValue: decimal("inStockEquipmentValue", {
      precision: 14,
      scale: 2,
    })
      .notNull()
      .default("0.00"),
    receivedEquipmentValue: decimal("receivedEquipmentValue", {
      precision: 14,
      scale: 2,
    })
      .notNull()
      .default("0.00"),
    coveredEquipmentValue: decimal("coveredEquipmentValue", {
      precision: 14,
      scale: 2,
    })
      .notNull()
      .default("0.00"),
    outstandingEquipmentValue: decimal("outstandingEquipmentValue", {
      precision: 14,
      scale: 2,
    })
      .notNull()
      .default("0.00"),
    percentOrdered: decimal("percentOrdered", { precision: 6, scale: 2 })
      .notNull()
      .default("0.00"),
    percentInStock: decimal("percentInStock", { precision: 6, scale: 2 })
      .notNull()
      .default("0.00"),
    percentReceived: decimal("percentReceived", { precision: 6, scale: 2 })
      .notNull()
      .default("0.00"),
    percentCovered: decimal("percentCovered", { precision: 6, scale: 2 })
      .notNull()
      .default("0.00"),
    percentOutstanding: decimal("percentOutstanding", {
      precision: 6,
      scale: 2,
    })
      .notNull()
      .default("0.00"),
    equipmentLineCount: int("equipmentLineCount").notNull().default(0),
    latestApprovedVoId: int("latestApprovedVoId"),
    latestApprovedVoNumber: varchar("latestApprovedVoNumber", { length: 64 }),
    latestApprovedVoAt: timestamp("latestApprovedVoAt"),
    reviewedAt: timestamp("reviewedAt"),
    reviewedByUserId: int("reviewedByUserId"),
    activatedAt: timestamp("activatedAt").notNull(),
    activatedByUserId: int("activatedByUserId"),
    activatedByName: varchar("activatedByName", { length: 255 }),
    deactivatedAt: timestamp("deactivatedAt"),
    holdReason: varchar("holdReason", { length: 255 }),
    completedAt: timestamp("completedAt"),
    completedByUserId: int("completedByUserId"),
    completedByName: varchar("completedByName", { length: 255 }),
    /** Scope hash at completion — a later VO that changes scope invalidates it. */
    completedScopeFingerprint: varchar("completedScopeFingerprint", {
      length: 128,
    }),
    completionNotes: text("completionNotes"),
    /**
     * Closure kind while status remains `completed` for backward compatibility.
     * - normal: Close Procurement through Portal
     * - previously_completed: legacy Close as Previously Completed / Close Historical Procurement
     * null on older rows treated as normal.
     */
    completionKind: mysqlEnum("completionKind", [
      "normal",
      "previously_completed",
    ]),
    /**
     * Client idempotency key for Close Historical Procurement.
     * Same-key replay returns the completed case without a second audit.
     */
    historicalClosureIdempotencyKey: varchar(
      "historicalClosureIdempotencyKey",
      {
        length: 191,
      }
    ),
    reopenedAt: timestamp("reopenedAt"),
    reopenedByUserId: int("reopenedByUserId"),
    reopenReason: text("reopenReason"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    proposalUid: uniqueIndex("project_procurements_proposal_uid").on(
      t.proposalId
    ),
    historicalClosureIdempotencyUid: uniqueIndex(
      "project_procurements_historical_closure_idempotency_uq"
    ).on(t.historicalClosureIdempotencyKey),
  })
);

export type ProcurementForecastSnapshot = {
  mode: "asap" | "month_year" | null;
  targetMonth: string | null;
  orderByDate: string | null;
  supplierReadyMonth: string | null;
  customerDeliveryMonth: string | null;
  leadTimeMonths: number | null;
  note: string | null;
};

/** Append-only before/after authority for Procurement-owned forecast edits. */
export const procurementForecastHistory = mysqlTable(
  "procurement_forecast_history",
  {
    id: int("id").autoincrement().primaryKey(),
    projectProcurementId: int("projectProcurementId").notNull(),
    proposalId: int("proposalId").notNull(),
    beforeJson: json("beforeJson")
      .$type<ProcurementForecastSnapshot>()
      .notNull(),
    afterJson: json("afterJson").$type<ProcurementForecastSnapshot>().notNull(),
    changedByUserId: int("changedByUserId").notNull(),
    changedByName: varchar("changedByName", { length: 255 }).notNull(),
    changedByRole: varchar("changedByRole", { length: 64 }).notNull(),
    source: varchar("source", { length: 64 }).notNull(),
    reason: text("reason"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    procurementCreatedIdx: index(
      "procurement_forecast_history_proc_created_idx"
    ).on(t.projectProcurementId, t.createdAt),
  })
);

export const procurementPickListLines = mysqlTable(
  "procurement_pick_list_lines",
  {
    id: int("id").autoincrement().primaryKey(),
    projectProcurementId: int("projectProcurementId").notNull(),
    proposalId: int("proposalId").notNull(),
    /**
     * Consolidated product identity (preferred) or legacy room-line key.
     * After approved-scope consolidation repair, this equals productIdentityKey.
     */
    stableLineKey: varchar("stableLineKey", { length: 191 }).notNull(),
    /** Stable purchasing identity (catalogue / SKU / fallback). */
    productIdentityKey: varchar("productIdentityKey", { length: 191 }),
    brand: varchar("brand", { length: 255 }),
    model: varchar("model", { length: 255 }),
    /** Manufacturer part / SKU — separate from description. */
    sku: varchar("sku", { length: 255 }),
    productDescription: text("productDescription"),
    /** Unit of measure — separate from description/SKU; blank when unavailable. */
    uom: varchar("uom", { length: 64 }),
    catalogProductId: int("catalogProductId"),
    catalogueKey: varchar("catalogueKey", { length: 128 }),
    finish: varchar("finish", { length: 255 }),
    systemName: varchar("systemName", { length: 255 }),
    roomName: varchar("roomName", { length: 255 }),
    quantityRequired: decimal("quantityRequired", { precision: 12, scale: 3 })
      .notNull()
      .default("0"),
    quantityOrdered: decimal("quantityOrdered", { precision: 12, scale: 3 })
      .notNull()
      .default("0"),
    quantityFromStock: decimal("quantityFromStock", { precision: 12, scale: 3 })
      .notNull()
      .default("0"),
    /** Accepted (condition = good) goods-receipt quantity — drives completion. */
    quantityReceivedAccepted: decimal("quantityReceivedAccepted", {
      precision: 12,
      scale: 3,
    })
      .notNull()
      .default("0"),
    /** Quantity satisfied by an approved replacement product on another line. */
    quantityReplaced: decimal("quantityReplaced", { precision: 12, scale: 3 })
      .notNull()
      .default("0"),
    /** Cumulative quantity delivered to the customer (from confirmed customer deliveries). */
    quantityDeliveredCustomer: decimal("quantityDeliveredCustomer", {
      precision: 12,
      scale: 3,
    })
      .notNull()
      .default("0"),
    /** Set on the original sold line when a replacement line was created for it. */
    replacedByLineId: int("replacedByLineId"),
    /** Set on a replacement line, pointing back at the original sold line. */
    replacesLineId: int("replacesLineId"),
    /** Optional manufacturer override for purchasing display when `brand` is unreliable. */
    manufacturerResolvedBrand: varchar("manufacturerResolvedBrand", {
      length: 255,
    }),
    soldUnitPrice: decimal("soldUnitPrice", { precision: 14, scale: 4 })
      .notNull()
      .default("0"),
    soldLineTotal: decimal("soldLineTotal", { precision: 14, scale: 2 })
      .notNull()
      .default("0"),
    /** Number of System/Room member lines consolidated into this purchasing row. */
    locationCount: int("locationCount").notNull().default(1),
    /** JSON array of location members (system/room/qty/UOM/source/VO/member sold values). */
    locationsJson: json("locationsJson"),
    mixedUnitPrice: boolean("mixedUnitPrice").notNull().default(false),
    uomMissing: boolean("uomMissing").notNull().default(false),
    uomConflict: boolean("uomConflict").notNull().default(false),
    needsManualReview: boolean("needsManualReview").notNull().default(false),
    reviewReasonsJson: json("reviewReasonsJson"),
    /** Authoritative service/category classification suggestion; staff still decides disposition. */
    serviceSuggestionReason: varchar("serviceSuggestionReason", {
      length: 255,
    }),
    bomCompletenessStatus: mysqlEnum("bomCompletenessStatus", [
      "ready",
      "needs_review",
      "incomplete",
    ])
      .notNull()
      .default("ready"),
    proposalRevisionId: int("proposalRevisionId"),
    lineStatus: mysqlEnum("lineStatus", [
      "unreviewed",
      "to_order",
      "ordered",
      "use_existing_stock",
      "on_hold",
      "not_required",
    ])
      .notNull()
      .default("unreviewed"),
    procurementNote: text("procurementNote"),
    orderDate: timestamp("orderDate"),
    supplierName: varchar("supplierName", { length: 255 }),
    externalPoRef: varchar("externalPoRef", { length: 255 }),
    voId: int("voId"),
    voNumber: varchar("voNumber", { length: 64 }),
    voApprovedAt: timestamp("voApprovedAt"),
    voChangeType: varchar("voChangeType", { length: 32 }),
    /** Exception: required qty reduced below already-ordered qty after VO. */
    orderedExceedsRequired: boolean("orderedExceedsRequired")
      .notNull()
      .default(false),
    sortOrder: int("sortOrder").notNull().default(0),
    /**
     * Set on structured lines created by legacy PDF-wrapper item entry
     * (migration 0193). Null for ordinary / pre-existing pick-list rows.
     */
    legacyPdfWrapperBatchId: int("legacyPdfWrapperBatchId"),
    /**
     * Set on the summary placeholder when an entry batch supersedes it.
     * Null means the placeholder is still active (or the line is not a
     * superseded PDF-wrapper placeholder).
     */
    legacyPdfWrapperSupersededByBatchId: int(
      "legacyPdfWrapperSupersededByBatchId"
    ),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    procurementLineUid: uniqueIndex("procurement_pick_lines_uid").on(
      t.projectProcurementId,
      t.stableLineKey
    ),
    pdfWrapperBatchIdx: index(
      "procurement_pick_lines_pdf_wrapper_batch_idx"
    ).on(t.legacyPdfWrapperBatchId),
    pdfWrapperSupersededIdx: index(
      "procurement_pick_lines_pdf_wrapper_superseded_idx"
    ).on(t.legacyPdfWrapperSupersededByBatchId),
  })
);

export const procurementActivityLog = mysqlTable("procurement_activity_log", {
  id: int("id").autoincrement().primaryKey(),
  projectProcurementId: int("projectProcurementId").notNull(),
  proposalId: int("proposalId").notNull(),
  pickListLineId: int("pickListLineId"),
  actionType: varchar("actionType", { length: 64 }).notNull(),
  actionLabel: varchar("actionLabel", { length: 512 }).notNull(),
  actorUserId: int("actorUserId"),
  actorName: varchar("actorName", { length: 255 }).notNull(),
  metadataJson: json("metadataJson"),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
});

export const procurementNotificationLog = mysqlTable(
  "procurement_notification_log",
  {
    id: int("id").autoincrement().primaryKey(),
    projectProcurementId: int("projectProcurementId"),
    proposalId: int("proposalId").notNull(),
    notificationKind: varchar("notificationKind", { length: 64 }).notNull(),
    recipientUserId: int("recipientUserId"),
    recipientEmail: varchar("recipientEmail", { length: 320 }).notNull(),
    subject: varchar("subject", { length: 512 }).notNull(),
    success: boolean("success").notNull().default(false),
    errorMessage: text("errorMessage"),
    providerMessageId: varchar("providerMessageId", { length: 255 }),
    metadataJson: json("metadataJson"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  }
);

export const procurementReconciliationRuns = mysqlTable(
  "procurement_reconciliation_runs",
  {
    id: int("id").autoincrement().primaryKey(),
    runKey: varchar("runKey", { length: 64 }).notNull(),
    status: varchar("status", { length: 32 }).notNull().default("running"),
    startedAt: timestamp("startedAt").defaultNow().notNull(),
    completedAt: timestamp("completedAt"),
    scanned: int("scanned").notNull().default(0),
    activated: int("activated").notNull().default(0),
    reactivated: int("reactivated").notNull().default(0),
    alreadyActive: int("alreadyActive").notNull().default(0),
    skippedPdf: int("skippedPdf").notNull().default(0),
    skippedNotPaid: int("skippedNotPaid").notNull().default(0),
    skippedNoScope: int("skippedNoScope").notNull().default(0),
    skippedOther: int("skippedOther").notNull().default(0),
    errorSummary: text("errorSummary"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    runKeyUid: uniqueIndex("procurement_reconciliation_runs_key_uid").on(
      t.runKey
    ),
  })
);

export type ProjectProcurement = typeof projectProcurements.$inferSelect;
export type ProcurementForecastHistory =
  typeof procurementForecastHistory.$inferSelect;
export type InsertProcurementForecastHistory =
  typeof procurementForecastHistory.$inferInsert;
export type ProcurementPickListLine =
  typeof procurementPickListLines.$inferSelect;
export type ProcurementActivityLog = typeof procurementActivityLog.$inferSelect;
export type ProcurementNotificationLog =
  typeof procurementNotificationLog.$inferSelect;
export type ProcurementReconciliationRun =
  typeof procurementReconciliationRuns.$inferSelect;

// ─── Procurement fulfilment (vendors, POs, receipts, replacements) ───────────

export const procurementVendors = mysqlTable(
  "procurement_vendors",
  {
    id: int("id").autoincrement().primaryKey(),
    legalName: varchar("legalName", { length: 255 }).notNull(),
    displayName: varchar("displayName", { length: 255 }),
    contactPerson: varchar("contactPerson", { length: 255 }),
    email: varchar("email", { length: 320 }),
    phone: varchar("phone", { length: 64 }),
    billingAddress: text("billingAddress"),
    currency: varchar("currency", { length: 8 }).notNull().default("SGD"),
    paymentTerms: varchar("paymentTerms", { length: 255 }),
    /** JSON string array of manufacturer brands this vendor supplies. */
    manufacturersJson: json("manufacturersJson").$type<string[] | null>(),
    taxGstInfo: text("taxGstInfo"),
    notes: text("notes"),
    active: boolean("active").notNull().default(true),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    nameIdx: index("procurement_vendors_name_idx").on(t.legalName),
  })
);

export const procurementManufacturers = mysqlTable(
  "procurement_manufacturers",
  {
    id: int("id").autoincrement().primaryKey(),
    brandName: varchar("brandName", { length: 191 }).notNull(),
    /** Short PO-number code (default first three letters). Collisions allowed but detectable. */
    code: varchar("code", { length: 8 }).notNull(),
    notes: text("notes"),
    active: boolean("active").notNull().default(true),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    brandUid: uniqueIndex("procurement_manufacturers_brand_uid").on(
      t.brandName
    ),
    codeIdx: index("procurement_manufacturers_code_idx").on(t.code),
  })
);

export const procurementStockAllocations = mysqlTable(
  "procurement_stock_allocations",
  {
    id: int("id").autoincrement().primaryKey(),
    projectProcurementId: int("projectProcurementId").notNull(),
    pickListLineId: int("pickListLineId").notNull(),
    productIdentityKey: varchar("productIdentityKey", { length: 191 }),
    brand: varchar("brand", { length: 255 }),
    model: varchar("model", { length: 255 }),
    sku: varchar("sku", { length: 255 }),
    description: text("description"),
    uom: varchar("uom", { length: 64 }),
    quantity: decimal("quantity", { precision: 12, scale: 3 })
      .notNull()
      .default("0"),
    allocatedAt: timestamp("allocatedAt").defaultNow().notNull(),
    allocatedByUserId: int("allocatedByUserId"),
    allocatedByName: varchar("allocatedByName", { length: 255 }),
    remarks: text("remarks"),
    reversedAt: timestamp("reversedAt"),
    reversedByUserId: int("reversedByUserId"),
    reversedByName: varchar("reversedByName", { length: 255 }),
    reverseReason: text("reverseReason"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    procurementIdx: index("procurement_stock_alloc_procurement_idx").on(
      t.projectProcurementId,
      t.pickListLineId
    ),
  })
);

export const procurementProductReplacements = mysqlTable(
  "procurement_product_replacements",
  {
    id: int("id").autoincrement().primaryKey(),
    projectProcurementId: int("projectProcurementId").notNull(),
    /** Original sold line — never overwritten; keeps its sold qty and value. */
    originalPickListLineId: int("originalPickListLineId").notNull(),
    replacementPickListLineId: int("replacementPickListLineId"),
    originalSku: varchar("originalSku", { length: 255 }),
    originalModel: varchar("originalModel", { length: 255 }),
    originalBrand: varchar("originalBrand", { length: 255 }),
    replacementSku: varchar("replacementSku", { length: 255 }),
    replacementModel: varchar("replacementModel", { length: 255 }),
    replacementBrand: varchar("replacementBrand", { length: 255 }),
    quantity: decimal("quantity", { precision: 12, scale: 3 })
      .notNull()
      .default("0"),
    reason: varchar("reason", { length: 64 }),
    requestedBy: varchar("requestedBy", { length: 255 }),
    approvedByUserId: int("approvedByUserId"),
    approvedByName: varchar("approvedByName", { length: 255 }),
    approvedAt: timestamp("approvedAt"),
    sellingPriceDifference: decimal("sellingPriceDifference", {
      precision: 14,
      scale: 2,
    })
      .notNull()
      .default("0.00"),
    buyingPriceDifference: decimal("buyingPriceDifference", {
      precision: 14,
      scale: 2,
    })
      .notNull()
      .default("0.00"),
    voRequired: boolean("voRequired").notNull().default(false),
    voId: int("voId"),
    /** VO / commercial approval reference when no VO record exists yet. */
    voApprovalReference: varchar("voApprovalReference", { length: 128 }),
    remarks: text("remarks"),
    status: mysqlEnum("status", ["pending", "approved", "cancelled"])
      .notNull()
      .default("pending"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    procurementIdx: index("procurement_replacement_procurement_idx").on(
      t.projectProcurementId,
      t.status
    ),
  })
);

export const procurementPurchaseOrders = mysqlTable(
  "procurement_purchase_orders",
  {
    id: int("id").autoincrement().primaryKey(),
    projectProcurementId: int("projectProcurementId"),
    ownerType: mysqlEnum("ownerType", [
      "customer_job",
      "inventory_replenishment",
    ])
      .notNull()
      .default("customer_job"),
    inventoryRequestId: int("inventoryRequestId"),
    /** NULL while draft; assigned at issue. MySQL unique permits many NULL drafts. */
    poNumber: varchar("poNumber", { length: 96 }),
    status: mysqlEnum("status", [
      "draft",
      "issued",
      "partially_received",
      "fully_received",
      "cancelled",
      "revised",
    ])
      .notNull()
      .default("draft"),
    manufacturerBrand: varchar("manufacturerBrand", { length: 255 }),
    manufacturerCode: varchar("manufacturerCode", { length: 16 }),
    vendorId: int("vendorId"),
    vendorNameSnapshot: varchar("vendorNameSnapshot", { length: 255 }),
    projectReferenceSnapshot: varchar("projectReferenceSnapshot", {
      length: 64,
    }),
    proposalRefSnapshot: varchar("proposalRefSnapshot", { length: 128 }),
    customerNameSnapshot: varchar("customerNameSnapshot", { length: 255 }),
    siteAddressSnapshot: text("siteAddressSnapshot"),
    inventoryRequestRefSnapshot: varchar("inventoryRequestRefSnapshot", {
      length: 64,
    }),
    inventoryRequestTitleSnapshot: varchar("inventoryRequestTitleSnapshot", {
      length: 255,
    }),
    inventoryDestinationSnapshot: text("inventoryDestinationSnapshot"),
    currency: varchar("currency", { length: 8 }).notNull().default("SGD"),
    paymentTerms: varchar("paymentTerms", { length: 255 }),
    etaDate: date("etaDate"),
    deliveryInstructions: text("deliveryInstructions"),
    vendorNotes: text("vendorNotes"),
    internalNotes: text("internalNotes"),
    issueDate: timestamp("issueDate"),
    issuedByUserId: int("issuedByUserId"),
    issuedByName: varchar("issuedByName", { length: 255 }),
    /** Caller-supplied key so a retried issue returns the same PO instead of a duplicate. */
    issueIdempotencyKey: varchar("issueIdempotencyKey", { length: 64 }),
    pdfStorageKey: text("pdfStorageKey"),
    pdfGeneratedAt: timestamp("pdfGeneratedAt"),
    /** pending | generating | ready | failed — the issued PO never rolls back with it. */
    pdfStatus: varchar("pdfStatus", { length: 16 })
      .notNull()
      .default("pending"),
    /** Claim marker: also the lock that stops competing recovery attempts. */
    pdfGenerationStartedAt: timestamp("pdfGenerationStartedAt"),
    pdfAttemptCount: int("pdfAttemptCount").notNull().default(0),
    pdfLastError: text("pdfLastError"),
    revisionOfPoId: int("revisionOfPoId"),
    revisionNumber: int("revisionNumber").notNull().default(0),
    cancelledAt: timestamp("cancelledAt"),
    cancelReason: text("cancelReason"),
    /**
     * portal_created: drafted/issued inside Portal.
     * registered_external: historical/external supplier order registered so receipt can proceed.
     */
    recordOrigin: mysqlEnum("recordOrigin", [
      "portal_created",
      "registered_external",
    ])
      .notNull()
      .default("portal_created"),
    registeredExternalAt: timestamp("registeredExternalAt"),
    registeredExternalByUserId: int("registeredExternalByUserId"),
    registeredExternalByName: varchar("registeredExternalByName", {
      length: 255,
    }),
    registrationReason: text("registrationReason"),
    registrationIdempotencyKey: varchar("registrationIdempotencyKey", {
      length: 191,
    }),
    registrationReversedAt: timestamp("registrationReversedAt"),
    registrationReversedByUserId: int("registrationReversedByUserId"),
    registrationReversedByName: varchar("registrationReversedByName", {
      length: 255,
    }),
    registrationReversalReason: text("registrationReversalReason"),
    /** Immutable official FX snapshot captured when the order is issued/registered. */
    estimatedExchangeRateToSgd: decimal("estimatedExchangeRateToSgd", {
      precision: 14,
      scale: 6,
    }),
    estimatedExchangeRateQuotedAt: timestamp("estimatedExchangeRateQuotedAt"),
    estimatedExchangeRateSource: varchar("estimatedExchangeRateSource", {
      length: 64,
    }),
    estimatedExchangeRateRequestedDate: date(
      "estimatedExchangeRateRequestedDate"
    ),
    estimatedExchangeRateEffectiveObservationDate: date(
      "estimatedExchangeRateEffectiveObservationDate"
    ),
    estimatedExchangeRateProvider: varchar("estimatedExchangeRateProvider", {
      length: 64,
    }),
    estimatedExchangeRateForeignSeriesKey: varchar(
      "estimatedExchangeRateForeignSeriesKey",
      {
        length: 64,
      }
    ),
    estimatedExchangeRateSgdSeriesKey: varchar(
      "estimatedExchangeRateSgdSeriesKey",
      { length: 64 }
    ),
    estimatedExchangeRateFetchedAt: timestamp("estimatedExchangeRateFetchedAt"),
    subtotal: decimal("subtotal", { precision: 14, scale: 2 })
      .notNull()
      .default("0.00"),
    /**
     * Human-facing operational progress — separate from structural `status`.
     * Null until the first audited status event (or system projection write).
     */
    operationalStatus: mysqlEnum("operationalStatus", [
      "po_being_prepared",
      "po_recorded",
      "po_sent",
      "waiting_for_supplier",
      "awaiting_payment",
      "ready_for_shipment",
      "shipped",
      "partially_received",
      "received",
      "problem_follow_up",
    ]),
    operationalStatusNote: text("operationalStatusNote"),
    operationalStatusUpdatedAt: timestamp("operationalStatusUpdatedAt"),
    operationalStatusUpdatedByUserId: int("operationalStatusUpdatedByUserId"),
    operationalStatusUpdatedByName: varchar("operationalStatusUpdatedByName", {
      length: 255,
    }),
    operationalStatusSource: varchar("operationalStatusSource", { length: 64 }),
    operationalStatusCarrier: varchar("operationalStatusCarrier", {
      length: 128,
    }),
    operationalStatusTrackingNumber: varchar(
      "operationalStatusTrackingNumber",
      {
        length: 128,
      }
    ),
    operationalStatusSupplierReadyDate: date(
      "operationalStatusSupplierReadyDate"
    ),
    operationalStatusExpectedShipmentDate: date(
      "operationalStatusExpectedShipmentDate"
    ),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    poNumberUid: uniqueIndex("procurement_po_number_uid").on(t.poNumber),
    procurementIdx: index("procurement_po_procurement_idx").on(
      t.projectProcurementId,
      t.status
    ),
    inventoryRequestIdx: index("procurement_po_inventory_request_idx").on(
      t.inventoryRequestId,
      t.status
    ),
    registrationIdempotencyUid: uniqueIndex(
      "procurement_po_registration_idempotency_uq"
    ).on(t.registrationIdempotencyKey),
    recordOriginIdx: index("procurement_po_record_origin_idx").on(
      t.projectProcurementId,
      t.recordOrigin,
      t.status
    ),
    operationalStatusIdx: index("procurement_po_operational_status_idx").on(
      t.projectProcurementId,
      t.operationalStatus,
      t.operationalStatusUpdatedAt
    ),
  })
);

export const procurementPurchaseOrderStatusEvents = mysqlTable(
  "procurement_purchase_order_status_events",
  {
    id: int("id").autoincrement().primaryKey(),
    purchaseOrderId: int("purchaseOrderId").notNull(),
    projectProcurementId: int("projectProcurementId"),
    previousStatus: mysqlEnum("previousStatus", [
      "po_being_prepared",
      "po_recorded",
      "po_sent",
      "waiting_for_supplier",
      "awaiting_payment",
      "ready_for_shipment",
      "shipped",
      "partially_received",
      "received",
      "problem_follow_up",
    ]),
    newStatus: mysqlEnum("newStatus", [
      "po_being_prepared",
      "po_recorded",
      "po_sent",
      "waiting_for_supplier",
      "awaiting_payment",
      "ready_for_shipment",
      "shipped",
      "partially_received",
      "received",
      "problem_follow_up",
    ]).notNull(),
    originalStaffInput: text("originalStaffInput"),
    plainLanguageNote: text("plainLanguageNote"),
    backwardReason: text("backwardReason"),
    carrier: varchar("carrier", { length: 128 }),
    trackingNumber: varchar("trackingNumber", { length: 128 }),
    supplierReadyDate: date("supplierReadyDate"),
    expectedShipmentDate: date("expectedShipmentDate"),
    source: varchar("source", { length: 64 }).notNull(),
    actorUserId: int("actorUserId"),
    actorName: varchar("actorName", { length: 255 }).notNull(),
    cleoConfidence: varchar("cleoConfidence", { length: 16 }),
    cleoReason: varchar("cleoReason", { length: 512 }),
    relatedMurhoDocumentId: int("relatedMurhoDocumentId"),
    idempotencyKey: varchar("idempotencyKey", { length: 191 }).notNull(),
    /** Semantic fingerprint of the authoritative request payload for replay parity. */
    requestFingerprint: varchar("requestFingerprint", { length: 64 }).notNull(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    poIdempotencyUq: uniqueIndex(
      "procurement_po_status_events_po_idempotency_uq"
    ).on(t.purchaseOrderId, t.idempotencyKey),
    poCreatedIdx: index("procurement_po_status_events_po_created_idx").on(
      t.purchaseOrderId,
      t.createdAt
    ),
    ppwCreatedIdx: index("procurement_po_status_events_ppw_created_idx").on(
      t.projectProcurementId,
      t.createdAt
    ),
  })
);

export const procurementExchangeRateObservations = mysqlTable(
  "procurement_exchange_rate_observations",
  {
    id: int("id").autoincrement().primaryKey(),
    provider: varchar("provider", { length: 64 }).notNull(),
    currency: varchar("currency", { length: 8 }).notNull(),
    rateToSgd: decimal("rateToSgd", { precision: 14, scale: 6 }).notNull(),
    requestedDate: date("requestedDate").notNull(),
    effectiveObservationDate: date("effectiveObservationDate").notNull(),
    foreignSeriesKey: varchar("foreignSeriesKey", { length: 64 }),
    sgdSeriesKey: varchar("sgdSeriesKey", { length: 64 }),
    fetchedAt: timestamp("fetchedAt").notNull(),
    rawResponseChecksum: varchar("rawResponseChecksum", {
      length: 64,
    }).notNull(),
    status: mysqlEnum("status", ["available", "error"])
      .notNull()
      .default("available"),
    safeErrorCode: varchar("safeErrorCode", { length: 64 }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    providerCurrencyRequestedUq: uniqueIndex(
      "procurement_fx_observation_provider_currency_requested_uq"
    ).on(t.provider, t.currency, t.requestedDate),
    effectiveLookupIdx: index("procurement_fx_observation_effective_idx").on(
      t.provider,
      t.currency,
      t.effectiveObservationDate
    ),
  })
);

/**
 * Durable official SGD estimate outbox (migration 0203).
 *
 * A supplier PO always saves; when the official rate provider is unavailable the
 * PO commits with NULL rate snapshot fields plus exactly one job row here, in the
 * same transaction. The worker locks the PO later and writes the rate once.
 */
export const procurementExchangeRateJobs = mysqlTable(
  "procurement_exchange_rate_jobs",
  {
    id: int("id").autoincrement().primaryKey(),
    idempotencyKey: varchar("idempotencyKey", { length: 191 }).notNull(),
    purchaseOrderId: int("purchaseOrderId").notNull(),
    projectProcurementId: int("projectProcurementId"),
    ownerType: varchar("ownerType", { length: 32 }).notNull(),
    /** NULL for inventory replenishment purchase orders. */
    ownerId: int("ownerId"),
    proposalId: int("proposalId"),
    currency: varchar("currency", { length: 8 }).notNull(),
    requestedDate: date("requestedDate").notNull(),
    status: mysqlEnum("status", [
      "pending",
      "processing",
      "retry",
      "completed",
      "review_required",
    ])
      .notNull()
      .default("pending"),
    attemptCount: int("attemptCount").notNull().default(0),
    nextRetryAt: timestamp("nextRetryAt"),
    leaseToken: varchar("leaseToken", { length: 64 }),
    leaseExpiresAt: timestamp("leaseExpiresAt"),
    lastSafeErrorCode: varchar("lastSafeErrorCode", { length: 64 }),
    completedAt: timestamp("completedAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    idempotencyUq: uniqueIndex("procurement_fx_jobs_idempotency_uq").on(
      t.idempotencyKey
    ),
    /** One active job per PO; also the lookup index by purchase order. */
    purchaseOrderUq: uniqueIndex("procurement_fx_jobs_purchase_order_uq").on(
      t.purchaseOrderId
    ),
    statusRetryIdx: index("procurement_fx_jobs_status_retry_idx").on(
      t.status,
      t.nextRetryAt
    ),
    statusLeaseIdx: index("procurement_fx_jobs_status_lease_idx").on(
      t.status,
      t.leaseExpiresAt
    ),
  })
);

export const procurementPurchaseOrderLines = mysqlTable(
  "procurement_purchase_order_lines",
  {
    id: int("id").autoincrement().primaryKey(),
    purchaseOrderId: int("purchaseOrderId").notNull(),
    pickListLineId: int("pickListLineId"),
    inventoryRequestLineId: int("inventoryRequestLineId"),
    productIdentityKey: varchar("productIdentityKey", { length: 191 }),
    brand: varchar("brand", { length: 255 }),
    model: varchar("model", { length: 255 }),
    sku: varchar("sku", { length: 255 }),
    description: text("description"),
    uom: varchar("uom", { length: 64 }),
    /** Snapshot of Purchase UOM at PO issue — historical rows may be null. */
    purchaseUomSnapshot: varchar("purchaseUomSnapshot", { length: 64 }),
    productUomProfileId: int("productUomProfileId"),
    quantity: decimal("quantity", { precision: 12, scale: 3 })
      .notNull()
      .default("0"),
    buyingUnitPrice: decimal("buyingUnitPrice", { precision: 14, scale: 4 })
      .notNull()
      .default("0"),
    standardCostSnapshot: decimal("standardCostSnapshot", {
      precision: 14,
      scale: 4,
    }),
    buyingPriceVarianceReason: varchar("buyingPriceVarianceReason", {
      length: 64,
    }),
    buyingPriceVarianceText: text("buyingPriceVarianceText"),
    lineTotal: decimal("lineTotal", { precision: 14, scale: 2 })
      .notNull()
      .default("0.00"),
    etaDate: date("etaDate"),
    quantityReceivedAccepted: decimal("quantityReceivedAccepted", {
      precision: 12,
      scale: 3,
    })
      .notNull()
      .default("0"),
    sortOrder: int("sortOrder").notNull().default(0),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    poIdx: index("procurement_po_lines_po_idx").on(
      t.purchaseOrderId,
      t.pickListLineId
    ),
    inventoryRequestLineIdx: index(
      "procurement_po_lines_inventory_request_line_idx"
    ).on(t.inventoryRequestLineId),
  })
);

export const procurementGoodsReceipts = mysqlTable(
  "procurement_goods_receipts",
  {
    id: int("id").autoincrement().primaryKey(),
    projectProcurementId: int("projectProcurementId"),
    ownerType: mysqlEnum("ownerType", [
      "customer_job",
      "inventory_replenishment",
    ])
      .notNull()
      .default("customer_job"),
    inventoryRequestId: int("inventoryRequestId"),
    purchaseOrderId: int("purchaseOrderId").notNull(),
    receiptIdempotencyKey: varchar("receiptIdempotencyKey", { length: 191 }),
    receiptNumber: varchar("receiptNumber", { length: 64 }),
    receiptDate: date("receiptDate"),
    supplierInvoiceNumber: varchar("supplierInvoiceNumber", { length: 128 }),
    vendorId: int("vendorId"),
    manufacturerBrand: varchar("manufacturerBrand", { length: 255 }),
    currency: varchar("currency", { length: 8 }).notNull().default("SGD"),
    exchangeRate: decimal("exchangeRate", { precision: 14, scale: 6 })
      .notNull()
      .default("1.000000"),
    freightTotal: decimal("freightTotal", { precision: 14, scale: 2 })
      .notNull()
      .default("0.00"),
    insuranceImportTotal: decimal("insuranceImportTotal", {
      precision: 14,
      scale: 2,
    })
      .notNull()
      .default("0.00"),
    dutyTotal: decimal("dutyTotal", { precision: 14, scale: 2 })
      .notNull()
      .default("0.00"),
    handlingTotal: decimal("handlingTotal", { precision: 14, scale: 2 })
      .notNull()
      .default("0.00"),
    otherLandedTotal: decimal("otherLandedTotal", { precision: 14, scale: 2 })
      .notNull()
      .default("0.00"),
    recoverableGstTotal: decimal("recoverableGstTotal", {
      precision: 14,
      scale: 2,
    })
      .notNull()
      .default("0.00"),
    freightAllocationMethod: mysqlEnum("freightAllocationMethod", [
      "by_value",
      "by_quantity",
      "manual",
    ])
      .notNull()
      .default("by_value"),
    /** Freight may be in a different currency from item buying currency. */
    freightCurrency: varchar("freightCurrency", { length: 8 }),
    freightExchangeRateToSgd: decimal("freightExchangeRateToSgd", {
      precision: 14,
      scale: 6,
    }),
    freightNote: text("freightNote"),
    insuranceCurrency: varchar("insuranceCurrency", { length: 8 }),
    insuranceExchangeRateToSgd: decimal("insuranceExchangeRateToSgd", {
      precision: 14,
      scale: 6,
    }),
    receivedByUserId: int("receivedByUserId"),
    receivedByName: varchar("receivedByName", { length: 255 }),
    notes: text("notes"),
    reversedAt: timestamp("reversedAt"),
    reversedByUserId: int("reversedByUserId"),
    reversalReason: text("reversalReason"),
    reversalIdempotencyKey: varchar("reversalIdempotencyKey", { length: 191 }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    procurementIdx: index("procurement_receipt_procurement_idx").on(
      t.projectProcurementId,
      t.purchaseOrderId
    ),
    inventoryRequestIdx: index("procurement_receipt_inventory_request_idx").on(
      t.inventoryRequestId,
      t.purchaseOrderId
    ),
    receiptIdempotencyUq: uniqueIndex("procurement_receipt_idempotency_uq").on(
      t.receiptIdempotencyKey
    ),
    reversalIdempotencyUq: uniqueIndex(
      "procurement_receipt_reversal_idempotency_uq"
    ).on(t.reversalIdempotencyKey),
  })
);

export const procurementGoodsReceiptLines = mysqlTable(
  "procurement_goods_receipt_lines",
  {
    id: int("id").autoincrement().primaryKey(),
    goodsReceiptId: int("goodsReceiptId").notNull(),
    purchaseOrderLineId: int("purchaseOrderLineId").notNull(),
    pickListLineId: int("pickListLineId"),
    inventoryRequestLineId: int("inventoryRequestLineId"),
    sku: varchar("sku", { length: 255 }),
    description: text("description"),
    uom: varchar("uom", { length: 64 }),
    purchaseUom: varchar("purchaseUom", { length: 64 }),
    referenceUom: varchar("referenceUom", { length: 64 }),
    conversionQuantitySnapshot: decimal("conversionQuantitySnapshot", {
      precision: 14,
      scale: 6,
    }),
    referenceQuantityReceived: decimal("referenceQuantityReceived", {
      precision: 12,
      scale: 3,
    }),
    actualBuyingUnitCost: decimal("actualBuyingUnitCost", {
      precision: 14,
      scale: 4,
    }),
    poBuyingUnitPriceSnapshot: decimal("poBuyingUnitPriceSnapshot", {
      precision: 14,
      scale: 4,
    }),
    buyingPriceVarianceReason: varchar("buyingPriceVarianceReason", {
      length: 64,
    }),
    buyingPriceVarianceText: text("buyingPriceVarianceText"),
    buyingPriceCurrency: varchar("buyingPriceCurrency", { length: 8 }),
    landedCostPerPurchaseUom: decimal("landedCostPerPurchaseUom", {
      precision: 14,
      scale: 4,
    }),
    landedCostPerReferenceUom: decimal("landedCostPerReferenceUom", {
      precision: 14,
      scale: 4,
    }),
    productUomProfileId: int("productUomProfileId"),
    productUomConversionId: int("productUomConversionId"),
    uomConversionSnapshotJson: json("uomConversionSnapshotJson").$type<Record<
      string,
      unknown
    > | null>(),
    quantityReceived: decimal("quantityReceived", { precision: 12, scale: 3 })
      .notNull()
      .default("0"),
    condition: mysqlEnum("condition", [
      "good",
      "damaged",
      "short_supplied",
      "incorrect_product",
      "quarantined",
      "returned_to_supplier",
    ])
      .notNull()
      .default("good"),
    buyingUnitPrice: decimal("buyingUnitPrice", { precision: 14, scale: 4 })
      .notNull()
      .default("0"),
    purchaseValue: decimal("purchaseValue", { precision: 14, scale: 2 })
      .notNull()
      .default("0.00"),
    allocatedFreight: decimal("allocatedFreight", { precision: 14, scale: 2 })
      .notNull()
      .default("0.00"),
    allocatedFreightCurrency: varchar("allocatedFreightCurrency", {
      length: 8,
    }),
    allocatedFreightSgd: decimal("allocatedFreightSgd", {
      precision: 14,
      scale: 2,
    }),
    insuranceImportShare: decimal("insuranceImportShare", {
      precision: 14,
      scale: 2,
    })
      .notNull()
      .default("0.00"),
    allocatedDuty: decimal("allocatedDuty", { precision: 14, scale: 2 })
      .notNull()
      .default("0.00"),
    allocatedHandling: decimal("allocatedHandling", { precision: 14, scale: 2 })
      .notNull()
      .default("0.00"),
    allocatedOtherLanded: decimal("allocatedOtherLanded", {
      precision: 14,
      scale: 2,
    })
      .notNull()
      .default("0.00"),
    insuranceImportCurrency: varchar("insuranceImportCurrency", { length: 8 }),
    insuranceImportSgd: decimal("insuranceImportSgd", {
      precision: 14,
      scale: 2,
    }),
    landedTotal: decimal("landedTotal", { precision: 14, scale: 2 })
      .notNull()
      .default("0.00"),
    effectiveLandedUnitCost: decimal("effectiveLandedUnitCost", {
      precision: 14,
      scale: 4,
    })
      .notNull()
      .default("0"),
    landedCostPerPurchaseUomSgd: decimal("landedCostPerPurchaseUomSgd", {
      precision: 14,
      scale: 4,
    }),
    landedCostPerReferenceUomSgd: decimal("landedCostPerReferenceUomSgd", {
      precision: 14,
      scale: 4,
    }),
    sgdLandedIsIndicative: boolean("sgdLandedIsIndicative"),
    /** Reporting copy in SGD: landed amounts × receipt exchange rate (1 when SGD). */
    landedTotalBaseSgd: decimal("landedTotalBaseSgd", {
      precision: 14,
      scale: 2,
    })
      .notNull()
      .default("0.00"),
    effectiveLandedUnitCostBaseSgd: decimal("effectiveLandedUnitCostBaseSgd", {
      precision: 14,
      scale: 4,
    })
      .notNull()
      .default("0"),
    freightPercentage: decimal("freightPercentage", { precision: 8, scale: 2 })
      .notNull()
      .default("0.00"),
    serialNumber: varchar("serialNumber", { length: 255 }),
    damageShortageNotes: text("damageShortageNotes"),
    /**
     * Exception resolution lifecycle (migration 0187 amendment) — NULL for
     * good-condition lines; 'open' from receipt time until an admin/inventory
     * user resolves it via `resolveReceiptException`, then 'resolved'. Never
     * flips back to 'open'.
     */
    exceptionLifecycle: mysqlEnum("exceptionLifecycle", ["open", "resolved"]),
    exceptionResolutionType: mysqlEnum("exceptionResolutionType", [
      "replacement_received",
      "balance_received",
      "returned_to_supplier",
      "supplier_credit",
      "accepted_as_is",
    ]),
    exceptionResolutionNote: text("exceptionResolutionNote"),
    exceptionLinkedReceiptLineId: int("exceptionLinkedReceiptLineId"),
    /** Quantity of the linked accepted receipt consumed by this resolution. */
    exceptionLinkedQuantity: decimal("exceptionLinkedQuantity", {
      precision: 12,
      scale: 3,
    }),
    exceptionResolvedByUserId: int("exceptionResolvedByUserId"),
    exceptionResolvedByName: varchar("exceptionResolvedByName", {
      length: 255,
    }),
    exceptionResolvedAt: timestamp("exceptionResolvedAt"),
    exceptionResolutionIdempotencyKey: varchar(
      "exceptionResolutionIdempotencyKey",
      { length: 191 }
    ),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    receiptIdx: index("procurement_receipt_lines_receipt_idx").on(
      t.goodsReceiptId,
      t.pickListLineId
    ),
    inventoryRequestLineIdx: index(
      "procurement_receipt_lines_inventory_request_line_idx"
    ).on(t.inventoryRequestLineId),
    exceptionIdempotencyUq: uniqueIndex(
      "procurement_receipt_lines_exception_idempotency_uq"
    ).on(t.exceptionResolutionIdempotencyKey),
    exceptionLifecycleIdx: index(
      "procurement_receipt_lines_exception_lifecycle_idx"
    ).on(t.exceptionLifecycle),
    exceptionLinkedIdx: index(
      "procurement_receipt_lines_exception_linked_idx"
    ).on(t.exceptionLinkedReceiptLineId),
  })
);

export const procurementSupplierStandardCosts = mysqlTable(
  "procurement_supplier_standard_costs",
  {
    id: int("id").autoincrement().primaryKey(),
    identityKey: varchar("identityKey", { length: 191 }).notNull(),
    catalogProductId: int("catalogProductId"),
    vendorId: int("vendorId"),
    vendorName: varchar("vendorName", { length: 255 }),
    currency: varchar("currency", { length: 8 }).notNull(),
    standardCost: decimal("standardCost", {
      precision: 14,
      scale: 4,
    }).notNull(),
    effectiveFrom: date("effectiveFrom").notNull(),
    supersededAt: timestamp("supersededAt"),
    sourceReference: varchar("sourceReference", { length: 255 }),
    changeReason: text("changeReason"),
    actorUserId: int("actorUserId"),
    actorName: varchar("actorName", { length: 255 }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    identityIdx: index("procurement_standard_cost_identity_idx").on(
      t.identityKey,
      t.effectiveFrom
    ),
    catalogIdx: index("procurement_standard_cost_catalog_idx").on(
      t.catalogProductId
    ),
  })
);

/**
 * Append-only audit trail for the receipt exception lifecycle — "opened" is
 * written by `receiveGoods` at receipt time; "resolved" is written by
 * `resolveReceiptException`. Never updated or deleted in place.
 */
export const procurementGoodsReceiptExceptionEvents = mysqlTable(
  "procurement_goods_receipt_exception_events",
  {
    id: int("id").autoincrement().primaryKey(),
    goodsReceiptLineId: int("goodsReceiptLineId").notNull(),
    goodsReceiptId: int("goodsReceiptId").notNull(),
    inventoryRequestId: int("inventoryRequestId").notNull(),
    eventType: varchar("eventType", { length: 32 }).notNull(),
    previousLifecycle: mysqlEnum("previousLifecycle", ["open", "resolved"]),
    newLifecycle: mysqlEnum("newLifecycle", ["open", "resolved"]),
    resolutionType: mysqlEnum("resolutionType", [
      "replacement_received",
      "balance_received",
      "returned_to_supplier",
      "supplier_credit",
      "accepted_as_is",
    ]),
    note: text("note"),
    linkedReceiptLineId: int("linkedReceiptLineId"),
    metadataJson: json("metadataJson"),
    idempotencyKey: varchar("idempotencyKey", { length: 191 }),
    actorUserId: int("actorUserId"),
    actorName: varchar("actorName", { length: 255 }).notNull(),
    actorRole: varchar("actorRole", { length: 64 }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    idempotencyUq: uniqueIndex(
      "procurement_receipt_exception_events_idempotency_uq"
    ).on(t.idempotencyKey),
    lineCreatedIdx: index(
      "procurement_receipt_exception_events_line_created_idx"
    ).on(t.goodsReceiptLineId, t.createdAt),
    requestCreatedIdx: index(
      "procurement_receipt_exception_events_request_created_idx"
    ).on(t.inventoryRequestId, t.createdAt),
  })
);

export const procurementNoOrderRequiredEvents = mysqlTable(
  "procurement_no_order_required_events",
  {
    id: int("id").autoincrement().primaryKey(),
    projectProcurementId: int("projectProcurementId").notNull(),
    proposalId: int("proposalId").notNull(),
    pickListLineId: int("pickListLineId").notNull(),
    eventType: mysqlEnum("eventType", [
      "marked_no_order_required",
      "reopened_for_ordering",
    ]).notNull(),
    reason: mysqlEnum("reason", [
      "service_manpower",
      "non_procurable_fee",
      "customer_supplied",
      "duplicate_or_proposal_error",
      "other",
    ]),
    note: text("note"),
    previousLineStatus: varchar("previousLineStatus", { length: 32 }).notNull(),
    newLineStatus: varchar("newLineStatus", { length: 32 }).notNull(),
    idempotencyKey: varchar("idempotencyKey", { length: 191 }),
    actorUserId: int("actorUserId"),
    actorName: varchar("actorName", { length: 255 }).notNull(),
    actorRole: varchar("actorRole", { length: 64 }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    idempotencyUq: uniqueIndex("procurement_no_order_events_idempotency_uq").on(
      t.idempotencyKey
    ),
    lineCreatedIdx: index("procurement_no_order_events_line_created_idx").on(
      t.pickListLineId,
      t.createdAt
    ),
    procurementCreatedIdx: index(
      "procurement_no_order_events_procurement_created_idx"
    ).on(t.projectProcurementId, t.createdAt),
  })
);

export const procurementInventoryRequests = mysqlTable(
  "procurement_inventory_requests",
  {
    id: int("id").autoincrement().primaryKey(),
    requestRef: varchar("requestRef", { length: 64 }).notNull(),
    title: varchar("title", { length: 255 }).notNull(),
    destination: text("destination").notNull(),
    purpose: text("purpose"),
    /** CHECK in migration 0187: 'date' requires needByDate set; 'asap' requires it NULL. No DB default — creation must choose explicitly. */
    needByMode: mysqlEnum("needByMode", ["asap", "date"]).notNull(),
    needByDate: date("needByDate"),
    priority: mysqlEnum("priority", ["low", "normal", "high", "urgent"])
      .notNull()
      .default("normal"),
    status: mysqlEnum("status", [
      "draft",
      "ready_for_ordering",
      "ordering",
      "partially_received",
      "fully_received",
      "completed",
      "on_hold",
      "cancelled",
      "review_required",
    ])
      .notNull()
      .default("draft"),
    requestedByUserId: int("requestedByUserId"),
    requestedByName: varchar("requestedByName", { length: 255 }),
    createdByUserId: int("createdByUserId"),
    createdByName: varchar("createdByName", { length: 255 }).notNull(),
    createIdempotencyKey: varchar("createIdempotencyKey", { length: 191 }),
    notes: text("notes"),
    holdReason: text("holdReason"),
    cancelReason: text("cancelReason"),
    completedAt: timestamp("completedAt"),
    completedByUserId: int("completedByUserId"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    requestRefUq: uniqueIndex("procurement_inventory_requests_ref_uq").on(
      t.requestRef
    ),
    createIdempotencyUq: uniqueIndex(
      "procurement_inventory_requests_create_idempotency_uq"
    ).on(t.createIdempotencyKey),
    statusNeedIdx: index("procurement_inventory_requests_status_need_idx").on(
      t.status,
      t.needByDate
    ),
  })
);

export const procurementInventoryRequestLines = mysqlTable(
  "procurement_inventory_request_lines",
  {
    id: int("id").autoincrement().primaryKey(),
    inventoryRequestId: int("inventoryRequestId").notNull(),
    productIdentityKey: varchar("productIdentityKey", { length: 191 }),
    catalogProductId: int("catalogProductId"),
    catalogueKey: varchar("catalogueKey", { length: 128 }),
    brand: varchar("brand", { length: 255 }),
    model: varchar("model", { length: 255 }),
    sku: varchar("sku", { length: 255 }),
    description: text("description"),
    finish: varchar("finish", { length: 255 }),
    uom: varchar("uom", { length: 64 }),
    quantityRequested: decimal("quantityRequested", { precision: 12, scale: 3 })
      .notNull()
      .default("0"),
    quantityOrdered: decimal("quantityOrdered", { precision: 12, scale: 3 })
      .notNull()
      .default("0"),
    quantityReceivedAccepted: decimal("quantityReceivedAccepted", {
      precision: 12,
      scale: 3,
    })
      .notNull()
      .default("0"),
    bomCompletenessStatus: mysqlEnum("bomCompletenessStatus", [
      "ready",
      "needs_review",
      "incomplete",
    ])
      .notNull()
      .default("ready"),
    reviewReasonsJson: json("reviewReasonsJson").$type<string[] | null>(),
    staffConfirmedFieldsJson: json("staffConfirmedFieldsJson").$type<
      string[] | null
    >(),
    note: text("note"),
    sortOrder: int("sortOrder").notNull().default(0),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    requestIdx: index("procurement_inventory_request_lines_request_idx").on(
      t.inventoryRequestId,
      t.sortOrder
    ),
    identityIdx: index("procurement_inventory_request_lines_identity_idx").on(
      t.productIdentityKey
    ),
  })
);

export const procurementInventoryRequestEvents = mysqlTable(
  "procurement_inventory_request_events",
  {
    id: int("id").autoincrement().primaryKey(),
    inventoryRequestId: int("inventoryRequestId").notNull(),
    inventoryRequestLineId: int("inventoryRequestLineId"),
    eventType: varchar("eventType", { length: 64 }).notNull(),
    previousStatus: varchar("previousStatus", { length: 32 }),
    newStatus: varchar("newStatus", { length: 32 }),
    reason: text("reason"),
    metadataJson: json("metadataJson").$type<Record<string, unknown> | null>(),
    idempotencyKey: varchar("idempotencyKey", { length: 191 }),
    actorUserId: int("actorUserId"),
    actorName: varchar("actorName", { length: 255 }).notNull(),
    actorRole: varchar("actorRole", { length: 64 }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    idempotencyUq: uniqueIndex(
      "procurement_inventory_request_events_idempotency_uq"
    ).on(t.idempotencyKey),
    requestCreatedIdx: index(
      "procurement_inventory_request_events_request_created_idx"
    ).on(t.inventoryRequestId, t.createdAt),
  })
);

export const procurementInventoryLots = mysqlTable(
  "procurement_inventory_lots",
  {
    id: int("id").autoincrement().primaryKey(),
    productIdentityKey: varchar("productIdentityKey", {
      length: 191,
    }).notNull(),
    catalogProductId: int("catalogProductId"),
    brand: varchar("brand", { length: 255 }),
    model: varchar("model", { length: 255 }),
    sku: varchar("sku", { length: 255 }),
    description: text("description"),
    finish: varchar("finish", { length: 255 }),
    uom: varchar("uom", { length: 64 }),
    purchaseUom: varchar("purchaseUom", { length: 64 }),
    referenceUom: varchar("referenceUom", { length: 64 }),
    conversionQuantitySnapshot: decimal("conversionQuantitySnapshot", {
      precision: 14,
      scale: 6,
    }),
    referenceQuantity: decimal("referenceQuantity", {
      precision: 12,
      scale: 3,
    }),
    landedCostPerReferenceUom: decimal("landedCostPerReferenceUom", {
      precision: 14,
      scale: 4,
    }),
    goodsReceiptId: int("goodsReceiptId").notNull(),
    goodsReceiptLineId: int("goodsReceiptLineId").notNull(),
    purchaseOrderId: int("purchaseOrderId").notNull(),
    inventoryRequestId: int("inventoryRequestId").notNull(),
    inventoryRequestLineId: int("inventoryRequestLineId").notNull(),
    receivedQuantity: decimal("receivedQuantity", { precision: 12, scale: 3 })
      .notNull()
      .default("0"),
    unitCost: decimal("unitCost", { precision: 14, scale: 4 })
      .notNull()
      .default("0"),
    currency: varchar("currency", { length: 8 }).notNull().default("SGD"),
    location: varchar("location", { length: 255 }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    receiptLineUq: uniqueIndex("procurement_inventory_lots_receipt_line_uq").on(
      t.goodsReceiptLineId
    ),
    identityIdx: index("procurement_inventory_lots_identity_idx").on(
      t.productIdentityKey,
      t.createdAt
    ),
    requestIdx: index("procurement_inventory_lots_request_idx").on(
      t.inventoryRequestId
    ),
  })
);

export const procurementInventoryMovements = mysqlTable(
  "procurement_inventory_movements",
  {
    id: int("id").autoincrement().primaryKey(),
    inventoryLotId: int("inventoryLotId").notNull(),
    movementType: mysqlEnum("movementType", [
      "receipt",
      "adjustment",
      "reservation",
      "allocation",
      "release",
      "return_to_supplier",
      "write_off",
    ]).notNull(),
    quantityDelta: decimal("quantityDelta", {
      precision: 12,
      scale: 3,
    }).notNull(),
    uomSnapshot: varchar("uomSnapshot", { length: 64 }),
    referenceUomSnapshot: varchar("referenceUomSnapshot", { length: 64 }),
    referenceQuantityDelta: decimal("referenceQuantityDelta", {
      precision: 12,
      scale: 3,
    }),
    goodsReceiptId: int("goodsReceiptId"),
    goodsReceiptLineId: int("goodsReceiptLineId"),
    projectProcurementId: int("projectProcurementId"),
    pickListLineId: int("pickListLineId"),
    reason: text("reason"),
    idempotencyKey: varchar("idempotencyKey", { length: 191 }).notNull(),
    actorUserId: int("actorUserId"),
    actorName: varchar("actorName", { length: 255 }).notNull(),
    metadataJson: json("metadataJson").$type<Record<string, unknown> | null>(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    idempotencyUq: uniqueIndex(
      "procurement_inventory_movements_idempotency_uq"
    ).on(t.idempotencyKey),
    lotCreatedIdx: index("procurement_inventory_movements_lot_created_idx").on(
      t.inventoryLotId,
      t.createdAt
    ),
    receiptIdx: index("procurement_inventory_movements_receipt_idx").on(
      t.goodsReceiptId,
      t.goodsReceiptLineId
    ),
  })
);

/**
 * Serial numbers captured at supplier receipt (never fabricated; 0/1/many per
 * receipt line). `serialNormalised` is globally unique — see migration 0184.
 */
export const procurementInventorySerials = mysqlTable(
  "procurement_inventory_serials",
  {
    id: int("id").autoincrement().primaryKey(),
    inventoryLotId: int("inventoryLotId").notNull(),
    goodsReceiptId: int("goodsReceiptId").notNull(),
    goodsReceiptLineId: int("goodsReceiptLineId").notNull(),
    purchaseOrderLineId: int("purchaseOrderLineId").notNull(),
    inventoryRequestLineId: int("inventoryRequestLineId").notNull(),
    serialOriginal: varchar("serialOriginal", { length: 255 }).notNull(),
    serialNormalised: varchar("serialNormalised", { length: 255 }).notNull(),
    status: mysqlEnum("status", [
      "available",
      "reserved",
      "allocated",
      "consumed",
      "returned",
      "void",
    ])
      .notNull()
      .default("available"),
    actorUserId: int("actorUserId"),
    actorName: varchar("actorName", { length: 255 }).notNull(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    normalisedUq: uniqueIndex("procurement_inventory_serials_normalised_uq").on(
      t.serialNormalised
    ),
    lotIdx: index("procurement_inventory_serials_lot_idx").on(t.inventoryLotId),
    receiptLineIdx: index("procurement_inventory_serials_receipt_line_idx").on(
      t.goodsReceiptLineId
    ),
    requestLineIdx: index("procurement_inventory_serials_request_line_idx").on(
      t.inventoryRequestLineId
    ),
  })
);

export const procurementExpectedCompletionHistory = mysqlTable(
  "procurement_expected_completion_history",
  {
    id: int("id").autoincrement().primaryKey(),
    proposalId: int("proposalId").notNull(),
    projectProcurementId: int("projectProcurementId"),
    previousDate: date("previousDate"),
    newDate: date("newDate"),
    /** customer | salesperson | projects_staff | procurement_staff | management | other */
    requestedByKind: varchar("requestedByKind", { length: 32 }).notNull(),
    requestedByName: varchar("requestedByName", { length: 255 }).notNull(),
    changedByUserId: int("changedByUserId"),
    changedByName: varchar("changedByName", { length: 255 }),
    notes: text("notes"),
    /** Where the edit happened (sales_proposal, procurement_pick_list, …). */
    sourceEditPoint: varchar("sourceEditPoint", { length: 64 }),
    /** Previous/new canonical timeline fields (additive; date columns remain compatibility). */
    previousTimelineType: varchar("previousTimelineType", { length: 16 }),
    newTimelineType: varchar("newTimelineType", { length: 16 }),
    previousTargetMonth: varchar("previousTargetMonth", { length: 7 }),
    newTargetMonth: varchar("newTargetMonth", { length: 7 }),
    previousForecastMonth: varchar("previousForecastMonth", { length: 7 }),
    newForecastMonth: varchar("newForecastMonth", { length: 7 }),
    previousTrackingOrigin: varchar("previousTrackingOrigin", { length: 32 }),
    newTrackingOrigin: varchar("newTrackingOrigin", { length: 32 }),
    /** customer_commitment | internal_forecast */
    changeKind: varchar("changeKind", { length: 32 }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    proposalIdx: index("procurement_completion_history_proposal_idx").on(
      t.proposalId
    ),
  })
);

/** Durable outbox for Procurement/Inventory activation emails. */
export const procurementActivationNotificationJobs = mysqlTable(
  "procurement_activation_notification_jobs",
  {
    id: int("id").autoincrement().primaryKey(),
    idempotencyKey: varchar("idempotencyKey", { length: 191 }).notNull(),
    projectProcurementId: int("projectProcurementId").notNull(),
    proposalId: int("proposalId").notNull(),
    activationOccurrenceKey: varchar("activationOccurrenceKey", {
      length: 191,
    }).notNull(),
    activationEvent: varchar("activationEvent", { length: 32 }).notNull(),
    recipientUserId: int("recipientUserId"),
    recipientEmail: varchar("recipientEmail", { length: 320 }),
    notificationKind: varchar("notificationKind", { length: 64 }).notNull(),
    subject: varchar("subject", { length: 512 }).notNull(),
    payloadJson: json("payloadJson").$type<Record<string, unknown>>().notNull(),
    status: mysqlEnum("status", [
      "pending",
      "processing",
      "retry",
      "completed",
      "review_required",
    ])
      .notNull()
      .default("pending"),
    attemptCount: int("attemptCount").notNull().default(0),
    lastError: text("lastError"),
    nextRetryAt: timestamp("nextRetryAt"),
    leaseToken: varchar("leaseToken", { length: 64 }),
    leaseExpiresAt: timestamp("leaseExpiresAt"),
    providerMessageId: varchar("providerMessageId", { length: 255 }),
    completedAt: timestamp("completedAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    idempotencyUq: uniqueIndex(
      "procurement_activation_notif_jobs_idempotency_uq"
    ).on(t.idempotencyKey),
    occurrenceRecipientUq: uniqueIndex(
      "procurement_activation_notif_jobs_occurrence_recipient_uq"
    ).on(t.projectProcurementId, t.activationOccurrenceKey, t.recipientUserId),
    statusRetryIdx: index(
      "procurement_activation_notif_jobs_status_retry_idx"
    ).on(t.status, t.nextRetryAt),
    statusLeaseIdx: index(
      "procurement_activation_notif_jobs_status_lease_idx"
    ).on(t.status, t.leaseExpiresAt),
    proposalCreatedIdx: index(
      "procurement_activation_notif_jobs_proposal_created_idx"
    ).on(t.proposalId, t.createdAt),
  })
);

/** Durable outbox for salesperson timeline / expected-completion change emails. */
export const procurementTimelineNotificationJobs = mysqlTable(
  "procurement_timeline_notification_jobs",
  {
    id: int("id").autoincrement().primaryKey(),
    idempotencyKey: varchar("idempotencyKey", { length: 191 }).notNull(),
    historyId: int("historyId").notNull(),
    proposalId: int("proposalId").notNull(),
    projectProcurementId: int("projectProcurementId"),
    recipientUserId: int("recipientUserId"),
    recipientEmail: varchar("recipientEmail", { length: 320 }),
    notificationKind: varchar("notificationKind", { length: 64 }).notNull(),
    subject: varchar("subject", { length: 512 }).notNull(),
    payloadJson: json("payloadJson").$type<Record<string, unknown>>().notNull(),
    status: mysqlEnum("status", [
      "pending",
      "processing",
      "retry",
      "completed",
      "review_required",
    ])
      .notNull()
      .default("pending"),
    attemptCount: int("attemptCount").notNull().default(0),
    lastError: text("lastError"),
    nextRetryAt: timestamp("nextRetryAt"),
    leaseToken: varchar("leaseToken", { length: 64 }),
    leaseExpiresAt: timestamp("leaseExpiresAt"),
    providerMessageId: varchar("providerMessageId", { length: 255 }),
    completedAt: timestamp("completedAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    idempotencyUq: uniqueIndex(
      "procurement_timeline_notif_jobs_idempotency_uq"
    ).on(t.idempotencyKey),
    statusRetryIdx: index(
      "procurement_timeline_notif_jobs_status_retry_idx"
    ).on(t.status, t.nextRetryAt),
    statusLeaseIdx: index(
      "procurement_timeline_notif_jobs_status_lease_idx"
    ).on(t.status, t.leaseExpiresAt),
    proposalCreatedIdx: index(
      "procurement_timeline_notif_jobs_proposal_created_idx"
    ).on(t.proposalId, t.createdAt),
  })
);

/** Admin-visible review queue for timeline notification ownership gaps. */
export const procurementTimelineReviewItems = mysqlTable(
  "procurement_timeline_review_items",
  {
    id: int("id").autoincrement().primaryKey(),
    familyRootId: int("familyRootId").notNull(),
    proposalId: int("proposalId").notNull(),
    reviewType: mysqlEnum("reviewType", ["missing_owner"]).notNull(),
    status: mysqlEnum("status", ["open", "resolved"]).notNull().default("open"),
    assignedRole: varchar("assignedRole", { length: 32 })
      .notNull()
      .default("admin"),
    detailsJson: json("detailsJson").$type<Record<string, unknown> | null>(),
    firstDetectedAt: timestamp("firstDetectedAt").defaultNow().notNull(),
    lastDetectedAt: timestamp("lastDetectedAt").defaultNow().notNull(),
    resolvedAt: timestamp("resolvedAt"),
    resolvedByUserId: int("resolvedByUserId"),
    resolvedReason: varchar("resolvedReason", { length: 255 }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    familyTypeUq: uniqueIndex("procurement_timeline_review_family_type_uq").on(
      t.familyRootId,
      t.reviewType
    ),
    statusUpdatedIdx: index(
      "procurement_timeline_review_status_updated_idx"
    ).on(t.status, t.updatedAt),
  })
);

export type ProcurementActivationNotificationJob =
  typeof procurementActivationNotificationJobs.$inferSelect;
export type InsertProcurementActivationNotificationJob =
  typeof procurementActivationNotificationJobs.$inferInsert;
export type ProcurementTimelineNotificationJob =
  typeof procurementTimelineNotificationJobs.$inferSelect;
export type InsertProcurementTimelineNotificationJob =
  typeof procurementTimelineNotificationJobs.$inferInsert;
export type ProcurementTimelineReviewItem =
  typeof procurementTimelineReviewItems.$inferSelect;
export type InsertProcurementTimelineReviewItem =
  typeof procurementTimelineReviewItems.$inferInsert;

// ---------------------------------------------------------------------------
// Procurement workflow actions (gate obligations, risks, flags)
// ---------------------------------------------------------------------------

export const procurementWorkflowActions = mysqlTable(
  "procurement_workflow_actions",
  {
    id: int("id").autoincrement().primaryKey(),
    projectProcurementId: int("projectProcurementId").notNull(),
    proposalId: int("proposalId").notNull(),
    occurrenceKey: varchar("occurrenceKey", { length: 191 }).notNull(),
    actionType: mysqlEnum("actionType", [
      "finance_issue_delivery_invoice",
      "finance_collect_delivery_payment",
      "finance_verify_reported_payment",
      "sales_follow_up_payment",
      "procurement_arrange_delivery",
      "payment_gate_review",
    ]).notNull(),
    lifecycle: mysqlEnum("lifecycle", [
      "open",
      "acknowledged",
      "resolved",
      "superseded",
      "cancelled",
    ])
      .notNull()
      .default("open"),
    primaryOwnerTeam: mysqlEnum("primaryOwnerTeam", [
      "sales",
      "finance",
      "procurement",
      "admin",
    ]).notNull(),
    assigneeUserId: int("assigneeUserId"),
    title: varchar("title", { length: 512 }).notNull(),
    reason: text("reason").notNull(),
    sourceFingerprint: varchar("sourceFingerprint", { length: 191 }).notNull(),
    openedAt: timestamp("openedAt").defaultNow().notNull(),
    acknowledgedAt: timestamp("acknowledgedAt"),
    acknowledgedByUserId: int("acknowledgedByUserId"),
    resolvedAt: timestamp("resolvedAt"),
    resolvedByUserId: int("resolvedByUserId"),
    resolvedReason: text("resolvedReason"),
    lastNudgedAt: timestamp("lastNudgedAt"),
    lastNudgedByUserId: int("lastNudgedByUserId"),
    nudgeCount: int("nudgeCount").notNull().default(0),
    customerReportedAmount: decimal("customerReportedAmount", {
      precision: 14,
      scale: 2,
    }),
    customerReportedReference: varchar("customerReportedReference", {
      length: 255,
    }),
    customerReportedAt: timestamp("customerReportedAt"),
    customerReportedByUserId: int("customerReportedByUserId"),
    metadataJson: json("metadataJson").$type<Record<string, unknown> | null>(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    occurrenceUq: uniqueIndex("procurement_workflow_actions_occurrence_uq").on(
      t.occurrenceKey
    ),
    procurementLifecycleIdx: index(
      "procurement_workflow_actions_procurement_lifecycle_idx"
    ).on(t.projectProcurementId, t.lifecycle),
    lifecycleTeamIdx: index(
      "procurement_workflow_actions_lifecycle_team_idx"
    ).on(t.lifecycle, t.primaryOwnerTeam),
  })
);

export const procurementWorkflowActionEvents = mysqlTable(
  "procurement_workflow_action_events",
  {
    id: int("id").autoincrement().primaryKey(),
    actionId: int("actionId").notNull(),
    projectProcurementId: int("projectProcurementId").notNull(),
    proposalId: int("proposalId").notNull(),
    eventType: varchar("eventType", { length: 64 }).notNull(),
    actorUserId: int("actorUserId"),
    actorName: varchar("actorName", { length: 255 }),
    actorRole: varchar("actorRole", { length: 64 }),
    previousLifecycle: varchar("previousLifecycle", { length: 32 }),
    newLifecycle: varchar("newLifecycle", { length: 32 }),
    metadataJson: json("metadataJson").$type<Record<string, unknown> | null>(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    actionCreatedIdx: index(
      "procurement_workflow_action_events_action_created_idx"
    ).on(t.actionId, t.createdAt),
  })
);

/** Durable outbox for workflow action notification emails — mirrors timeline notification jobs. */
export const procurementWorkflowNotificationJobs = mysqlTable(
  "procurement_workflow_notification_jobs",
  {
    id: int("id").autoincrement().primaryKey(),
    idempotencyKey: varchar("idempotencyKey", { length: 191 }).notNull(),
    actionId: int("actionId").notNull(),
    actionEventId: int("actionEventId"),
    projectProcurementId: int("projectProcurementId").notNull(),
    proposalId: int("proposalId").notNull(),
    recipientUserId: int("recipientUserId"),
    recipientEmail: varchar("recipientEmail", { length: 320 }),
    notificationKind: varchar("notificationKind", { length: 64 }).notNull(),
    subject: varchar("subject", { length: 512 }).notNull(),
    payloadJson: json("payloadJson").$type<Record<string, unknown>>().notNull(),
    status: mysqlEnum("status", [
      "pending",
      "processing",
      "retry",
      "completed",
      "review_required",
    ])
      .notNull()
      .default("pending"),
    attemptCount: int("attemptCount").notNull().default(0),
    lastError: text("lastError"),
    nextRetryAt: timestamp("nextRetryAt"),
    leaseToken: varchar("leaseToken", { length: 64 }),
    leaseExpiresAt: timestamp("leaseExpiresAt"),
    providerMessageId: varchar("providerMessageId", { length: 255 }),
    completedAt: timestamp("completedAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    idempotencyUq: uniqueIndex(
      "procurement_workflow_notif_jobs_idempotency_uq"
    ).on(t.idempotencyKey),
    statusRetryIdx: index(
      "procurement_workflow_notif_jobs_status_retry_idx"
    ).on(t.status, t.nextRetryAt),
    statusLeaseIdx: index(
      "procurement_workflow_notif_jobs_status_lease_idx"
    ).on(t.status, t.leaseExpiresAt),
    proposalCreatedIdx: index(
      "procurement_workflow_notif_jobs_proposal_created_idx"
    ).on(t.proposalId, t.createdAt),
  })
);

export type ProcurementWorkflowAction =
  typeof procurementWorkflowActions.$inferSelect;
export type InsertProcurementWorkflowAction =
  typeof procurementWorkflowActions.$inferInsert;
export type ProcurementWorkflowActionEvent =
  typeof procurementWorkflowActionEvents.$inferSelect;
export type InsertProcurementWorkflowActionEvent =
  typeof procurementWorkflowActionEvents.$inferInsert;
export type ProcurementWorkflowNotificationJob =
  typeof procurementWorkflowNotificationJobs.$inferSelect;
export type InsertProcurementWorkflowNotificationJob =
  typeof procurementWorkflowNotificationJobs.$inferInsert;

export const procurementEtaHistory = mysqlTable(
  "procurement_eta_history",
  {
    id: int("id").autoincrement().primaryKey(),
    purchaseOrderId: int("purchaseOrderId").notNull(),
    purchaseOrderLineId: int("purchaseOrderLineId"),
    previousEta: date("previousEta"),
    newEta: date("newEta"),
    changedByUserId: int("changedByUserId"),
    changedByName: varchar("changedByName", { length: 255 }),
    reason: text("reason"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    poIdx: index("procurement_eta_history_po_idx").on(t.purchaseOrderId),
  })
);

export type ProcurementVendor = typeof procurementVendors.$inferSelect;
export type InsertProcurementVendor = typeof procurementVendors.$inferInsert;
export type ProcurementManufacturer =
  typeof procurementManufacturers.$inferSelect;
export type InsertProcurementManufacturer =
  typeof procurementManufacturers.$inferInsert;
export type ProcurementStockAllocation =
  typeof procurementStockAllocations.$inferSelect;
export type InsertProcurementStockAllocation =
  typeof procurementStockAllocations.$inferInsert;
export type ProcurementProductReplacement =
  typeof procurementProductReplacements.$inferSelect;
export type InsertProcurementProductReplacement =
  typeof procurementProductReplacements.$inferInsert;
export type ProcurementPurchaseOrder =
  typeof procurementPurchaseOrders.$inferSelect;
export type InsertProcurementPurchaseOrder =
  typeof procurementPurchaseOrders.$inferInsert;
export type ProcurementExchangeRateObservation =
  typeof procurementExchangeRateObservations.$inferSelect;
export type InsertProcurementExchangeRateObservation =
  typeof procurementExchangeRateObservations.$inferInsert;
export type ProcurementExchangeRateJob =
  typeof procurementExchangeRateJobs.$inferSelect;
export type InsertProcurementExchangeRateJob =
  typeof procurementExchangeRateJobs.$inferInsert;
export type ProcurementPurchaseOrderLine =
  typeof procurementPurchaseOrderLines.$inferSelect;
export type InsertProcurementPurchaseOrderLine =
  typeof procurementPurchaseOrderLines.$inferInsert;
export type ProcurementGoodsReceipt =
  typeof procurementGoodsReceipts.$inferSelect;
export type InsertProcurementGoodsReceipt =
  typeof procurementGoodsReceipts.$inferInsert;
export type ProcurementGoodsReceiptLine =
  typeof procurementGoodsReceiptLines.$inferSelect;
export type InsertProcurementGoodsReceiptLine =
  typeof procurementGoodsReceiptLines.$inferInsert;
export type ProcurementGoodsReceiptExceptionEvent =
  typeof procurementGoodsReceiptExceptionEvents.$inferSelect;
export type InsertProcurementGoodsReceiptExceptionEvent =
  typeof procurementGoodsReceiptExceptionEvents.$inferInsert;
export type ProcurementExpectedCompletionHistory =
  typeof procurementExpectedCompletionHistory.$inferSelect;
export type ProcurementEtaHistory = typeof procurementEtaHistory.$inferSelect;

// ─── Product UOM profiles + cost observations (migration 0195) ───────────────

export const procurementProductUomProfiles = mysqlTable(
  "procurement_product_uom_profiles",
  {
    id: int("id").autoincrement().primaryKey(),
    identityKey: varchar("identityKey", { length: 191 }).notNull(),
    identitySource: mysqlEnum("identitySource", [
      "catalogue_product_id",
      "catalogue_key_or_sku",
      "brand_model",
    ]).notNull(),
    catalogProductId: int("catalogProductId"),
    catalogueKey: varchar("catalogueKey", { length: 128 }),
    sku: varchar("sku", { length: 255 }),
    brand: varchar("brand", { length: 255 }),
    model: varchar("model", { length: 255 }),
    referenceUom: varchar("referenceUom", { length: 32 }).notNull(),
    defaultPurchaseUom: varchar("defaultPurchaseUom", { length: 32 }).notNull(),
    defaultDeliveryUom: varchar("defaultDeliveryUom", { length: 32 }).notNull(),
    confirmationState: mysqlEnum("confirmationState", [
      "unconfirmed",
      "confirmed",
    ])
      .notNull()
      .default("unconfirmed"),
    confirmedByUserId: int("confirmedByUserId"),
    confirmedByName: varchar("confirmedByName", { length: 255 }),
    confirmedAt: timestamp("confirmedAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    identityUq: uniqueIndex("procurement_product_uom_profiles_identity_uq").on(
      t.identityKey
    ),
    catalogIdx: index("procurement_product_uom_profiles_catalog_idx").on(
      t.catalogProductId
    ),
  })
);

export const procurementProductUomConversions = mysqlTable(
  "procurement_product_uom_conversions",
  {
    id: int("id").autoincrement().primaryKey(),
    profileId: int("profileId").notNull(),
    fromUom: varchar("fromUom", { length: 32 }).notNull(),
    referenceUom: varchar("referenceUom", { length: 32 }).notNull(),
    conversionQuantity: decimal("conversionQuantity", {
      precision: 14,
      scale: 6,
    }).notNull(),
    status: mysqlEnum("status", ["active", "superseded"])
      .notNull()
      .default("active"),
    reason: text("reason"),
    confirmedByUserId: int("confirmedByUserId"),
    confirmedByName: varchar("confirmedByName", { length: 255 }),
    confirmedAt: timestamp("confirmedAt"),
    supersededAt: timestamp("supersededAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
    /**
     * Durable uniqueness: at most one active conversion per profileId+fromUom.
     * NULL when superseded — MySQL unique index allows multiple NULLs.
     */
    activeFromKey: varchar("activeFromKey", { length: 96 }).generatedAlwaysAs(
      sql`CASE WHEN \`status\` = 'active' THEN CONCAT(\`profileId\`, ':', \`fromUom\`) ELSE NULL END`,
      { mode: "stored" }
    ),
  },
  t => ({
    profileIdx: index("procurement_product_uom_conversions_profile_idx").on(
      t.profileId,
      t.status,
      t.fromUom
    ),
    activeFromUq: uniqueIndex(
      "procurement_product_uom_conversions_active_from_uq"
    ).on(t.activeFromKey),
  })
);

export const procurementProductUomAudits = mysqlTable(
  "procurement_product_uom_audits",
  {
    id: int("id").autoincrement().primaryKey(),
    profileId: int("profileId").notNull(),
    conversionId: int("conversionId"),
    action: varchar("action", { length: 64 }).notNull(),
    reason: text("reason").notNull(),
    actorUserId: int("actorUserId"),
    actorName: varchar("actorName", { length: 255 }),
    actorRole: varchar("actorRole", { length: 64 }),
    beforeJson: json("beforeJson").$type<Record<string, unknown> | null>(),
    afterJson: json("afterJson").$type<Record<string, unknown> | null>(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    profileIdx: index("procurement_product_uom_audits_profile_idx").on(
      t.profileId,
      t.createdAt
    ),
  })
);

export const procurementBuyingPriceObservations = mysqlTable(
  "procurement_buying_price_observations",
  {
    id: int("id").autoincrement().primaryKey(),
    identityKey: varchar("identityKey", { length: 191 }).notNull(),
    catalogProductId: int("catalogProductId"),
    vendorId: int("vendorId"),
    currency: varchar("currency", { length: 8 }).notNull(),
    purchaseUom: varchar("purchaseUom", { length: 32 }).notNull(),
    buyingUnitPrice: decimal("buyingUnitPrice", {
      precision: 14,
      scale: 4,
    }).notNull(),
    conversionSnapshotJson: json("conversionSnapshotJson").$type<Record<
      string,
      unknown
    > | null>(),
    observedAt: timestamp("observedAt").defaultNow().notNull(),
    source: mysqlEnum("source", [
      "purchase_order",
      "goods_receipt",
      "manual_setup",
    ]).notNull(),
    purchaseOrderId: int("purchaseOrderId"),
    purchaseOrderLineId: int("purchaseOrderLineId"),
    goodsReceiptId: int("goodsReceiptId"),
    goodsReceiptLineId: int("goodsReceiptLineId"),
    eligible: boolean("eligible").notNull().default(true),
    reversedAt: timestamp("reversedAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    identityIdx: index("procurement_buying_price_observations_identity_idx").on(
      t.identityKey,
      t.purchaseUom,
      t.currency,
      t.eligible
    ),
    receiptLineUq: uniqueIndex(
      "procurement_buying_price_observations_receipt_line_uq"
    ).on(t.goodsReceiptLineId),
  })
);

export const procurementLandedCostObservations = mysqlTable(
  "procurement_landed_cost_observations",
  {
    id: int("id").autoincrement().primaryKey(),
    identityKey: varchar("identityKey", { length: 191 }).notNull(),
    goodsReceiptId: int("goodsReceiptId").notNull(),
    goodsReceiptLineId: int("goodsReceiptLineId").notNull(),
    currency: varchar("currency", { length: 8 }).notNull(),
    exchangeRateToSgd: decimal("exchangeRateToSgd", { precision: 14, scale: 6 })
      .notNull()
      .default("1.000000"),
    purchaseUom: varchar("purchaseUom", { length: 32 }).notNull(),
    referenceUom: varchar("referenceUom", { length: 32 }).notNull(),
    conversionQuantitySnapshot: decimal("conversionQuantitySnapshot", {
      precision: 14,
      scale: 6,
    }).notNull(),
    allocatedFreight: decimal("allocatedFreight", { precision: 14, scale: 2 })
      .notNull()
      .default("0.00"),
    landedCostPerPurchaseUom: decimal("landedCostPerPurchaseUom", {
      precision: 14,
      scale: 4,
    }).notNull(),
    landedCostPerReferenceUom: decimal("landedCostPerReferenceUom", {
      precision: 14,
      scale: 4,
    }).notNull(),
    landedCostPerReferenceUomSgd: decimal("landedCostPerReferenceUomSgd", {
      precision: 14,
      scale: 4,
    }),
    sgdIsIndicative: boolean("sgdIsIndicative").notNull().default(true),
    eligible: boolean("eligible").notNull().default(true),
    reversedAt: timestamp("reversedAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    receiptLineUq: uniqueIndex(
      "procurement_landed_cost_observations_receipt_line_uq"
    ).on(t.goodsReceiptLineId),
    receiptIdx: index("procurement_landed_cost_observations_receipt_idx").on(
      t.goodsReceiptId,
      t.goodsReceiptLineId
    ),
    identityIdx: index("procurement_landed_cost_observations_identity_idx").on(
      t.identityKey,
      t.eligible
    ),
  })
);

export const procurementLandedCostAdjustments = mysqlTable(
  "procurement_landed_cost_adjustments",
  {
    id: int("id").autoincrement().primaryKey(),
    goodsReceiptId: int("goodsReceiptId").notNull(),
    goodsReceiptLineId: int("goodsReceiptLineId"),
    amount: decimal("amount", { precision: 14, scale: 2 }).notNull(),
    currency: varchar("currency", { length: 8 }).notNull(),
    reason: text("reason").notNull(),
    actorUserId: int("actorUserId"),
    actorName: varchar("actorName", { length: 255 }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  }
);

export type ProcurementProductUomProfile =
  typeof procurementProductUomProfiles.$inferSelect;
export type InsertProcurementProductUomProfile =
  typeof procurementProductUomProfiles.$inferInsert;
export type ProcurementProductUomConversion =
  typeof procurementProductUomConversions.$inferSelect;
export type InsertProcurementProductUomConversion =
  typeof procurementProductUomConversions.$inferInsert;
export type ProcurementProductUomAudit =
  typeof procurementProductUomAudits.$inferSelect;
export type ProcurementBuyingPriceObservation =
  typeof procurementBuyingPriceObservations.$inferSelect;
export type ProcurementLandedCostObservation =
  typeof procurementLandedCostObservations.$inferSelect;
export type ProcurementLandedCostAdjustment =
  typeof procurementLandedCostAdjustments.$inferSelect;

// ─── Proposal billing groups (opt-in Group Billing) ──────────────────────────

export const proposalBillingGroups = mysqlTable("proposal_billing_groups", {
  id: int("id").autoincrement().primaryKey(),
  customerId: int("customerId").notNull(),
  lockedAt: timestamp("lockedAt"),
  lockedReason: varchar("lockedReason", { length: 255 }),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
});

export const proposalBillingGroupMembers = mysqlTable(
  "proposal_billing_group_members",
  {
    id: int("id").autoincrement().primaryKey(),
    billingGroupId: int("billingGroupId").notNull(),
    proposalId: int("proposalId").notNull().unique(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  }
);

export const proposalBillingEvents = mysqlTable("proposal_billing_events", {
  id: int("id").autoincrement().primaryKey(),
  billingGroupId: int("billingGroupId").notNull(),
  createdByUserId: int("createdByUserId").notNull(),
  createdByName: varchar("createdByName", { length: 255 }).notNull(),
  kind: mysqlEnum("kind", [
    "full_group",
    "approved_only",
    "selected_tranches",
  ]).notNull(),
  totalAmountSgd: decimal("totalAmountSgd", {
    precision: 12,
    scale: 2,
  }).notNull(),
  includedProposalIds: json("includedProposalIds").$type<number[]>().notNull(),
  excludedProposalIds: json("excludedProposalIds").$type<number[]>().notNull(),
  dealInvoiceIds: json("dealInvoiceIds").$type<number[]>().notNull(),
  idempotencyKey: varchar("idempotencyKey", { length: 191 }).unique(),
  allocationsJson: json("allocationsJson").$type<Array<{
    proposalId: number;
    proposalRef: string;
    instalmentStage: string;
    dealInvoiceId: number;
    invoiceNumber: string | null;
    amountSgd: number;
    revenueTrancheId?: number | null;
  }> | null>(),
  excludedReasonsJson: json("excludedReasonsJson").$type<Array<{
    proposalId: number;
    proposalRef: string;
    reason: string;
  }> | null>(),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
});

export const proposalBillingIdempotency = mysqlTable(
  "proposal_billing_idempotency",
  {
    id: int("id").autoincrement().primaryKey(),
    idempotencyKey: varchar("idempotencyKey", { length: 191 })
      .notNull()
      .unique(),
    billingGroupId: int("billingGroupId").notNull(),
    kind: mysqlEnum("kind", [
      "full_group",
      "approved_only",
      "selected_tranches",
    ]).notNull(),
    createdByUserId: int("createdByUserId").notNull(),
    status: mysqlEnum("status", ["in_progress", "completed", "failed"])
      .notNull()
      .default("in_progress"),
    resultJson: json("resultJson"),
    errorMessage: text("errorMessage"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    completedAt: timestamp("completedAt"),
  }
);

export const proposalBillingObligationClaims = mysqlTable(
  "proposal_billing_obligation_claims",
  {
    id: int("id").autoincrement().primaryKey(),
    obligationKey: varchar("obligationKey", { length: 191 }).notNull().unique(),
    proposalId: int("proposalId").notNull(),
    instalmentStage: varchar("instalmentStage", { length: 100 }).notNull(),
    dealInvoiceId: int("dealInvoiceId").notNull(),
    billingEventId: int("billingEventId"),
    status: mysqlEnum("status", ["claimed", "released"])
      .notNull()
      .default("claimed"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    releasedAt: timestamp("releasedAt"),
  }
);

export type ProposalBillingGroup = typeof proposalBillingGroups.$inferSelect;
export type ProposalBillingGroupMember =
  typeof proposalBillingGroupMembers.$inferSelect;
export type ProposalBillingEvent = typeof proposalBillingEvents.$inferSelect;
export type ProposalBillingIdempotency =
  typeof proposalBillingIdempotency.$inferSelect;
export type ProposalBillingObligationClaim =
  typeof proposalBillingObligationClaims.$inferSelect;

export const proposalExpiryOccurrences = mysqlTable(
  "proposal_expiry_occurrences",
  {
    id: int("id").autoincrement().primaryKey(),
    occurrenceKey: varchar("occurrenceKey", { length: 191 }).notNull(),
    proposalId: int("proposalId").notNull(),
    linkBExpiresAt: timestamp("linkBExpiresAt").notNull(),
    status: varchar("status", { length: 32 }).notNull().default("processing"),
    skipReason: varchar("skipReason", { length: 64 }),
    attempts: int("attempts").notNull().default(0),
    lostAppliedAt: timestamp("lostAppliedAt"),
    notificationStatus: varchar("notificationStatus", { length: 32 })
      .notNull()
      .default("pending"),
    notificationError: text("notificationError"),
    cleoStatus: varchar("cleoStatus", { length: 32 })
      .notNull()
      .default("pending"),
    cleoError: text("cleoError"),
    lastAttemptAt: timestamp("lastAttemptAt"),
    completedAt: timestamp("completedAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    occurrenceKeyUid: uniqueIndex("proposal_expiry_occurrences_key_uid").on(
      t.occurrenceKey
    ),
    proposalIdx: index("proposal_expiry_occurrences_proposal_idx").on(
      t.proposalId
    ),
  })
);

export type ProposalExpiryOccurrence =
  typeof proposalExpiryOccurrences.$inferSelect;

export const proposalExpiryEvaluationAudit = mysqlTable(
  "proposal_expiry_evaluation_audit",
  {
    id: int("id").autoincrement().primaryKey(),
    runId: varchar("runId", { length: 64 }).notNull(),
    proposalId: int("proposalId").notNull(),
    occurrenceKey: varchar("occurrenceKey", { length: 191 }),
    result: varchar("result", { length: 64 }).notNull(),
    reasonsJson: json("reasonsJson").$type<
      string[] | Record<string, unknown> | null
    >(),
    detail: varchar("detail", { length: 512 }),
    evaluatedAt: timestamp("evaluatedAt").defaultNow().notNull(),
  },
  t => ({
    runIdx: index("proposal_expiry_eval_audit_run_idx").on(
      t.runId,
      t.evaluatedAt
    ),
    proposalIdx: index("proposal_expiry_eval_audit_proposal_idx").on(
      t.proposalId,
      t.evaluatedAt
    ),
    resultIdx: index("proposal_expiry_eval_audit_result_idx").on(
      t.result,
      t.evaluatedAt
    ),
  })
);

export type ProposalExpiryEvaluationAudit =
  typeof proposalExpiryEvaluationAudit.$inferSelect;

/** Durable quarantine: active when releasedAt IS NULL. IDs live in DB, not app source. */
export const proposalExpiryManualHolds = mysqlTable(
  "proposal_expiry_manual_holds",
  {
    id: int("id").autoincrement().primaryKey(),
    proposalId: int("proposalId").notNull(),
    reason: varchar("reason", { length: 64 }).notNull(),
    detail: text("detail"),
    heldAt: timestamp("heldAt").defaultNow().notNull(),
    heldByUserId: int("heldByUserId"),
    heldByRole: varchar("heldByRole", { length: 32 }),
    source: varchar("source", { length: 64 }).notNull().default("manual"),
    releasedAt: timestamp("releasedAt"),
    releasedByUserId: int("releasedByUserId"),
    releasedByRole: varchar("releasedByRole", { length: 32 }),
    releaseNote: text("releaseNote"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    proposalUid: uniqueIndex("proposal_expiry_manual_holds_proposal_uid").on(
      t.proposalId
    ),
    releasedIdx: index("proposal_expiry_manual_holds_released_idx").on(
      t.releasedAt
    ),
  })
);

export type ProposalExpiryManualHold =
  typeof proposalExpiryManualHolds.$inferSelect;

// ─── Proposal Signals (Cleo Sales Intelligence) ─────────────────────────────
export const proposalSignals = mysqlTable("proposal_signals", {
  id: int("id").autoincrement().primaryKey(),
  /** Null for SVR signals with no exact proposal relationship (do not fabricate). */
  proposalId: int("proposalId"),
  customerId: int("customerId").notNull(),
  ownerUserId: int("ownerUserId").notNull(),
  signalType: varchar("signalType", { length: 64 }).notNull(),
  severity: mysqlEnum("severity", ["low", "medium", "high", "critical"])
    .notNull()
    .default("medium"),
  lifecycle: mysqlEnum("lifecycle", [
    "open",
    "acknowledged",
    "actioned",
    "waiting_customer",
    "waiting_internal",
    "resolved",
    "dismissed",
  ])
    .notNull()
    .default("open"),
  title: varchar("title", { length: 255 }).notNull(),
  description: text("description"),
  reason: text("reason"),
  dueDate: varchar("dueDate", { length: 10 }),
  acknowledgedAt: timestamp("acknowledgedAt"),
  acknowledgedByUserId: int("acknowledgedByUserId"),
  actionedAt: timestamp("actionedAt"),
  actionedByUserId: int("actionedByUserId"),
  actionNote: text("actionNote"),
  nextReviewAt: timestamp("nextReviewAt"),
  waitingReason: varchar("waitingReason", { length: 500 }),
  resolvedAt: timestamp("resolvedAt"),
  resolvedByUserId: int("resolvedByUserId"),
  resolvedReason: varchar("resolvedReason", { length: 255 }),
  autoResolved: boolean("autoResolved").notNull().default(false),
  dismissedAt: timestamp("dismissedAt"),
  dismissedByUserId: int("dismissedByUserId"),
  dismissedReason: varchar("dismissedReason", { length: 500 }),
  dedupeKey: varchar("dedupeKey", { length: 191 }).notNull().unique(),
  metadataJson: json("metadataJson").$type<Record<string, unknown> | null>(),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
  updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
});

export type ProposalSignal = typeof proposalSignals.$inferSelect;
export type InsertProposalSignal = typeof proposalSignals.$inferInsert;

// ─── Cleo Briefing Events (append-only audit log) ───────────────────────────
export const cleoBriefingEvents = mysqlTable("cleo_briefing_events", {
  id: int("id").autoincrement().primaryKey(),
  signalId: int("signalId").notNull(),
  /** Null for SVR-only signals — never insert a fabricated proposal id. */
  proposalId: int("proposalId"),
  /** proposal | svr */
  sourceType: varchar("sourceType", { length: 32 }),
  svrId: int("svrId"),
  customerId: int("customerId").notNull(),
  userId: int("userId").notNull(),
  signalType: varchar("signalType", { length: 64 }).notNull(),
  eventType: varchar("eventType", { length: 64 }).notNull(),
  recommendationShown: text("recommendationShown"),
  recommendationShownAt: timestamp("recommendationShownAt"),
  recommendationSelected: boolean("recommendationSelected").default(false),
  alternativeActionChosen: varchar("alternativeActionChosen", { length: 100 }),
  naturalLanguageInput: text("naturalLanguageInput"),
  structuredInterpretation: json("structuredInterpretation").$type<Record<
    string,
    unknown
  > | null>(),
  clarificationQuestion: text("clarificationQuestion"),
  clarificationAnswer: text("clarificationAnswer"),
  confirmedUpdate: json("confirmedUpdate").$type<Record<
    string,
    unknown
  > | null>(),
  lifecycleBefore: varchar("lifecycleBefore", { length: 32 }),
  lifecycleAfter: varchar("lifecycleAfter", { length: 32 }),
  nextReviewDate: varchar("nextReviewDate", { length: 10 }),
  messageGenerated: text("messageGenerated"),
  messageFormat: varchar("messageFormat", { length: 20 }),
  messageCopied: boolean("messageCopied").default(false),
  messageEdited: boolean("messageEdited").default(false),
  createdAt: timestamp("createdAt").defaultNow().notNull(),
});

export type CleoBriefingEvent = typeof cleoBriefingEvents.$inferSelect;
export type InsertCleoBriefingEvent = typeof cleoBriefingEvents.$inferInsert;

/** Customer-centric Cleo work items (not proposal-signal rows). */
export const cleoCustomerWorkItems = mysqlTable(
  "cleo_customer_work_items",
  {
    id: int("id").autoincrement().primaryKey(),
    customerId: int("customerId").notNull(),
    ownerUserId: int("ownerUserId").notNull(),
    proposalId: int("proposalId"),
    objectiveType: varchar("objectiveType", { length: 64 }).notNull(),
    priority: int("priority").notNull().default(50),
    lifecycle: mysqlEnum("lifecycle", [
      "open",
      "snoozed",
      "waiting",
      "resolved",
      "closed",
    ])
      .notNull()
      .default("open"),
    nextReviewAt: timestamp("nextReviewAt"),
    nextReviewDateSgt: varchar("nextReviewDateSgt", { length: 10 }),
    contextSnapshot: json("contextSnapshot").$type<Record<
      string,
      unknown
    > | null>(),
    contextHash: varchar("contextHash", { length: 64 }),
    contextGeneration: int("contextGeneration").notNull().default(1),
    dedupeKey: varchar("dedupeKey", { length: 191 }).notNull().unique(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
    resolvedAt: timestamp("resolvedAt"),
  },
  t => ({
    ownerReviewIdx: index("idx_ccu_owner_review").on(
      t.ownerUserId,
      t.lifecycle,
      t.nextReviewDateSgt
    ),
    customerOwnerIdx: index("idx_ccu_customer_owner").on(
      t.customerId,
      t.ownerUserId
    ),
  })
);

export type CleoCustomerWorkItem = typeof cleoCustomerWorkItems.$inferSelect;
export type InsertCleoCustomerWorkItem =
  typeof cleoCustomerWorkItems.$inferInsert;

/** Append-only Cleo customer conversation / audit events. */
export const cleoCustomerConversationEvents = mysqlTable(
  "cleo_customer_conversation_events",
  {
    id: int("id").autoincrement().primaryKey(),
    workItemId: int("workItemId"),
    customerId: int("customerId").notNull(),
    ownerUserId: int("ownerUserId").notNull(),
    userId: int("userId").notNull(),
    eventType: varchar("eventType", { length: 64 }).notNull(),
    staffMessage: text("staffMessage"),
    assistantMessage: text("assistantMessage"),
    interpretationJson: json("interpretationJson").$type<Record<
      string,
      unknown
    > | null>(),
    customerLogId: int("customerLogId"),
    reminderId: int("reminderId"),
    undoUntil: timestamp("undoUntil"),
    undoneAt: timestamp("undoneAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    customerCreatedIdx: index("idx_ccu_conv_customer").on(
      t.customerId,
      t.createdAt
    ),
    workItemIdx: index("idx_ccu_conv_work_item").on(t.workItemId),
  })
);

export type CleoCustomerConversationEvent =
  typeof cleoCustomerConversationEvents.$inferSelect;
export type InsertCleoCustomerConversationEvent =
  typeof cleoCustomerConversationEvents.$inferInsert;

/** Durable digest occurrence / idempotency keys (Singapore calendar date). */
export const cleoDigestOccurrences = mysqlTable(
  "cleo_digest_occurrences",
  {
    id: int("id").autoincrement().primaryKey(),
    digestKind: varchar("digestKind", { length: 64 }).notNull(),
    occurrenceKey: varchar("occurrenceKey", { length: 191 }).notNull(),
    sgtDateKey: varchar("sgtDateKey", { length: 10 }).notNull(),
    recipientUserId: int("recipientUserId"),
    status: varchar("status", { length: 32 }).notNull().default("claimed"),
    metadataJson: json("metadataJson").$type<Record<string, unknown> | null>(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    kindKeyUnique: uniqueIndex("uq_cleo_digest_occurrence").on(
      t.digestKind,
      t.occurrenceKey
    ),
  })
);

export type CleoDigestOccurrence = typeof cleoDigestOccurrences.$inferSelect;
export type InsertCleoDigestOccurrence =
  typeof cleoDigestOccurrences.$inferInsert;

// ─── Variation Orders (Phase 1 — commercial delta to approved D-Tools proposals) ─

export const proposalVariationOrders = mysqlTable(
  "proposal_variation_orders",
  {
    id: int("id").autoincrement().primaryKey(),
    proposalId: int("proposalId").notNull(),
    voNumber: varchar("voNumber", { length: 32 }).notNull(),
    status: mysqlEnum("status", [
      "draft",
      "sent",
      "approved",
      "rejected",
      "cancelled",
    ])
      .notNull()
      .default("draft"),
    createdByUserId: int("createdByUserId").notNull(),
    createdByName: varchar("createdByName", { length: 255 }),
    originalContractValue: decimal("originalContractValue", {
      precision: 12,
      scale: 2,
    })
      .notNull()
      .default("0"),
    variationAmount: decimal("variationAmount", { precision: 12, scale: 2 })
      .notNull()
      .default("0"),
    revisedContractValue: decimal("revisedContractValue", {
      precision: 12,
      scale: 2,
    })
      .notNull()
      .default("0"),
    draftWorkspaceJson: json("draftWorkspaceJson").$type<Record<
      string,
      unknown
    > | null>(),
    /** Commercial Payment Details snapshot — frozen on approve (single pricing authority). */
    paymentDetailsJson: json("paymentDetailsJson").$type<Record<
      string,
      unknown
    > | null>(),
    publicToken: varchar("publicToken", { length: 64 }),
    linkBExpiresAt: timestamp("linkBExpiresAt"),
    sentAt: timestamp("sentAt"),
    approvedAt: timestamp("approvedAt"),
    approvedEmail: varchar("approvedEmail", { length: 255 }),
    approvedName: varchar("approvedName", { length: 255 }),
    approvedIpAddress: varchar("approvedIpAddress", { length: 64 }),
    rejectedAt: timestamp("rejectedAt"),
    rejectedEmail: varchar("rejectedEmail", { length: 255 }),
    rejectedName: varchar("rejectedName", { length: 255 }),
    rejectedIpAddress: varchar("rejectedIpAddress", { length: 64 }),
    cancelledAt: timestamp("cancelledAt"),
    /** Optional salesperson note when soft-cancelling a draft/sent VO. */
    cancellationReason: varchar("cancellationReason", { length: 512 }),
    /** Optional overall customer-facing reason for this VO (Link A / Link B). */
    changeReason: text("changeReason"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    proposalVoUnique: uniqueIndex("proposal_vo_number_unique").on(
      t.proposalId,
      t.voNumber
    ),
    proposalIdx: index("proposal_vo_proposal_idx").on(t.proposalId),
    tokenIdx: uniqueIndex("proposal_vo_public_token_unique").on(t.publicToken),
  })
);

export const proposalVariationOrderLines = mysqlTable(
  "proposal_variation_order_lines",
  {
    id: int("id").autoincrement().primaryKey(),
    voId: int("voId").notNull(),
    changeType: mysqlEnum("changeType", [
      "added",
      "removed",
      "quantity_changed",
    ]).notNull(),
    stableLineKey: varchar("stableLineKey", { length: 191 }).notNull(),
    name: varchar("name", { length: 512 }).notNull(),
    brand: varchar("brand", { length: 255 }),
    model: varchar("model", { length: 255 }),
    description: text("description"),
    systemKey: varchar("systemKey", { length: 255 }),
    roomKey: varchar("roomKey", { length: 255 }),
    systemId: varchar("systemId", { length: 255 }),
    systemName: varchar("systemName", { length: 255 }),
    roomId: varchar("roomId", { length: 255 }),
    roomName: varchar("roomName", { length: 255 }),
    parentStableLineKey: varchar("parentStableLineKey", { length: 191 }),
    isAccessory: boolean("isAccessory").default(false).notNull(),
    quantityBefore: decimal("quantityBefore", { precision: 12, scale: 3 }),
    quantityAfter: decimal("quantityAfter", { precision: 12, scale: 3 }),
    unitPrice: decimal("unitPrice", { precision: 12, scale: 2 })
      .notNull()
      .default("0"),
    lineTotalDelta: decimal("lineTotalDelta", { precision: 12, scale: 2 })
      .notNull()
      .default("0"),
    sortOrder: int("sortOrder").notNull().default(0),
    sourceProposalItemId: int("sourceProposalItemId"),
    /** Optional customer-facing reason for this change (shown on VO document + portal). */
    changeReason: text("changeReason"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    voIdx: index("proposal_vo_lines_vo_idx").on(t.voId),
  })
);

export const proposalVariationOrderEvents = mysqlTable(
  "proposal_variation_order_events",
  {
    id: int("id").autoincrement().primaryKey(),
    voId: int("voId").notNull(),
    proposalId: int("proposalId").notNull(),
    eventType: varchar("eventType", { length: 64 }).notNull(),
    actorUserId: int("actorUserId"),
    actorName: varchar("actorName", { length: 255 }).notNull(),
    actorRole: varchar("actorRole", { length: 64 }),
    metadataJson: json("metadataJson").$type<Record<string, unknown> | null>(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    voIdx: index("proposal_vo_events_vo_idx").on(t.voId),
    proposalIdx: index("proposal_vo_events_proposal_idx").on(t.proposalId),
  })
);

/** Updated System Overview rows — contractual VO amendments applied to proposal only on approve. */
export const proposalVariationOrderSystemOverviews = mysqlTable(
  "proposal_variation_order_system_overviews",
  {
    id: int("id").autoincrement().primaryKey(),
    voId: int("voId").notNull(),
    systemKey: varchar("systemKey", { length: 191 }).notNull(),
    systemId: varchar("systemId", { length: 255 }),
    systemName: varchar("systemName", { length: 512 }).notNull(),
    originalOverview: text("originalOverview").notNull(),
    revisedOverview: text("revisedOverview").notNull(),
    sortOrder: int("sortOrder").notNull().default(0),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    voIdx: index("proposal_vo_overview_vo_idx").on(t.voId),
    voSystemUnique: uniqueIndex("proposal_vo_overview_vo_system_unique").on(
      t.voId,
      t.systemKey
    ),
  })
);

export type ProposalVariationOrder =
  typeof proposalVariationOrders.$inferSelect;
export type ProposalVariationOrderLine =
  typeof proposalVariationOrderLines.$inferSelect;
export type ProposalVariationOrderEvent =
  typeof proposalVariationOrderEvents.$inferSelect;
export type ProposalVariationOrderSystemOverview =
  typeof proposalVariationOrderSystemOverviews.$inferSelect;

// ─── Catalogue Intelligence (Cat Intel) ──────────────────────────────────────
/**
 * Evidence store for commercial learning.
 * Observation types V1: selling_price | duplicate_candidate
 * Future types (search_term, buy_price, lead_time, …) use the same table.
 * Cat Intel NEVER overwrites catalogue_items.price.
 */
export const catalogueIntelObservations = mysqlTable(
  "catalogue_intel_observations",
  {
    id: int("id").autoincrement().primaryKey(),
    /** selling_price | duplicate_candidate | (future) search_term | buy_price | … */
    observationType: varchar("observationType", { length: 64 }).notNull(),
    /** Canonical retail catalogue product when known / matched. */
    catalogueItemId: int("catalogueItemId"),
    uploadedBrand: varchar("uploadedBrand", { length: 255 }),
    uploadedModel: varchar("uploadedModel", { length: 255 }),
    uploadedName: varchar("uploadedName", { length: 512 }),
    uploadedDisplayName: varchar("uploadedDisplayName", { length: 512 }),
    uploadedSku: varchar("uploadedSku", { length: 128 }),
    unitPrice: decimal("unitPrice", { precision: 12, scale: 2 }),
    quantity: int("quantity"),
    currency: varchar("currency", { length: 8 }).notNull().default("SGD"),
    /** dtools_json | manual_proposal | purchase_order | … */
    sourceType: varchar("sourceType", { length: 64 }).notNull(),
    sourceEntityType: varchar("sourceEntityType", { length: 64 }),
    sourceEntityId: int("sourceEntityId"),
    sourceLineRef: varchar("sourceLineRef", { length: 128 }),
    /** e.g. proposalRef */
    sourceRef: varchar("sourceRef", { length: 64 }),
    /** won | live | draft | lost | unknown */
    proposalQuality: varchar("proposalQuality", { length: 32 }),
    pipelineStage: varchar("pipelineStage", { length: 32 }),
    /** For duplicate_candidate: same as catalogueItemId (existing product). Kept explicit for queries. */
    duplicateCandidateCatalogueItemId: int("duplicateCandidateCatalogueItemId"),
    matchConfidence: decimal("matchConfidence", { precision: 5, scale: 2 }),
    matchReasonsJson: json("matchReasonsJson").$type<string[] | null>(),
    payloadJson: json("payloadJson").$type<Record<string, unknown> | null>(),
    /** Stable identity for idempotent retries. */
    idempotencyKey: varchar("idempotencyKey", { length: 191 }),
    /** When set, observation was recorded but excluded from recommendations. */
    exclusionReason: varchar("exclusionReason", { length: 128 }),
    /** duplicate_candidate review: open | linked | kept_separate | dismissed */
    reviewStatus: varchar("reviewStatus", { length: 32 }),
    reviewedAt: timestamp("reviewedAt"),
    reviewedByUserId: int("reviewedByUserId"),
    observedAt: timestamp("observedAt").notNull(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    catalogueTypeObservedIdx: index(
      "cat_intel_obs_catalogue_type_observed_idx"
    ).on(t.catalogueItemId, t.observationType, t.observedAt),
    typeObservedIdx: index("cat_intel_obs_type_observed_idx").on(
      t.observationType,
      t.observedAt
    ),
    sourceEntityIdx: index("cat_intel_obs_source_entity_idx").on(
      t.sourceEntityType,
      t.sourceEntityId
    ),
    duplicateIdx: index("cat_intel_obs_duplicate_idx").on(
      t.duplicateCandidateCatalogueItemId
    ),
    idempotencyUq: uniqueIndex("cat_intel_obs_idempotency_uq").on(
      t.idempotencyKey
    ),
    reviewStatusIdx: index("cat_intel_obs_review_status_idx").on(
      t.observationType,
      t.reviewStatus
    ),
  })
);

export type CatalogueIntelObservation =
  typeof catalogueIntelObservations.$inferSelect;
export type InsertCatalogueIntelObservation =
  typeof catalogueIntelObservations.$inferInsert;

/** Durable Cat Intel observation jobs (queued after proposal/import). */
export const catalogueIntelObservationJobs = mysqlTable(
  "catalogue_intel_observation_jobs",
  {
    id: int("id").autoincrement().primaryKey(),
    proposalId: int("proposalId").notNull(),
    idempotencyKey: varchar("idempotencyKey", { length: 191 }).notNull(),
    payloadJson: json("payloadJson").$type<Record<string, unknown>>().notNull(),
    status: mysqlEnum("status", [
      "queued",
      "processing",
      "retry",
      "failed",
      "done",
    ])
      .notNull()
      .default("queued"),
    attempts: int("attempts").notNull().default(0),
    lastError: text("lastError"),
    nextRunAt: timestamp("nextRunAt"),
    lockedAt: timestamp("lockedAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    idempotencyUq: uniqueIndex("cat_intel_jobs_idempotency_uq").on(
      t.idempotencyKey
    ),
    statusNextIdx: index("cat_intel_jobs_status_next_idx").on(
      t.status,
      t.nextRunAt
    ),
    proposalIdx: index("cat_intel_jobs_proposal_idx").on(t.proposalId),
  })
);

export type CatalogueIntelObservationJob =
  typeof catalogueIntelObservationJobs.$inferSelect;
export type InsertCatalogueIntelObservationJob =
  typeof catalogueIntelObservationJobs.$inferInsert;

/**
 * Durable Cleo signal reconciliation jobs.
 * Finance/SVR/proposal paths enqueue after success; worker never writes financial state.
 */
export const cleoSignalReconcileJobs = mysqlTable(
  "cleo_signal_reconcile_jobs",
  {
    id: int("id").autoincrement().primaryKey(),
    entityType: varchar("entityType", { length: 32 }).notNull(),
    entityId: int("entityId").notNull(),
    reason: varchar("reason", { length: 128 }).notNull(),
    idempotencyKey: varchar("idempotencyKey", { length: 191 }).notNull(),
    status: mysqlEnum("status", [
      "pending",
      "running",
      "completed",
      "failed",
      /** Durable admin-visible state for unknown/unregistered signal types. */
      "review_required",
    ])
      .notNull()
      .default("pending"),
    attemptCount: int("attemptCount").notNull().default(0),
    lastError: text("lastError"),
    nextRetryAt: timestamp("nextRetryAt"),
    /** Exclusive worker lease — required for claim/complete, not just status=running. */
    leaseToken: varchar("leaseToken", { length: 64 }),
    leaseExpiresAt: timestamp("leaseExpiresAt"),
    /** Incremented on every enqueue so events during processing are not lost. */
    eventGeneration: int("eventGeneration").notNull().default(0),
    /** Generation copied at claim time; completion requeues when eventGeneration advanced. */
    processingGeneration: int("processingGeneration"),
    /** Unknown-type review snapshot (admin-visible; never shown to sales). */
    reviewSignalType: varchar("reviewSignalType", { length: 64 }),
    reviewPayloadJson: json("reviewPayloadJson").$type<Record<
      string,
      unknown
    > | null>(),
    firstDetectedAt: timestamp("firstDetectedAt"),
    latestOccurrenceAt: timestamp("latestOccurrenceAt"),
    completedAt: timestamp("completedAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    idempotencyUq: uniqueIndex("cleo_sig_reconcile_idempotency_uq").on(
      t.idempotencyKey
    ),
    statusNextIdx: index("cleo_sig_reconcile_status_next_idx").on(
      t.status,
      t.nextRetryAt
    ),
    entityIdx: index("cleo_sig_reconcile_entity_idx").on(
      t.entityType,
      t.entityId
    ),
    leaseIdx: index("cleo_sig_reconcile_lease_idx").on(
      t.status,
      t.leaseExpiresAt
    ),
    reviewIdx: index("cleo_sig_reconcile_review_idx").on(
      t.status,
      t.latestOccurrenceAt
    ),
  })
);

export type CleoSignalReconcileJob =
  typeof cleoSignalReconcileJobs.$inferSelect;
export type InsertCleoSignalReconcileJob =
  typeof cleoSignalReconcileJobs.$inferInsert;

/**
 * Durable invoice.lifecycle_changed outbox.
 * Every authoritative invoice mutation enqueues after commit; convergence handler is idempotent.
 */
export const invoiceLifecycleEvents = mysqlTable(
  "invoice_lifecycle_events",
  {
    id: int("id").autoincrement().primaryKey(),
    eventType: varchar("eventType", { length: 64 })
      .notNull()
      .default("invoice.lifecycle_changed"),
    invoiceKind: varchar("invoiceKind", { length: 32 }).notNull(),
    invoiceId: int("invoiceId").notNull(),
    previousState: varchar("previousState", { length: 64 }),
    newState: varchar("newState", { length: 64 }).notNull(),
    proposalId: int("proposalId"),
    dealId: int("dealId"),
    svrId: int("svrId"),
    customerId: int("customerId"),
    ownerUserId: int("ownerUserId"),
    invoiceNumber: varchar("invoiceNumber", { length: 128 }),
    occurredAt: timestamp("occurredAt").defaultNow().notNull(),
    actorSource: varchar("actorSource", { length: 128 }).notNull(),
    paymentRef: varchar("paymentRef", { length: 191 }),
    replacementInvoiceId: int("replacementInvoiceId"),
    creditRef: varchar("creditRef", { length: 191 }),
    payloadJson: json("payloadJson").$type<Record<string, unknown> | null>(),
    idempotencyKey: varchar("idempotencyKey", { length: 191 }).notNull(),
    eventGeneration: int("eventGeneration").notNull().default(1),
    eventVersion: int("eventVersion").notNull().default(1),
    status: mysqlEnum("status", [
      "pending",
      "running",
      "completed",
      "failed",
      "review_required",
    ])
      .notNull()
      .default("pending"),
    attemptCount: int("attemptCount").notNull().default(0),
    lastError: text("lastError"),
    nextRetryAt: timestamp("nextRetryAt"),
    leaseToken: varchar("leaseToken", { length: 64 }),
    leaseExpiresAt: timestamp("leaseExpiresAt"),
    processingGeneration: int("processingGeneration"),
    completedAt: timestamp("completedAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    idempotencyUq: uniqueIndex("inv_lifecycle_idempotency_uq").on(
      t.idempotencyKey
    ),
    statusNextIdx: index("inv_lifecycle_status_next_idx").on(
      t.status,
      t.nextRetryAt
    ),
    invoiceIdx: index("inv_lifecycle_invoice_idx").on(
      t.invoiceKind,
      t.invoiceId
    ),
    leaseIdx: index("inv_lifecycle_lease_idx").on(t.status, t.leaseExpiresAt),
  })
);

export type InvoiceLifecycleEvent = typeof invoiceLifecycleEvents.$inferSelect;
export type InsertInvoiceLifecycleEvent =
  typeof invoiceLifecycleEvents.$inferInsert;

export const invoiceLifecycleDiscrepancies = mysqlTable(
  "invoice_lifecycle_discrepancies",
  {
    id: int("id").autoincrement().primaryKey(),
    invoiceKind: varchar("invoiceKind", { length: 32 }).notNull(),
    invoiceId: int("invoiceId").notNull(),
    signalId: int("signalId"),
    discrepancyType: varchar("discrepancyType", { length: 64 }).notNull(),
    detailJson: json("detailJson").$type<Record<string, unknown> | null>(),
    resolvedAt: timestamp("resolvedAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    invoiceIdx: index("inv_lifecycle_disc_invoice_idx").on(
      t.invoiceKind,
      t.invoiceId
    ),
    openIdx: index("inv_lifecycle_disc_open_idx").on(
      t.resolvedAt,
      t.discrepancyType
    ),
  })
);

export type InvoiceLifecycleDiscrepancy =
  typeof invoiceLifecycleDiscrepancies.$inferSelect;

/** Proposal-approval WhatsApp OTPs (separate from Clarity One login OTPs). */
export const proposalApprovalOtps = mysqlTable(
  "proposal_approval_otps",
  {
    id: int("id").autoincrement().primaryKey(),
    proposalId: int("proposalId").notNull(),
    customerId: int("customerId"),
    approverName: varchar("approverName", { length: 255 }).notNull(),
    phoneNormalized: varchar("phoneNormalized", { length: 20 }).notNull(),
    phoneDisplay: varchar("phoneDisplay", { length: 32 }).notNull(),
    emailNormalized: varchar("emailNormalized", { length: 320 }),
    otpHash: varchar("otpHash", { length: 64 }).notNull(),
    expiresAt: timestamp("expiresAt").notNull(),
    usedAt: timestamp("usedAt"),
    attemptCount: int("attemptCount").default(0).notNull(),
    ipAddress: varchar("ipAddress", { length: 45 }),
    userAgent: varchar("userAgent", { length: 512 }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    proposalPhoneIdx: index("proposal_approval_otps_proposal_phone_idx").on(
      t.proposalId,
      t.phoneNormalized,
      t.createdAt
    ),
    phoneIdx: index("proposal_approval_otps_phone_idx").on(
      t.phoneNormalized,
      t.createdAt
    ),
  })
);

export type ProposalApprovalOtp = typeof proposalApprovalOtps.$inferSelect;
export type InsertProposalApprovalOtp =
  typeof proposalApprovalOtps.$inferInsert;

export const proposalApprovalOtpAuditLog = mysqlTable(
  "proposal_approval_otp_audit_log",
  {
    id: int("id").autoincrement().primaryKey(),
    eventType: mysqlEnum("eventType", [
      "otp_requested",
      "otp_sent",
      "otp_send_failed",
      "otp_verified",
      "otp_failed",
      "otp_expired",
      "otp_rate_limited",
      "approval_completed",
    ]).notNull(),
    proposalId: int("proposalId").notNull(),
    customerId: int("customerId"),
    phoneNormalized: varchar("phoneNormalized", { length: 20 }),
    otpId: int("otpId"),
    reason: varchar("reason", { length: 255 }),
    ipAddress: varchar("ipAddress", { length: 45 }),
    userAgent: varchar("userAgent", { length: 512 }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    proposalIdx: index("proposal_approval_otp_audit_proposal_idx").on(
      t.proposalId,
      t.createdAt
    ),
    phoneIdx: index("proposal_approval_otp_audit_phone_idx").on(
      t.phoneNormalized,
      t.createdAt
    ),
  })
);

export type ProposalApprovalOtpAuditLog =
  typeof proposalApprovalOtpAuditLog.$inferSelect;
export type InsertProposalApprovalOtpAuditLog =
  typeof proposalApprovalOtpAuditLog.$inferInsert;

/** Immutable WhatsApp-OTP proposal approval evidence (one successful row per proposal revision). */
export const proposalApprovalEvidence = mysqlTable(
  "proposal_approval_evidence",
  {
    id: int("id").autoincrement().primaryKey(),
    auditReference: varchar("auditReference", { length: 64 }).notNull(),
    /** Approved proposal_headers row id (same as revision id in this model). */
    proposalId: int("proposalId").notNull(),
    /** Immutable commercial document id (= proposal_headers.id for that revision). */
    proposalRevisionId: int("proposalRevisionId").notNull(),
    proposalFamilyRootId: int("proposalFamilyRootId"),
    customerId: int("customerId"),
    approverName: varchar("approverName", { length: 255 }).notNull(),
    verifiedWhatsAppNormalized: varchar("verifiedWhatsAppNormalized", {
      length: 20,
    }).notNull(),
    verifiedWhatsAppDisplay: varchar("verifiedWhatsAppDisplay", {
      length: 32,
    }).notNull(),
    approvalEmail: varchar("approvalEmail", { length: 320 }),
    proposalNumber: varchar("proposalNumber", { length: 64 }).notNull(),
    proposalTitle: varchar("proposalTitle", { length: 512 }),
    approvedTotal: decimal("approvedTotal", {
      precision: 14,
      scale: 2,
    }).notNull(),
    currency: varchar("currency", { length: 8 }).notNull().default("SGD"),
    revisionLabel: varchar("revisionLabel", { length: 64 }),
    /** Full immutable commercial snapshot at approval (never regenerated from live rows). */
    proposalSnapshotJson: json("proposalSnapshotJson")
      .$type<Record<string, unknown>>()
      .notNull(),
    approvedPdfStorageKey: varchar("approvedPdfStorageKey", { length: 512 }),
    pdfSnapshotStatus: mysqlEnum("pdfSnapshotStatus", [
      "pending",
      "ready",
      "failed",
    ])
      .notNull()
      .default("pending"),
    /** FK-ish reference to proposal_approval_otps.id used for successful verification. */
    otpSessionId: int("otpSessionId").notNull(),
    otpVerifiedAt: timestamp("otpVerifiedAt").notNull(),
    approvedAt: timestamp("approvedAt").notNull(),
    approvedAtSingapore: varchar("approvedAtSingapore", {
      length: 64,
    }).notNull(),
    verificationChannel: varchar("verificationChannel", { length: 32 })
      .notNull()
      .default("whatsapp_otp"),
    publicLinkPath: varchar("publicLinkPath", { length: 255 }),
    ipAddress: varchar("ipAddress", { length: 45 }),
    userAgent: varchar("userAgent", { length: 512 }),
    confirmationEmailRequested: int("confirmationEmailRequested")
      .notNull()
      .default(0),
    confirmationEmailAddress: varchar("confirmationEmailAddress", {
      length: 320,
    }),
    confirmationEmailStatus: mysqlEnum("confirmationEmailStatus", [
      "not_requested",
      "queued",
      "sent",
      "failed",
    ])
      .notNull()
      .default("not_requested"),
    confirmationEmailQueuedAt: timestamp("confirmationEmailQueuedAt"),
    confirmationEmailSentAt: timestamp("confirmationEmailSentAt"),
    confirmationEmailFailedAt: timestamp("confirmationEmailFailedAt"),
    confirmationEmailFailureReason: varchar("confirmationEmailFailureReason", {
      length: 512,
    }),
    /** Provider message id (Resend) — staff/debug only. */
    confirmationEmailMessageId: varchar("confirmationEmailMessageId", {
      length: 191,
    }),
    /** Internal delivery correlation id — staff/debug only. */
    confirmationEmailCorrelationId: varchar("confirmationEmailCorrelationId", {
      length: 191,
    }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    revisionUq: uniqueIndex("proposal_approval_evidence_revision_uq").on(
      t.proposalRevisionId
    ),
    auditUq: uniqueIndex("proposal_approval_evidence_audit_uq").on(
      t.auditReference
    ),
    proposalIdx: index("proposal_approval_evidence_proposal_idx").on(
      t.proposalId,
      t.approvedAt
    ),
    phoneIdx: index("proposal_approval_evidence_phone_idx").on(
      t.verifiedWhatsAppNormalized,
      t.approvedAt
    ),
    customerIdx: index("proposal_approval_evidence_customer_idx").on(
      t.customerId,
      t.approvedAt
    ),
    otpSessionIdx: index("proposal_approval_evidence_otp_session_idx").on(
      t.otpSessionId
    ),
  })
);

export type ProposalApprovalEvidence =
  typeof proposalApprovalEvidence.$inferSelect;
export type InsertProposalApprovalEvidence =
  typeof proposalApprovalEvidence.$inferInsert;

// ─── Warranty Cards / Drafts ─────────────────────────────────────────────────
export const warrantyCards = mysqlTable(
  "warranty_cards",
  {
    id: int("id").autoincrement().primaryKey(),
    customerId: int("customerId").notNull(),
    sourceInvoiceType: varchar("sourceInvoiceType", { length: 32 }).notNull(),
    sourceInvoiceId: int("sourceInvoiceId").notNull(),
    sourceProposalId: int("sourceProposalId"),
    sourceDealId: int("sourceDealId"),
    commercialScopeKey: varchar("commercialScopeKey", {
      length: 191,
    }).notNull(),
    status: varchar("status", { length: 32 }).notNull().default("draft"),
    customerNameSnapshot: varchar("customerNameSnapshot", {
      length: 255,
    }).notNull(),
    customerContactSnapshot: varchar("customerContactSnapshot", { length: 64 }),
    customerEmailSnapshot: varchar("customerEmailSnapshot", { length: 320 }),
    siteAddressSnapshot: text("siteAddressSnapshot"),
    invoiceNumberSnapshot: varchar("invoiceNumberSnapshot", { length: 100 }),
    invoiceDateSnapshot: timestamp("invoiceDateSnapshot"),
    finalSettlementDateSnapshot: timestamp("finalSettlementDateSnapshot"),
    proposalRefSnapshot: varchar("proposalRefSnapshot", { length: 100 }),
    salespersonNameSnapshot: varchar("salespersonNameSnapshot", {
      length: 255,
    }),
    termsVersion: varchar("termsVersion", { length: 64 }),
    eligibilityOverrideReason: text("eligibilityOverrideReason"),
    eligibilityOverrideBy: int("eligibilityOverrideBy"),
    eligibilityOverrideAt: timestamp("eligibilityOverrideAt"),
    createdBy: int("createdBy"),
    updatedBy: int("updatedBy"),
    submittedBy: int("submittedBy"),
    submittedAt: timestamp("submittedAt"),
    submissionCount: int("submissionCount").notNull().default(0),
    resubmissionHistoryJson: json("resubmissionHistoryJson").$type<Array<{
      submittedBy: number;
      submittedAt: string;
      submissionNumber: number;
      previousStatus: string;
    }> | null>(),
    notifiedApproverUserIdsJson: json("notifiedApproverUserIdsJson").$type<
      number[] | null
    >(),
    notifiedApproverEmailsJson: json("notifiedApproverEmailsJson").$type<
      string[] | null
    >(),
    approvalNotificationStatus: varchar("approvalNotificationStatus", {
      length: 32,
    }),
    approvalNotificationError: text("approvalNotificationError"),
    approvalNotificationAt: timestamp("approvalNotificationAt"),
    approvalNotificationDeliveryLogId: int("approvalNotificationDeliveryLogId"),
    changeRequestReason: text("changeRequestReason"),
    changeRequestedBy: int("changeRequestedBy"),
    changeRequestedAt: timestamp("changeRequestedAt"),
    approvedBy: int("approvedBy"),
    approvedAt: timestamp("approvedAt"),
    issuedBy: int("issuedBy"),
    voidedBy: int("voidedBy"),
    voidReason: text("voidReason"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
    issuedAt: timestamp("issuedAt"),
    voidedAt: timestamp("voidedAt"),
    holdReason: text("holdReason"),
    heldAt: timestamp("heldAt"),
    reviewReason: text("reviewReason"),
    reviewFlaggedAt: timestamp("reviewFlaggedAt"),
  },
  t => ({
    scopeUq: uniqueIndex("warranty_cards_scope_uid").on(t.commercialScopeKey),
    customerIdx: index("warranty_cards_customer_idx").on(
      t.customerId,
      t.status
    ),
    invoiceIdx: index("warranty_cards_invoice_idx").on(
      t.sourceInvoiceType,
      t.sourceInvoiceId
    ),
    proposalIdx: index("warranty_cards_proposal_idx").on(t.sourceProposalId),
    dealIdx: index("warranty_cards_deal_idx").on(t.sourceDealId),
    statusIdx: index("warranty_cards_status_idx").on(t.status, t.submittedAt),
    submittedByIdx: index("warranty_cards_submitted_by_idx").on(
      t.submittedBy,
      t.status
    ),
  })
);

export type WarrantyCard = typeof warrantyCards.$inferSelect;
export type InsertWarrantyCard = typeof warrantyCards.$inferInsert;

export const warrantyItems = mysqlTable(
  "warranty_items",
  {
    id: int("id").autoincrement().primaryKey(),
    warrantyCardId: int("warrantyCardId").notNull(),
    productId: int("productId"),
    sourceCommercialLineKey: varchar("sourceCommercialLineKey", {
      length: 191,
    }),
    brand: varchar("brand", { length: 255 }).notNull(),
    model: varchar("model", { length: 255 }).notNull(),
    /** Short product title from proposal displayName/name — never long description prose. */
    productName: varchar("productName", { length: 255 }),
    finish: varchar("finish", { length: 255 }),
    quantity: int("quantity").notNull().default(1),
    unitIndex: int("unitIndex"),
    serialNumber: varchar("serialNumber", { length: 255 }),
    serialNumberNormalized: varchar("serialNumberNormalized", { length: 255 }),
    serialNotApplicable: int("serialNotApplicable").notNull().default(0),
    warrantyStartDate: timestamp("warrantyStartDate"),
    warrantyDurationMonths: int("warrantyDurationMonths"),
    warrantyExpiryDate: timestamp("warrantyExpiryDate"),
    warrantyPeriodSource: varchar("warrantyPeriodSource", { length: 64 }),
    warrantyPeriodEvidenceJson: json("warrantyPeriodEvidenceJson").$type<Record<
      string,
      unknown
    > | null>(),
    manufacturerWarrantyLabel: varchar("manufacturerWarrantyLabel", {
      length: 255,
    }),
    manufacturerTermsUrl: text("manufacturerTermsUrl"),
    manufacturerTermsDocumentKey: varchar("manufacturerTermsDocumentKey", {
      length: 512,
    }),
    imageStorageKey: varchar("imageStorageKey", { length: 512 }),
    imageUrl: text("imageUrl"),
    imageAlt: varchar("imageAlt", { length: 512 }),
    imageSourceType: varchar("imageSourceType", { length: 32 }).default("none"),
    imageSourceUrl: text("imageSourceUrl"),
    imageSourceDomain: varchar("imageSourceDomain", { length: 255 }),
    imageSelectedBy: int("imageSelectedBy"),
    imageSelectedAt: timestamp("imageSelectedAt"),
    included: int("included").notNull().default(1),
    staffNote: text("staffNote"),
    suggestionSource: varchar("suggestionSource", { length: 64 }),
    manualEdit: int("manualEdit").notNull().default(0),
    issued: int("issued").notNull().default(0),
    issuedAt: timestamp("issuedAt"),
    originalWarrantyHolderCustomerId: int("originalWarrantyHolderCustomerId"),
    originalPurchaserNameSnapshot: varchar("originalPurchaserNameSnapshot", {
      length: 255,
    }),
    originalInvoiceNumberSnapshot: varchar("originalInvoiceNumberSnapshot", {
      length: 100,
    }),
    originalPurchaseContactSnapshot: varchar(
      "originalPurchaseContactSnapshot",
      { length: 320 }
    ),
    originalSiteSnapshot: text("originalSiteSnapshot"),
    issuedBrand: varchar("issuedBrand", { length: 255 }),
    issuedModel: varchar("issuedModel", { length: 255 }),
    issuedProductName: varchar("issuedProductName", { length: 255 }),
    issuedFinish: varchar("issuedFinish", { length: 255 }),
    issuedSerialNumber: varchar("issuedSerialNumber", { length: 255 }),
    issuedWarrantyStartDate: timestamp("issuedWarrantyStartDate"),
    issuedWarrantyDurationMonths: int("issuedWarrantyDurationMonths"),
    issuedWarrantyExpiryDate: timestamp("issuedWarrantyExpiryDate"),
    issuedManufacturerWarrantyLabel: varchar(
      "issuedManufacturerWarrantyLabel",
      { length: 255 }
    ),
    issuedManufacturerTermsUrl: text("issuedManufacturerTermsUrl"),
    issuedManufacturerTermsDocumentKey: varchar(
      "issuedManufacturerTermsDocumentKey",
      { length: 512 }
    ),
    issuedImageStorageKey: varchar("issuedImageStorageKey", { length: 512 }),
    issuedImageUrl: text("issuedImageUrl"),
    issuedImageAlt: varchar("issuedImageAlt", { length: 512 }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    cardIdx: index("warranty_items_card_idx").on(t.warrantyCardId, t.included),
    productIdx: index("warranty_items_product_idx").on(t.productId),
    serialIdx: index("warranty_items_serial_idx").on(t.serialNumberNormalized),
    serialNormUq: uniqueIndex("warranty_items_serial_norm_uid").on(
      t.serialNumberNormalized
    ),
    holderIdx: index("warranty_items_holder_idx").on(
      t.originalWarrantyHolderCustomerId,
      t.issued
    ),
    sourceLineIdx: index("warranty_items_source_line_idx").on(
      t.sourceCommercialLineKey
    ),
  })
);

export type WarrantyItem = typeof warrantyItems.$inferSelect;
export type InsertWarrantyItem = typeof warrantyItems.$inferInsert;

/**
 * Cached outcome for manufacturer warranty-period verification.
 * Stores either verified manufacturer evidence or an explicit unverified fallback.
 */
export const warrantyPeriodResearchCache = mysqlTable(
  "warranty_period_research_cache",
  {
    id: int("id").autoincrement().primaryKey(),
    productSignature: varchar("productSignature", { length: 191 }).notNull(),
    market: varchar("market", { length: 32 }).notNull().default("sg"),
    sourceType: varchar("sourceType", { length: 64 }).notNull(),
    warrantyDurationMonths: int("warrantyDurationMonths"),
    manufacturerWarrantyLabel: varchar("manufacturerWarrantyLabel", {
      length: 255,
    }).notNull(),
    evidenceUrl: text("evidenceUrl"),
    evidenceJson: json("evidenceJson").$type<Record<string, unknown> | null>(),
    evidenceRetrievedAt: timestamp("evidenceRetrievedAt"),
    matchedBrand: varchar("matchedBrand", { length: 255 }),
    matchedModel: varchar("matchedModel", { length: 255 }),
    confidence: varchar("confidence", { length: 16 }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    signatureMarketUq: uniqueIndex(
      "warranty_period_research_cache_signature_market_uq"
    ).on(t.productSignature, t.market),
    updatedIdx: index("warranty_period_research_cache_updated_idx").on(
      t.updatedAt
    ),
  })
);

export type WarrantyPeriodResearchCache =
  typeof warrantyPeriodResearchCache.$inferSelect;
export type InsertWarrantyPeriodResearchCache =
  typeof warrantyPeriodResearchCache.$inferInsert;

export const warrantyAuditEvents = mysqlTable(
  "warranty_audit_events",
  {
    id: int("id").autoincrement().primaryKey(),
    warrantyCardId: int("warrantyCardId").notNull(),
    warrantyItemId: int("warrantyItemId"),
    action: varchar("action", { length: 64 }).notNull(),
    actorUserId: int("actorUserId"),
    actorRole: varchar("actorRole", { length: 32 }),
    previousValueJson: json("previousValueJson").$type<Record<
      string,
      unknown
    > | null>(),
    newValueJson: json("newValueJson").$type<Record<string, unknown> | null>(),
    reason: text("reason"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    cardIdx: index("warranty_audit_card_idx").on(t.warrantyCardId, t.createdAt),
    actionIdx: index("warranty_audit_action_idx").on(t.action, t.createdAt),
  })
);

export type WarrantyAuditEvent = typeof warrantyAuditEvents.$inferSelect;
export type InsertWarrantyAuditEvent = typeof warrantyAuditEvents.$inferInsert;

/** Idempotent customer Warranty Ready WhatsApp occurrence (one per issued warranty card). */
export const customerWarrantyReadyWhatsappOccurrences = mysqlTable(
  "customer_warranty_ready_whatsapp_occurrences",
  {
    id: int("id").autoincrement().primaryKey(),
    occurrenceKey: varchar("occurrenceKey", { length: 191 }).notNull(),
    canonicalCustomerId: int("canonicalCustomerId").notNull(),
    warrantyCardId: int("warrantyCardId").notNull(),
    status: varchar("status", { length: 64 }).notNull().default("queued"),
    channel: varchar("channel", { length: 24 }).notNull().default("whatsapp"),
    contentSid: varchar("contentSid", { length: 64 }).notNull(),
    recipientFingerprint: varchar("recipientFingerprint", { length: 128 }),
    recipientMasked: varchar("recipientMasked", { length: 64 }),
    providerMessageSid: varchar("providerMessageSid", { length: 34 }),
    deliveryLogId: int("deliveryLogId"),
    suppressionReason: varchar("suppressionReason", { length: 128 }),
    failureReason: varchar("failureReason", { length: 255 }),
    leaseToken: varchar("leaseToken", { length: 64 }),
    leaseExpiresAt: timestamp("leaseExpiresAt"),
    attemptedAt: timestamp("attemptedAt"),
    sentAt: timestamp("sentAt"),
    suppressedAt: timestamp("suppressedAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    keyUq: uniqueIndex("cwr_wa_occ_key_uq").on(t.occurrenceKey),
    customerStatusIdx: index("cwr_wa_occ_customer_status_idx").on(
      t.canonicalCustomerId,
      t.status
    ),
    statusLeaseIdx: index("cwr_wa_occ_status_lease_idx").on(
      t.status,
      t.leaseExpiresAt
    ),
  })
);
export type CustomerWarrantyReadyWhatsappOccurrence =
  typeof customerWarrantyReadyWhatsappOccurrences.$inferSelect;

/** Canonical-customer 72h successful-dispatch cooldown for Warranty Ready WhatsApp. */
export const customerWarrantyReadyWhatsappCooldowns = mysqlTable(
  "customer_warranty_ready_whatsapp_cooldowns",
  {
    canonicalCustomerId: int("canonicalCustomerId").primaryKey(),
    lastSuccessfulSentAt: timestamp("lastSuccessfulSentAt"),
    lastSuccessfulOccurrenceId: int("lastSuccessfulOccurrenceId"),
    sendingLeaseToken: varchar("sendingLeaseToken", { length: 64 }),
    sendingLeaseExpiresAt: timestamp("sendingLeaseExpiresAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  }
);
export type CustomerWarrantyReadyWhatsappCooldown =
  typeof customerWarrantyReadyWhatsappCooldowns.$inferSelect;

/** Durable Admin follow-up when Warranty Ready WhatsApp cannot be sent (phone/access). */
export const customerWarrantyReadyAdminActions = mysqlTable(
  "customer_warranty_ready_admin_actions",
  {
    id: int("id").autoincrement().primaryKey(),
    idempotencyKey: varchar("idempotencyKey", { length: 191 }).notNull(),
    canonicalCustomerId: int("canonicalCustomerId").notNull(),
    warrantyCardId: int("warrantyCardId"),
    reasonCode: varchar("reasonCode", { length: 64 }).notNull(),
    message: varchar("message", { length: 512 }).notNull(),
    deepLink: varchar("deepLink", { length: 512 }),
    lifecycle: varchar("lifecycle", { length: 32 }).notNull().default("open"),
    openedAt: timestamp("openedAt").defaultNow().notNull(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    keyUq: uniqueIndex("cwr_wa_admin_key_uq").on(t.idempotencyKey),
    customerIdx: index("cwr_wa_admin_customer_idx").on(
      t.canonicalCustomerId,
      t.lifecycle
    ),
  })
);
export type CustomerWarrantyReadyAdminAction =
  typeof customerWarrantyReadyAdminActions.$inferSelect;

/** Exclusive Singapore-day lease for scheduled jobs (one owner per job+day). */
export const schedulerDayLeases = mysqlTable(
  "scheduler_day_leases",
  {
    id: int("id").autoincrement().primaryKey(),
    jobName: varchar("jobName", { length: 64 }).notNull(),
    singaporeDay: varchar("singaporeDay", { length: 10 }).notNull(),
    leaseOwner: varchar("leaseOwner", { length: 128 }).notNull(),
    leaseToken: varchar("leaseToken", { length: 64 }).notNull(),
    leaseExpiresAt: timestamp("leaseExpiresAt").notNull(),
    status: mysqlEnum("status", ["claimed", "completed", "failed"])
      .notNull()
      .default("claimed"),
    claimedAt: timestamp("claimedAt").defaultNow().notNull(),
    completedAt: timestamp("completedAt"),
    lastError: text("lastError"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    jobDayUq: uniqueIndex("scheduler_day_leases_job_day_uq").on(
      t.jobName,
      t.singaporeDay
    ),
    statusExpIdx: index("scheduler_day_leases_status_exp_idx").on(
      t.status,
      t.leaseExpiresAt
    ),
  })
);

export type SchedulerDayLease = typeof schedulerDayLeases.$inferSelect;
export type InsertSchedulerDayLease = typeof schedulerDayLeases.$inferInsert;

/**
 * Idempotent invoice reminder occurrence claims.
 * Identity: invoice + reminder stage + channel policy (not per-transport dual-send).
 */
export const invoiceReminderOccurrences = mysqlTable(
  "invoice_reminder_occurrences",
  {
    id: int("id").autoincrement().primaryKey(),
    occurrenceKey: varchar("occurrenceKey", { length: 191 }).notNull(),
    invoiceKind: mysqlEnum("invoiceKind", ["deal", "svr", "portal"]).notNull(),
    invoiceId: int("invoiceId").notNull(),
    customerId: int("customerId"),
    reminderStage: varchar("reminderStage", { length: 64 }).notNull(),
    channelPolicy: varchar("channelPolicy", { length: 64 }).notNull(),
    /** Non-null only for staff-confirmed reminders; prevents concurrent same-channel occurrences. */
    staffChannelKey: varchar("staffChannelKey", { length: 191 }),
    status: mysqlEnum("status", [
      "claimed",
      "sent",
      "skipped",
      "failed",
      "reconciled",
    ])
      .notNull()
      .default("claimed"),
    attemptedChannel: varchar("attemptedChannel", { length: 16 }),
    deliveryLogId: int("deliveryLogId"),
    skipReason: varchar("skipReason", { length: 128 }),
    errorMessage: text("errorMessage"),
    clientOccurrenceId: varchar("clientOccurrenceId", { length: 64 }),
    leaseToken: varchar("leaseToken", { length: 64 }),
    leaseExpiresAt: timestamp("leaseExpiresAt"),
    initiatedByUserId: int("initiatedByUserId"),
    initiatedByRole: varchar("initiatedByRole", { length: 32 }),
    initiatedByName: varchar("initiatedByName", { length: 191 }),
    initiationReason: varchar("initiationReason", { length: 128 }),
    claimedAt: timestamp("claimedAt").defaultNow().notNull(),
    completedAt: timestamp("completedAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    keyUq: uniqueIndex("invoice_reminder_occurrences_key_uq").on(
      t.occurrenceKey
    ),
    staffChannelUq: uniqueIndex(
      "invoice_reminder_occurrences_staff_channel_uq"
    ).on(t.staffChannelKey),
    invoiceIdx: index("invoice_reminder_occurrences_invoice_idx").on(
      t.invoiceKind,
      t.invoiceId,
      t.reminderStage
    ),
  })
);

export type InvoiceReminderOccurrence =
  typeof invoiceReminderOccurrences.$inferSelect;
export type InsertInvoiceReminderOccurrence =
  typeof invoiceReminderOccurrences.$inferInsert;

/**
 * Internal-only payment reconciliation queue.
 * Suspicious provider/accounting evidence vs Portal invoice/lifecycle state.
 * Staff confirm payment through the normal payment path — never auto-mark paid.
 */
export const paymentReconciliationCases = mysqlTable(
  "payment_reconciliation_cases",
  {
    id: int("id").autoincrement().primaryKey(),
    identityKey: varchar("identityKey", { length: 191 }).notNull(),
    mismatchReason: varchar("mismatchReason", { length: 64 }).notNull(),
    status: mysqlEnum("status", ["open", "in_review", "resolved", "dismissed"])
      .notNull()
      .default("open"),
    customerId: int("customerId"),
    proposalId: int("proposalId"),
    dealInvoiceId: int("dealInvoiceId"),
    siteVisitBillingId: int("siteVisitBillingId"),
    amount: decimal("amount", { precision: 12, scale: 2 }),
    evidenceJson: json("evidenceJson").$type<Record<string, unknown> | null>(),
    lastDetectedAt: timestamp("lastDetectedAt").defaultNow().notNull(),
    resolvedAt: timestamp("resolvedAt"),
    resolvedByUserId: int("resolvedByUserId"),
    resolutionNote: text("resolutionNote"),
    /** Increments on create and each reopen cycle for alert dedupe. */
    alertCycle: int("alertCycle").notNull().default(1),
    /** Unique key `{caseId}:cycle:{n}` for one notification per open/reopen cycle. */
    alertNotificationKey: varchar("alertNotificationKey", { length: 160 }),
    alertAcknowledgedAt: timestamp("alertAcknowledgedAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    identityUq: uniqueIndex("payment_reconciliation_cases_identity_uq").on(
      t.identityKey
    ),
    statusIdx: index("payment_reconciliation_cases_status_idx").on(
      t.status,
      t.lastDetectedAt
    ),
    invoiceIdx: index("payment_reconciliation_cases_invoice_idx").on(
      t.dealInvoiceId
    ),
    alertKeyUq: uniqueIndex("payment_recon_alert_key_uid").on(
      t.alertNotificationKey
    ),
  })
);

export type PaymentReconciliationCase =
  typeof paymentReconciliationCases.$inferSelect;
export type InsertPaymentReconciliationCase =
  typeof paymentReconciliationCases.$inferInsert;

/**
 * Portal-authoritative outbound Xero cancellation synchronization.
 * One-way: Portal cancel → Xero void. Never imports Xero-only voids into Portal.
 */
export const invoiceCancellationSync = mysqlTable(
  "invoice_cancellation_sync",
  {
    id: int("id").autoincrement().primaryKey(),
    invoiceDomain: varchar("invoiceDomain", { length: 16 }).notNull(),
    invoiceId: int("invoiceId").notNull(),
    xeroInvoiceId: varchar("xeroInvoiceId", { length: 64 }),
    xeroInvoiceNumber: varchar("xeroInvoiceNumber", { length: 64 }),
    reason: varchar("reason", { length: 255 }),
    initiatedByUserId: int("initiatedByUserId"),
    initiatedByName: varchar("initiatedByName", { length: 255 }),
    initiatedAt: timestamp("initiatedAt").defaultNow().notNull(),
    syncState: varchar("syncState", { length: 48 }).notNull(),
    attemptCount: int("attemptCount").notNull().default(0),
    lastAttemptAt: timestamp("lastAttemptAt"),
    confirmedXeroStatus: varchar("confirmedXeroStatus", { length: 40 }),
    xeroConfirmedAt: timestamp("xeroConfirmedAt"),
    errorCode: varchar("errorCode", { length: 64 }),
    errorMessage: text("errorMessage"),
    idempotencyKey: varchar("idempotencyKey", { length: 128 }).notNull(),
    groupBillingRole: varchar("groupBillingRole", { length: 40 }),
    resolvedAt: timestamp("resolvedAt"),
    auditJson: json("auditJson").$type<Array<Record<string, unknown>> | null>(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    idemUq: uniqueIndex("invoice_cancel_sync_idem_uid").on(t.idempotencyKey),
    domainIdUq: uniqueIndex("invoice_cancel_sync_domain_id_uid").on(
      t.invoiceDomain,
      t.invoiceId
    ),
    stateIdx: index("invoice_cancel_sync_state_idx").on(t.syncState),
    xeroIdx: index("invoice_cancel_sync_xero_idx").on(t.xeroInvoiceId),
  })
);

export type InvoiceCancellationSync =
  typeof invoiceCancellationSync.$inferSelect;
export type InsertInvoiceCancellationSync =
  typeof invoiceCancellationSync.$inferInsert;

/**
 * Durable customer-level follow-up preferences (relationship Cleo).
 * One row per customer — doNotContact suppresses CUSTOMER_RELATIONSHIP_FOLLOW_UP.
 * suppressUntil (YYYY-MM-DD SG) temporarily suppresses until that date (inclusive).
 */
export const customerFollowUpPreferences = mysqlTable(
  "customer_follow_up_preferences",
  {
    id: int("id").autoincrement().primaryKey(),
    customerId: int("customerId").notNull(),
    doNotContact: boolean("doNotContact").notNull().default(false),
    reason: varchar("reason", { length: 128 }),
    /** SG calendar date YYYY-MM-DD; suppress relationship follow-up until this day inclusive. */
    suppressUntil: varchar("suppressUntil", { length: 10 }),
    updatedByUserId: int("updatedByUserId"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    customerUq: uniqueIndex("customer_follow_up_preferences_customer_uq").on(
      t.customerId
    ),
  })
);

export type CustomerFollowUpPreference =
  typeof customerFollowUpPreferences.$inferSelect;
export type InsertCustomerFollowUpPreference =
  typeof customerFollowUpPreferences.$inferInsert;

// ─── Procurement customer delivery + MuRho DO (migration 0183) ──────────────────
//
// Supplier receipt (procurement_goods_receipts) ≠ customer delivery. These tables
// record goods delivered *to the customer*, evidenced by an uploaded Delivery
// Order (DO) original, and the downstream warranty-card handoffs per checked serial.

/** Uploaded DO originals with deterministic extraction lifecycle. */
export const procurementCustomerDeliveryDocuments = mysqlTable(
  "procurement_customer_delivery_documents",
  {
    id: int("id").autoincrement().primaryKey(),
    projectProcurementId: int("projectProcurementId").notNull(),
    proposalId: int("proposalId").notNull(),
    lifecycle: mysqlEnum("lifecycle", [
      "uploaded",
      "extracting",
      "needs_review",
      "confirmed",
      "failed_retryable",
      "failed_review_required",
      "superseded",
    ])
      .notNull()
      .default("uploaded"),
    filename: varchar("filename", { length: 512 }).notNull(),
    mimeType: varchar("mimeType", { length: 128 }).notNull(),
    byteSize: int("byteSize").notNull(),
    checksumSha256: varchar("checksumSha256", { length: 64 }).notNull(),
    storageKey: varchar("storageKey", { length: 512 }).notNull(),
    uploadedByUserId: int("uploadedByUserId"),
    extractionMethod: mysqlEnum("extractionMethod", [
      "excel_deterministic",
      "pdf_text_llm",
      "image_llm",
      "image_manual",
      "manual",
      "none",
    ])
      .notNull()
      .default("none"),
    extractionWarningsJson: json("extractionWarningsJson").$type<
      string[] | null
    >(),
    extractedPayloadJson: json("extractedPayloadJson").$type<Record<
      string,
      unknown
    > | null>(),
    errorCode: varchar("errorCode", { length: 64 }),
    errorMessageSafe: varchar("errorMessageSafe", { length: 512 }),
    supersededByDocumentId: int("supersededByDocumentId"),
    confirmedDeliveryId: int("confirmedDeliveryId"),
    extractionAttemptCount: int("extractionAttemptCount").notNull().default(0),
    extractionLeaseToken: varchar("extractionLeaseToken", { length: 64 }),
    extractionLeaseExpiresAt: timestamp("extractionLeaseExpiresAt"),
    extractionEnqueuedAt: timestamp("extractionEnqueuedAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    checksumUq: uniqueIndex("procurement_cust_delivery_docs_checksum_uq").on(
      t.projectProcurementId,
      t.checksumSha256
    ),
    procurementLifecycleIdx: index(
      "procurement_cust_delivery_docs_procurement_lifecycle_idx"
    ).on(t.projectProcurementId, t.lifecycle),
    proposalIdx: index("procurement_cust_delivery_docs_proposal_idx").on(
      t.proposalId,
      t.createdAt
    ),
  })
);

export const procurementCustomerDeliveryDocumentEvents = mysqlTable(
  "procurement_customer_delivery_document_events",
  {
    id: int("id").autoincrement().primaryKey(),
    documentId: int("documentId").notNull(),
    projectProcurementId: int("projectProcurementId").notNull(),
    proposalId: int("proposalId").notNull(),
    eventType: varchar("eventType", { length: 64 }).notNull(),
    actorUserId: int("actorUserId"),
    actorName: varchar("actorName", { length: 255 }),
    actorRole: varchar("actorRole", { length: 64 }),
    previousLifecycle: varchar("previousLifecycle", { length: 32 }),
    newLifecycle: varchar("newLifecycle", { length: 32 }),
    metadataJson: json("metadataJson").$type<Record<string, unknown> | null>(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    docCreatedIdx: index(
      "procurement_cust_delivery_doc_events_doc_created_idx"
    ).on(t.documentId, t.createdAt),
  })
);

/** Delivery drafts + confirmed customer deliveries. */
export const procurementCustomerDeliveries = mysqlTable(
  "procurement_customer_deliveries",
  {
    id: int("id").autoincrement().primaryKey(),
    projectProcurementId: int("projectProcurementId").notNull(),
    proposalId: int("proposalId").notNull(),
    source: mysqlEnum("source", ["do_upload", "manual"])
      .notNull()
      .default("manual"),
    documentId: int("documentId"),
    lifecycle: mysqlEnum("lifecycle", [
      "draft",
      "needs_review",
      "ready_to_confirm",
      "confirming",
      "confirmed",
      "reversed",
      "superseded",
    ])
      .notNull()
      .default("draft"),
    deliveryDate: date("deliveryDate"),
    reference: varchar("reference", { length: 255 }),
    staffNote: text("staffNote"),
    paymentGateSnapshotJson: json("paymentGateSnapshotJson").$type<Record<
      string,
      unknown
    > | null>(),
    customerId: int("customerId"),
    customerSiteId: int("customerSiteId"),
    confirmedAt: timestamp("confirmedAt"),
    confirmedByUserId: int("confirmedByUserId"),
    reversedAt: timestamp("reversedAt"),
    reverseReason: text("reverseReason"),
    occurrenceKey: varchar("occurrenceKey", { length: 191 }).notNull(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    occurrenceUq: uniqueIndex("procurement_cust_deliveries_occurrence_uq").on(
      t.occurrenceKey
    ),
    procurementLifecycleIdx: index(
      "procurement_cust_deliveries_procurement_lifecycle_idx"
    ).on(t.projectProcurementId, t.lifecycle),
    documentIdx: index("procurement_cust_deliveries_document_idx").on(
      t.documentId
    ),
    customerSiteIdx: index("procurement_cust_deliveries_customer_site_idx").on(
      t.customerId,
      t.customerSiteId
    ),
  })
);

/** Per-BOM-line delivered quantities; immutable after the parent delivery confirms. */
export const procurementCustomerDeliveryLines = mysqlTable(
  "procurement_customer_delivery_lines",
  {
    id: int("id").autoincrement().primaryKey(),
    deliveryId: int("deliveryId").notNull(),
    pickListLineId: int("pickListLineId"),
    approvalGroup: varchar("approvalGroup", { length: 64 })
      .notNull()
      .default("original"),
    matchMethod: mysqlEnum("matchMethod", [
      "stable_line_key",
      "catalog_product_id",
      "exact_sku",
      "exact_brand_model",
      "fuzzy_suggestion",
      "manual",
      "unmatched",
    ])
      .notNull()
      .default("unmatched"),
    matchConfidence: decimal("matchConfidence", { precision: 5, scale: 4 })
      .notNull()
      .default("0"),
    matchConfirmed: boolean("matchConfirmed").notNull().default(false),
    isAdditionalNonBom: boolean("isAdditionalNonBom").notNull().default(false),
    stableLineKeySnapshot: varchar("stableLineKeySnapshot", { length: 191 }),
    productIdentityKeySnapshot: varchar("productIdentityKeySnapshot", {
      length: 191,
    }),
    catalogProductIdSnapshot: int("catalogProductIdSnapshot"),
    brandSnapshot: varchar("brandSnapshot", { length: 255 }),
    modelSnapshot: varchar("modelSnapshot", { length: 255 }),
    finishSnapshot: varchar("finishSnapshot", { length: 255 }),
    skuSnapshot: varchar("skuSnapshot", { length: 255 }),
    descriptionSnapshot: text("descriptionSnapshot"),
    quantityDelivered: decimal("quantityDelivered", { precision: 12, scale: 3 })
      .notNull()
      .default("0"),
    quantityRequiredSnapshot: decimal("quantityRequiredSnapshot", {
      precision: 12,
      scale: 3,
    }),
    /** Selected/authoritative UOM for this draft line (nullable historically). */
    uom: varchar("uom", { length: 64 }),
    /** Immutable UOM copied at confirm / draft freeze. */
    uomSnapshot: varchar("uomSnapshot", { length: 64 }),
    uomSource: mysqlEnum("uomSource", [
      "bom",
      "product_profile",
      "manual_selection",
      "historical_null",
    ]),
    actualPartNumber: varchar("actualPartNumber", { length: 255 }),
    unresolvedReason: varchar("unresolvedReason", { length: 128 }),
    sortOrder: int("sortOrder").notNull().default(0),
    metadataJson: json("metadataJson").$type<Record<string, unknown> | null>(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    deliveryIdx: index("procurement_cust_delivery_lines_delivery_idx").on(
      t.deliveryId,
      t.sortOrder
    ),
    pickLineIdx: index("procurement_cust_delivery_lines_pick_line_idx").on(
      t.pickListLineId
    ),
  })
);

export const procurementCustomerDeliverySerials = mysqlTable(
  "procurement_customer_delivery_serials",
  {
    id: int("id").autoincrement().primaryKey(),
    deliveryLineId: int("deliveryLineId").notNull(),
    deliveryId: int("deliveryId").notNull(),
    serialNumber: varchar("serialNumber", { length: 255 }).notNull(),
    serialNormalized: varchar("serialNormalized", { length: 255 }).notNull(),
    createWarrantySelected: boolean("createWarrantySelected")
      .notNull()
      .default(true),
    warrantyHandoffId: int("warrantyHandoffId"),
    currentlyAtSite: boolean("currentlyAtSite").notNull().default(true),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    lineNormUq: uniqueIndex(
      "procurement_cust_delivery_serials_line_norm_uq"
    ).on(t.deliveryLineId, t.serialNormalized),
    deliveryIdx: index("procurement_cust_delivery_serials_delivery_idx").on(
      t.deliveryId
    ),
    normIdx: index("procurement_cust_delivery_serials_norm_idx").on(
      t.serialNormalized
    ),
  })
);

export const procurementCustomerDeliveryEvents = mysqlTable(
  "procurement_customer_delivery_events",
  {
    id: int("id").autoincrement().primaryKey(),
    deliveryId: int("deliveryId").notNull(),
    deliveryLineId: int("deliveryLineId"),
    projectProcurementId: int("projectProcurementId").notNull(),
    proposalId: int("proposalId").notNull(),
    eventType: varchar("eventType", { length: 64 }).notNull(),
    actorUserId: int("actorUserId"),
    actorName: varchar("actorName", { length: 255 }),
    actorRole: varchar("actorRole", { length: 64 }),
    previousLifecycle: varchar("previousLifecycle", { length: 32 }),
    newLifecycle: varchar("newLifecycle", { length: 32 }),
    reason: text("reason"),
    metadataJson: json("metadataJson").$type<Record<string, unknown> | null>(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    deliveryCreatedIdx: index(
      "procurement_cust_delivery_events_delivery_created_idx"
    ).on(t.deliveryId, t.createdAt),
  })
);

export const DELIVERED_ITEM_ADJUSTMENT_TYPE_VALUES = [
  "quantity_correction",
  "quantity_returned",
  "quantity_added",
  "description_correction",
  "uom_correction",
  "serial_returned",
  "serial_correction",
] as const;

/** Append-only current-position adjustments. Never rewrite confirmed original lines. */
export const procurementCustomerDeliveryAdjustments = mysqlTable(
  "procurement_customer_delivery_adjustments",
  {
    id: int("id").autoincrement().primaryKey(),
    occurrenceKey: varchar("occurrenceKey", { length: 191 }).notNull(),
    deliveryId: int("deliveryId").notNull(),
    deliveryLineId: int("deliveryLineId").notNull(),
    customerId: int("customerId").notNull(),
    customerSiteId: int("customerSiteId").notNull(),
    adjustmentType: mysqlEnum("adjustmentType", [
      ...DELIVERED_ITEM_ADJUSTMENT_TYPE_VALUES,
    ]).notNull(),
    beforeValueJson: json("beforeValueJson")
      .$type<Record<string, unknown>>()
      .notNull(),
    requestedDeltaJson: json("requestedDeltaJson")
      .$type<Record<string, unknown>>()
      .notNull(),
    afterValueJson: json("afterValueJson")
      .$type<Record<string, unknown>>()
      .notNull(),
    reason: varchar("reason", { length: 500 }).notNull(),
    actorUserId: int("actorUserId").notNull(),
    actorNameSnapshot: varchar("actorNameSnapshot", { length: 255 }).notNull(),
    actorRoleSnapshot: varchar("actorRoleSnapshot", { length: 64 }).notNull(),
    singaporeAt: timestamp("singaporeAt").notNull(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    occurrenceUq: uniqueIndex("procurement_cust_delivery_adj_occurrence_uq").on(
      t.occurrenceKey
    ),
    lineIdx: index("procurement_cust_delivery_adj_line_idx").on(
      t.deliveryLineId,
      t.createdAt
    ),
    customerSiteIdx: index(
      "procurement_cust_delivery_adj_customer_site_idx"
    ).on(t.customerId, t.customerSiteId),
  })
);
export type ProcurementCustomerDeliveryAdjustment =
  typeof procurementCustomerDeliveryAdjustments.$inferSelect;

/** Idempotent warranty-card handoff, one row per checked serial on a confirmed delivery. */
export const procurementDeliveryWarrantyHandoffs = mysqlTable(
  "procurement_delivery_warranty_handoffs",
  {
    id: int("id").autoincrement().primaryKey(),
    deliveryId: int("deliveryId").notNull(),
    deliveryLineId: int("deliveryLineId").notNull(),
    deliverySerialId: int("deliverySerialId"),
    projectProcurementId: int("projectProcurementId").notNull(),
    proposalId: int("proposalId").notNull(),
    sourceKey: varchar("sourceKey", { length: 191 }).notNull(),
    lifecycle: mysqlEnum("lifecycle", [
      "pending",
      "creating",
      "draft_created",
      "skipped",
      "failed_retryable",
      "review_required",
      "superseded",
      "voided",
    ])
      .notNull()
      .default("pending"),
    warrantyCardId: int("warrantyCardId"),
    warrantyItemId: int("warrantyItemId"),
    customerId: int("customerId"),
    brandSnapshot: varchar("brandSnapshot", { length: 255 }),
    modelSnapshot: varchar("modelSnapshot", { length: 255 }),
    finishSnapshot: varchar("finishSnapshot", { length: 255 }),
    productNameSnapshot: varchar("productNameSnapshot", { length: 512 }),
    productImageUrlSnapshot: text("productImageUrlSnapshot"),
    productImageSourceType: varchar("productImageSourceType", { length: 32 }),
    serialNumber: varchar("serialNumber", { length: 255 }).notNull(),
    serialNormalized: varchar("serialNormalized", { length: 255 }).notNull(),
    deliveryDate: date("deliveryDate"),
    warrantyPeriodSource: varchar("warrantyPeriodSource", { length: 64 }),
    warrantyPeriodEvidenceJson: json("warrantyPeriodEvidenceJson").$type<Record<
      string,
      unknown
    > | null>(),
    attemptCount: int("attemptCount").notNull().default(0),
    lastError: text("lastError"),
    nextRetryAt: timestamp("nextRetryAt"),
    leaseToken: varchar("leaseToken", { length: 64 }),
    leaseExpiresAt: timestamp("leaseExpiresAt"),
    deepLink: varchar("deepLink", { length: 512 }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    sourceUq: uniqueIndex(
      "procurement_delivery_warranty_handoffs_source_uq"
    ).on(t.sourceKey),
    lifecycleRetryIdx: index(
      "procurement_delivery_warranty_handoffs_lifecycle_retry_idx"
    ).on(t.lifecycle, t.nextRetryAt),
    lifecycleLeaseIdx: index(
      "procurement_delivery_warranty_handoffs_lifecycle_lease_idx"
    ).on(t.lifecycle, t.leaseExpiresAt),
    deliveryIdx: index(
      "procurement_delivery_warranty_handoffs_delivery_idx"
    ).on(t.deliveryId),
  })
);

/**
 * Durable Admin review action for warranties that defaulted to 12 months because
 * an authoritative period could not be verified.
 */
export const procurementWarrantyPeriodReviewActions = mysqlTable(
  "procurement_warranty_period_review_actions",
  {
    id: int("id").autoincrement().primaryKey(),
    idempotencyKey: varchar("idempotencyKey", { length: 191 }).notNull(),
    projectProcurementId: int("projectProcurementId").notNull(),
    proposalId: int("proposalId").notNull(),
    handoffId: int("handoffId").notNull(),
    warrantyCardId: int("warrantyCardId"),
    warrantyItemId: int("warrantyItemId"),
    deepLink: varchar("deepLink", { length: 512 }),
    message: varchar("message", { length: 512 }).notNull(),
    lifecycle: mysqlEnum("lifecycle", [
      "open",
      "acknowledged",
      "resolved",
      "cancelled",
    ])
      .notNull()
      .default("open"),
    openedAt: timestamp("openedAt").defaultNow().notNull(),
    acknowledgedAt: timestamp("acknowledgedAt"),
    acknowledgedByUserId: int("acknowledgedByUserId"),
    resolvedAt: timestamp("resolvedAt"),
    resolvedByUserId: int("resolvedByUserId"),
    resolvedReason: varchar("resolvedReason", { length: 255 }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    idempotencyUq: uniqueIndex(
      "procurement_warranty_period_review_actions_idem_uq"
    ).on(t.idempotencyKey),
    handoffUq: uniqueIndex(
      "procurement_warranty_period_review_actions_handoff_uq"
    ).on(t.handoffId),
    procurementLifecycleIdx: index(
      "procurement_warranty_period_review_actions_proc_lifecycle_idx"
    ).on(t.projectProcurementId, t.lifecycle),
  })
);

/** Learned "usually no warranty card" signal, keyed by a coarse product signature. */
export const procurementWarrantyProductDecisions = mysqlTable(
  "procurement_warranty_product_decisions",
  {
    id: int("id").autoincrement().primaryKey(),
    productSignature: varchar("productSignature", { length: 191 }).notNull(),
    selectedCount: int("selectedCount").notNull().default(0),
    skippedCount: int("skippedCount").notNull().default(0),
    lastDecisionAt: timestamp("lastDecisionAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    signatureUq: uniqueIndex(
      "procurement_warranty_product_decisions_signature_uq"
    ).on(t.productSignature),
  })
);

/** Non-BOM items that appeared on a customer delivery and need disposition. */
export const procurementAdditionalDeliveryItems = mysqlTable(
  "procurement_additional_delivery_items",
  {
    id: int("id").autoincrement().primaryKey(),
    deliveryId: int("deliveryId").notNull(),
    deliveryLineId: int("deliveryLineId"),
    projectProcurementId: int("projectProcurementId").notNull(),
    proposalId: int("proposalId").notNull(),
    svrAdditionalItemId: int("svrAdditionalItemId"),
    itemLabel: varchar("itemLabel", { length: 512 }).notNull(),
    quantity: decimal("quantity", { precision: 12, scale: 3 })
      .notNull()
      .default("0"),
    uom: varchar("uom", { length: 64 }),
    uomSnapshot: varchar("uomSnapshot", { length: 64 }),
    deliveryDate: date("deliveryDate"),
    sourceRequester: varchar("sourceRequester", { length: 255 }),
    valueAmount: decimal("valueAmount", { precision: 14, scale: 2 }),
    status: mysqlEnum("status", [
      "unresolved",
      "disposed_accepted",
      "disposed_rejected",
      "disposed_pending_allowance",
    ])
      .notNull()
      .default("unresolved"),
    dispositionNote: text("dispositionNote"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    procurementStatusIdx: index(
      "procurement_additional_delivery_items_procurement_status_idx"
    ).on(t.projectProcurementId, t.status),
    deliveryIdx: index("procurement_additional_delivery_items_delivery_idx").on(
      t.deliveryId
    ),
    svrItemUq: uniqueIndex(
      "procurement_additional_delivery_items_svr_item_uq"
    ).on(t.svrAdditionalItemId),
  })
);

/** Durable safe-issue diagnostic reports; site-wide reusable, wired only for procurement/DO callers. */
export const procurementSafeIssueReports = mysqlTable(
  "procurement_safe_issue_reports",
  {
    id: int("id").autoincrement().primaryKey(),
    reportUuid: varchar("reportUuid", { length: 64 }).notNull(),
    route: varchar("route", { length: 191 }).notNull(),
    projectProcurementId: int("projectProcurementId"),
    inventoryRequestId: int("inventoryRequestId"),
    proposalId: int("proposalId"),
    documentId: int("documentId"),
    workflowStage: varchar("workflowStage", { length: 64 }).notNull(),
    safeErrorCode: varchar("safeErrorCode", { length: 64 }).notNull(),
    operation: varchar("operation", { length: 128 }).notNull(),
    safeCountsJson: json("safeCountsJson").$type<Record<
      string,
      unknown
    > | null>(),
    buildCommit: varchar("buildCommit", { length: 64 }),
    reporterUserId: int("reporterUserId"),
    redactedSummary: text("redactedSummary"),
    deepLink: varchar("deepLink", { length: 512 }),
    status: mysqlEnum("status", [
      "pending",
      "processing",
      "retry",
      "completed",
      "review_required",
    ])
      .notNull()
      .default("pending"),
    attemptCount: int("attemptCount").notNull().default(0),
    lastError: text("lastError"),
    nextRetryAt: timestamp("nextRetryAt"),
    leaseToken: varchar("leaseToken", { length: 64 }),
    leaseExpiresAt: timestamp("leaseExpiresAt"),
    providerMessageId: varchar("providerMessageId", { length: 255 }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    uuidUq: uniqueIndex("procurement_safe_issue_reports_uuid_uq").on(
      t.reportUuid
    ),
    statusRetryIdx: index("procurement_safe_issue_reports_status_retry_idx").on(
      t.status,
      t.nextRetryAt
    ),
    statusLeaseIdx: index("procurement_safe_issue_reports_status_lease_idx").on(
      t.status,
      t.leaseExpiresAt
    ),
    procurementIdx: index("procurement_safe_issue_reports_procurement_idx").on(
      t.projectProcurementId,
      t.createdAt
    ),
    inventoryRequestIdx: index(
      "procurement_safe_issue_reports_inventory_request_idx"
    ).on(t.inventoryRequestId, t.createdAt),
  })
);

export type ProcurementCustomerDeliveryDocument =
  typeof procurementCustomerDeliveryDocuments.$inferSelect;
export type InsertProcurementCustomerDeliveryDocument =
  typeof procurementCustomerDeliveryDocuments.$inferInsert;
export type ProcurementCustomerDeliveryDocumentEvent =
  typeof procurementCustomerDeliveryDocumentEvents.$inferSelect;
export type InsertProcurementCustomerDeliveryDocumentEvent =
  typeof procurementCustomerDeliveryDocumentEvents.$inferInsert;
export type ProcurementCustomerDelivery =
  typeof procurementCustomerDeliveries.$inferSelect;
export type InsertProcurementCustomerDelivery =
  typeof procurementCustomerDeliveries.$inferInsert;
export type ProcurementCustomerDeliveryLine =
  typeof procurementCustomerDeliveryLines.$inferSelect;
export type InsertProcurementCustomerDeliveryLine =
  typeof procurementCustomerDeliveryLines.$inferInsert;
export type ProcurementCustomerDeliverySerial =
  typeof procurementCustomerDeliverySerials.$inferSelect;
export type InsertProcurementCustomerDeliverySerial =
  typeof procurementCustomerDeliverySerials.$inferInsert;
export type ProcurementCustomerDeliveryEvent =
  typeof procurementCustomerDeliveryEvents.$inferSelect;
export type InsertProcurementCustomerDeliveryEvent =
  typeof procurementCustomerDeliveryEvents.$inferInsert;
export type ProcurementDeliveryWarrantyHandoff =
  typeof procurementDeliveryWarrantyHandoffs.$inferSelect;
export type InsertProcurementDeliveryWarrantyHandoff =
  typeof procurementDeliveryWarrantyHandoffs.$inferInsert;
export type ProcurementWarrantyProductDecision =
  typeof procurementWarrantyProductDecisions.$inferSelect;
export type InsertProcurementWarrantyProductDecision =
  typeof procurementWarrantyProductDecisions.$inferInsert;
export type ProcurementAdditionalDeliveryItem =
  typeof procurementAdditionalDeliveryItems.$inferSelect;
export type InsertProcurementAdditionalDeliveryItem =
  typeof procurementAdditionalDeliveryItems.$inferInsert;
export type ProcurementSafeIssueReport =
  typeof procurementSafeIssueReports.$inferSelect;
export type InsertProcurementSafeIssueReport =
  typeof procurementSafeIssueReports.$inferInsert;

// ─── Admin customer duplicate review / merge governance ─────────────────────

export const CUSTOMER_DUPLICATE_CANDIDATE_STATUSES = [
  "open",
  "snoozed",
  "not_duplicate",
  "merged",
  "review_required",
] as const;
export type CustomerDuplicateCandidateStatus =
  (typeof CUSTOMER_DUPLICATE_CANDIDATE_STATUSES)[number];

export const CUSTOMER_DUPLICATE_CONFIDENCE_LEVELS = [
  "high",
  "medium",
  "low",
] as const;
export type CustomerDuplicateConfidenceLevel =
  (typeof CUSTOMER_DUPLICATE_CONFIDENCE_LEVELS)[number];

export const CUSTOMER_NOT_DUPLICATE_REASONS = [
  "household_member",
  "similar_name",
  "shared_contact_but_separate",
  "business_and_personal",
  "verified_distinct_identity",
  "different_customers_phone_numbers_differ",
  "other",
] as const;
export type CustomerNotDuplicateReason =
  (typeof CUSTOMER_NOT_DUPLICATE_REASONS)[number];

export const CUSTOMER_MERGE_OCCURRENCE_STATUSES = [
  "previewed",
  "blocked",
  "in_progress",
  "completed",
  "failed",
  "recovery_requested",
] as const;
export type CustomerMergeOccurrenceStatus =
  (typeof CUSTOMER_MERGE_OCCURRENCE_STATUSES)[number];

/** Durable Cleo/admin duplicate-candidate queue. Never auto-merges. */
export const customerDuplicateCandidates = mysqlTable(
  "customer_duplicate_candidates",
  {
    id: int("id").autoincrement().primaryKey(),
    customerAId: int("customerAId").notNull(),
    customerBId: int("customerBId").notNull(),
    /** Order-independent pair key: `${minId}:${maxId}`. */
    pairKey: varchar("pairKey", { length: 64 }).notNull(),
    status: mysqlEnum("status", [...CUSTOMER_DUPLICATE_CANDIDATE_STATUSES])
      .notNull()
      .default("open"),
    confidenceScore: int("confidenceScore").notNull().default(0),
    confidenceLevel: mysqlEnum("confidenceLevel", [
      ...CUSTOMER_DUPLICATE_CONFIDENCE_LEVELS,
    ])
      .notNull()
      .default("low"),
    reasonsJson: json("reasonsJson").$type<string[]>().notNull(),
    fingerprintA: varchar("fingerprintA", { length: 128 }).notNull(),
    fingerprintB: varchar("fingerprintB", { length: 128 }).notNull(),
    firstDetectedAt: timestamp("firstDetectedAt").defaultNow().notNull(),
    lastDetectedAt: timestamp("lastDetectedAt").defaultNow().notNull(),
    nextReviewAt: timestamp("nextReviewAt"),
    dismissedByUserId: int("dismissedByUserId"),
    dismissedAt: timestamp("dismissedAt"),
    dismissalReason: mysqlEnum("dismissalReason", [
      ...CUSTOMER_NOT_DUPLICATE_REASONS,
    ]),
    dismissalNote: text("dismissalNote"),
    mergedOccurrenceId: int("mergedOccurrenceId"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    pairKeyUq: uniqueIndex("customer_duplicate_candidates_pair_key_uq").on(
      t.pairKey
    ),
    statusIdx: index("customer_duplicate_candidates_status_idx").on(
      t.status,
      t.nextReviewAt
    ),
    customerAIdx: index("customer_duplicate_candidates_a_idx").on(
      t.customerAId
    ),
    customerBIdx: index("customer_duplicate_candidates_b_idx").on(
      t.customerBId
    ),
  })
);
export type CustomerDuplicateCandidate =
  typeof customerDuplicateCandidates.$inferSelect;
export type InsertCustomerDuplicateCandidate =
  typeof customerDuplicateCandidates.$inferInsert;

/** One merge attempt / preview / execution audit row. */
export const customerMergeOccurrences = mysqlTable(
  "customer_merge_occurrences",
  {
    id: int("id").autoincrement().primaryKey(),
    canonicalCustomerId: int("canonicalCustomerId").notNull(),
    duplicateCustomerId: int("duplicateCustomerId").notNull(),
    actorUserId: int("actorUserId").notNull(),
    actorName: varchar("actorName", { length: 255 }).notNull(),
    actorRole: varchar("actorRole", { length: 64 }).notNull(),
    reason: text("reason"),
    previewHash: varchar("previewHash", { length: 128 }).notNull(),
    previewVersion: varchar("previewVersion", { length: 32 })
      .notNull()
      .default("v1"),
    beforeSnapshotsJson: json("beforeSnapshotsJson")
      .$type<Record<string, unknown>>()
      .notNull(),
    retainedFieldsJson: json("retainedFieldsJson")
      .$type<Record<string, unknown>>()
      .notNull(),
    dependencyActionsJson: json("dependencyActionsJson")
      .$type<unknown[]>()
      .notNull(),
    blockersCheckedJson: json("blockersCheckedJson")
      .$type<unknown[]>()
      .notNull(),
    status: mysqlEnum("status", [...CUSTOMER_MERGE_OCCURRENCE_STATUSES])
      .notNull()
      .default("previewed"),
    failureDetails: text("failureDetails"),
    recordsRepointed: int("recordsRepointed").notNull().default(0),
    recordsPreservedHistorical: int("recordsPreservedHistorical")
      .notNull()
      .default(0),
    customerCommunicationsSent: int("customerCommunicationsSent")
      .notNull()
      .default(0),
    startedAt: timestamp("startedAt"),
    completedAt: timestamp("completedAt"),
    failedAt: timestamp("failedAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    canonicalIdx: index("customer_merge_occurrences_canonical_idx").on(
      t.canonicalCustomerId
    ),
    duplicateIdx: index("customer_merge_occurrences_duplicate_idx").on(
      t.duplicateCustomerId
    ),
    statusIdx: index("customer_merge_occurrences_status_idx").on(
      t.status,
      t.createdAt
    ),
    previewHashIdx: index("customer_merge_occurrences_preview_hash_idx").on(
      t.previewHash
    ),
  })
);
export type CustomerMergeOccurrence =
  typeof customerMergeOccurrences.$inferSelect;
export type InsertCustomerMergeOccurrence =
  typeof customerMergeOccurrences.$inferInsert;

/** Per-table / per-domain actions taken during a merge. */
export const customerMergeRecordActions = mysqlTable(
  "customer_merge_record_actions",
  {
    id: int("id").autoincrement().primaryKey(),
    occurrenceId: int("occurrenceId").notNull(),
    tableName: varchar("tableName", { length: 128 }).notNull(),
    columnName: varchar("columnName", { length: 128 }).notNull(),
    classification: varchar("classification", { length: 64 }).notNull(),
    recordCount: int("recordCount").notNull().default(0),
    actionTaken: varchar("actionTaken", { length: 64 }).notNull(),
    reversible: boolean("reversible").notNull().default(false),
    detailsJson: json("detailsJson").$type<Record<string, unknown> | null>(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    occurrenceIdx: index("customer_merge_record_actions_occurrence_idx").on(
      t.occurrenceId
    ),
  })
);
export type CustomerMergeRecordAction =
  typeof customerMergeRecordActions.$inferSelect;
export type InsertCustomerMergeRecordAction =
  typeof customerMergeRecordActions.$inferInsert;

/**
 * Historical identity aliases so search / Clarity1 OTP resolve to the canonical customer
 * after merge without rewriting immutable history.
 */
export const customerIdentityAliases = mysqlTable(
  "customer_identity_aliases",
  {
    id: int("id").autoincrement().primaryKey(),
    canonicalCustomerId: int("canonicalCustomerId").notNull(),
    sourceCustomerId: int("sourceCustomerId").notNull(),
    aliasType: mysqlEnum("aliasType", [
      "name",
      "phone",
      "email",
      "xero_contact_id",
      "customer_id_redirect",
    ]).notNull(),
    aliasValue: varchar("aliasValue", { length: 512 }).notNull(),
    aliasValueNormalized: varchar("aliasValueNormalized", {
      length: 512,
    }).notNull(),
    mergeOccurrenceId: int("mergeOccurrenceId"),
    active: boolean("active").notNull().default(true),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    normIdx: index("customer_identity_aliases_norm_idx").on(
      t.aliasType,
      t.aliasValueNormalized,
      t.active
    ),
    canonicalIdx: index("customer_identity_aliases_canonical_idx").on(
      t.canonicalCustomerId
    ),
    sourceIdx: index("customer_identity_aliases_source_idx").on(
      t.sourceCustomerId
    ),
  })
);
export type CustomerIdentityAlias = typeof customerIdentityAliases.$inferSelect;
export type InsertCustomerIdentityAlias =
  typeof customerIdentityAliases.$inferInsert;

export const HISTORICAL_SVR_RECONCILIATION_APPLY_RUN_STATUSES = [
  "in_progress",
  "completed",
  "failed",
] as const;
export type HistoricalSvrReconciliationApplyRunStatus =
  (typeof HISTORICAL_SVR_RECONCILIATION_APPLY_RUN_STATUSES)[number];

/**
 * One governed unmatched-SVR reconciliation apply run for an exact dry-run fingerprint.
 * Fingerprint is unique to enforce idempotent reruns.
 */
export const historicalSvrReconciliationApplyRuns = mysqlTable(
  "historical_svr_reconciliation_apply_runs",
  {
    id: int("id").autoincrement().primaryKey(),
    dryRunFingerprint: varchar("dryRunFingerprint", { length: 64 }).notNull(),
    targetSetFingerprint: varchar("targetSetFingerprint", {
      length: 64,
    }).notNull(),
    resumeBaselineFingerprint: varchar("resumeBaselineFingerprint", {
      length: 64,
    }).notNull(),
    actorUserId: int("actorUserId"),
    actorName: varchar("actorName", { length: 255 }).notNull(),
    actorRole: varchar("actorRole", { length: 64 }).notNull(),
    actorSource: varchar("actorSource", { length: 64 })
      .notNull()
      .default("script"),
    approvedFamiliesJson: json("approvedFamiliesJson")
      .$type<unknown[]>()
      .notNull(),
    targetVisitIdsJson: json("targetVisitIdsJson").$type<number[]>().notNull(),
    targetVisitNumbersJson: json("targetVisitNumbersJson")
      .$type<string[]>()
      .notNull(),
    targetRowsJson: json("targetRowsJson").$type<unknown[]>().notNull(),
    status: mysqlEnum("status", [
      ...HISTORICAL_SVR_RECONCILIATION_APPLY_RUN_STATUSES,
    ])
      .notNull()
      .default("in_progress"),
    writesAttempted: int("writesAttempted").notNull().default(0),
    correspondenceSent: int("correspondenceSent").notNull().default(0),
    failureDetails: text("failureDetails"),
    resumeCount: int("resumeCount").notNull().default(0),
    lastFailureAt: timestamp("lastFailureAt"),
    lastResumedAt: timestamp("lastResumedAt"),
    leaseToken: varchar("leaseToken", { length: 64 }),
    leaseExpiresAt: timestamp("leaseExpiresAt"),
    completedAt: timestamp("completedAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    fingerprintTargetUq: uniqueIndex(
      "historical_svr_reconciliation_apply_runs_fingerprint_target_uq"
    ).on(t.dryRunFingerprint, t.targetSetFingerprint),
    statusIdx: index("historical_svr_reconciliation_apply_runs_status_idx").on(
      t.status,
      t.createdAt
    ),
  })
);
export type HistoricalSvrReconciliationApplyRun =
  typeof historicalSvrReconciliationApplyRuns.$inferSelect;
export type InsertHistoricalSvrReconciliationApplyRun =
  typeof historicalSvrReconciliationApplyRuns.$inferInsert;

/**
 * Immutable per-SVR reconciliation history. Unique visitId guarantees idempotent apply.
 */
export const historicalSvrReconciliationAppliedRows = mysqlTable(
  "historical_svr_reconciliation_applied_rows",
  {
    id: int("id").autoincrement().primaryKey(),
    runId: int("runId").notNull(),
    visitId: int("visitId").notNull(),
    visitNumber: varchar("visitNumber", { length: 64 }).notNull(),
    classification: varchar("classification", { length: 64 }).notNull(),
    dryRunFingerprint: varchar("dryRunFingerprint", { length: 64 }).notNull(),
    targetSetFingerprint: varchar("targetSetFingerprint", {
      length: 64,
    }).notNull(),
    approvedFamilyId: varchar("approvedFamilyId", { length: 191 }),
    mergeOccurrenceId: int("mergeOccurrenceId"),
    actorUserId: int("actorUserId"),
    actorName: varchar("actorName", { length: 255 }).notNull(),
    actorRole: varchar("actorRole", { length: 64 }).notNull(),
    actorSource: varchar("actorSource", { length: 64 })
      .notNull()
      .default("script"),
    previousCustomerId: int("previousCustomerId"),
    newCustomerId: int("newCustomerId").notNull(),
    previousSiteId: int("previousSiteId"),
    newSiteId: int("newSiteId").notNull(),
    createdSiteId: int("createdSiteId"),
    createdSiteStateJson: json("createdSiteStateJson").$type<Record<
      string,
      unknown
    > | null>(),
    previousPicId: int("previousPicId"),
    previousPicName: varchar("previousPicName", { length: 255 }),
    newPicId: int("newPicId"),
    newPicName: varchar("newPicName", { length: 255 }),
    exactEvidenceJson: json("exactEvidenceJson").$type<string[]>().notNull(),
    competingCandidatesJson: json("competingCandidatesJson")
      .$type<unknown[]>()
      .notNull(),
    beforeStateJson: json("beforeStateJson")
      .$type<Record<string, unknown>>()
      .notNull(),
    afterStateJson: json("afterStateJson")
      .$type<Record<string, unknown>>()
      .notNull(),
    unmatchedRawName: varchar("unmatchedRawName", { length: 255 }),
    unmatchedRawPhone: varchar("unmatchedRawPhone", { length: 50 }),
    unmatchedRawAddress: text("unmatchedRawAddress"),
    appliedAt: timestamp("appliedAt").defaultNow().notNull(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    visitUq: uniqueIndex(
      "historical_svr_reconciliation_applied_rows_visit_uq"
    ).on(t.visitId),
    runIdx: index("historical_svr_reconciliation_applied_rows_run_idx").on(
      t.runId,
      t.createdAt
    ),
    mergeIdx: index("historical_svr_reconciliation_applied_rows_merge_idx").on(
      t.mergeOccurrenceId
    ),
  })
);
export type HistoricalSvrReconciliationAppliedRow =
  typeof historicalSvrReconciliationAppliedRows.$inferSelect;
export type InsertHistoricalSvrReconciliationAppliedRow =
  typeof historicalSvrReconciliationAppliedRows.$inferInsert;

export const HISTORICAL_SVR_RECONCILIATION_APPLY_EVENT_TYPES = [
  "started",
  "resumed",
  "merge_completed",
  "svr_applied",
  "svr_already_applied",
  "failed",
  "completed",
] as const;
export type HistoricalSvrReconciliationApplyEventType =
  (typeof HISTORICAL_SVR_RECONCILIATION_APPLY_EVENT_TYPES)[number];

export const historicalSvrReconciliationApplyRunEvents = mysqlTable(
  "historical_svr_reconciliation_apply_run_events",
  {
    id: int("id").autoincrement().primaryKey(),
    runId: int("runId").notNull(),
    eventType: mysqlEnum("eventType", [
      ...HISTORICAL_SVR_RECONCILIATION_APPLY_EVENT_TYPES,
    ]).notNull(),
    visitId: int("visitId"),
    familyId: varchar("familyId", { length: 191 }),
    mergeOccurrenceId: int("mergeOccurrenceId"),
    detailsJson: json("detailsJson").$type<Record<string, unknown> | null>(),
    actorUserId: int("actorUserId"),
    actorName: varchar("actorName", { length: 255 }).notNull(),
    actorRole: varchar("actorRole", { length: 64 }).notNull(),
    actorSource: varchar("actorSource", { length: 64 })
      .notNull()
      .default("script"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    runIdx: index("historical_svr_reconciliation_apply_run_events_run_idx").on(
      t.runId,
      t.createdAt
    ),
    visitIdx: index(
      "historical_svr_reconciliation_apply_run_events_visit_idx"
    ).on(t.visitId),
  })
);
export type HistoricalSvrReconciliationApplyRunEvent =
  typeof historicalSvrReconciliationApplyRunEvents.$inferSelect;
export type InsertHistoricalSvrReconciliationApplyRunEvent =
  typeof historicalSvrReconciliationApplyRunEvents.$inferInsert;

/** Durable wallet reconciliation audit for customer merge (never deletes wallet history). */
export const kaleidescapeWalletReconcileOccurrences = mysqlTable(
  "kaleidescape_wallet_reconcile_occurrences",
  {
    id: int("id").autoincrement().primaryKey(),
    mergeOccurrenceId: int("mergeOccurrenceId").notNull(),
    mode: mysqlEnum("mode", [
      "duplicate_snapshot_continuation",
      "independent_balances",
    ]).notNull(),
    survivingWalletId: int("survivingWalletId").notNull(),
    retiredWalletId: int("retiredWalletId").notNull(),
    transferAmount: decimal("transferAmount", { precision: 12, scale: 2 })
      .notNull()
      .default("0.00"),
    survivingBeforeBalance: decimal("survivingBeforeBalance", {
      precision: 12,
      scale: 2,
    }).notNull(),
    retiredBeforeBalance: decimal("retiredBeforeBalance", {
      precision: 12,
      scale: 2,
    }).notNull(),
    survivingAfterBalance: decimal("survivingAfterBalance", {
      precision: 12,
      scale: 2,
    }).notNull(),
    beforeSnapshotsJson: json("beforeSnapshotsJson")
      .$type<Record<string, unknown>>()
      .notNull(),
    afterSnapshotsJson: json("afterSnapshotsJson")
      .$type<Record<string, unknown>>()
      .notNull(),
    idempotencyKey: varchar("idempotencyKey", { length: 128 }).notNull(),
    actorUserId: int("actorUserId").notNull(),
    reason: text("reason"),
    status: mysqlEnum("status", ["completed", "failed"])
      .notNull()
      .default("completed"),
    failureDetails: text("failureDetails"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    idempotencyUq: uniqueIndex(
      "kaleidescape_wallet_reconcile_idempotency_uq"
    ).on(t.idempotencyKey),
    mergeIdx: index("kaleidescape_wallet_reconcile_merge_idx").on(
      t.mergeOccurrenceId
    ),
  })
);
export type KaleidescapeWalletReconcileOccurrence =
  typeof kaleidescapeWalletReconcileOccurrences.$inferSelect;
export type InsertKaleidescapeWalletReconcileOccurrence =
  typeof kaleidescapeWalletReconcileOccurrences.$inferInsert;

// ─── Legacy PDF-wrapper item entry (migration 0193) ──────────────────────────

export const procurementPdfWrapperEntryBatches = mysqlTable(
  "procurement_pdf_wrapper_entry_batches",
  {
    id: int("id").autoincrement().primaryKey(),
    projectProcurementId: int("projectProcurementId").notNull(),
    proposalId: int("proposalId").notNull(),
    sourcePlaceholderLineId: int("sourcePlaceholderLineId").notNull(),
    clientIdempotencyKey: varchar("clientIdempotencyKey", {
      length: 191,
    }).notNull(),
    actorUserId: int("actorUserId"),
    actorName: varchar("actorName", { length: 255 }).notNull(),
    reason: text("reason"),
    expectedTotal: decimal("expectedTotal", { precision: 14, scale: 2 })
      .notNull()
      .default("0.00"),
    enteredTotal: decimal("enteredTotal", { precision: 14, scale: 2 })
      .notNull()
      .default("0.00"),
    adjustmentTotal: decimal("adjustmentTotal", { precision: 14, scale: 2 })
      .notNull()
      .default("0.00"),
    adjustmentsJson:
      json("adjustmentsJson").$type<Array<{ label: string; amount: number }>>(),
    lifecycle: mysqlEnum("lifecycle", ["active", "reversed"])
      .notNull()
      .default("active"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    reversedAt: timestamp("reversedAt"),
    reversedByUserId: int("reversedByUserId"),
    reversedReason: text("reversedReason"),
  },
  t => ({
    idempotencyUq: uniqueIndex(
      "procurement_pdf_wrapper_entry_batches_idempotency_uq"
    ).on(t.clientIdempotencyKey),
    procurementIdx: index(
      "procurement_pdf_wrapper_entry_batches_procurement_idx"
    ).on(t.projectProcurementId, t.lifecycle),
    placeholderIdx: index(
      "procurement_pdf_wrapper_entry_batches_placeholder_idx"
    ).on(t.sourcePlaceholderLineId, t.lifecycle),
  })
);

export const procurementPdfWrapperEntryBatchLines = mysqlTable(
  "procurement_pdf_wrapper_entry_batch_lines",
  {
    id: int("id").autoincrement().primaryKey(),
    batchId: int("batchId").notNull(),
    pickListLineId: int("pickListLineId").notNull(),
    sortOrder: int("sortOrder").notNull().default(0),
    productName: text("productName").notNull(),
    brand: varchar("brand", { length: 255 }),
    quantity: decimal("quantity", { precision: 12, scale: 3 })
      .notNull()
      .default("0"),
    uom: varchar("uom", { length: 64 }),
    soldUnitPrice: decimal("soldUnitPrice", { precision: 14, scale: 4 })
      .notNull()
      .default("0"),
    soldLineTotal: decimal("soldLineTotal", { precision: 14, scale: 2 })
      .notNull()
      .default("0.00"),
    sku: varchar("sku", { length: 255 }),
    finish: varchar("finish", { length: 255 }),
    systemName: varchar("systemName", { length: 255 }),
    roomName: varchar("roomName", { length: 255 }),
    procurementNote: text("procurementNote"),
    catalogProductId: int("catalogProductId"),
    disposition: mysqlEnum("disposition", [
      "needs_sourcing",
      "no_order_required",
    ])
      .notNull()
      .default("needs_sourcing"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    batchIdx: index("procurement_pdf_wrapper_entry_batch_lines_batch_idx").on(
      t.batchId,
      t.sortOrder
    ),
    pickIdx: index("procurement_pdf_wrapper_entry_batch_lines_pick_idx").on(
      t.pickListLineId
    ),
  })
);

export const procurementPdfWrapperEntryEvents = mysqlTable(
  "procurement_pdf_wrapper_entry_events",
  {
    id: int("id").autoincrement().primaryKey(),
    batchId: int("batchId").notNull(),
    projectProcurementId: int("projectProcurementId").notNull(),
    eventType: mysqlEnum("eventType", [
      "confirmed",
      "reversed",
      "blocked",
    ]).notNull(),
    actorUserId: int("actorUserId"),
    actorName: varchar("actorName", { length: 255 }).notNull(),
    payloadJson: json("payloadJson"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    batchIdx: index("procurement_pdf_wrapper_entry_events_batch_idx").on(
      t.batchId,
      t.createdAt
    ),
    procurementIdx: index(
      "procurement_pdf_wrapper_entry_events_procurement_idx"
    ).on(t.projectProcurementId, t.createdAt),
  })
);

export type ProcurementPdfWrapperEntryBatch =
  typeof procurementPdfWrapperEntryBatches.$inferSelect;
export type InsertProcurementPdfWrapperEntryBatch =
  typeof procurementPdfWrapperEntryBatches.$inferInsert;
export type ProcurementPdfWrapperEntryBatchLine =
  typeof procurementPdfWrapperEntryBatchLines.$inferSelect;
export type InsertProcurementPdfWrapperEntryBatchLine =
  typeof procurementPdfWrapperEntryBatchLines.$inferInsert;
export type ProcurementPdfWrapperEntryEvent =
  typeof procurementPdfWrapperEntryEvents.$inferSelect;
export type InsertProcurementPdfWrapperEntryEvent =
  typeof procurementPdfWrapperEntryEvents.$inferInsert;

// ─── Secure customer Link B (tokenized public authorization) ───────────────

export const proposalLinkBFamilyIssuances = mysqlTable(
  "proposal_link_b_family_issuances",
  {
    id: int("id").autoincrement().primaryKey(),
    familyRootProposalId: int("familyRootProposalId").notNull(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    createdByUserId: int("createdByUserId"),
  },
  t => ({
    familyRootUq: uniqueIndex("proposal_link_b_family_root_uq").on(
      t.familyRootProposalId
    ),
  })
);

export const proposalLinkBCredentials = mysqlTable(
  "proposal_link_b_credentials",
  {
    id: int("id").autoincrement().primaryKey(),
    proposalId: int("proposalId").notNull(),
    familyIssuanceId: int("familyIssuanceId").notNull(),
    issuanceSequence: int("issuanceSequence").notNull(),
    tokenHash: varchar("tokenHash", { length: 64 }).notNull(),
    /** AES-GCM ciphertext of normalized bearer token (recoverable for Copy/resend). */
    tokenCiphertext: text("tokenCiphertext").notNull(),
    tokenShortCode: varchar("tokenShortCode", { length: 8 }).notNull(),
    readableRefSnapshot: varchar("readableRefSnapshot", {
      length: 128,
    }).notNull(),
    issuedAt: timestamp("issuedAt").defaultNow().notNull(),
    issuedByUserId: int("issuedByUserId"),
    revokedAt: timestamp("revokedAt"),
    revokedReason: varchar("revokedReason", { length: 64 }),
    supersededByCredentialId: int("supersededByCredentialId"),
    lastAccessedAt: timestamp("lastAccessedAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    tokenHashUq: uniqueIndex("proposal_link_b_credentials_token_hash_uq").on(
      t.tokenHash
    ),
    proposalIdx: index("proposal_link_b_credentials_proposal_idx").on(
      t.proposalId
    ),
    familySeqIdx: index("proposal_link_b_credentials_family_seq_idx").on(
      t.familyIssuanceId,
      t.issuanceSequence
    ),
    activeProposalIdx: index(
      "proposal_link_b_credentials_active_proposal_idx"
    ).on(t.proposalId, t.revokedAt),
  })
);

export type ProposalLinkBFamilyIssuance =
  typeof proposalLinkBFamilyIssuances.$inferSelect;
export type InsertProposalLinkBFamilyIssuance =
  typeof proposalLinkBFamilyIssuances.$inferInsert;
export type ProposalLinkBCredential =
  typeof proposalLinkBCredentials.$inferSelect;
export type InsertProposalLinkBCredential =
  typeof proposalLinkBCredentials.$inferInsert;

// ─── Murho Document Bridge (Release 2) ───────────────────────────────────────

export const procurementMurhoDocuments = mysqlTable(
  "procurement_murho_documents",
  {
    id: int("id").autoincrement().primaryKey(),
    documentUuid: varchar("documentUuid", { length: 36 }).notNull(),
    checksumSha256: varchar("checksumSha256", { length: 64 }).notNull(),
    storageKey: varchar("storageKey", { length: 512 }).notNull(),
    originalFilename: varchar("originalFilename", { length: 512 }).notNull(),
    mimeType: varchar("mimeType", { length: 128 }).notNull(),
    byteSize: int("byteSize").notNull(),
    detectedDocumentType: mysqlEnum("detectedDocumentType", [
      "murho_po",
      "murho_inbound",
      "murho_delivery_order",
      "unknown",
    ])
      .notNull()
      .default("unknown"),
    confirmedDocumentType: mysqlEnum("confirmedDocumentType", [
      "murho_po",
      "murho_inbound",
      "murho_delivery_order",
    ]),
    murhoDocumentNumber: varchar("murhoDocumentNumber", { length: 128 }),
    murhoPoReference: varchar("murhoPoReference", { length: 128 }),
    documentDate: date("documentDate"),
    supplierNameSnapshot: varchar("supplierNameSnapshot", { length: 255 }),
    customerNameSnapshot: varchar("customerNameSnapshot", { length: 255 }),
    projectReferenceSnapshot: varchar("projectReferenceSnapshot", {
      length: 128,
    }),
    currencySnapshot: varchar("currencySnapshot", { length: 8 }),
    proposedProjectProcurementId: int("proposedProjectProcurementId"),
    confirmedProjectProcurementId: int("confirmedProjectProcurementId"),
    proposedPurchaseOrderId: int("proposedPurchaseOrderId"),
    confirmedPurchaseOrderId: int("confirmedPurchaseOrderId"),
    proposedMatchConfidence: decimal("proposedMatchConfidence", {
      precision: 5,
      scale: 4,
    }),
    proposedMatchReason: varchar("proposedMatchReason", { length: 255 }),
    matchMethod: varchar("matchMethod", { length: 64 }),
    extractionMethod: mysqlEnum("extractionMethod", [
      "excel_deterministic",
      "pdf_text",
      "pdf_manual",
      "manual",
      "none",
    ])
      .notNull()
      .default("none"),
    extractedPayloadJson: json("extractedPayloadJson").$type<Record<
      string,
      unknown
    > | null>(),
    extractionWarningsJson: json("extractionWarningsJson").$type<
      string[] | null
    >(),
    lifecycle: mysqlEnum("lifecycle", [
      "uploaded",
      "extracting",
      "needs_classification",
      "needs_review",
      "confirmed",
      "failed_retryable",
      "review_required",
      "reversed",
      "superseded",
    ])
      .notNull()
      .default("uploaded"),
    safeErrorCode: varchar("safeErrorCode", { length: 64 }),
    safeErrorMessage: varchar("safeErrorMessage", { length: 512 }),
    extractionAttemptCount: int("extractionAttemptCount").notNull().default(0),
    extractionLeaseToken: varchar("extractionLeaseToken", { length: 64 }),
    extractionLeaseExpiresAt: timestamp("extractionLeaseExpiresAt"),
    extractionEnqueuedAt: timestamp("extractionEnqueuedAt"),
    extractionNextRetryAt: timestamp("extractionNextRetryAt"),
    confirmationIdempotencyKey: varchar("confirmationIdempotencyKey", {
      length: 128,
    }),
    confirmationLeaseToken: varchar("confirmationLeaseToken", { length: 64 }),
    confirmationLeaseExpiresAt: timestamp("confirmationLeaseExpiresAt"),
    confirmationAttemptCount: int("confirmationAttemptCount")
      .notNull()
      .default(0),
    purchaseOrderLinkMode: mysqlEnum("purchaseOrderLinkMode", [
      "attached_existing",
      "created_new",
      "completed_existing_draft",
    ]),
    confirmedGoodsReceiptId: int("confirmedGoodsReceiptId"),
    confirmedCustomerDeliveryId: int("confirmedCustomerDeliveryId"),
    confirmedPurchaseOrderCreatedId: int("confirmedPurchaseOrderCreatedId"),
    mixedShipmentDetected: boolean("mixedShipmentDetected")
      .notNull()
      .default(false),
    uploadedByUserId: int("uploadedByUserId"),
    confirmedByUserId: int("confirmedByUserId"),
    confirmedAt: timestamp("confirmedAt"),
    reversedAt: timestamp("reversedAt"),
    reversedByUserId: int("reversedByUserId"),
    reversalReason: varchar("reversalReason", { length: 512 }),
    supersededByDocumentId: int("supersededByDocumentId"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    uuidUq: uniqueIndex("procurement_murho_documents_uuid_uq").on(
      t.documentUuid
    ),
    checksumUq: uniqueIndex("procurement_murho_documents_checksum_uq").on(
      t.checksumSha256
    ),
    confirmedTypeNumberUq: uniqueIndex(
      "procurement_murho_documents_confirmed_type_number_uq"
    ).on(t.confirmedDocumentType, t.murhoDocumentNumber),
    confirmIdempotencyUq: uniqueIndex(
      "procurement_murho_documents_confirm_idempotency_uq"
    ).on(t.confirmationIdempotencyKey),
    lifecycleIdx: index("procurement_murho_documents_lifecycle_idx").on(
      t.lifecycle,
      t.createdAt
    ),
    ppwIdx: index("procurement_murho_documents_ppw_idx").on(
      t.confirmedProjectProcurementId,
      t.lifecycle
    ),
    poIdx: index("procurement_murho_documents_po_idx").on(
      t.confirmedPurchaseOrderId,
      t.lifecycle
    ),
  })
);

export const procurementMurhoDocumentLines = mysqlTable(
  "procurement_murho_document_lines",
  {
    id: int("id").autoincrement().primaryKey(),
    documentId: int("documentId").notNull(),
    sourceSheetName: varchar("sourceSheetName", { length: 128 }),
    sourceRowNumber: int("sourceRowNumber"),
    sortOrder: int("sortOrder").notNull().default(0),
    skuSnapshot: varchar("skuSnapshot", { length: 255 }),
    actualPartNumberSnapshot: varchar("actualPartNumberSnapshot", {
      length: 255,
    }),
    brandSnapshot: varchar("brandSnapshot", { length: 255 }),
    modelSnapshot: varchar("modelSnapshot", { length: 255 }),
    descriptionSnapshot: text("descriptionSnapshot"),
    finishSnapshot: varchar("finishSnapshot", { length: 128 }),
    uomSnapshot: varchar("uomSnapshot", { length: 64 }),
    orderedQuantity: decimal("orderedQuantity", { precision: 14, scale: 3 }),
    receivedQuantity: decimal("receivedQuantity", { precision: 14, scale: 3 }),
    deliveredQuantity: decimal("deliveredQuantity", {
      precision: 14,
      scale: 3,
    }),
    confirmedQuantity: decimal("confirmedQuantity", {
      precision: 14,
      scale: 3,
    }),
    unitPriceSnapshot: decimal("unitPriceSnapshot", {
      precision: 14,
      scale: 4,
    }),
    serialNumbersJson: json("serialNumbersJson").$type<string[] | null>(),
    remarksSnapshot: text("remarksSnapshot"),
    proposedPickListLineId: int("proposedPickListLineId"),
    confirmedPickListLineId: int("confirmedPickListLineId"),
    proposedPurchaseOrderLineId: int("proposedPurchaseOrderLineId"),
    confirmedPurchaseOrderLineId: int("confirmedPurchaseOrderLineId"),
    matchConfidence: decimal("matchConfidence", { precision: 5, scale: 4 }),
    matchMethod: varchar("matchMethod", { length: 64 }),
    matchReason: varchar("matchReason", { length: 255 }),
    selected: boolean("selected").notNull().default(true),
    condition: varchar("condition", { length: 32 }),
    createWarrantySelected: boolean("createWarrantySelected")
      .notNull()
      .default(true),
    lineWarningsJson: json("lineWarningsJson").$type<string[] | null>(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    docIdx: index("procurement_murho_document_lines_doc_idx").on(
      t.documentId,
      t.sortOrder
    ),
  })
);

export const procurementMurhoDocumentEvents = mysqlTable(
  "procurement_murho_document_events",
  {
    id: int("id").autoincrement().primaryKey(),
    documentId: int("documentId").notNull(),
    eventType: varchar("eventType", { length: 64 }).notNull(),
    actorUserId: int("actorUserId"),
    actorName: varchar("actorName", { length: 255 }),
    actorRole: varchar("actorRole", { length: 64 }),
    previousLifecycle: varchar("previousLifecycle", { length: 32 }),
    newLifecycle: varchar("newLifecycle", { length: 32 }),
    metadataJson: json("metadataJson").$type<Record<string, unknown> | null>(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    docCreatedIdx: index(
      "procurement_murho_document_events_doc_created_idx"
    ).on(t.documentId, t.createdAt),
  })
);

export type ProcurementMurhoDocument =
  typeof procurementMurhoDocuments.$inferSelect;
export type InsertProcurementMurhoDocument =
  typeof procurementMurhoDocuments.$inferInsert;
export type ProcurementMurhoDocumentLine =
  typeof procurementMurhoDocumentLines.$inferSelect;
export type InsertProcurementMurhoDocumentLine =
  typeof procurementMurhoDocumentLines.$inferInsert;
export type ProcurementMurhoDocumentEvent =
  typeof procurementMurhoDocumentEvents.$inferSelect;

// ─── Procurement Correspondence Inbox (migration 0208) ───────────────────────

export const procurementCorrespondenceMailboxConnections = mysqlTable(
  "procurement_correspondence_mailbox_connections",
  {
    id: int("id").autoincrement().primaryKey(),
    provider: varchar("provider", { length: 32 }).notNull().default("gmail"),
    mailboxEmail: varchar("mailboxEmail", { length: 320 }),
    lifecycle: mysqlEnum("lifecycle", [
      "disconnected",
      "connected_disabled",
      "enabled",
      "paused",
      "attention_required",
    ])
      .notNull()
      .default("disconnected"),
    refreshTokenCiphertext: text("refreshTokenCiphertext"),
    refreshTokenIv: varchar("refreshTokenIv", { length: 64 }),
    refreshTokenAuthTag: varchar("refreshTokenAuthTag", { length: 64 }),
    grantedScopesJson: json("grantedScopesJson").$type<string[] | null>(),
    currentHistoryId: varchar("currentHistoryId", { length: 64 }),
    importStartDate: date("importStartDate"),
    initialImportCompletedAt: timestamp("initialImportCompletedAt"),
    lastPollAttemptedAt: timestamp("lastPollAttemptedAt"),
    lastPollSucceededAt: timestamp("lastPollSucceededAt"),
    lastSafeErrorCode: varchar("lastSafeErrorCode", { length: 64 }),
    pollLeaseToken: varchar("pollLeaseToken", { length: 64 }),
    pollLeaseExpiresAt: timestamp("pollLeaseExpiresAt"),
    lastPreviewJson: json("lastPreviewJson").$type<Record<
      string,
      unknown
    > | null>(),
    lastPreviewAt: timestamp("lastPreviewAt"),
    importLifecycle: mysqlEnum("importLifecycle", [
      "idle",
      "previewed",
      "importing",
      "completed",
      "failed",
      "review_required",
    ])
      .notNull()
      .default("idle"),
    importPageToken: varchar("importPageToken", { length: 256 }),
    importLastSucceededAt: timestamp("importLastSucceededAt"),
    importConfirmedAt: timestamp("importConfirmedAt"),
    importMessagesProcessed: int("importMessagesProcessed")
      .notNull()
      .default(0),
    /** 1 while not disconnected; NULL when disconnected so MySQL unique permits many historical rows. */
    activeSlot: int("activeSlot"),
    connectedByUserId: int("connectedByUserId"),
    connectedAt: timestamp("connectedAt"),
    enabledByUserId: int("enabledByUserId"),
    enabledAt: timestamp("enabledAt"),
    pausedByUserId: int("pausedByUserId"),
    pausedAt: timestamp("pausedAt"),
    disconnectedByUserId: int("disconnectedByUserId"),
    disconnectedAt: timestamp("disconnectedAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    activeProviderUq: uniqueIndex(
      "procurement_coin_mailbox_active_provider_uq"
    ).on(t.provider, t.activeSlot),
    lifecycleIdx: index("procurement_coin_mailbox_lifecycle_idx").on(
      t.lifecycle,
      t.updatedAt
    ),
  })
);

export const procurementCorrespondenceOauthStates = mysqlTable(
  "procurement_correspondence_oauth_states",
  {
    id: int("id").autoincrement().primaryKey(),
    stateHash: varchar("stateHash", { length: 128 }).notNull(),
    initiatingAdminUserId: int("initiatingAdminUserId").notNull(),
    expiresAt: timestamp("expiresAt").notNull(),
    consumedAt: timestamp("consumedAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    stateHashUq: uniqueIndex("procurement_coin_oauth_state_hash_uq").on(
      t.stateHash
    ),
    adminCreatedIdx: index("procurement_coin_oauth_admin_created_idx").on(
      t.initiatingAdminUserId,
      t.createdAt
    ),
  })
);

export const procurementCorrespondenceMessages = mysqlTable(
  "procurement_correspondence_messages",
  {
    id: int("id").autoincrement().primaryKey(),
    connectionId: int("connectionId").notNull(),
    gmailMessageId: varchar("gmailMessageId", { length: 128 }).notNull(),
    gmailThreadId: varchar("gmailThreadId", { length: 128 }).notNull(),
    rfcMessageId: varchar("rfcMessageId", { length: 512 }),
    inReplyTo: varchar("inReplyTo", { length: 512 }),
    referencesHeader: text("referencesHeader"),
    messageDate: timestamp("messageDate"),
    direction: mysqlEnum("direction", ["inbound", "copied_outbound", "unknown"])
      .notNull()
      .default("unknown"),
    senderDisplay: varchar("senderDisplay", { length: 255 }),
    senderNormalized: varchar("senderNormalized", { length: 320 }),
    toNormalizedJson: json("toNormalizedJson").$type<string[] | null>(),
    ccNormalizedJson: json("ccNormalizedJson").$type<string[] | null>(),
    subject: varchar("subject", { length: 998 }),
    escapedPlainTextPreview: varchar("escapedPlainTextPreview", {
      length: 512,
    }),
    sanitizedPlainTextBody: mediumtext("sanitizedPlainTextBody"),
    attachmentCount: int("attachmentCount").notNull().default(0),
    gmailHistoryId: varchar("gmailHistoryId", { length: 64 }),
    ingestionMode: mysqlEnum("ingestionMode", [
      "live_poll",
      "historical_import",
    ])
      .notNull()
      .default("live_poll"),
    checksumSha256: varchar("checksumSha256", { length: 64 }).notNull(),
    safeErrorCode: varchar("safeErrorCode", { length: 64 }),
    lifecycle: mysqlEnum("lifecycle", [
      "imported",
      "matching",
      "matched",
      "needs_review",
      "processed",
      "failed_retryable",
      "review_required",
    ])
      .notNull()
      .default("imported"),
    matchedPurchaseOrderId: int("matchedPurchaseOrderId"),
    matchedProjectProcurementId: int("matchedProjectProcurementId"),
    matchMethod: varchar("matchMethod", { length: 64 }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    connectionGmailUq: uniqueIndex(
      "procurement_coin_messages_connection_gmail_uq"
    ).on(t.connectionId, t.gmailMessageId),
    threadIdx: index("procurement_coin_messages_thread_idx").on(
      t.gmailThreadId,
      t.messageDate
    ),
    lifecycleIdx: index("procurement_coin_messages_lifecycle_idx").on(
      t.lifecycle,
      t.createdAt
    ),
    poIdx: index("procurement_coin_messages_po_idx").on(
      t.matchedPurchaseOrderId,
      t.messageDate
    ),
  })
);

export const procurementCorrespondenceAttachments = mysqlTable(
  "procurement_correspondence_attachments",
  {
    id: int("id").autoincrement().primaryKey(),
    messageId: int("messageId").notNull(),
    // Gmail attachment IDs are opaque ASCII tokens and can exceed 256 chars.
    // Production stores this column with an ASCII collation so its composite
    // unique index stays within MySQL's key-byte limit.
    gmailAttachmentId: varchar("gmailAttachmentId", { length: 1024 }).notNull(),
    originalFilename: varchar("originalFilename", { length: 512 }).notNull(),
    safeFilename: varchar("safeFilename", { length: 512 }).notNull(),
    mimeType: varchar("mimeType", { length: 128 }).notNull(),
    byteSize: int("byteSize").notNull(),
    actualByteSize: int("actualByteSize"),
    checksumSha256: varchar("checksumSha256", { length: 64 }),
    storageKey: varchar("storageKey", { length: 512 }),
    extractionLifecycle: mysqlEnum("extractionLifecycle", [
      "pending",
      "extracted",
      "unsupported",
      "oversized",
      "failed_retryable",
      "review_required",
    ])
      .notNull()
      .default("pending"),
    safeErrorCode: varchar("safeErrorCode", { length: 64 }),
    extractedSafeText: mediumtext("extractedSafeText"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    messageGmailUq: uniqueIndex(
      "procurement_coin_attachments_message_gmail_uq"
    ).on(t.messageId, t.gmailAttachmentId),
    messageIdx: index("procurement_coin_attachments_message_idx").on(
      t.messageId,
      t.extractionLifecycle
    ),
  })
);

export const procurementCorrespondenceThreadBindings = mysqlTable(
  "procurement_correspondence_thread_bindings",
  {
    id: int("id").autoincrement().primaryKey(),
    gmailThreadId: varchar("gmailThreadId", { length: 128 }).notNull(),
    purchaseOrderId: int("purchaseOrderId").notNull(),
    projectProcurementId: int("projectProcurementId"),
    source: mysqlEnum("source", [
      "staff_confirmed",
      "exact_po_reference",
    ]).notNull(),
    confidence: decimal("confidence", { precision: 5, scale: 4 }),
    active: boolean("active").notNull().default(true),
    activeThreadKey: varchar("activeThreadKey", {
      length: 128,
    }).generatedAlwaysAs(
      sql`CASE WHEN \`active\` = true THEN \`gmailThreadId\` ELSE NULL END`,
      { mode: "stored" }
    ),
    boundByUserId: int("boundByUserId"),
    boundByName: varchar("boundByName", { length: 255 }),
    boundAt: timestamp("boundAt"),
    reversedByUserId: int("reversedByUserId"),
    reversedByName: varchar("reversedByName", { length: 255 }),
    reversedAt: timestamp("reversedAt"),
    reversalReason: varchar("reversalReason", { length: 512 }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    threadIdx: index("procurement_coin_thread_bindings_thread_idx").on(
      t.gmailThreadId,
      t.active
    ),
    poIdx: index("procurement_coin_thread_bindings_po_idx").on(
      t.purchaseOrderId,
      t.active
    ),
    activeThreadUq: uniqueIndex(
      "procurement_coin_thread_bindings_active_uq"
    ).on(t.activeThreadKey),
  })
);

export const procurementCorrespondenceMatchCandidates = mysqlTable(
  "procurement_correspondence_match_candidates",
  {
    id: int("id").autoincrement().primaryKey(),
    messageId: int("messageId").notNull(),
    purchaseOrderId: int("purchaseOrderId"),
    projectProcurementId: int("projectProcurementId"),
    method: varchar("method", { length: 64 }).notNull(),
    confidence: decimal("confidence", { precision: 5, scale: 4 }),
    safeReason: varchar("safeReason", { length: 512 }).notNull(),
    lifecycle: mysqlEnum("lifecycle", [
      "proposed",
      "accepted",
      "rejected",
      "superseded",
    ])
      .notNull()
      .default("proposed"),
    reviewedByUserId: int("reviewedByUserId"),
    reviewedByName: varchar("reviewedByName", { length: 255 }),
    reviewedAt: timestamp("reviewedAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    messageIdx: index("procurement_coin_match_candidates_message_idx").on(
      t.messageId,
      t.lifecycle
    ),
    identityUq: uniqueIndex("procurement_coin_match_candidates_identity_uq").on(
      t.messageId,
      t.purchaseOrderId,
      t.method
    ),
  })
);

export const procurementCorrespondenceInterpretations = mysqlTable(
  "procurement_correspondence_interpretations",
  {
    id: int("id").autoincrement().primaryKey(),
    messageId: int("messageId").notNull(),
    purchaseOrderId: int("purchaseOrderId"),
    proposedOperationalStatus: mysqlEnum("proposedOperationalStatus", [
      "po_being_prepared",
      "po_recorded",
      "po_sent",
      "waiting_for_supplier",
      "awaiting_payment",
      "ready_for_shipment",
      "shipped",
      "partially_received",
      "received",
      "problem_follow_up",
    ]),
    plainLanguageSummary: text("plainLanguageSummary").notNull(),
    supplierInvoiceFlag: boolean("supplierInvoiceFlag")
      .notNull()
      .default(false),
    paymentRequestFlag: boolean("paymentRequestFlag").notNull().default(false),
    extractedInvoiceNumber: varchar("extractedInvoiceNumber", { length: 128 }),
    extractedInvoiceDate: date("extractedInvoiceDate"),
    extractedAmount: decimal("extractedAmount", { precision: 14, scale: 2 }),
    extractedCurrency: varchar("extractedCurrency", { length: 8 }),
    extractedDueDate: date("extractedDueDate"),
    supplierReadyDate: date("supplierReadyDate"),
    expectedShipmentDate: date("expectedShipmentDate"),
    carrier: varchar("carrier", { length: 128 }),
    trackingNumber: varchar("trackingNumber", { length: 128 }),
    partialShipmentFlag: boolean("partialShipmentFlag")
      .notNull()
      .default(false),
    problemFollowUpFlag: boolean("problemFollowUpFlag")
      .notNull()
      .default(false),
    fieldConfidenceJson: json("fieldConfidenceJson").$type<Record<
      string,
      string
    > | null>(),
    safeExplanation: varchar("safeExplanation", { length: 512 }).notNull(),
    interpreterVersion: varchar("interpreterVersion", { length: 32 }).notNull(),
    lifecycle: mysqlEnum("lifecycle", [
      "proposed",
      "confirmed",
      "rejected",
      "superseded",
    ])
      .notNull()
      .default("proposed"),
    applicationMode: mysqlEnum("applicationMode", [
      "staff_confirmed",
      "cleo_automatic",
    ]),
    originalProposedJson: json("originalProposedJson").$type<Record<
      string,
      unknown
    > | null>(),
    confirmedEditedJson: json("confirmedEditedJson").$type<Record<
      string,
      unknown
    > | null>(),
    reviewedByUserId: int("reviewedByUserId"),
    reviewedByName: varchar("reviewedByName", { length: 255 }),
    reviewedAt: timestamp("reviewedAt"),
    acknowledgedAt: timestamp("acknowledgedAt"),
    acknowledgedByUserId: int("acknowledgedByUserId"),
    acknowledgedByName: varchar("acknowledgedByName", { length: 255 }),
    rejectionReason: varchar("rejectionReason", { length: 512 }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    messageIdx: index("procurement_coin_interpretations_message_idx").on(
      t.messageId,
      t.lifecycle
    ),
    poIdx: index("procurement_coin_interpretations_po_idx").on(
      t.purchaseOrderId,
      t.lifecycle
    ),
    unacknowledgedIdx: index(
      "procurement_coin_interpretations_unacknowledged_idx"
    ).on(t.applicationMode, t.acknowledgedAt, t.reviewedAt),
    messageUq: uniqueIndex("procurement_coin_interpretations_message_uq").on(
      t.messageId
    ),
  })
);

export const procurementCorrespondenceIntelligence = mysqlTable(
  "procurement_correspondence_intelligence",
  {
    id: int("id").autoincrement().primaryKey(),
    messageId: int("messageId").notNull(),
    relevance: mysqlEnum("relevance", [
      "procurement_related",
      "unrelated",
      "uncertain",
    ]).notNull(),
    confidence: decimal("confidence", { precision: 5, scale: 4 }).notNull(),
    intent: varchar("intent", { length: 64 }).notNull(),
    supplierName: varchar("supplierName", { length: 255 }),
    extractedReferencesJson: json("extractedReferencesJson").$type<
      string[] | null
    >(),
    evidenceJson: json("evidenceJson").$type<string[] | null>(),
    safeExplanation: varchar("safeExplanation", { length: 512 }).notNull(),
    recommendedAction: varchar("recommendedAction", { length: 64 }).notNull(),
    modelProvider: varchar("modelProvider", { length: 32 })
      .notNull()
      .default("openai"),
    interpreterVersion: varchar("interpreterVersion", { length: 32 }).notNull(),
    failureCode: varchar("failureCode", { length: 64 }),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    messageUq: uniqueIndex("procurement_coin_intelligence_message_uq").on(
      t.messageId
    ),
    relevanceIdx: index("procurement_coin_intelligence_relevance_idx").on(
      t.relevance,
      t.createdAt
    ),
  })
);

export const procurementCorrespondenceReviewItems = mysqlTable(
  "procurement_correspondence_review_items",
  {
    id: int("id").autoincrement().primaryKey(),
    messageId: int("messageId"),
    interpretationId: int("interpretationId"),
    purchaseOrderId: int("purchaseOrderId"),
    projectProcurementId: int("projectProcurementId"),
    connectionId: int("connectionId"),
    owner: mysqlEnum("owner", ["procurement", "finance", "admin"]).notNull(),
    kind: mysqlEnum("kind", [
      "unmatched_email",
      "ambiguous_po",
      "unreadable_attachment",
      "proposed_status",
      "supplier_invoice",
      "payment_request",
      "processing_failure",
      "mailbox_failure",
    ]).notNull(),
    title: varchar("title", { length: 255 }).notNull(),
    plainLanguageReason: varchar("plainLanguageReason", {
      length: 512,
    }).notNull(),
    lifecycle: mysqlEnum("lifecycle", [
      "open",
      "acknowledged",
      "resolved",
      "superseded",
    ])
      .notNull()
      .default("open"),
    occurrenceKey: varchar("occurrenceKey", { length: 191 }).notNull(),
    acknowledgedByUserId: int("acknowledgedByUserId"),
    acknowledgedAt: timestamp("acknowledgedAt"),
    resolvedByUserId: int("resolvedByUserId"),
    resolvedAt: timestamp("resolvedAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    occurrenceUq: uniqueIndex("procurement_coin_review_items_occurrence_uq").on(
      t.occurrenceKey
    ),
    ownerLifecycleIdx: index(
      "procurement_coin_review_items_owner_lifecycle_idx"
    ).on(t.owner, t.lifecycle, t.createdAt),
    messageIdx: index("procurement_coin_review_items_message_idx").on(
      t.messageId,
      t.lifecycle
    ),
  })
);

export const procurementCorrespondenceJobs = mysqlTable(
  "procurement_correspondence_jobs",
  {
    id: int("id").autoincrement().primaryKey(),
    jobKind: mysqlEnum("jobKind", [
      "poll",
      "historical_import",
      "ingest_message",
      "extract_attachment",
      "match_message",
      "interpret_message",
    ]).notNull(),
    connectionId: int("connectionId"),
    messageId: int("messageId"),
    attachmentId: int("attachmentId"),
    lifecycle: mysqlEnum("lifecycle", [
      "pending",
      "processing",
      "retry",
      "completed",
      "review_required",
    ])
      .notNull()
      .default("pending"),
    leaseToken: varchar("leaseToken", { length: 64 }),
    leaseExpiresAt: timestamp("leaseExpiresAt"),
    attemptCount: int("attemptCount").notNull().default(0),
    nextRetryAt: timestamp("nextRetryAt"),
    safeErrorCode: varchar("safeErrorCode", { length: 64 }),
    idempotencyKey: varchar("idempotencyKey", { length: 191 }).notNull(),
    progressJson: json("progressJson").$type<Record<string, unknown> | null>(),
    completedAt: timestamp("completedAt"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    idempotencyUq: uniqueIndex("procurement_coin_jobs_idempotency_uq").on(
      t.idempotencyKey
    ),
    statusRetryIdx: index("procurement_coin_jobs_status_retry_idx").on(
      t.lifecycle,
      t.nextRetryAt
    ),
    statusLeaseIdx: index("procurement_coin_jobs_status_lease_idx").on(
      t.lifecycle,
      t.leaseExpiresAt
    ),
  })
);

export type ProcurementCorrespondenceMailboxConnection =
  typeof procurementCorrespondenceMailboxConnections.$inferSelect;
export type InsertProcurementCorrespondenceMailboxConnection =
  typeof procurementCorrespondenceMailboxConnections.$inferInsert;
export type ProcurementCorrespondenceOauthState =
  typeof procurementCorrespondenceOauthStates.$inferSelect;
export type ProcurementCorrespondenceMessage =
  typeof procurementCorrespondenceMessages.$inferSelect;
export type InsertProcurementCorrespondenceMessage =
  typeof procurementCorrespondenceMessages.$inferInsert;
export type ProcurementCorrespondenceAttachment =
  typeof procurementCorrespondenceAttachments.$inferSelect;
export type ProcurementCorrespondenceThreadBinding =
  typeof procurementCorrespondenceThreadBindings.$inferSelect;
export type ProcurementCorrespondenceMatchCandidate =
  typeof procurementCorrespondenceMatchCandidates.$inferSelect;
export type ProcurementCorrespondenceInterpretation =
  typeof procurementCorrespondenceInterpretations.$inferSelect;
export type ProcurementCorrespondenceReviewItem =
  typeof procurementCorrespondenceReviewItems.$inferSelect;
export type ProcurementCorrespondenceJob =
  typeof procurementCorrespondenceJobs.$inferSelect;

// ─── Legacy procurement / delivery intake (migration 0240) ──────────────────
// Pre-Portal supplier orders and customer DOs have no accepted Portal proposal.
// Keep them explicit instead of manufacturing placeholder commercial records.
export const procurementLegacyCases = mysqlTable(
  "procurement_legacy_cases",
  {
    id: int("id").autoincrement().primaryKey(),
    sourceType: mysqlEnum("sourceType", ["coin_email", "manual_do"]).notNull(),
    sourceMessageId: int("sourceMessageId"),
    gmailThreadId: varchar("gmailThreadId", { length: 128 }),
    customerId: int("customerId").notNull(),
    customerSiteId: int("customerSiteId"),
    reference: varchar("reference", { length: 255 }),
    title: varchar("title", { length: 512 }).notNull(),
    status: mysqlEnum("status", [
      "tracking",
      "delivered",
      "completed",
      "cancelled",
    ])
      .notNull()
      .default("tracking"),
    createdByUserId: int("createdByUserId").notNull(),
    createdByName: varchar("createdByName", { length: 255 }).notNull(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    sourceMessageUq: uniqueIndex(
      "procurement_legacy_cases_source_message_uq"
    ).on(t.sourceMessageId),
    threadIdx: index("procurement_legacy_cases_thread_idx").on(
      t.gmailThreadId,
      t.status
    ),
    customerIdx: index("procurement_legacy_cases_customer_idx").on(
      t.customerId,
      t.status
    ),
  })
);

export const procurementLegacyDeliveries = mysqlTable(
  "procurement_legacy_deliveries",
  {
    id: int("id").autoincrement().primaryKey(),
    legacyCaseId: int("legacyCaseId"),
    murhoDocumentId: int("murhoDocumentId").notNull(),
    customerId: int("customerId").notNull(),
    customerSiteId: int("customerSiteId"),
    deliveryDate: date("deliveryDate").notNull(),
    reference: varchar("reference", { length: 255 }),
    confirmedByUserId: int("confirmedByUserId").notNull(),
    confirmedByName: varchar("confirmedByName", { length: 255 }).notNull(),
    confirmedAt: timestamp("confirmedAt").defaultNow().notNull(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    documentUq: uniqueIndex("procurement_legacy_deliveries_document_uq").on(
      t.murhoDocumentId
    ),
    customerIdx: index("procurement_legacy_deliveries_customer_idx").on(
      t.customerId,
      t.deliveryDate
    ),
  })
);

export const procurementLegacyDeliveryLines = mysqlTable(
  "procurement_legacy_delivery_lines",
  {
    id: int("id").autoincrement().primaryKey(),
    legacyDeliveryId: int("legacyDeliveryId").notNull(),
    murhoDocumentLineId: int("murhoDocumentLineId").notNull(),
    sortOrder: int("sortOrder").notNull().default(0),
    brand: varchar("brand", { length: 255 }),
    model: varchar("model", { length: 255 }),
    description: text("description").notNull(),
    finish: varchar("finish", { length: 255 }),
    sku: varchar("sku", { length: 255 }),
    quantity: decimal("quantity", { precision: 14, scale: 3 }).notNull(),
    uom: varchar("uom", { length: 64 }),
    serialNumbersJson: json("serialNumbersJson").$type<string[] | null>(),
    createWarrantySelected: boolean("createWarrantySelected")
      .notNull()
      .default(true),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    deliveryIdx: index("procurement_legacy_delivery_lines_delivery_idx").on(
      t.legacyDeliveryId,
      t.sortOrder
    ),
    sourceLineUq: uniqueIndex("procurement_legacy_delivery_lines_source_uq").on(
      t.legacyDeliveryId,
      t.murhoDocumentLineId
    ),
  })
);

export const procurementLegacyEvents = mysqlTable(
  "procurement_legacy_events",
  {
    id: int("id").autoincrement().primaryKey(),
    legacyCaseId: int("legacyCaseId"),
    legacyDeliveryId: int("legacyDeliveryId"),
    eventType: varchar("eventType", { length: 64 }).notNull(),
    actorUserId: int("actorUserId").notNull(),
    actorName: varchar("actorName", { length: 255 }).notNull(),
    metadataJson: json("metadataJson").$type<Record<string, unknown> | null>(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    caseIdx: index("procurement_legacy_events_case_idx").on(
      t.legacyCaseId,
      t.createdAt
    ),
    deliveryIdx: index("procurement_legacy_events_delivery_idx").on(
      t.legacyDeliveryId,
      t.createdAt
    ),
  })
);

export type ProcurementLegacyCase = typeof procurementLegacyCases.$inferSelect;
export type ProcurementLegacyDelivery =
  typeof procurementLegacyDeliveries.$inferSelect;
export type ProcurementLegacyDeliveryLine =
  typeof procurementLegacyDeliveryLines.$inferSelect;

// ─── Mass DO workbook staging (migration 0248) ─────────────────────────────
// This is an evidence/review boundary. Preview and reconciliation are non-
// mutating; only the separate, Admin-confirmed pilot authority may commit rows.
export const procurementMassDoBatches = mysqlTable(
  "procurement_mass_do_batches",
  {
    id: int("id").autoincrement().primaryKey(),
    batchUuid: varchar("batchUuid", { length: 36 }).notNull(),
    checksumSha256: varchar("checksumSha256", { length: 64 }).notNull(),
    originalFilename: varchar("originalFilename", { length: 512 }).notNull(),
    storageKey: varchar("storageKey", { length: 1000 }).notNull(),
    byteSize: int("byteSize").notNull(),
    lifecycle: mysqlEnum("lifecycle", [
      "staged",
      "review_required",
      "ready",
      "committed",
      "cancelled",
    ])
      .notNull()
      .default("staged"),
    notificationMode: mysqlEnum("notificationMode", [
      "suppressed_until_pilot_approved",
    ])
      .notNull()
      .default("suppressed_until_pilot_approved"),
    summaryJson: json("summaryJson").$type<Record<string, number>>().notNull(),
    warningsJson: json("warningsJson").$type<string[] | null>(),
    uploadedByUserId: int("uploadedByUserId").notNull(),
    uploadedByName: varchar("uploadedByName", { length: 255 }).notNull(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    uuidUq: uniqueIndex("procurement_mass_do_batches_uuid_uq").on(t.batchUuid),
    checksumUq: uniqueIndex("procurement_mass_do_batches_checksum_uq").on(
      t.checksumSha256
    ),
    lifecycleIdx: index("procurement_mass_do_batches_lifecycle_idx").on(
      t.lifecycle,
      t.createdAt
    ),
  })
);

export const procurementMassDoRows = mysqlTable(
  "procurement_mass_do_rows",
  {
    id: int("id").autoincrement().primaryKey(),
    batchId: int("batchId").notNull(),
    fingerprint: varchar("fingerprint", { length: 64 }).notNull(),
    sheetName: varchar("sheetName", { length: 255 }).notNull(),
    sourceRowNumber: int("sourceRowNumber").notNull(),
    sourceJson: json("sourceJson")
      .$type<Record<string, string | number | boolean | null>>()
      .notNull(),
    doReference: varchar("doReference", { length: 255 }),
    deliveryDate: date("deliveryDate"),
    customerText: varchar("customerText", { length: 255 }),
    siteText: text("siteText"),
    brand: varchar("brand", { length: 255 }),
    model: varchar("model", { length: 255 }),
    sku: varchar("sku", { length: 255 }),
    description: text("description"),
    finish: varchar("finish", { length: 255 }),
    quantity: decimal("quantity", { precision: 14, scale: 3 }),
    uom: varchar("uom", { length: 64 }),
    serialsJson: json("serialsJson").$type<string[] | null>(),
    issuesJson: json("issuesJson").$type<string[] | null>(),
    lifecycle: mysqlEnum("lifecycle", [
      "ready",
      "review_required",
      "skipped",
      "committed",
    ])
      .notNull()
      .default("review_required"),
    resolvedCustomerId: int("resolvedCustomerId"),
    resolvedCustomerSiteId: int("resolvedCustomerSiteId"),
    resolvedCatalogProductId: int("resolvedCatalogProductId"),
    resolvedByUserId: int("resolvedByUserId"),
    resolvedAt: timestamp("resolvedAt"),
    committedLegacyDeliveryId: int("committedLegacyDeliveryId"),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
    updatedAt: timestamp("updatedAt").defaultNow().onUpdateNow().notNull(),
  },
  t => ({
    sourceUq: uniqueIndex("procurement_mass_do_rows_source_uq").on(
      t.batchId,
      t.sheetName,
      t.sourceRowNumber
    ),
    lifecycleIdx: index("procurement_mass_do_rows_lifecycle_idx").on(
      t.batchId,
      t.lifecycle
    ),
    doIdx: index("procurement_mass_do_rows_do_idx").on(
      t.batchId,
      t.doReference
    ),
  })
);

export const procurementMassDoEvents = mysqlTable(
  "procurement_mass_do_events",
  {
    id: int("id").autoincrement().primaryKey(),
    batchId: int("batchId").notNull(),
    eventType: varchar("eventType", { length: 64 }).notNull(),
    actorUserId: int("actorUserId").notNull(),
    actorName: varchar("actorName", { length: 255 }).notNull(),
    metadataJson: json("metadataJson").$type<Record<string, unknown> | null>(),
    createdAt: timestamp("createdAt").defaultNow().notNull(),
  },
  t => ({
    batchIdx: index("procurement_mass_do_events_batch_idx").on(
      t.batchId,
      t.createdAt
    ),
  })
);

export type ProcurementMassDoBatch =
  typeof procurementMassDoBatches.$inferSelect;
export type ProcurementMassDoRow = typeof procurementMassDoRows.$inferSelect;

